import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PizzaSaleModeSelector from "../menu/PizzaSaleModeSelector";
import OrderBillActions from "../common/OrderBillActions";
import { getOrderDiscountBlockReason, previewOrderDiscount } from "../../services/local/orderDiscount";
import { getDefaultPizzaSaleMode, getPizzaStartingPrice, getPizzaSizePriceSummary, getPizzaPortionScale, isPizzaSaleModeEnabled, validatePizzaCartItems } from "../../services/local/pizzaHalfAndHalf";
import useConnectedWaiters from "../common/useConnectedWaiters";
import { assertAcceptingNewOrders } from "../../services/supabase/commissionDebtTermsService";
import { compactPromotionPaymentFields, resolveOrderPromotionPayment, promotionPaymentNotice } from "../../services/supabase/edge-functions/_shared/promotionPayments";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import OperationalWorkspaceSwitcher from "../common/OperationalWorkspaceSwitcher";
import { useAccessControl } from "../context/useAccessControl";
import { useIngredientes } from "../context/IngredientesContext";
import IngredientImage from "../common/IngredientImage";
import {
  DIAS_SEMANA,
  getRestaurantProfile,
  mergeRemoteBranchIntoProfilePreservingLocalPreferences,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
  buildBranchClosingControlStorageKey,
  formatBranchClosingTime,
  getBranchClosingControl,
  getEffectiveBranchOperatingState,
  selectNewestBranchClosingControl,
} from "../../services/local/branchClosingControl";
import {
  consumeIngredientStock,
  getIngredientStock,
  getIngredientStockValidationMessage,
  getOperationalProductStockState,
  hydrateIngredientStockFromRemote,
  INGREDIENT_STOCK_UPDATED_EVENT,
  restoreIngredientStock,
  validateIngredientStock,
} from "../../services/local/ingredientStock";
import {
  getAuthoritativePaymentOperationForOrder,
  getPaymentOperations,
  isPaymentOperationConfirmed,
  resolveOperationalOrderPaymentState,
  savePaymentOperations,
} from "../../services/local/paymentOperations";
import { resolveBranchCommissionRateByChannel } from "../../services/local/paymentSettings";
import {
  buildOperationalOrdersStorageKey,
  getCurrentSalonReservations,
  getSalonReservationTimestamp,
  getSalonReservationWindow,
  getOperationalOrdersState,
  isSalonReservationBlockingTable,
  normalizeSalonReservations,
  normalizeSalonTables,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
  saveOperationalOrdersState,
} from "../../services/local/operationalOrders";
import {
  PROMOTIONS_UPDATED_EVENT,
  buildPromotionsStorageKey,
  getPromotions,
} from "../../services/local/promotions";
import {
  buildOperationalConsumptions,
  createOperationalOrderItem,
  filterDirectDeliveryOrderItems,
  filterKitchenOrderItems,
  orderRequiresKitchen,
  resolveOrderKitchenModeAtDispatch,
} from "../../services/local/orderModel";
import {
  buildPizzaHalfAndHalfOptions,
  buildPizzaHalfAndHalfSelectionDetails,
  createPizzaHalfAndHalfSelection,
  createPizzaSizeSelectionDetail,
  findPizzaSizeOption,
  getPizzaHalfAndHalfPricing,
  getPizzaProductName,
  getPizzaSizeOptions,
  normalizePizzaSizeOptions,
  productHasPizzaSizeOptions,
  resolvePizzaProductPrice,
} from "../../services/local/pizzaHalfAndHalf";
import {
  normalizeEmpanadaCookingOptions,
  normalizeEmpanadaSaleOptions,
} from "../../services/local/empanadaSales";
import { loadLocalMenuSnapshot } from "../../services/local/menuStorage";
import {
  getBranchTablesSnapshot,
  saveBranchTablesSnapshot,
} from "../../services/local/branchTablesSnapshot";
import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "../../services/local/menuIngredientModel";
import { getBranchMenuSnapshot } from "../../services/supabase/menuService";
import { syncManualCommissionDebtForOperation } from "../../services/supabase/commissionLedgerService";
import { refundApprovedDigitalPayment } from "../../services/supabase/kioskDigitalPaymentsService";
import { recordMzRuntimeUsage } from "../../services/supabase/mzUsageRuntimeService";
import {
  claimSharedOperationalOrderPayment,
  hydrateSharedOperationalStateToLocal,
  patchSharedOperationalOrder,
  setSharedOrderDiscount,
  getSharedOrderBill,
  pushSharedPaymentOperationsState,
  pushSharedOperationalState,
} from "../../services/supabase/operationalStateService";
import {
  buildPaymentRailSnapshot,
  isDigitalPaymentRail,
} from "../../services/payments/paymentRails";
import { isSupabaseConfigured } from "../../services/supabase/config";
import {
  getPublicKioskMenuByBranchId,
  listBranchTablesWithQrs,
} from "../../services/supabase/qrService";
import { hydrateBranchPromotionsToLocal } from "../../services/supabase/promotionsService";
import {
  createExternalOrder,
  calculateExternalDeliveryQuote,
  dispatchExternalOrderToKitchen,
  enrichExternalOperationalItemsWithMenu,
  formatExternalPhone,
  listExternalCustomers,
  listExternalDeliveryZones,
  resolveExternalKitchenDispatchContext,
  transitionExternalOrderStatus,
} from "../../services/supabase/externalOrdersService";
import { buildPromotionPricing, isPromotionAvailableNow } from "../../services/promotions/promotionEngine";
import {
  getBranchPublicProfile,
  listBranchWaiters,
} from "../../services/supabase/companyAdminService";
import {
  assignWaiterToTable,
  assignWaiterServiceRequest,
  buildWaiterOperationsStorageKey,
  completeWaiterServiceRequest,
  createDeliveryAlert,
  doesWaiterServiceRequestMatchTable,
  getWaiterAssignmentForTable,
  getWaiterOperationsState,
  getPendingWaiterServiceRequests,
  releaseWaiterTableAssignment,
  syncBranchActiveWaiters,
  WAITER_OPERATIONS_UPDATED_EVENT,
} from "../../services/local/waiterOperations";
import {
  formatPreparationNumber,
  printCustomerPickupTicket,
  printCustomerBill,
  printKitchenTicket,
} from "../../services/local/kitchenPrinting";
import {
  buildMozzoAndroidCustomerTicketPayload,
  buildMozzoAndroidKitchenTicketPayload,
  isMozzoAndroidBridgeAvailable,
  printTicketWithMozzoAndroid,
} from "../../services/local/androidKioskBridge";
import {
  buildMozzoWindowsCustomerTicketPayload,
  buildMozzoWindowsKitchenTicketPayload,
  isMozzoWindowsBridgeAvailable,
  printTicketWithMozzoWindows,
} from "../../services/local/windowsKioskBridge";

const createEmptyOperationalState = () => ({
  mesasActivas: [],
  pedidosHistoricos: [],
  salonTables: [],
  salonLayout: {},
  salonReservations: {},
  salonRooms: [],
  salonTableRooms: {},
  salonFixtures: {},
});

const alertasBase = [
  "Hay una portada cargada para el restaurante.",
  "La carta ya tiene categorias y platos listos para pruebas.",
  "El QR por mesa esta disponible para seguir iterando.",
];

const MANUAL_OPERATIONAL_MZ_RESET_UNITS = 2000;
const OPERATIONAL_REMOTE_SYNC_HOLD_MS = 6500;
const OPERATIONAL_MESA_OVERRIDE_MS = 45000;
const MESA_PHASE_SYNC_PRIORITY = {
  new: 0,
  preparation: 1,
  ready: 2,
  closed: 3,
  cancelled: 3,
};
const ORDER_REPORT_STATUS_PRIORITY = {
  "Nuevo pedido": 0,
  "En preparacion": 1,
  "Listo para entregar": 2,
  "Entrega parcial": 3,
  "Esperando cierre": 4,
  Cerrado: 5,
  Cancelado: 6,
};

const buildManualPaymentOperationId = () =>
  `manual-order-payment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getMesaPhaseSyncPriority = (phase) =>
  MESA_PHASE_SYNC_PRIORITY[String(phase || "").trim().toLowerCase()] ?? 0;

const normalizePromotionSchedule = (schedule) => {
  const incoming = Array.isArray(schedule) ? schedule : [];

  return DIAS_SEMANA.map((dia, index) => {
    const found = incoming.find((item) => item?.id === dia.id);
    return {
      id: dia.id,
      label: dia.label,
      active: Boolean(found?.active),
      allDay: Boolean(found?.allDay),
      apertura: found?.apertura || (index < 5 ? "08:00" : "09:00"),
      cierre: found?.cierre || (index < 5 ? "12:00" : "13:00"),
    };
  });
};

const buildPromotionScheduleSummary = (schedule) => {
  const normalized = normalizePromotionSchedule(schedule);
  const activeDays = normalized.filter((day) => day.active);

  if (activeDays.length === 0) {
    return "Disponible todos los dias";
  }

  return activeDays
    .map((day) =>
      day.allDay ? `${day.label} todo el dia` : `${day.label} ${day.apertura} a ${day.cierre}`
    )
    .join(" · ");
};

const getPromotionRecordType = (promotion) =>
  promotion?.type === "promotion" ? "promotion" : "combo";

const initialManualOrderForm = {
  destinationType: "table",
  tableId: "",
  comensales: "2",
  dinerName: "",
  canal: "Salon",
  customerFirstName: "",
  customerLastName: "",
  customerPhone: "",
  deliveryZoneId: "",
  addressStreet: "",
  addressStreetNumber: "",
  addressNeighborhood: "",
  addressCity: "",
  addressReference: "",
  menuCategoryId: "",
  menuProductId: "",
  menuProductQuantity: "1",
  paymentFlow: "later",
  paymentMethod: "efectivo",
  notes: "",
};

const EMPTY_SALON_RESERVATION_FORM = {
  customerName: "",
  partySize: "2",
  reservedFor: "",
  blockMinutesBefore: "120",
  durationMinutes: "120",
  notes: "",
};

const SALON_RESERVATION_BLOCK_OPTIONS = Object.freeze([
  { value: 30, label: "30 min antes" },
  { value: 60, label: "1 hora antes" },
  { value: 90, label: "1 h 30 min antes" },
  { value: 120, label: "2 horas antes" },
  { value: 180, label: "3 horas antes" },
]);

const SALON_RESERVATION_DURATION_OPTIONS = Object.freeze([
  { value: 60, label: "1 hora" },
  { value: 90, label: "1 h 30 min" },
  { value: 120, label: "2 horas" },
  { value: 180, label: "3 horas" },
  { value: 240, label: "4 horas" },
]);

const formatSalonReservationDateTime = (value) => {
  const date = new Date(value || "");

  if (Number.isNaN(date.getTime())) {
    return "Horario pendiente";
  }

  return new Intl.DateTimeFormat("es-AR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatSalonReservationBlockLead = (minutes) => {
  const safeMinutes = Math.max(0, Number(minutes ?? 120));

  if (safeMinutes < 60) {
    return `${safeMinutes} min antes`;
  }

  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;
  return remainingMinutes
    ? `${hours} h ${remainingMinutes} min antes`
    : `${hours} ${hours === 1 ? "hora" : "horas"} antes`;
};

const DEFAULT_SALON_ROOM_ID = "salon-principal";
const DEFAULT_SALON_ROOM = Object.freeze({
  id: DEFAULT_SALON_ROOM_ID,
  name: "Salon principal",
});

const SALON_FIXTURE_DEFINITIONS = Object.freeze([
  { type: "bar", label: "Barra", width: 30, height: 12 },
  { type: "counter", label: "Mostrador", width: 24, height: 10 },
  { type: "entrance", label: "Entrada", width: 16, height: 7 },
  { type: "exit", label: "Salida", width: 16, height: 7 },
  { type: "kitchen", label: "Cocina", width: 24, height: 10 },
]);

const SALON_FIXTURE_BY_TYPE = Object.fromEntries(
  SALON_FIXTURE_DEFINITIONS.map((definition) => [definition.type, definition])
);

const SALON_DOOR_FIXTURE_TYPES = new Set(["entrance", "exit"]);

const isSalonDoorFixtureType = (type) =>
  SALON_DOOR_FIXTURE_TYPES.has(String(type || "").trim().toLowerCase());

const isVerticalSalonDoorFixture = (fixture) =>
  isSalonDoorFixtureType(fixture?.type) &&
  Number(fixture?.height || 0) > Number(fixture?.width || 0);

const clampSalonFixtureSize = (value, axis = "width", type = "") => {
  const minimum = axis === "height" ? 5 : isSalonDoorFixtureType(type) ? 4 : 8;
  const maximum = axis === "height" ? 40 : 65;
  return Math.min(maximum, Math.max(minimum, Number(value || minimum)));
};

const clampSalonFixturePosition = (value, size) => {
  const halfSize = Math.max(2, Number(size || 0) / 2);
  return Math.min(99 - halfSize, Math.max(1 + halfSize, Number(value || 0)));
};

const buildSalonFixtureId = (type) =>
  `${String(type || "elemento")}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

const normalizeSalonFixtureListForUi = (fixtures) => {
  const normalizedFixtures = [];
  const seenIds = new Set();

  (Array.isArray(fixtures) ? fixtures : []).forEach((fixture) => {
    const type = String(fixture?.type || "").trim().toLowerCase();
    const definition = SALON_FIXTURE_BY_TYPE[type];
    const id = String(fixture?.id || "").trim();

    if (!definition || !id || seenIds.has(id)) {
      return;
    }

    const height = clampSalonFixtureSize(
      fixture?.height || definition.height,
      "height",
      type
    );
    const rawWidth = clampSalonFixtureSize(
      fixture?.width || definition.width,
      "width",
      type
    );
    const width =
      isSalonDoorFixtureType(type) && height > rawWidth
        ? Math.min(rawWidth, 5)
        : rawWidth;

    seenIds.add(id);
    normalizedFixtures.push({
      id,
      type,
      label: String(fixture?.label || definition.label).trim().slice(0, 40) || definition.label,
      x: clampSalonFixturePosition(fixture?.x, width),
      y: clampSalonFixturePosition(fixture?.y, height),
      width,
      height,
    });
  });

  return normalizedFixtures;
};

const normalizeSalonFixturesForUi = (fixturesByRoom) => {
  if (!fixturesByRoom || typeof fixturesByRoom !== "object" || Array.isArray(fixturesByRoom)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(fixturesByRoom).map(([roomId, fixtures]) => [
      roomId,
      normalizeSalonFixtureListForUi(fixtures),
    ])
  );
};

const buildDefaultSalonFixtures = (roomId) => [
  {
    id: `${roomId}-entrada-inicial`,
    type: "entrance",
    label: "Entrada",
    x: 12,
    y: 92,
    width: 16,
    height: 7,
  },
  {
    id: `${roomId}-cocina-inicial`,
    type: "kitchen",
    label: "Cocina",
    x: 84,
    y: 9,
    width: 24,
    height: 10,
  },
];

const resolveSalonFixturesForRoom = (fixturesByRoom, roomId) => {
  if (Object.prototype.hasOwnProperty.call(fixturesByRoom || {}, roomId)) {
    return normalizeSalonFixtureListForUi(fixturesByRoom[roomId]);
  }

  return normalizeSalonFixtureListForUi(buildDefaultSalonFixtures(roomId));
};

const normalizeSalonRoomsForUi = (rooms) => {
  const normalizedRooms = [];
  const seenIds = new Set();

  (Array.isArray(rooms) ? rooms : []).forEach((room) => {
    const id = String(room?.id || "").trim();
    const name = String(room?.name || "").trim();

    if (!id || !name || seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    normalizedRooms.push({
      id,
      name: name.slice(0, 60),
      createdAt: String(room?.createdAt || ""),
    });
  });

  return normalizedRooms.length ? normalizedRooms : [{ ...DEFAULT_SALON_ROOM }];
};

const buildSalonRoomId = (name) => {
  const slug = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 36) || "salon";

  return `${slug}-${Date.now().toString(36)}`;
};

const clampSalonPosition = (value) =>
  Math.min(92, Math.max(8, Number(value || 0)));

const SALON_TABLE_SHAPES = Object.freeze([
  { id: "circle", label: "Redonda" },
  { id: "square", label: "Cuadrada" },
  { id: "rectangle", label: "Rectangular" },
]);
const DEFAULT_SALON_TABLE_GEOMETRY = Object.freeze({
  shape: "circle",
  width: 88,
  height: 88,
});
const SALON_TABLE_MIN_WIDTH = 64;
const SALON_TABLE_MAX_WIDTH = 180;
const SALON_TABLE_MIN_HEIGHT = 52;
const SALON_TABLE_MAX_HEIGHT = 140;

const clampSalonTableDimension = (value, axis = "width") => {
  const numericValue = Number(value);
  const fallback =
    axis === "height"
      ? DEFAULT_SALON_TABLE_GEOMETRY.height
      : DEFAULT_SALON_TABLE_GEOMETRY.width;
  const minimum = axis === "height" ? SALON_TABLE_MIN_HEIGHT : SALON_TABLE_MIN_WIDTH;
  const maximum = axis === "height" ? SALON_TABLE_MAX_HEIGHT : SALON_TABLE_MAX_WIDTH;

  return Math.min(maximum, Math.max(minimum, Number.isFinite(numericValue) ? numericValue : fallback));
};

const normalizeSalonTableRotation = (value) => {
  const normalized = Math.round(Number(value || 0));
  return normalized === 90 || normalized === 270 ? 90 : 0;
};

const normalizeSalonTableGeometry = (value = {}) => {
  const requestedShape = String(value?.shape || "").trim().toLowerCase();
  const shape = SALON_TABLE_SHAPES.some((option) => option.id === requestedShape)
    ? requestedShape
    : DEFAULT_SALON_TABLE_GEOMETRY.shape;
  const width = clampSalonTableDimension(value?.width, "width");

  if (shape !== "rectangle") {
    return { shape, width, height: width, rotation: 0 };
  }

  return {
    shape,
    width: Math.max(80, width),
    height: clampSalonTableDimension(value?.height || 76, "height"),
    rotation: normalizeSalonTableRotation(value?.rotation),
  };
};

const buildSalonTableShapeGeometry = (currentValue = {}, nextShape = "circle") => {
  const current = normalizeSalonTableGeometry(currentValue);

  if (nextShape === "rectangle") {
    return normalizeSalonTableGeometry({
      shape: nextShape,
      width: Math.max(112, current.width),
      height: current.shape === "rectangle" ? current.height : Math.max(60, Math.round(current.width * 0.68)),
      rotation: current.shape === "rectangle" ? current.rotation : 0,
    });
  }

  const size = Math.max(current.width, current.height);
  return normalizeSalonTableGeometry({ shape: nextShape, width: size, height: size });
};

const rotateSalonRectangleGeometry = (currentValue = {}) => {
  const current = normalizeSalonTableGeometry(currentValue);

  if (current.shape !== "rectangle") {
    return current;
  }

  return normalizeSalonTableGeometry({
    ...current,
    width: current.height,
    height: current.width,
    rotation: current.rotation === 90 ? 0 : 90,
  });
};

const getSalonTableBorderRadius = (shape) => {
  if (shape === "circle") return "50%";
  if (shape === "square") return "20px";
  return "18px";
};

const clampSalonTablePosition = (value, tablePixels, floorPixels) => {
  const numericValue = Number(value || 0);
  const halfSizePercent = floorPixels
    ? Math.min(48, Math.max(2, (Number(tablePixels || 0) / floorPixels) * 50))
    : 8;

  return Math.min(100 - halfSizePercent, Math.max(halfSizePercent, numericValue));
};

const buildDefaultSalonPosition = (index, total) => {
  const safeTotal = Math.max(1, Number(total || 1));
  const columns = Math.min(5, Math.max(2, Math.ceil(Math.sqrt(safeTotal))));
  const rows = Math.max(1, Math.ceil(safeTotal / columns));

  return {
    x: clampSalonPosition(((index % columns) + 0.5) * (100 / columns)),
    y: clampSalonPosition((Math.floor(index / columns) + 0.5) * (100 / rows)),
  };
};

const resolveSalonTableLayout = (layoutEntry, index, total) => {
  const fallbackPosition = buildDefaultSalonPosition(index, total);
  const incoming = layoutEntry && typeof layoutEntry === "object" ? layoutEntry : {};
  const incomingX = Number(incoming.x);
  const incomingY = Number(incoming.y);

  return {
    x: Number.isFinite(incomingX) ? incomingX : fallbackPosition.x,
    y: Number.isFinite(incomingY) ? incomingY : fallbackPosition.y,
    ...normalizeSalonTableGeometry(incoming),
  };
};

const resolveManualExternalPaymentMethod = (paymentMethod, destinationType) => {
  if (paymentMethod === "deep_link") return "mercado_pago";
  if (paymentMethod === "transferencia") return "transfer";
  if (paymentMethod === "tarjeta") return "card_on_delivery";
  return destinationType === "delivery" ? "cash_on_delivery" : "cash";
};

const buildManualCustomerName = (form = {}) =>
  [form.customerFirstName, form.customerLastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

const getManualKnownCustomerName = (customer = {}) =>
  [customer.first_name, customer.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ") || "Cliente sin nombre";

const getManualKnownCustomerPhone = (customer = {}) =>
  customer.phone_original || customer.phone_normalized || "";

const getManualKnownCustomerDefaultAddress = (customer = {}) => {
  const addresses = Array.isArray(customer.addresses) ? customer.addresses : [];
  return addresses.find((address) => address?.is_default) || addresses[0] || null;
};

const formatManualKnownCustomerAddress = (address = {}) => {
  if (!address) return "Sin direccion guardada";

  const streetLine = [address.street, address.street_number || address.streetNumber]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  const areaLine = [address.neighborhood, address.city]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

  return [streetLine, areaLine].filter(Boolean).join(" - ") || "Sin direccion guardada";
};

const buildManualKnownCustomerSearchText = (customer = {}) => {
  const address = getManualKnownCustomerDefaultAddress(customer);
  return [
    getManualKnownCustomerName(customer),
    getManualKnownCustomerPhone(customer),
    customer.phone_normalized,
    formatManualKnownCustomerAddress(address),
    address?.reference,
  ]
    .map((part) => String(part || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");
};

const resolveManualPaymentMethodLabel = (paymentMethod = "") => {
  const normalizedMethod = String(paymentMethod || "").trim().toLowerCase();

  if (normalizedMethod === "transferencia") return "Transferencia";
  if (normalizedMethod === "tarjeta") return "Tarjeta";
  if (normalizedMethod === "deep_link") return "Pago digital";
  if (normalizedMethod === "efectivo") return "Efectivo";

  return "Caja";
};

const resolveNextManualPreparationNumber = (ordersState = {}) => {
  const allOrders = [
    ...(ordersState.mesasActivas || []),
    ...(ordersState.pedidosHistoricos || []),
  ];

  const maxNumber = allOrders.reduce((currentMax, order) => {
    const parsed = Number(order?.preparationNumber || 0);

    if (!Number.isFinite(parsed)) {
      return currentMax;
    }

    return Math.max(currentMax, Math.floor(parsed));
  }, 0);

  return maxNumber + 1;
};

const normalizeInicioEntityId = (value) => String(value || "").trim();

const normalizeManualCatalogCategory = (category = {}) => {
  const id = normalizeInicioEntityId(
    category.id || category.categoryId || category.category_id
  );
  const name = String(category.nombre || category.name || "")
    .trim()
    .replace(/\s+/g, " ") || "Categoria";

  return {
    ...category,
    id,
    name,
    nombre: name,
  };
};

const normalizeManualCatalogIngredient = (ingredient = {}) => {
  const id = normalizeInicioEntityId(
    ingredient.id || ingredient.ingredientId || ingredient.ingredient_id
  );
  const name = String(ingredient.nombre || ingredient.name || "")
    .trim()
    .replace(/\s+/g, " ") || "Ingrediente";
  const imageUrl =
    ingredient.imagen ||
    ingredient.imageUrl ||
    ingredient.image_url ||
    ingredient.fotoUrl ||
    "";
  const price = Number(ingredient.precio ?? ingredient.price ?? 0);
  const type = ingredient.tipo || ingredient.type || "opcional";
  const menuBaseQuantity = getIngredientMenuBaseQuantity(ingredient);
  const consumptionQuantity = getIngredientConsumptionQuantity(ingredient);

  return {
    ...ingredient,
    id,
    name,
    nombre: name,
    imageUrl,
    image_url: imageUrl,
    imagen: imageUrl,
    price,
    precio: price,
    type,
    tipo: type,
    baseQuantity: menuBaseQuantity,
    menuBaseQuantity,
    consumptionQuantity,
    stockQuantity: consumptionQuantity,
    canRemove: ingredient.canRemove === true || ingredient.can_remove === true,
    canAddMore: ingredient.canAddMore === true || ingredient.can_add_more === true,
    maxExtraQuantity: Number(
      ingredient.maxExtraQuantity ||
        ingredient.max_extra_quantity ||
        DEFAULT_MANUAL_EXTRA_QUANTITY
    ),
  };
};

const normalizeManualCatalogProduct = (product = {}) => {
  const id = normalizeInicioEntityId(product.id || product.productId || product.product_id);
  const categoryId = normalizeInicioEntityId(
    product.categoria || product.categoryId || product.category_id
  );
  const subcategoryId = normalizeInicioEntityId(
    product.subcategoriaId ||
      product.subcategoryId ||
      product.subcategory_id ||
      product.subcategoria_id
  );
  const name = String(product.nombre || product.name || "")
    .trim()
    .replace(/\s+/g, " ") || "Producto";
  const description = String(product.descripcion || product.description || "").trim();
  const subcategoryName = String(
    product.subcategoria || product.subcategoryName || product.subcategory_name || ""
  )
    .trim()
    .replace(/\s+/g, " ");
  const imageUrl =
    product.imagen ||
    product.imageUrl ||
    product.image_url ||
    product.fotoUrl ||
    product.foto_url ||
    "";
  const priceMode =
    String(product.modoPrecio || product.priceMode || product.price_mode || "")
      .trim()
      .toLowerCase() || "manual";
  const price = Number(product.precio ?? product.price ?? 0);
  const suggestedPrice = Number(
    product.precioSugerido ?? product.suggestedPrice ?? product.suggested_price ?? price
  );
  const ingredients = (
    Array.isArray(product.ingredientes)
      ? product.ingredientes
      : Array.isArray(product.ingredients)
        ? product.ingredients
        : []
  ).map(normalizeManualCatalogIngredient);
  const pizzaSizeOptions = normalizePizzaSizeOptions(
    product.pizzaSizeOptions ||
      product.pizza_size_options ||
      product.pizzaSizes ||
      product.pizza_sizes ||
      []
  );
  const empanadaSaleOptions = normalizeEmpanadaSaleOptions(
    product.empanadaSaleOptions ||
      product.empanada_sale_options ||
      product.empanadaOptions ||
      product.empanada_options ||
      []
  );
  const empanadaCookingOptions = normalizeEmpanadaCookingOptions(
    product.empanadaCookingOptions ||
      product.empanada_cooking_options ||
      product.empanadaCookOptions ||
      product.empanada_cook_options ||
      []
  );

  return {
    ...product,
    id,
    categoryId,
    category_id: categoryId,
    categoria: categoryId,
    subcategoryId: subcategoryId,
    subcategory_id: subcategoryId,
    subcategoriaId: subcategoryId,
    name,
    nombre: name,
    description,
    descripcion: description,
    subcategoryName,
    subcategoria: subcategoryName,
    imageUrl,
    image_url: imageUrl,
    imagen: imageUrl,
    priceMode,
    modoPrecio: priceMode,
    price,
    precio: price,
    suggestedPrice,
    precioSugerido: suggestedPrice,
    ingredients,
    ingredientes: ingredients.map((ingredient) => ({ ...ingredient })),
    pizzaSizeOptions,
    pizza_size_options: pizzaSizeOptions,
    empanadaSaleOptions,
    empanada_sale_options: empanadaSaleOptions,
    empanadaCookingOptions,
    empanada_cooking_options: empanadaCookingOptions,
    active: product.active !== false,
  };
};

const normalizeManualMenuCatalog = (snapshot = {}, { kioskMode = false } = {}) => {
  const products = Array.isArray(snapshot?.products)
    ? snapshot.products
        .map(normalizeManualCatalogProduct)
        .filter((product) => product.id && product.active !== false)
        .filter((product) =>
          kioskMode ? product.linkedOnly !== true && product.linked_only !== true : true
        )
    : [];
  const productCategoryIds = new Set(
    products
      .map((product) =>
        normalizeInicioEntityId(product.categoria || product.categoryId || product.category_id)
      )
      .filter(Boolean)
  );
  const categories = Array.isArray(snapshot?.categories)
    ? snapshot.categories
        .map(normalizeManualCatalogCategory)
        .filter((category) => category.id)
        .filter((category) => (kioskMode ? productCategoryIds.has(category.id) : true))
    : [];
  const explicitSubcategories = Array.isArray(snapshot?.subcategories)
    ? snapshot.subcategories
    : Array.isArray(snapshot?.subcategorias)
      ? snapshot.subcategorias
      : [];
  const derivedSubcategories = new Map();

  products.forEach((product) => {
    const subcategoryId = product.subcategoriaId || product.subcategoryId || "";
    const subcategoryName = product.subcategoria || product.subcategoryName || "";

    if (!subcategoryId && !subcategoryName) {
      return;
    }

    derivedSubcategories.set(subcategoryId || subcategoryName, {
      id: subcategoryId,
      name: subcategoryName,
      nombre: subcategoryName,
    });
  });

  return {
    categories,
    products,
    subcategories: explicitSubcategories.length
      ? explicitSubcategories
      : Array.from(derivedSubcategories.values()),
  };
};

const getInicioStoredState = (restaurant) => {
  return getOperationalOrdersState(restaurant, createEmptyOperationalState);
};

const saveInicioStoredState = (restaurant, payload) => {
  try {
    const savedPayload = saveOperationalOrdersState(
      restaurant,
      payload,
      createEmptyOperationalState
    );
    const remoteSync = pushSharedOperationalState(
      restaurant,
      savedPayload,
      createEmptyOperationalState
    );
    void remoteSync.catch(
      (error) => {
        console.error(
          "No pudimos sincronizar el estado operativo compartido de Inicio:",
          error
        );
      }
    );
    return remoteSync;
  } catch (error) {
    console.error("No pudimos guardar el estado operativo de Inicio:", error);
    return Promise.resolve(null);
  }
};

const formatElapsed = (dateString) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return `${hours} h ${minutes} min`;
};

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString("es-AR")}`;

const getValidDate = (value) => {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getStartOfDay = (value) => {
  const date = getValidDate(value);

  if (!date) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

const getEndOfDay = (value) => {
  const date = getValidDate(value);

  if (!date) {
    return null;
  }

  date.setHours(23, 59, 59, 999);
  return date;
};

const formatDateInputValue = (value) => {
  const date = getValidDate(value);

  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatShortDate = (value) => {
  const date = getValidDate(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleDateString("es-AR");
};

const formatDateTime = (value) => {
  const date = getValidDate(value);

  if (!date) {
    return "-";
  }

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeOrderStatusLabel = (value) => {
  const nextValue = String(value || "").trim();
  return nextValue || "Sin estado";
};

const sortOrdersByRequestedAtDesc = (a, b) => {
  const nextTime = getValidDate(b?.requestedAt)?.getTime() || 0;
  const prevTime = getValidDate(a?.requestedAt)?.getTime() || 0;
  return nextTime - prevTime;
};

const compareOrderStatuses = (left, right) => {
  const leftPriority =
    ORDER_REPORT_STATUS_PRIORITY[normalizeOrderStatusLabel(left)] ?? Number.MAX_SAFE_INTEGER;
  const rightPriority =
    ORDER_REPORT_STATUS_PRIORITY[normalizeOrderStatusLabel(right)] ?? Number.MAX_SAFE_INTEGER;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  return normalizeOrderStatusLabel(left).localeCompare(normalizeOrderStatusLabel(right), "es");
};

const buildOrderDateRange = ({ from, to } = {}) => {
  let normalizedFrom = String(from || "").trim();
  let normalizedTo = String(to || "").trim();

  if (normalizedFrom && normalizedTo && normalizedFrom > normalizedTo) {
    [normalizedFrom, normalizedTo] = [normalizedTo, normalizedFrom];
  }

  return {
    from: normalizedFrom,
    to: normalizedTo,
    fromDate: normalizedFrom ? getStartOfDay(`${normalizedFrom}T00:00:00`) : null,
    toDate: normalizedTo ? getEndOfDay(`${normalizedTo}T00:00:00`) : null,
  };
};

const isOrderWithinRange = (order, range) => {
  const requestedDate = getValidDate(order?.requestedAt);

  if (!requestedDate) {
    return false;
  }

  if (range?.fromDate && requestedDate < range.fromDate) {
    return false;
  }

  if (range?.toDate && requestedDate > range.toDate) {
    return false;
  }

  return true;
};

const buildOrdersReportFileName = (range) => {
  const fromPart = range?.from || "inicio";
  const toPart = range?.to || "hoy";
  return `pedidos-${fromPart}-a-${toPart}.pdf`;
};

const createPaymentHistoryEntry = (label) => ({
  id: `payment-history-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  label,
  at: new Date().toISOString(),
});

const getElapsedTone = (dateString) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));

  if (diffMinutes >= 35) {
    return {
      backgroundColor: "#fef2f2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
    };
  }

  if (diffMinutes >= 25) {
    return {
      backgroundColor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    };
  }

  return {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #86efac",
  };
};

const orderNeedsKitchen = (order) =>
  typeof order?.requiresKitchen === "boolean"
    ? order.requiresKitchen
    : orderRequiresKitchen(order);

const getMesaKitchenItems = (mesa) => filterKitchenOrderItems(mesa?.items);
const getMesaDirectDeliveryItems = (mesa) =>
  filterDirectDeliveryOrderItems(mesa?.items);
const getMesaPendingDirectDeliveryItems = (mesa) =>
  getMesaDirectDeliveryItems(mesa).filter((item) => !item.delivered);
const hasMixedKitchenAndDirectDelivery = (mesa) =>
  getMesaKitchenItems(mesa).length > 0 && getMesaDirectDeliveryItems(mesa).length > 0;
const getDirectDeliveryItemsSummary = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => item?.nombre || item?.productName || item?.name || "Producto")
    .filter(Boolean)
    .join(", ");

const getMesaStatus = (mesa) => {
  const deliveredCount = mesa.items.filter((item) => item.delivered).length;

  if (deliveredCount === mesa.items.length) {
    return "Esperando cierre";
  }

  if (deliveredCount > 0) {
    return "Entrega parcial";
  }

  if (
    !orderNeedsKitchen(mesa) &&
    ["preparation", "ready"].includes(String(mesa?.phase || "").trim().toLowerCase())
  ) {
    return "Listo para entregar";
  }

  if (mesa.phase === "ready") {
    return "Listo para entregar";
  }

  if (mesa.phase === "preparation") {
    return "En preparacion";
  }

  return "Nuevo pedido";
};

const getStatusBadgeStyle = (status) => {
  if (status === "En preparacion") {
    return {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fca5a5",
      boxShadow: "inset 0 0 0 1px rgba(239, 68, 68, 0.08)",
    };
  }

  return null;
};

const getKitchenModeLabel = (mode) => {
  if (mode === "printer") {
    return "Impresora";
  }

  if (mode === "screen") {
    return "Pantalla";
  }

  return "Manual";
};

// eslint-disable-next-line no-unused-vars
const getKitchenDispatchStatusLabel = (mesa) => {
  if (!orderNeedsKitchen(mesa)) {
    return "No requiere cocina";
  }

  const modeLabel = getKitchenModeLabel(mesa?.kitchenModeSnapshot);
  const status = mesa?.kitchenDispatchStatus || "";

  if (status === "manual_confirmed") {
    return `${modeLabel} · avisado desde Inicio`;
  }

  if (status === "printer_pending") {
    return `${modeLabel} · pendiente de conectar impresion`;
  }

  if (status === "screen_pending") {
    return `${modeLabel} · pendiente de tablero cocina`;
  }

  if (mesa?.phase === "preparation" || mesa?.phase === "ready") {
    return `${modeLabel} · en seguimiento`;
  }

  return modeLabel;
};

// eslint-disable-next-line no-unused-vars
const getKitchenDispatchVisual = (mesa) => {
  if (!orderNeedsKitchen(mesa)) {
    return {
      badgeLabel: "Sin cocina",
      badgeStyle: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      },
      cardStyle: {
        border: "1px solid #bbf7d0",
        background:
          "linear-gradient(180deg, rgba(248,255,250,1) 0%, rgba(240,253,244,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(22, 101, 52, 0.08)",
      },
      accent: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
    };
  }

  const mode = mesa?.kitchenModeSnapshot || "manual";
  const status = mesa?.kitchenDispatchStatus || "";

  if (status === "manual_confirmed") {
    return {
      badgeLabel: "Avisado a cocina",
      badgeStyle: {
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
        border: "1px solid #fca5a5",
      },
      cardStyle: {
        border: "1px solid #fecaca",
        background:
          "linear-gradient(180deg, rgba(255,251,251,1) 0%, rgba(255,245,245,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(185, 28, 28, 0.08)",
      },
      accent: "linear-gradient(90deg, #f97316 0%, #ef4444 100%)",
    };
  }

  if (status === "printer_pending" || mode === "printer") {
    return {
      badgeLabel: "Impresion pendiente",
      badgeStyle: {
        backgroundColor: "#ede9fe",
        color: "#6d28d9",
        border: "1px solid #c4b5fd",
      },
      cardStyle: {
        border: "1px solid #ddd6fe",
        background:
          "linear-gradient(180deg, rgba(250,248,255,1) 0%, rgba(245,243,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(109, 40, 217, 0.08)",
      },
      accent: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
    };
  }

  if (status === "screen_pending" || mode === "screen") {
    return {
      badgeLabel: "Pendiente de tomar",
      badgeStyle: {
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
        border: "1px solid #93c5fd",
      },
      cardStyle: {
        border: "1px solid #bfdbfe",
        background:
          "linear-gradient(180deg, rgba(248,251,255,1) 0%, rgba(239,246,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(29, 78, 216, 0.08)",
      },
      accent: "linear-gradient(90deg, #38bdf8 0%, #2563eb 100%)",
    };
  }

  return {
    badgeLabel: "Pendiente de salida",
    badgeStyle: {
      backgroundColor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    cardStyle: {
      border: "1px solid #fed7aa",
      background:
        "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
      boxShadow: "0 18px 34px rgba(194, 65, 12, 0.08)",
    },
    accent: "linear-gradient(90deg, #fb923c 0%, #f97316 100%)",
  };
};

const getMesaMissingItems = (mesa) => {
  const missingItems = mesa.items
    .filter((item) => !item.delivered)
    .map((item) => item.nombre);

  return missingItems.length > 0 ? missingItems.join(", ") : "Sin faltantes";
};

const getKitchenDispatchWorkflowState = (mesa) => {
  const mode = mesa?.kitchenModeSnapshot || "manual";
  const status = mesa?.kitchenDispatchStatus || "";
  const phase = mesa?.phase || "new";

  if (!orderNeedsKitchen(mesa)) {
    return { mode, phase, status, step: "not_required" };
  }

  if (phase === "ready") {
    return { mode, phase, status, step: "ready" };
  }

  if (mode === "manual") {
    if (status === "manual_confirmed") {
      return { mode, phase, status, step: "manual_confirmed" };
    }

    if (phase === "preparation") {
      return { mode, phase, status, step: "manual_pending" };
    }
  }

  if (status === "printer_printed") {
    return { mode, phase, status, step: "printer_printed" };
  }

  if (status === "printer_pending" || (mode === "printer" && phase === "preparation")) {
    return { mode, phase, status, step: "printer_pending" };
  }

  if (status === "screen_pending") {
    return { mode, phase, status, step: "screen_pending" };
  }

  if (mode === "screen" && phase === "preparation") {
    return { mode, phase, status, step: "screen_confirmed" };
  }

  return { mode, phase, status, step: "idle" };
};

const getKitchenDispatchStatusLabelSafe = (mesa) => {
  const workflow = getKitchenDispatchWorkflowState(mesa);
  const modeLabel = getKitchenModeLabel(mesa?.kitchenModeSnapshot);

  if (workflow.step === "not_required") {
    return "No requiere cocina";
  }

  if (workflow.phase === "new" && isMesaCashPendingBySnapshot(mesa)) {
    return `${modeLabel} - pendiente de cobro en caja`;
  }

  if (workflow.step === "ready") {
    return `${modeLabel} - listo para entregar`;
  }

  if (workflow.step === "manual_pending") {
    return `${modeLabel} - pendiente de avisar a cocina`;
  }

  if (workflow.step === "manual_confirmed") {
    return `${modeLabel} - avisado desde Inicio`;
  }

  if (workflow.step === "printer_pending") {
    return `${modeLabel} - pendiente de imprimir comanda`;
  }

  if (workflow.step === "printer_printed") {
    return `${modeLabel} - comanda impresa`;
  }

  if (workflow.step === "screen_pending") {
    return `${modeLabel} - pendiente de tomar en cocina`;
  }

  if (workflow.step === "screen_confirmed") {
    return `${modeLabel} - en preparacion en cocina`;
  }

  if (workflow.phase === "preparation") {
    return `${modeLabel} - en seguimiento`;
  }

  return modeLabel;
};

const getKitchenProgressDetail = (mesa) => {
  const workflow = getKitchenDispatchWorkflowState(mesa);

  if (workflow.step === "not_required") {
    const isAwaitingCash = workflow.phase === "new" && isMesaCashPendingBySnapshot(mesa);

    return {
      label: isAwaitingCash ? "Pendiente de cobro" : "Listo para entregar",
      detail: isAwaitingCash
        ? "Este pedido no requiere cocina; confirma el cobro para habilitar la entrega."
        : "Este pedido no requiere preparacion en cocina y puede entregarse directamente.",
    };
  }

  if (workflow.phase === "new") {
    if (isMesaCashPendingBySnapshot(mesa)) {
      return {
        label: "Pendiente de cobro",
        detail: "Caja tiene que cobrar este pedido antes de enviarlo a cocina.",
      };
    }

    return {
      label: "Sin enviar a cocina",
      detail: "Todavia no se paso a preparacion desde Inicio.",
    };
  }

  if (workflow.step === "manual_pending") {
    return {
      label: "Pendiente de aviso",
      detail: "Salon todavia no marco este pedido como avisado a cocina.",
    };
  }

  if (workflow.step === "manual_confirmed") {
    return {
      label: "Avisado a cocina",
      detail: "Cocina ya fue avisada y el seguimiento sigue manualmente.",
    };
  }

  if (workflow.step === "printer_pending") {
    return {
      label: "Pendiente de impresion",
      detail: "Todavia falta imprimir la comanda para que cocina lo tome.",
    };
  }

  if (workflow.step === "printer_printed") {
    return {
      label: "Comanda enviada",
      detail: "La comanda ya salio a cocina y el pedido esta en seguimiento.",
    };
  }

  if (workflow.step === "screen_pending") {
    return {
      label: "Pendiente de tomar",
      detail: "La pantalla de cocina ya recibio el pedido, pero todavia nadie lo tomo.",
    };
  }

  if (workflow.step === "screen_confirmed") {
    return {
      label: "En preparacion",
      detail:
        "La cocina ya tomo el pedido en pantalla. El jefe de cocina debe marcarlo como listo para que salon pueda asignar la entrega.",
    };
  }

  if (workflow.step === "ready") {
    return {
      label: "Listo para entregar",
      detail: "Cocina ya lo marco como listo y ahora salon puede coordinar la entrega.",
    };
  }

  return {
    label: "En seguimiento",
    detail: "El pedido sigue el circuito operativo configurado para la sucursal.",
  };
};

const getKitchenDispatchVisualSafe = (mesa) => {
  const workflow = getKitchenDispatchWorkflowState(mesa);
  const { step } = workflow;

  if (step === "not_required") {
    return {
      badgeLabel: "Sin cocina",
      badgeStyle: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      },
      cardStyle: {
        border: "1px solid #bbf7d0",
        background:
          "linear-gradient(180deg, rgba(248,255,250,1) 0%, rgba(240,253,244,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(22, 101, 52, 0.08)",
      },
      accent: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
    };
  }

  if (step === "ready") {
    return {
      badgeLabel: "Listo para entregar",
      badgeStyle: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      },
      cardStyle: {
        border: "1px solid #bbf7d0",
        background:
          "linear-gradient(180deg, rgba(248,255,250,1) 0%, rgba(240,253,244,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(22, 101, 52, 0.08)",
      },
      accent: "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)",
    };
  }

  if (step === "manual_pending") {
    return {
      badgeLabel: "Por avisar a cocina",
      badgeStyle: {
        backgroundColor: "#fff7ed",
        color: "#c2410c",
        border: "1px solid #fdba74",
      },
      cardStyle: {
        border: "1px solid #fed7aa",
        background:
          "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(194, 65, 12, 0.08)",
      },
      accent: "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
    };
  }

  if (step === "manual_confirmed") {
    return {
      badgeLabel: "Avisado a cocina",
      badgeStyle: {
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
        border: "1px solid #fca5a5",
      },
      cardStyle: {
        border: "1px solid #fecaca",
        background:
          "linear-gradient(180deg, rgba(255,251,251,1) 0%, rgba(255,245,245,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(185, 28, 28, 0.08)",
      },
      accent: "linear-gradient(90deg, #f97316 0%, #ef4444 100%)",
    };
  }

  if (step === "printer_pending") {
    return {
      badgeLabel: "Impresion pendiente",
      badgeStyle: {
        backgroundColor: "#ede9fe",
        color: "#6d28d9",
        border: "1px solid #c4b5fd",
      },
      cardStyle: {
        border: "1px solid #ddd6fe",
        background:
          "linear-gradient(180deg, rgba(250,248,255,1) 0%, rgba(245,243,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(109, 40, 217, 0.08)",
      },
      accent: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
    };
  }

  if (step === "printer_printed") {
    return {
      badgeLabel: "Comanda impresa",
      badgeStyle: {
        backgroundColor: "#f5f3ff",
        color: "#6d28d9",
        border: "1px solid #c4b5fd",
      },
      cardStyle: {
        border: "1px solid #ddd6fe",
        background:
          "linear-gradient(180deg, rgba(252,250,255,1) 0%, rgba(245,243,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(109, 40, 217, 0.08)",
      },
      accent: "linear-gradient(90deg, #a78bfa 0%, #7c3aed 100%)",
    };
  }

  if (step === "screen_pending") {
    return {
      badgeLabel: "Pendiente de tomar",
      badgeStyle: {
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
        border: "1px solid #93c5fd",
      },
      cardStyle: {
        border: "1px solid #bfdbfe",
        background:
          "linear-gradient(180deg, rgba(248,251,255,1) 0%, rgba(239,246,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(29, 78, 216, 0.08)",
      },
      accent: "linear-gradient(90deg, #38bdf8 0%, #2563eb 100%)",
    };
  }

  if (step === "screen_confirmed") {
    return {
      badgeLabel: "En cocina",
      badgeStyle: {
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
        border: "1px solid #93c5fd",
      },
      cardStyle: {
        border: "1px solid #bfdbfe",
        background:
          "linear-gradient(180deg, rgba(248,251,255,1) 0%, rgba(239,246,255,0.98) 100%)",
        boxShadow: "0 18px 34px rgba(29, 78, 216, 0.08)",
      },
      accent: "linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)",
    };
  }

  return {
    badgeLabel: "Pendiente de salida",
    badgeStyle: {
      backgroundColor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    cardStyle: {
      border: "1px solid #fed7aa",
      background:
        "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
      boxShadow: "0 18px 34px rgba(194, 65, 12, 0.08)",
    },
    accent: "linear-gradient(90deg, #fb923c 0%, #f97316 100%)",
  };
};

const getKitchenNextActionLabel = (mesa) => {
  const workflow = getKitchenDispatchWorkflowState(mesa);

  if (workflow.step === "not_required") {
    if (workflow.phase === "new" && isMesaCashPendingBySnapshot(mesa)) {
      return "Caja tiene que confirmar el cobro antes de habilitar la entrega.";
    }

    return workflow.phase === "new"
      ? "Este pedido no requiere cocina. Confirmalo para dejarlo listo para entregar."
      : "Este pedido no requiere cocina. Asigna un mozo o registra la entrega directamente.";
  }

  if (workflow.phase === "new") {
    if (isMesaCashPendingBySnapshot(mesa)) {
      return "Caja tiene que cobrar este pedido antes de mandarlo a cocina.";
    }

    return "Aun no entro a cocina. Primero pasalo a preparacion desde Inicio.";
  }

  if (workflow.step === "manual_pending") {
    return "En modo manual alguien del salon tiene que avisar este pedido a cocina.";
  }

  if (workflow.step === "manual_confirmed") {
    return "Cocina ya fue avisada. Cuando este resuelto, marcalo como listo para entregar.";
  }

  if (workflow.step === "ready") {
    return "El pedido ya esta listo. Solo resta coordinar y registrar la entrega.";
  }

  if (workflow.step === "printer_pending") {
    return "La sucursal trabaja con impresora. Imprime la comanda para que cocina tome el pedido.";
  }

  if (workflow.step === "printer_printed") {
    return "La comanda ya salio para cocina. Si hace falta, puedes reimprimirla antes de marcar el pedido como listo.";
  }

  if (workflow.step === "screen_pending") {
    return "El pedido ya entro en la pantalla de cocina, pero todavia falta que alguien lo tome.";
  }

  if (workflow.step === "screen_confirmed") {
    return "La cocina ya lo esta preparando. Cuando el jefe de cocina lo marque como listo, recien ahi podras asignar la entrega.";
  }

  return "El pedido ya esta siguiendo el circuito operativo de la sucursal.";
};

const canRequestReadyDelivery = (mesa) => {
  const workflow = getKitchenDispatchWorkflowState(mesa);

  if (workflow.step === "not_required") {
    return workflow.phase !== "new";
  }

  if (workflow.phase === "new") {
    return false;
  }

  if (workflow.phase === "ready") {
    return true;
  }

  if (workflow.mode === "manual") {
    return workflow.step !== "manual_pending";
  }

  if (workflow.mode === "printer") {
    return workflow.step !== "printer_pending";
  }

  if (workflow.mode === "screen") {
    return workflow.step === "ready";
  }

  return true;
};

const getMesaCustomerName = (mesa) =>
  String(mesa?.dinerName || mesa?.customerName || mesa?.customerAlias || "").trim();

const getMesaPreparationNumber = (mesa) => {
  return formatPreparationNumber(mesa?.preparationNumber) || null;
};

const isMesaCashPendingBySnapshot = (mesa) => {
  const paymentMethod = String(
    mesa?.selectedPaymentMethod || mesa?.requestedPaymentMethod || ""
  )
    .trim()
    .toLowerCase();
  const paymentStatus = String(mesa?.paymentStatus || "pending")
    .trim()
    .toLowerCase();
  const isCounterCollection =
    paymentMethod === "counter" ||
    paymentMethod === "caja" ||
    paymentMethod === "efectivo" ||
    paymentMethod === "cash";

  return (
    mesa?.source === "kiosk" &&
    isCounterCollection &&
    paymentStatus !== "paid" &&
    paymentStatus !== "closed"
  );
};

const getMesaPaymentMethodLabel = (mesa) => {
  if (mesa?.paymentMethod === "bonified") return "Cuenta bonificada por el local";
  const paymentMethod = String(
    mesa?.selectedPaymentMethod ||
      mesa?.requestedPaymentMethod ||
      mesa?.paymentMethod ||
      ""
  )
    .trim()
    .toLowerCase();

  if (["deep_link", "mercado_pago", "qr", "pay_by_phone"].includes(paymentMethod)) {
    return "QR / billetera digital";
  }

  if (["counter", "caja", "cash", "efectivo"].includes(paymentMethod)) {
    return mesa?.source === "app" || mesa?.source === "qr"
      ? "Pagar despues"
      : "Pagar en caja";
  }

  if (paymentMethod === "transferencia") {
    return "Transferencia";
  }

  if (["card", "tarjeta"].includes(paymentMethod)) {
    return "Tarjeta";
  }

  if (["waiter_point", "point"].includes(paymentMethod)) {
    return "Tarjeta / Point";
  }

  return paymentMethod ? paymentMethod.replace(/_/g, " ") : "Sin definir";
};

const getMesaPaymentStatusLabel = (mesa) => {
  const paymentStatus = String(mesa?.paymentStatus || "pending")
    .trim()
    .toLowerCase();

  if (["paid", "approved", "closed", "completed", "accredited"].includes(paymentStatus)) {
    return "Pago verificado";
  }

  if (["refunded", "reversed"].includes(paymentStatus)) {
    return "Devuelto";
  }

  if (["cancelled", "rejected", "failed", "expired"].includes(paymentStatus)) {
    return "No pagado";
  }

  if (["link_sent", "pending_confirmation", "qr_ready"].includes(paymentStatus)) {
    return "Esperando confirmacion digital";
  }

  return "Pendiente de pago";
};

const getMesaMetaLabel = (mesa) => {
  const baseLabel = `${mesa.comensales} comensales - ${mesa.canal}`;
  const customerName = getMesaCustomerName(mesa);

  return customerName ? `${baseLabel} · ${customerName}` : baseLabel;
};

const getMesaOperationalLabel = (mesa) => {
  const baseLabel = getMesaMetaLabel(mesa);
  const preparationNumber = getMesaPreparationNumber(mesa);

  return preparationNumber ? `${baseLabel} · #${preparationNumber}` : baseLabel;
};

const getMesaOrderSummary = (mesa) => mesa.items.map((item) => item.nombre).join(", ");
const getDeliveredCount = (mesa) => mesa.items.filter((item) => item.delivered).length;

const resolveMenuProductPrice = (product) => {
  if (!product) {
    return 0;
  }

  if (productHasPizzaSizeOptions(product)) {
    const prices = getPizzaSizeOptions(product)
      .map(getPizzaStartingPrice)
      .filter((price) => price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  const priceMode = String(product.modoPrecio || product.priceMode || product.price_mode || "")
    .trim()
    .toLowerCase();
  const price = Number(product.precio ?? product.price ?? 0);
  const suggestedPrice = Number(
    product.precioSugerido ?? product.suggestedPrice ?? product.suggested_price ?? price
  );

  if (priceMode === "manual") {
    return price;
  }

  return suggestedPrice || price;
};

const buildCatalogItemSummary = (items = []) =>
  items.map((item) => `${item.quantity}x ${item.name}`).join(", ");

const DEFAULT_MANUAL_EXTRA_QUANTITY = 2;

const createManualSelectionDraft = (product) =>
  (product?.ingredients || []).reduce((acc, ingredient) => {
    acc[ingredient.id] = Number(ingredient.baseQuantity || 0);
    return acc;
  }, {});

const getManualCustomizationSummary = (product, selection = {}) => {
  if (!product?.ingredients?.length) {
    return null;
  }

  let removedUnits = 0;
  let extraUnits = 0;
  let extraCost = 0;

  product.ingredients.forEach((ingredient) => {
    const baseQuantity = Number(ingredient.baseQuantity || 0);
    const selectedQuantity = Number(selection?.[ingredient.id] ?? baseQuantity);

    if (selectedQuantity < baseQuantity) {
      removedUnits += baseQuantity - selectedQuantity;
    }

    if (selectedQuantity > baseQuantity) {
      const extra = selectedQuantity - baseQuantity;
      extraUnits += extra;
      extraCost += extra * Number(ingredient.price || 0);
    }
  });

  if (!removedUnits && !extraUnits) {
    return null;
  }

  return {
    removedUnits,
    extraUnits,
    extraCost,
  };
};

const getManualIngredientStateLabel = (ingredient, quantity) => {
  const baseQuantity = Number(ingredient.baseQuantity || 0);

  if (baseQuantity <= 0 && quantity <= 0) {
    return "Sin agregar";
  }

  if (quantity <= 0) {
    return "Quitado";
  }

  if (quantity === baseQuantity) {
    return "Normal";
  }

  if (quantity > baseQuantity) {
    return `+${quantity - baseQuantity} extra`;
  }

  return "Ajustado";
};

const buildManualSelectionDetails = (product, selection = {}) => {
  if (!product?.ingredients?.length) {
    return [];
  }

  return product.ingredients.reduce((acc, ingredient) => {
    const baseQuantity = Number(ingredient.baseQuantity || 0);
    const selectedQuantity = Number(selection?.[ingredient.id] ?? baseQuantity);

    if (selectedQuantity === baseQuantity) {
      return acc;
    }

    if (selectedQuantity <= 0 && baseQuantity > 0) {
      acc.push(`Sin ${ingredient.name}`);
      return acc;
    }

    if (selectedQuantity < baseQuantity) {
      acc.push(`-${baseQuantity - selectedQuantity} ${ingredient.name}`);
      return acc;
    }

    if (selectedQuantity > baseQuantity) {
      acc.push(`+${selectedQuantity - baseQuantity} ${ingredient.name}`);
    }

    return acc;
  }, []);
};

const getRestaurantOperationalState = (
  horarioDetallado,
  nowDate,
  closingControl
) => {
  const branchState = getEffectiveBranchOperatingState(
    horarioDetallado,
    nowDate,
    closingControl
  );
  const isExtended =
    branchState.isExtensionActive && !branchState.isScheduledOpen;
  const isOpen = branchState.isOpen;

  return {
    ...branchState,
    isExtended,
    isOpen,
    title: isExtended
      ? "Horario extendido"
      : isOpen
        ? "Restaurante operativo"
        : "Restaurante inoperativo",
    serviceText: isExtended
      ? `Atencion extendida hasta las ${formatBranchClosingTime(
          branchState.effectiveClosingAt
        )}.`
      : isOpen
        ? "Tomando pedidos y operando con normalidad."
        : "Fuera de horario. Solo finalizando pedidos ya ingresados.",
    intakeText: isOpen ? "Recepcion habilitada" : "No se toman pedidos nuevos",
  };
};

const LIVE_METRIC_ACCENTS = {
  sky: {
    glow: "rgba(255, 239, 221, 0.54)",
    border: "rgba(251, 191, 154, 0.78)",
    shadow: "rgba(194, 120, 67, 0.24)",
    wash:
      "linear-gradient(135deg, rgba(255,251,246,0.99) 0%, rgba(255,255,255,0.74) 30%, rgba(255,237,213,0.54) 68%, rgba(254,215,170,0.72) 100%)",
    horizontal:
      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 16%, rgba(255,237,213,0.96) 34%, rgba(251,191,154,0.98) 50%, rgba(255,237,213,0.96) 66%, rgba(255,255,255,0.98) 84%, rgba(255,255,255,0) 100%)",
    vertical:
      "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 16%, rgba(255,237,213,0.96) 34%, rgba(251,191,154,0.98) 50%, rgba(255,237,213,0.96) 66%, rgba(255,255,255,0.98) 84%, rgba(255,255,255,0) 100%)",
  },
  emerald: {
    glow: "rgba(255, 239, 221, 0.54)",
    border: "rgba(251, 191, 154, 0.78)",
    shadow: "rgba(194, 120, 67, 0.24)",
    wash:
      "linear-gradient(135deg, rgba(255,251,246,0.99) 0%, rgba(255,255,255,0.74) 30%, rgba(255,237,213,0.54) 68%, rgba(254,215,170,0.72) 100%)",
    horizontal:
      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 16%, rgba(255,237,213,0.96) 34%, rgba(251,191,154,0.98) 50%, rgba(255,237,213,0.96) 66%, rgba(255,255,255,0.98) 84%, rgba(255,255,255,0) 100%)",
    vertical:
      "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 16%, rgba(255,237,213,0.96) 34%, rgba(251,191,154,0.98) 50%, rgba(255,237,213,0.96) 66%, rgba(255,255,255,0.98) 84%, rgba(255,255,255,0) 100%)",
  },
};

const SERVICE_NOTIFICATION_ACCENT = {
  glow: "rgba(248, 113, 113, 0.18)",
  border: "rgba(253, 186, 116, 0.38)",
  horizontal:
    "linear-gradient(90deg, rgba(220,38,38,0) 0%, rgba(248,113,113,0.95) 18%, rgba(250,204,21,0.98) 50%, rgba(248,113,113,0.95) 82%, rgba(220,38,38,0) 100%)",
  vertical:
    "linear-gradient(180deg, rgba(220,38,38,0) 0%, rgba(248,113,113,0.95) 18%, rgba(250,204,21,0.98) 50%, rgba(248,113,113,0.95) 82%, rgba(220,38,38,0) 100%)",
};

/* Legacy local notification helpers kept only as historical reference.
const playNewOrderChimeLocal = () => {
  if (typeof window === "undefined") {
    return;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextClass) {
    return;
  }

  try {
    const context = new AudioContextClass();
    const now = context.currentTime;
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.9, now);
    masterGain.connect(context.destination);

    const createBellStrike = (startAt, fundamental, volumeScale = 1) => {
      const partials = [
        { ratio: 1, gain: 0.24, decay: 1.35, type: "sine" },
        { ratio: 2.52, gain: 0.13, decay: 1.05, type: "triangle" },
        { ratio: 3.97, gain: 0.085, decay: 0.88, type: "sine" },
        { ratio: 5.35, gain: 0.05, decay: 0.68, type: "triangle" },
      ];

      partials.forEach((partial) => {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const endAt = startAt + partial.decay;

        oscillator.type = partial.type;
        oscillator.frequency.setValueAtTime(fundamental * partial.ratio, startAt);
        oscillator.detune.setValueAtTime(partial.ratio > 1 ? 6 : 0, startAt);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.linearRampToValueAtTime(
          partial.gain * volumeScale,
          startAt + 0.012
        );
        gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);

        oscillator.connect(gainNode);
        gainNode.connect(masterGain);

        oscillator.start(startAt);
        oscillator.stop(endAt);
      });
    };

    createBellStrike(now, 880, 1);
    createBellStrike(now + 0.16, 1320, 0.42);

    window.setTimeout(() => {
      context.close().catch(() => {});
    }, 1900);
  } catch (error) {
    // Si el navegador bloquea audio automático, dejamos solo las otras alertas.
  }
};

const showBrowserOrderNotification = (mesa, extraCount = 0) => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return;
  }

  const title =
    extraCount > 0
      ? `Entraron ${extraCount + 1} pedidos nuevos`
      : `Nuevo pedido en ${mesa.mesa}`;
  const body =
    extraCount > 0
      ? `${mesa.mesa} y otras mesas quedaron pendientes de revisar.`
      : getMesaOrderSummary(mesa);

  const createNotification = () => {
    try {
      const notification = new Notification(title, {
        body,
        tag: `mozzoapp-order-${mesa.id}`,
        renotify: true,
        silent: true,
      });

      window.setTimeout(() => notification.close(), 7000);
    } catch (error) {
      // Ignoramos navegadores que no soporten esta forma de notificación.
    }
  };

  if (Notification.permission === "granted") {
    createNotification();
    return;
  }

  if (Notification.permission === "default") {
    Notification.requestPermission()
      .then((permission) => {
        if (permission === "granted") {
          createNotification();
        }
      })
      .catch(() => {});
  }
};
*/

function Inicio() {
  const navigate = useNavigate();
  const {
    canAccessModule,
    canPerformAction,
    currentAccessRoleLabel,
    currentRestaurant,
    currentBranch,
    currentCompany,
    isCreatorMode,
    user,
  } = useAccessControl();
  const { ingredientes } = useIngredientes();
  const operationalRestaurant = useMemo(
    () => ({
      id: currentBranch?.id || currentRestaurant?.id || "",
      name: currentBranch?.name || currentRestaurant?.name || "",
    }),
    [currentBranch?.id, currentBranch?.name, currentRestaurant?.id, currentRestaurant?.name]
  );
  const stockFeatureEnabled =
    (currentBranch || currentRestaurant || {}).stockEnabled !== false;
  const [manualOrderForm, setManualOrderForm] = useState(initialManualOrderForm);
  const [manualOrderItems, setManualOrderItems] = useState([]);
  const [manualOrderSelection, setManualOrderSelection] = useState({});
  const [manualOrderPizzaSizeOptionId, setManualOrderPizzaSizeOptionId] =
    useState("");
  const [manualOrderPizzaPortionMode, setManualOrderPizzaPortionMode] = useState("full");
  const [manualOrderPizzaHalfProductId, setManualOrderPizzaHalfProductId] =
    useState("");
  const [manualOrderSearch, setManualOrderSearch] = useState("");
  const [manualOrderMessage, setManualOrderMessage] = useState({ type: "", text: "" });
  const [manualDeliveryZones, setManualDeliveryZones] = useState([]);
  const [manualKnownCustomers, setManualKnownCustomers] = useState([]);
  const [manualCustomerSearch, setManualCustomerSearch] = useState("");
  const [isManualOrderSaving, setIsManualOrderSaving] = useState(false);
  const [ingredientStockSnapshot, setIngredientStockSnapshot] = useState({});
  const [isManualOrderExpanded, setIsManualOrderExpanded] = useState(false);
  const [isPromosExpanded, setIsPromosExpanded] = useState(false);
  const [isStockAlertExpanded, setIsStockAlertExpanded] = useState(false);
  const [perfil, setPerfil] = useState(() => getRestaurantProfile(operationalRestaurant));
  const [detalleActivo, setDetalleActivo] = useState(null);
  const [detalleMesaId, setDetalleMesaId] = useState("");
  const [salonSelectedTableId, setSalonSelectedTableId] = useState("");
  const [salonLayout, setSalonLayout] = useState(
    () => getInicioStoredState(operationalRestaurant).salonLayout || {}
  );
  const [salonReservations, setSalonReservations] = useState(
    () => getInicioStoredState(operationalRestaurant).salonReservations || {}
  );
  const [salonRooms, setSalonRooms] = useState(() =>
    normalizeSalonRoomsForUi(getInicioStoredState(operationalRestaurant).salonRooms)
  );
  const [salonTableRooms, setSalonTableRooms] = useState(
    () => getInicioStoredState(operationalRestaurant).salonTableRooms || {}
  );
  const [salonFixtures, setSalonFixtures] = useState(() =>
    normalizeSalonFixturesForUi(
      getInicioStoredState(operationalRestaurant).salonFixtures
    )
  );
  const [salonActiveRoomId, setSalonActiveRoomId] = useState(
    DEFAULT_SALON_ROOM_ID
  );
  const [salonSelectedFixtureId, setSalonSelectedFixtureId] = useState("");
  const [salonRoomsManagerOpen, setSalonRoomsManagerOpen] = useState(false);
  const [salonNewRoomName, setSalonNewRoomName] = useState("");
  const [salonEditMode, setSalonEditMode] = useState(false);
  const [salonTableFormatDraft, setSalonTableFormatDraft] = useState(
    () => ({ ...DEFAULT_SALON_TABLE_GEOMETRY })
  );
  const [salonReservationTableId, setSalonReservationTableId] = useState("");
  const [salonReservationEditId, setSalonReservationEditId] = useState("");
  const [salonReservationsOpen, setSalonReservationsOpen] = useState(false);
  const [salonReservationForm, setSalonReservationForm] = useState(
    EMPTY_SALON_RESERVATION_FORM
  );
  const [notificationDetailMesaId, setNotificationDetailMesaId] = useState("");
  const [mesasActivas, setMesasActivas] = useState(
    () => getInicioStoredState(operationalRestaurant).mesasActivas
  );
  const [pedidosHistoricos, setPedidosHistoricos] = useState(
    () => getInicioStoredState(operationalRestaurant).pedidosHistoricos
  );
  const [promotions, setPromotions] = useState(() => getPromotions(operationalRestaurant));
  const [menuCatalog, setMenuCatalog] = useState({
    categories: [],
    products: [],
  });
  const [availableTables, setAvailableTables] = useState(() =>
    getBranchTablesSnapshot(currentBranch?.id)
  );
  const sharedSalonTables = useMemo(
    () =>
      normalizeSalonTables(
        availableTables.map((table) => ({
          id: table.id,
          branchId: table.branchId || currentBranch?.id || "",
          tableNumber: table.tableNumber,
          label: table.label,
          active: table.active !== false,
        }))
      ),
    [availableTables, currentBranch?.id]
  );
  const [activeWaiters, setActiveWaiters] = useState([]);
  const [waiterOps, setWaiterOps] = useState(() =>
    getWaiterOperationsState(operationalRestaurant)
  );
  const [deliveryAssignmentMesaId, setDeliveryAssignmentMesaId] = useState("");
  const [deliveryAssignmentWaiterId, setDeliveryAssignmentWaiterId] = useState("");
  const [deliveryAssignmentItemIds, setDeliveryAssignmentItemIds] = useState([]);
  const [waiterRequestAssignmentId, setWaiterRequestAssignmentId] = useState("");
  const [waiterRequestAssignmentWaiterId, setWaiterRequestAssignmentWaiterId] = useState("");
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [branchClosingControl, setBranchClosingControl] = useState(() =>
    getBranchClosingControl(operationalRestaurant)
  );
  const [serviceAlert, setServiceAlert] = useState(null);
  const [localPaymentCloseAction, setLocalPaymentCloseAction] = useState({
    mesaId: "",
    method: "",
  });
  const [orderReportFilters, setOrderReportFilters] = useState(() => {
    const today = formatDateInputValue(new Date());
    return {
      from: today,
      to: today,
      statusMode: "all",
      statuses: [],
    };
  });
  const knownNotificationIdsRef = useRef(new Set());
  const notificationHydratedRef = useRef(false);
  const syncingOperationalStateRef = useRef(false);
  const sharedOperationalStateReadyRef = useRef(
    Boolean(isCreatorMode || !isSupabaseConfigured)
  );
  const operationalSyncHoldUntilRef = useRef(0);
  const salonStructureSyncPendingRef = useRef(0);
  const mesaMutationOverridesRef = useRef(new Map());
  const mesaActionLocksRef = useRef(new Map());
  const manualOrderSubmissionLockRef = useRef(false);
  const autoKitchenPrintKnownIdsRef = useRef(new Set());
  const autoKitchenPrintInFlightIdsRef = useRef(new Set());
  const autoKitchenPrintHydratedRef = useRef(false);
  const printKitchenOrderTicketRef = useRef(null);
  const salonFloorRef = useRef(null);
  const salonDragRef = useRef(null);
  const salonFixtureDragRef = useRef(null);
  const canSeeInicioModule = canAccessModule("inicio");
  // Permisos v1.1: Inicio resuelve toma, cobro y mutaciones de pedidos desde accessControl.
  const canTakeOrders = canSeeInicioModule && canPerformAction("take_orders");
  const canModifyOrders = canSeeInicioModule && canPerformAction("modify_orders");
  const canCancelOrders = canSeeInicioModule && canPerformAction("cancel_orders");
  const canRefundOrders = canSeeInicioModule && canPerformAction("refund_orders");
  const canChargeOrders = canSeeInicioModule && canPerformAction("charge_orders");
  const canExportOperationalReports =
    canSeeInicioModule && canPerformAction("export_reports");
  const canOpenPromosModule = canAccessModule("promos");
  const isManualOrderKioskMode = useMemo(() => {
    const branchServiceMode = String(currentBranch?.serviceMode || currentBranch?.service_mode || "")
      .trim()
      .toLowerCase();
    const restaurantServiceMode = String(
      currentRestaurant?.serviceMode || currentRestaurant?.service_mode || ""
    )
      .trim()
      .toLowerCase();

    return (
      branchServiceMode === "kiosk" ||
      (!branchServiceMode && restaurantServiceMode === "kiosk")
    );
  }, [
    currentBranch?.serviceMode,
    currentBranch?.service_mode,
    currentRestaurant?.serviceMode,
    currentRestaurant?.service_mode,
  ]);
  const manualOrderDestinationType = isManualOrderKioskMode
    ? "pickup"
    : manualOrderForm.destinationType || "table";
  const notifyInicioPermissionDenied = useCallback(
    (actionLabel, channel = "service") => {
      const text = `${currentAccessRoleLabel} no puede ${actionLabel} desde Inicio.`;

      if (channel === "manual") {
        setManualOrderMessage({ type: "error", text });
        return false;
      }

      setServiceAlert({
        id: `perm-${Date.now()}`,
        title: "Acceso restringido",
        text,
      });
      return false;
    },
    [currentAccessRoleLabel]
  );
  const acquireMesaActionLock = useCallback((mesaId, actionKey, ttlMs = 1500) => {
    const safeMesaId = String(mesaId || "").trim();
    const safeActionKey = String(actionKey || "").trim();

    if (!safeMesaId || !safeActionKey) {
      return true;
    }

    const lockId = `${safeMesaId}:${safeActionKey}`;
    const now = Date.now();
    const lockedUntil = Number(mesaActionLocksRef.current.get(lockId) || 0);

    if (lockedUntil > now) {
      return false;
    }

    mesaActionLocksRef.current.set(lockId, now + ttlMs);
    return true;
  }, []);
  const releaseMesaActionLock = useCallback((mesaId, actionKey) => {
    const safeMesaId = String(mesaId || "").trim();
    const safeActionKey = String(actionKey || "").trim();

    if (!safeMesaId || !safeActionKey) {
      return;
    }

    mesaActionLocksRef.current.delete(`${safeMesaId}:${safeActionKey}`);
  }, []);
  const scheduleMesaActionLockRelease = useCallback(
    (mesaId, actionKey, delayMs = 1800) => {
      window.setTimeout(() => {
        releaseMesaActionLock(mesaId, actionKey);
      }, delayMs);
    },
    [releaseMesaActionLock]
  );
  const ensureInicioPermission = useCallback(
    (allowed, actionLabel, channel = "service") =>
      allowed || notifyInicioPermissionDenied(actionLabel, channel),
    [notifyInicioPermissionDenied]
  );
  const handleOpenPromosModule = useCallback(() => {
    if (!ensureInicioPermission(canOpenPromosModule, "abrir el modulo Promos")) {
      return;
    }

    navigate("/app/promos");
  }, [canOpenPromosModule, ensureInicioPermission, navigate]);
  const syncManualCommissionDebtSnapshot = useCallback(
    async (operation, note = "") => {
      if (
        !operation?.id ||
        !operationalRestaurant?.id ||
        isCreatorMode ||
        !isSupabaseConfigured
      ) {
        return null;
      }

      try {
        return await syncManualCommissionDebtForOperation({
          branch: operationalRestaurant,
          operation,
          note,
          metadata: {
            uiSource: "inicio",
          },
        });
      } catch (error) {
        console.error(
          "No pudimos sincronizar la deuda manual de comision desde Inicio:",
          error
        );
        return null;
      }
    },
    [isCreatorMode, operationalRestaurant]
  );
  const registerManualOperationalMzUsage = useCallback(
    async ({
      orderId,
      paymentOperationId,
      sourceChannel = "manual",
      sourceMode = "unknown",
      metadata = {},
    } = {}) => {
      if (
        !orderId ||
        !paymentOperationId ||
        !operationalRestaurant?.id ||
        isCreatorMode ||
        !isSupabaseConfigured
      ) {
        return null;
      }

      return recordMzRuntimeUsage({
        branchId: operationalRestaurant.id,
        eventKey: "counter_order_confirmed",
        orderId,
        paymentOperationId,
        sourceChannel,
        sourceMode,
        metadata: {
          uiSource: "inicio",
          ...metadata,
        },
      });
    },
    [isCreatorMode, operationalRestaurant]
  );
  const beginOperationalRemoteSyncHold = useCallback(
    (durationMs = OPERATIONAL_REMOTE_SYNC_HOLD_MS) => {
      operationalSyncHoldUntilRef.current = Math.max(
        operationalSyncHoldUntilRef.current,
        Date.now() + Math.max(0, Number(durationMs || 0))
      );
    },
    []
  );
  const shouldHoldOperationalRemoteSync = useCallback(
    () =>
      salonStructureSyncPendingRef.current > 0 ||
      Date.now() < operationalSyncHoldUntilRef.current,
    []
  );
  const rememberMesaMutationOverride = useCallback(
    (mesaId, patch = {}, durationMs = OPERATIONAL_MESA_OVERRIDE_MS) => {
      const normalizedMesaId = String(mesaId || "").trim();

      if (!normalizedMesaId) {
        return;
      }

      mesaMutationOverridesRef.current.set(normalizedMesaId, {
        ...patch,
        expiresAt:
          Date.now() + Math.max(1000, Number(durationMs || OPERATIONAL_MESA_OVERRIDE_MS)),
      });
    },
    []
  );
  const applyMesaMutationOverridesToState = useCallback((state) => {
    const baseState =
      state && typeof state === "object"
        ? state
        : {
            mesasActivas: [],
            pedidosHistoricos: [],
            salonTables: [],
            salonLayout: {},
            salonReservations: {},
            salonRooms: [],
            salonTableRooms: {},
            salonFixtures: {},
          };
    const now = Date.now();
    const activeMesas = Array.isArray(baseState.mesasActivas)
      ? baseState.mesasActivas
      : [];
    const historicalOrders = Array.isArray(baseState.pedidosHistoricos)
      ? baseState.pedidosHistoricos
      : [];

    Array.from(mesaMutationOverridesRef.current.entries()).forEach(
      ([mesaId, override]) => {
        if (!override || Number(override.expiresAt || 0) <= now) {
          mesaMutationOverridesRef.current.delete(mesaId);
        }
      }
    );

    const nextMesasActivas = activeMesas
      .map((mesa) => {
        const override = mesaMutationOverridesRef.current.get(mesa.id);

        if (!override) {
          return mesa;
        }

        const overridePhase = String(override.phase || "").trim().toLowerCase();

        if (overridePhase === "closed" || overridePhase === "cancelled") {
          return null;
        }

        const mesaPhasePriority = getMesaPhaseSyncPriority(mesa.phase);
        const overridePhasePriority = getMesaPhaseSyncPriority(override.phase);
        const shouldApplyPhase =
          overridePhase && overridePhasePriority >= mesaPhasePriority;

        return {
          ...mesa,
          ...(shouldApplyPhase && override.phase ? { phase: override.phase } : {}),
          ...(override.paymentStatus ? { paymentStatus: override.paymentStatus } : {}),
          ...(override.cashPaymentPending !== undefined
            ? { cashPaymentPending: override.cashPaymentPending }
            : {}),
          ...(override.stockDiscounted !== undefined
            ? { stockDiscounted: override.stockDiscounted }
            : {}),
          ...(override.kitchenModeSnapshot
            ? { kitchenModeSnapshot: override.kitchenModeSnapshot }
            : {}),
          ...(override.kitchenDispatchStatus
            ? { kitchenDispatchStatus: override.kitchenDispatchStatus }
            : {}),
          ...(override.kitchenDispatchedAt
            ? { kitchenDispatchedAt: override.kitchenDispatchedAt }
            : {}),
          ...(override.kitchenDispatchedBy
            ? { kitchenDispatchedBy: override.kitchenDispatchedBy }
            : {}),
          ...(override.cashCollectedAt
            ? { cashCollectedAt: override.cashCollectedAt }
            : {}),
          ...(override.cashCollectedBy
            ? { cashCollectedBy: override.cashCollectedBy }
            : {}),
        };
      })
      .filter(Boolean);

    return {
      mesasActivas: nextMesasActivas,
      pedidosHistoricos: historicalOrders,
      salonTables: normalizeSalonTables(baseState.salonTables),
      salonLayout:
        baseState.salonLayout && typeof baseState.salonLayout === "object"
          ? baseState.salonLayout
          : {},
      salonReservations:
        baseState.salonReservations && typeof baseState.salonReservations === "object"
          ? baseState.salonReservations
          : {},
      salonRooms: normalizeSalonRoomsForUi(baseState.salonRooms),
      salonTableRooms:
        baseState.salonTableRooms && typeof baseState.salonTableRooms === "object"
          ? baseState.salonTableRooms
          : {},
      salonFixtures: normalizeSalonFixturesForUi(baseState.salonFixtures),
    };
  }, []);
  const applySharedOrderMutationPayload = useCallback(
    (payload) => {
      if (!payload || typeof payload !== "object") {
        return null;
      }

      const nextState = applyMesaMutationOverridesToState(payload);

      beginOperationalRemoteSyncHold();
      syncingOperationalStateRef.current = true;
      saveOperationalOrdersState(
        operationalRestaurant,
        nextState,
        createEmptyOperationalState
      );
      setMesasActivas(nextState.mesasActivas);
      setPedidosHistoricos(nextState.pedidosHistoricos);
      setSalonLayout(nextState.salonLayout || {});
      setSalonReservations(nextState.salonReservations || {});
      setSalonRooms(normalizeSalonRoomsForUi(nextState.salonRooms));
      setSalonTableRooms(nextState.salonTableRooms || {});
      setSalonFixtures(normalizeSalonFixturesForUi(nextState.salonFixtures));

      return nextState;
    },
    [
      applyMesaMutationOverridesToState,
      beginOperationalRemoteSyncHold,
      operationalRestaurant,
    ]
  );
  const applySharedPaymentClaimPayload = useCallback(
    (payload) => {
      const nextState = applySharedOrderMutationPayload(payload);

      if (Array.isArray(payload?.paymentOperations)) {
        savePaymentOperations(operationalRestaurant, payload.paymentOperations);
      }

      return nextState;
    },
    [applySharedOrderMutationPayload, operationalRestaurant]
  );

  useEffect(() => {
    setPerfil(getRestaurantProfile(operationalRestaurant));
    const nextState = getInicioStoredState(operationalRestaurant);
    sharedOperationalStateReadyRef.current = Boolean(
      isCreatorMode || !isSupabaseConfigured || !operationalRestaurant?.id
    );
    syncingOperationalStateRef.current = true;
    setMesasActivas(nextState.mesasActivas);
    setPedidosHistoricos(nextState.pedidosHistoricos);
    setSalonLayout(nextState.salonLayout || {});
    setSalonReservations(nextState.salonReservations || {});
    const nextSalonRooms = normalizeSalonRoomsForUi(nextState.salonRooms);
    setSalonRooms(nextSalonRooms);
    setSalonTableRooms(nextState.salonTableRooms || {});
    setSalonFixtures(normalizeSalonFixturesForUi(nextState.salonFixtures));
    setSalonActiveRoomId(nextSalonRooms[0].id);
    setSalonSelectedFixtureId("");
    setSalonRoomsManagerOpen(false);
    setSalonNewRoomName("");
    setManualOrderForm(initialManualOrderForm);
    setManualOrderItems([]);
    setManualOrderSelection({});
    setManualOrderPizzaSizeOptionId("");
    setManualOrderPizzaHalfProductId("");
    setManualOrderSearch("");
    setManualOrderMessage({ type: "", text: "" });
    setIsManualOrderExpanded(false);
    setIsPromosExpanded(false);
    setIsStockAlertExpanded(false);
    setDetalleActivo(null);
    setDetalleMesaId("");
    setSalonSelectedTableId("");
    setSalonEditMode(false);
    setSalonReservationTableId("");
    setSalonReservationEditId("");
    setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
    setNotificationDetailMesaId("");
    setDeliveryAssignmentMesaId("");
    setDeliveryAssignmentWaiterId("");
    setWaiterRequestAssignmentId("");
    setWaiterRequestAssignmentWaiterId("");
    setServiceAlert(null);
    setLocalPaymentCloseAction({ mesaId: "", method: "" });
    knownNotificationIdsRef.current = new Set();
    notificationHydratedRef.current = false;
    operationalSyncHoldUntilRef.current = 0;
    salonStructureSyncPendingRef.current = 0;
    mesaMutationOverridesRef.current = new Map();
    manualOrderSubmissionLockRef.current = false;
    setIsManualOrderSaving(false);
    autoKitchenPrintKnownIdsRef.current = new Set();
    autoKitchenPrintInFlightIdsRef.current = new Set();
    autoKitchenPrintHydratedRef.current = false;
    setPromotions(getPromotions(operationalRestaurant));
    setActiveWaiters([]);
    setWaiterOps(getWaiterOperationsState(operationalRestaurant));
  }, [isCreatorMode, operationalRestaurant]);

  useEffect(() => {
    let active = true;

    const syncRemoteProfile = async () => {
      if (!operationalRestaurant?.id || isCreatorMode || !isSupabaseConfigured) {
        return;
      }

      try {
        const localProfile = getRestaurantProfile(operationalRestaurant);
        const remoteProfile = await getBranchPublicProfile(operationalRestaurant.id);
        const mergedProfile = saveRestaurantProfile(
          operationalRestaurant,
          mergeRemoteBranchIntoProfilePreservingLocalPreferences(
            localProfile,
            remoteProfile
          )
        );

        if (active) {
          setPerfil(mergedProfile);
        }
      } catch (error) {
        if (active) {
          console.error(
            "No pudimos sincronizar el modo de cocina de la sucursal en Inicio:",
            error
          );
        }
      }
    };

    syncRemoteProfile();

    return () => {
      active = false;
    };
  }, [isCreatorMode, operationalRestaurant]);

  useEffect(() => {
    if (syncingOperationalStateRef.current) {
      syncingOperationalStateRef.current = false;
      return;
    }

    if (!sharedOperationalStateReadyRef.current) {
      return;
    }

    saveInicioStoredState(operationalRestaurant, {
      mesasActivas,
      pedidosHistoricos,
      ...(sharedSalonTables.length ? { salonTables: sharedSalonTables } : {}),
      salonLayout,
      salonReservations,
      salonRooms,
      salonTableRooms,
      salonFixtures,
    });
  }, [
    mesasActivas,
    operationalRestaurant,
    pedidosHistoricos,
    sharedSalonTables,
    salonLayout,
    salonReservations,
    salonRooms,
    salonTableRooms,
    salonFixtures,
  ]);

  useEffect(() => {
    const restaurantId = operationalRestaurant?.id || "default";

    const syncWaiterOps = () => {
      setWaiterOps(getWaiterOperationsState(operationalRestaurant));
    };

    const handleWaiterUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      setWaiterOps(
        event.detail?.payload || getWaiterOperationsState(operationalRestaurant)
      );
    };

    const handleStorage = (event) => {
      if (!event.key || event.key === buildWaiterOperationsStorageKey(operationalRestaurant?.id)) {
        syncWaiterOps();
      }
    };

    syncWaiterOps();
    window.addEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [operationalRestaurant]);

  useEffect(() => {
    if (!notificationDetailMesaId) {
      return;
    }

    const stillExists = mesasActivas.some((mesa) => mesa.id === notificationDetailMesaId);

    if (!stillExists) {
      setNotificationDetailMesaId("");
    }
  }, [notificationDetailMesaId, mesasActivas]);

  useEffect(() => {
    if (!deliveryAssignmentMesaId) {
      return;
    }

    const stillExists = mesasActivas.some((mesa) => mesa.id === deliveryAssignmentMesaId);

    if (!stillExists) {
      setDeliveryAssignmentMesaId("");
      setDeliveryAssignmentWaiterId("");
      setDeliveryAssignmentItemIds([]);
    }
  }, [deliveryAssignmentMesaId, mesasActivas]);

  useEffect(() => {
    if (!waiterRequestAssignmentId) {
      return;
    }

    const stillExists = (waiterOps.serviceRequests || []).some(
      (request) =>
        request.id === waiterRequestAssignmentId &&
        request.status === "pending" &&
        (!currentBranch?.id || request.branchId === currentBranch.id)
    );

    if (!stillExists) {
      setWaiterRequestAssignmentId("");
      setWaiterRequestAssignmentWaiterId("");
    }
  }, [currentBranch?.id, waiterOps.serviceRequests, waiterRequestAssignmentId]);

  useEffect(() => {
    if (!notificationDetailMesaId) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setNotificationDetailMesaId("");
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [notificationDetailMesaId]);

  useEffect(() => {
    if (!deliveryAssignmentMesaId) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setDeliveryAssignmentMesaId("");
        setDeliveryAssignmentWaiterId("");
        setDeliveryAssignmentItemIds([]);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [deliveryAssignmentMesaId]);

  useEffect(() => {
    if (!waiterRequestAssignmentId) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setWaiterRequestAssignmentId("");
        setWaiterRequestAssignmentWaiterId("");
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [waiterRequestAssignmentId]);

  useEffect(() => {
    if (detalleActivo !== "salon") {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setDetalleActivo(null);
        setSalonSelectedTableId("");
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [detalleActivo]);

  useEffect(() => {
    const restaurantId = operationalRestaurant?.id || "default";

    const syncOperationalState = () => {
      if (shouldHoldOperationalRemoteSync()) {
        return;
      }

      const nextState = applyMesaMutationOverridesToState(
        getInicioStoredState(operationalRestaurant)
      );
      syncingOperationalStateRef.current = true;
      setMesasActivas(nextState.mesasActivas);
      setPedidosHistoricos(nextState.pedidosHistoricos);
      setSalonLayout(nextState.salonLayout || {});
      setSalonReservations(nextState.salonReservations || {});
      setSalonRooms(normalizeSalonRoomsForUi(nextState.salonRooms));
      setSalonTableRooms(nextState.salonTableRooms || {});
      setSalonFixtures(normalizeSalonFixturesForUi(nextState.salonFixtures));
    };

    const handleOperationalOrdersUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      if (shouldHoldOperationalRemoteSync()) {
        return;
      }

      const nextPayload = applyMesaMutationOverridesToState(
        event.detail?.payload || getInicioStoredState(operationalRestaurant)
      );
      syncingOperationalStateRef.current = true;
      setMesasActivas(nextPayload.mesasActivas || []);
      setPedidosHistoricos(nextPayload.pedidosHistoricos || []);
      setSalonLayout(nextPayload.salonLayout || {});
      setSalonReservations(nextPayload.salonReservations || {});
      setSalonRooms(normalizeSalonRoomsForUi(nextPayload.salonRooms));
      setSalonTableRooms(nextPayload.salonTableRooms || {});
      setSalonFixtures(normalizeSalonFixturesForUi(nextPayload.salonFixtures));
    };

    const handleStorage = (event) => {
      if (event.key !== buildOperationalOrdersStorageKey(operationalRestaurant?.id)) {
        return;
      }

      syncOperationalState();
    };

    window.addEventListener(
      OPERATIONAL_ORDERS_UPDATED_EVENT,
      handleOperationalOrdersUpdate
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        OPERATIONAL_ORDERS_UPDATED_EVENT,
        handleOperationalOrdersUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [
    applyMesaMutationOverridesToState,
    operationalRestaurant,
    shouldHoldOperationalRemoteSync,
  ]);

  useEffect(() => {
    if (!operationalRestaurant?.id || isCreatorMode || !isSupabaseConfigured) {
      return undefined;
    }

    let active = true;
    let syncInFlight = false;
    let syncQueued = false;

    const syncSharedOperationalState = async () => {
      if (syncInFlight) {
        syncQueued = true;
        return;
      }

      syncInFlight = true;

      try {
        const result = await hydrateSharedOperationalStateToLocal(
          operationalRestaurant,
          createEmptyOperationalState,
          {
            shouldApplyOperationalState: () =>
              active && !shouldHoldOperationalRemoteSync(),
          }
        );

        if (!active) {
          return;
        }

        sharedOperationalStateReadyRef.current = true;

        if (!result?.supported) {
          return;
        }

        if (result.payload?.waiterOps) {
          setWaiterOps(result.payload.waiterOps);
        }

        if (
          result.skippedOperationalHydration ||
          shouldHoldOperationalRemoteSync()
        ) {
          return;
        }

        const reconciledState = applyMesaMutationOverridesToState({
          mesasActivas: Array.isArray(result.payload?.mesasActivas)
            ? result.payload.mesasActivas
            : [],
          pedidosHistoricos: Array.isArray(result.payload?.pedidosHistoricos)
            ? result.payload.pedidosHistoricos
            : [],
          salonLayout:
            result.payload?.salonLayout && typeof result.payload.salonLayout === "object"
              ? result.payload.salonLayout
              : {},
          salonReservations:
            result.payload?.salonReservations &&
            typeof result.payload.salonReservations === "object"
              ? result.payload.salonReservations
              : {},
          salonRooms: Array.isArray(result.payload?.salonRooms)
            ? result.payload.salonRooms
            : [],
          salonTableRooms:
            result.payload?.salonTableRooms &&
            typeof result.payload.salonTableRooms === "object"
              ? result.payload.salonTableRooms
              : {},
          salonFixtures: normalizeSalonFixturesForUi(result.payload?.salonFixtures),
        });

        syncingOperationalStateRef.current = true;
        setMesasActivas(reconciledState.mesasActivas);
        setPedidosHistoricos(reconciledState.pedidosHistoricos);
        setSalonLayout(reconciledState.salonLayout || {});
        setSalonReservations(reconciledState.salonReservations || {});
        setSalonRooms(normalizeSalonRoomsForUi(reconciledState.salonRooms));
        setSalonTableRooms(reconciledState.salonTableRooms || {});
        setSalonFixtures(normalizeSalonFixturesForUi(reconciledState.salonFixtures));
      } catch (error) {
        sharedOperationalStateReadyRef.current = true;
        console.error(
          "No pudimos sincronizar Inicio con el estado operativo compartido:",
          error
        );
      } finally {
        syncInFlight = false;

        if (active && syncQueued) {
          syncQueued = false;
          void syncSharedOperationalState();
        }
      }
    };

    void syncSharedOperationalState();
    const intervalId = window.setInterval(syncSharedOperationalState, 4000);
    window.addEventListener("focus", syncSharedOperationalState);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncSharedOperationalState);
    };
  }, [
    applyMesaMutationOverridesToState,
    isCreatorMode,
    operationalRestaurant,
    shouldHoldOperationalRemoteSync,
  ]);

  useEffect(() => {
    let active = true;

    const syncPromotions = () => {
      setPromotions(getPromotions(operationalRestaurant));
    };

    const syncRemotePromotions = async () => {
      if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        syncPromotions();
        return;
      }

      try {
        const remotePromotions = await hydrateBranchPromotionsToLocal(
          operationalRestaurant,
          currentBranch.id
        );

        if (!active) {
          return;
        }

        setPromotions(remotePromotions);
      } catch (error) {
        if (!active) {
          return;
        }

        syncPromotions();
      }
    };

    const handlePromotionsUpdated = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = operationalRestaurant?.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      if (Array.isArray(event.detail?.promotions)) {
        setPromotions(event.detail.promotions);
        return;
      }

      void syncRemotePromotions();
    };

    const handleStorage = (event) => {
      if (event.key !== buildPromotionsStorageKey(operationalRestaurant?.id)) {
        return;
      }

      void syncRemotePromotions();
    };

    void syncRemotePromotions();
    window.addEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      active = false;
      window.removeEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentBranch?.id, isCreatorMode, operationalRestaurant]);

  useEffect(() => {
    let active = true;

    const applyLocalStock = () => {
      if (!active) {
        return;
      }

      setIngredientStockSnapshot(getIngredientStock(operationalRestaurant));
    };

    if (!operationalRestaurant?.id || !stockFeatureEnabled) {
      setIngredientStockSnapshot({});
      return undefined;
    }

    const syncRemoteStock = async () => {
      try {
        const remoteStock = await hydrateIngredientStockFromRemote(
          operationalRestaurant
        );

        if (active) {
          setIngredientStockSnapshot(remoteStock || {});
        }
      } catch {
        applyLocalStock();
      }
    };

    const handleStockUpdated = (event) => {
      const updatedRestaurantId = String(
        event?.detail?.restaurantId || "default"
      );
      const currentRestaurantId = String(operationalRestaurant?.id || "default");

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      if (active) {
        setIngredientStockSnapshot(event?.detail?.stockMap || {});
      }
    };

    applyLocalStock();
    void syncRemoteStock();
    window.addEventListener(INGREDIENT_STOCK_UPDATED_EVENT, handleStockUpdated);

    return () => {
      active = false;
      window.removeEventListener(
        INGREDIENT_STOCK_UPDATED_EVENT,
        handleStockUpdated
      );
    };
  }, [operationalRestaurant, stockFeatureEnabled]);

  useEffect(() => {
    let active = true;

    const loadMenuCatalog = async () => {
      const branchId = currentBranch?.id || currentRestaurant?.id;
      const shouldUseKioskCatalog = isManualOrderKioskMode;

      if (!branchId) {
        if (active) {
          setMenuCatalog({ categories: [], products: [] });
        }
        return;
      }

      try {
        const snapshot =
          shouldUseKioskCatalog && isSupabaseConfigured && !isCreatorMode
            ? await getPublicKioskMenuByBranchId(branchId, 1)
            : isSupabaseConfigured && !isCreatorMode
              ? await getBranchMenuSnapshot(branchId)
              : loadLocalMenuSnapshot(branchId);

        if (!active) {
          return;
        }

        setMenuCatalog(
          normalizeManualMenuCatalog(snapshot, {
            kioskMode: shouldUseKioskCatalog,
          })
        );
      } catch (error) {
        if (active) {
          setMenuCatalog({ categories: [], products: [] });
        }
      }
    };

    loadMenuCatalog();

    return () => {
      active = false;
    };
  }, [
    currentBranch?.id,
    currentRestaurant?.id,
    isManualOrderKioskMode,
    isCreatorMode,
  ]);

  useEffect(() => {
    let active = true;
    let retryTimer = null;

    const loadTables = async (attempt = 0) => {
      if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        if (active) {
          setAvailableTables(
            currentBranch?.id ? getBranchTablesSnapshot(currentBranch.id) : []
          );
        }
        return;
      }

      const cachedTables = getBranchTablesSnapshot(currentBranch.id);

      if (attempt === 0 && cachedTables.length > 0 && active) {
        setAvailableTables(cachedTables);
      }

      try {
        const nextTables = await listBranchTablesWithQrs(currentBranch.id);

        if (active) {
          const activeTables = nextTables.filter((table) => table.active !== false);
          saveBranchTablesSnapshot(currentBranch.id, activeTables);
          setAvailableTables(activeTables);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        if (cachedTables.length > 0) {
          setAvailableTables(cachedTables);
        }

        if (attempt < 2) {
          retryTimer = window.setTimeout(
            () => void loadTables(attempt + 1),
            1200 * (attempt + 1)
          );
        } else {
          console.error("No pudimos refrescar las mesas de Inicio:", error);
        }
      }
    };

    void loadTables();

    return () => {
      active = false;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [currentBranch?.id, isCreatorMode]);

  useEffect(() => {
    let active = true;

    const loadDeliveryZones = async () => {
      if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        if (active) {
          setManualDeliveryZones([]);
        }
        return;
      }

      try {
        const zones = await listExternalDeliveryZones(currentBranch.id);
        if (active) {
          setManualDeliveryZones(
            (Array.isArray(zones) ? zones : []).filter((zone) => zone?.active !== false)
          );
        }
      } catch (error) {
        if (active) {
          setManualDeliveryZones([]);
        }
      }
    };

    loadDeliveryZones();

    return () => {
      active = false;
    };
  }, [currentBranch?.id, isCreatorMode]);

  useEffect(() => {
    let active = true;

    const loadManualKnownCustomers = async () => {
      const companyId =
        currentCompany?.id ||
        currentBranch?.companyId ||
        currentBranch?.company_id ||
        currentRestaurant?.companyId ||
        currentRestaurant?.company_id ||
        "";

      if (
        isManualOrderKioskMode ||
        !companyId ||
        isCreatorMode ||
        !isSupabaseConfigured
      ) {
        if (active) {
          setManualKnownCustomers([]);
        }
        return;
      }

      try {
        const customers = await listExternalCustomers({ companyId, limit: 120 });
        if (active) {
          setManualKnownCustomers(Array.isArray(customers) ? customers : []);
        }
      } catch (error) {
        if (active) {
          setManualKnownCustomers([]);
        }
      }
    };

    loadManualKnownCustomers();

    return () => {
      active = false;
    };
  }, [
    currentBranch?.companyId,
    currentBranch?.company_id,
    currentCompany?.id,
    currentRestaurant?.companyId,
    currentRestaurant?.company_id,
    isCreatorMode,
    isManualOrderKioskMode,
  ]);

  useEffect(() => {
    let active = true;

    const loadWaiters = async () => {
      if (
        !currentCompany?.id ||
        !currentBranch?.id ||
        isCreatorMode ||
        !isSupabaseConfigured
      ) {
        if (active) {
          setActiveWaiters([]);
          if (currentBranch?.id && operationalRestaurant?.id) {
            syncBranchActiveWaiters(
              operationalRestaurant,
              currentBranch.id,
              []
            );
          }
        }
        return;
      }

      try {
        const nextWaiters = await listBranchWaiters(currentCompany.id, currentBranch.id);

        if (!active) {
          return;
        }

        const nextActiveWaiters = nextWaiters.filter(
          (waiter) => waiter.branchAccessActive
        );

        setActiveWaiters(nextActiveWaiters);
        syncBranchActiveWaiters(
          operationalRestaurant,
          currentBranch.id,
          nextActiveWaiters
        );
      } catch (error) {
        if (active) {
          setActiveWaiters([]);
          syncBranchActiveWaiters(operationalRestaurant, currentBranch.id, []);
        }
      }
    };

    loadWaiters();

    return () => {
      active = false;
    };
  }, [currentBranch?.id, currentCompany?.id, isCreatorMode, operationalRestaurant]);

  useEffect(() => {
    setBranchClosingControl(getBranchClosingControl(operationalRestaurant));
    const currentRestaurantId = operationalRestaurant?.id || "default";
    const handleClosingControlUpdate = (event) => {
      if ((event.detail?.branchId || "default") !== currentRestaurantId) {
        return;
      }

      setBranchClosingControl((currentControl) =>
        selectNewestBranchClosingControl(
          currentControl,
          event.detail?.payload || getBranchClosingControl(operationalRestaurant)
        )
      );
    };
    const handleClosingStorage = (event) => {
      if (
        event.key !==
        buildBranchClosingControlStorageKey(operationalRestaurant?.id)
      ) {
        return;
      }

      setBranchClosingControl(getBranchClosingControl(operationalRestaurant));
    };

    window.addEventListener(
      BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
      handleClosingControlUpdate
    );
    window.addEventListener("storage", handleClosingStorage);

    return () => {
      window.removeEventListener(
        BRANCH_CLOSING_CONTROL_UPDATED_EVENT,
        handleClosingControlUpdate
      );
      window.removeEventListener("storage", handleClosingStorage);
    };
  }, [operationalRestaurant]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 15000);

    return () => window.clearInterval(timer);
  }, []);

  const caracteristicas = useMemo(
    () => (perfil.caracteristicas || []).filter(Boolean),
    [perfil.caracteristicas]
  );
  const operationalState = useMemo(
    () =>
      getRestaurantOperationalState(
        perfil.horarioDetallado,
        new Date(currentTime),
        branchClosingControl
      ),
    [branchClosingControl, currentTime, perfil.horarioDetallado]
  );

  const getMesaPaymentSnapshot = useCallback(
    (mesa) => {
      const fallback = {
        requestedMethod: String(mesa?.requestedPaymentMethod || "").trim().toLowerCase(),
        selectedMethod: String(mesa?.selectedPaymentMethod || "").trim().toLowerCase(),
        paymentRail: String(mesa?.paymentRail || "").trim().toLowerCase(),
        status: String(mesa?.paymentStatus || "").trim().toLowerCase(),
        paidAt: mesa?.paidAt || "",
      };

      const targetOperation = getAuthoritativePaymentOperationForOrder(
        getPaymentOperations(operationalRestaurant),
        mesa?.id,
        mesa?.paymentOperationId
      );

      if (!targetOperation) {
        return fallback;
      }

      return {
        requestedMethod: String(targetOperation.requestedMethod || fallback.requestedMethod)
          .trim()
          .toLowerCase(),
        selectedMethod: String(targetOperation.selectedMethod || fallback.selectedMethod)
          .trim()
          .toLowerCase(),
        paymentRail: String(targetOperation.paymentRail || fallback.paymentRail)
          .trim()
          .toLowerCase(),
        status: String(targetOperation.status || fallback.status)
          .trim()
          .toLowerCase(),
        paidAt: targetOperation.paidAt || fallback.paidAt || "",
      };
    },
    [operationalRestaurant]
  );

  const buildFallbackPaymentOperationFromMesa = useCallback(
    (mesa, overrides = {}) => {
      const paymentOperationId = String(mesa?.paymentOperationId || "").trim();

      if (!paymentOperationId) {
        return null;
      }

      const paymentSnapshot = getMesaPaymentSnapshot(mesa);
      const source = String(mesa?.source || "").trim().toLowerCase();
      const selectedMethod = String(
        overrides.selectedMethod ||
          paymentSnapshot.selectedMethod ||
          paymentSnapshot.requestedMethod ||
          mesa?.selectedPaymentMethod ||
          mesa?.requestedPaymentMethod ||
          ""
      )
        .trim()
        .toLowerCase() || (source === "kiosk" ? "pay_at_counter" : "counter");
      const branchPaymentContext = currentBranch || currentRestaurant || {};
      const commissionChannel =
        source === "kiosk"
          ? "kiosk"
          : source === "app" || source === "qr"
            ? "menu_qr"
            : "manual";
      const paymentRailSnapshot = buildPaymentRailSnapshot({
        source,
        checkoutMethod: selectedMethod,
        paymentProvider: branchPaymentContext?.paymentProvider || "",
        splitMode: branchPaymentContext?.paymentSplitMode || "",
        isKioskExperience: source === "kiosk",
      });
      const itemsSummary = buildCatalogItemSummary(
        Array.isArray(mesa?.items)
          ? mesa.items.map((item) => ({
              quantity: Number(item?.quantity || 0),
              name:
                item?.productName ||
                item?.name ||
                item?.title ||
                item?.label ||
                "Producto",
            }))
          : []
      );

      return {
        id: paymentOperationId,
        orderId: mesa?.id || "",
        tableId: mesa?.tableId || "",
        mesa: mesa?.mesa || "",
        customerName: getMesaCustomerName(mesa) || "",
        canal: mesa?.canal || (source === "kiosk" ? "Kiosk" : "QR"),
        source: source || "manual",
        requestedMethod: selectedMethod,
        selectedMethod,
        paymentRail: paymentRailSnapshot.rail,
        paymentRailProvider: paymentRailSnapshot.provider,
        paymentRailLabel: paymentRailSnapshot.label,
        chargePresentation: paymentRailSnapshot.chargePresentation,
        commissionCollectionMode: paymentRailSnapshot.commissionCollectionMode,
        splitExecutionMode: paymentRailSnapshot.splitExecutionMode,
        interoperabilityObjective: paymentRailSnapshot.interoperabilityObjective,
        paymentSplitMode: paymentRailSnapshot.splitMode,
        commissionRate: resolveBranchCommissionRateByChannel(
          branchPaymentContext,
          commissionChannel
        ),
        status: String(
          overrides.status || paymentSnapshot.status || mesa?.paymentStatus || "pending"
        )
          .trim()
          .toLowerCase(),
        amount: Number(mesa?.pricing?.finalTotal ?? mesa?.totalAmount ?? 0),
        pricing: mesa?.pricing || null,
        orderedAt: mesa?.orderedAt || overrides.orderedAt || new Date().toISOString(),
        paidAt: overrides.paidAt ?? paymentSnapshot.paidAt ?? mesa?.paidAt ?? null,
        closedAt: overrides.closedAt ?? mesa?.closedAt ?? null,
        itemsSummary,
        notes: "",
        history: [
          createPaymentHistoryEntry(
            "Operacion de pago reconstruida desde el pedido operativo para completar el cobro en caja."
          ),
        ],
      };
    },
    [currentBranch, currentRestaurant, getMesaPaymentSnapshot]
  );

  const isMesaCashPendingForKitchen = useCallback(
    (mesa) => {
      const paymentState = resolveOperationalOrderPaymentState(
        mesa,
        getPaymentOperations(operationalRestaurant),
        mesa?.paymentOperationId
      );
      const paymentSnapshot = getMesaPaymentSnapshot(mesa);
      const paymentMethod =
        paymentSnapshot.selectedMethod || paymentSnapshot.requestedMethod || "";
      const isCounterCollection =
        paymentMethod === "counter" ||
        paymentMethod === "pay_at_counter" ||
        paymentMethod === "caja" ||
        paymentMethod === "efectivo" ||
        paymentMethod === "cash";

      return (
        mesa?.source === "kiosk" &&
        isCounterCollection &&
        paymentState.canCollectCash
      );
    },
    [getMesaPaymentSnapshot, operationalRestaurant]
  );
  const isMesaDigitalPaymentPendingForKitchen = useCallback(
    (mesa) => {
      const paymentState = resolveOperationalOrderPaymentState(
        mesa,
        getPaymentOperations(operationalRestaurant),
        mesa?.paymentOperationId
      );
      const paymentSnapshot = getMesaPaymentSnapshot(mesa);
      const paymentMethod =
        paymentSnapshot.selectedMethod || paymentSnapshot.requestedMethod || "";
      const isDigitalQrCollection =
        paymentMethod === "pay_by_phone" || paymentSnapshot.paymentRail === "kiosk_qr";

      return (
        mesa?.source === "kiosk" &&
        isDigitalQrCollection &&
        paymentState.hasPendingDigitalPayment
      );
    },
    [getMesaPaymentSnapshot, operationalRestaurant]
  );

  const notificaciones = useMemo(
    () =>
      mesasActivas.filter(
        (mesa) => mesa.phase === "new" && mesa.items.every((item) => !item.delivered)
      ),
    [mesasActivas]
  );
  const waiterServiceNotifications = useMemo(
    () => getPendingWaiterServiceRequests(waiterOps, currentBranch?.id || ""),
    [currentBranch?.id, waiterOps]
  );
  const serviceNotificationItems = useMemo(
    () => [
      ...notificaciones.map((mesa) => ({
        id: mesa.id,
        kind: "order",
        mesa: mesa.mesa,
        requestedAt: mesa.orderedAt,
        summary: getMesaOrderSummary(mesa),
      })),
      ...waiterServiceNotifications.map((request) => ({
        id: request.id,
        kind: "waiter-request",
        mesa: request.mesa,
        requestedAt: request.requestedAt,
        summary: request.summary || "Cliente pide ayuda del mozo.",
      })),
    ],
    [notificaciones, waiterServiceNotifications]
  );
  const heroNotificationPreviewItems = useMemo(
    () =>
      [...serviceNotificationItems]
        .sort(
          (left, right) =>
            new Date(right.requestedAt || 0).getTime() -
            new Date(left.requestedAt || 0).getTime()
        )
        .slice(0, 4),
    [serviceNotificationItems]
  );
  const heroOrderNotificationCount = notificaciones.length;
  const heroWaiterRequestCount = waiterServiceNotifications.length;

  useEffect(() => {
    const currentIds = new Set(serviceNotificationItems.map((item) => item.id));

    if (!notificationHydratedRef.current) {
      knownNotificationIdsRef.current = currentIds;
      notificationHydratedRef.current = true;
      return;
    }

    const newNotifications = serviceNotificationItems.filter(
      (item) => !knownNotificationIdsRef.current.has(item.id)
    );

    if (newNotifications.length > 0) {
      const firstNotification = newNotifications[0];
      setServiceAlert({
        id: `service-alert-${Date.now()}`,
        title:
          newNotifications.length > 1
            ? `Entraron ${newNotifications.length} avisos nuevos`
            : firstNotification.kind === "waiter-request"
              ? `Llamado en ${firstNotification.mesa}`
              : `Nuevo pedido en ${firstNotification.mesa}`,
        text:
          newNotifications.length > 1
            ? `${firstNotification.mesa} y otros avisos quedaron pendientes de revisar.`
            : firstNotification.summary,
      });
    }

    knownNotificationIdsRef.current = currentIds;
  }, [notificaciones, serviceNotificationItems]);

  useEffect(() => {
    if (!serviceAlert?.id) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setServiceAlert(null);
    }, 6500);

    return () => window.clearTimeout(timer);
  }, [serviceAlert]);

  const mesasListasParaCerrar = useMemo(
    () => mesasActivas.filter((mesa) => mesa.items.every((item) => item.delivered)),
    [mesasActivas]
  );

  const pendientesSalida = useMemo(
    () =>
      mesasActivas
        .filter(
          (mesa) =>
            mesa.phase !== "cancelled" &&
            mesa.phase !== "closed" &&
            mesa.items.some((item) => !item.delivered)
        )
        .map((mesa) => ({
          id: mesa.id,
          mesa: mesa.mesa,
          canal: mesa.canal,
          source: mesa.source || "",
          comensales: mesa.comensales,
          customerName: getMesaCustomerName(mesa),
          tableId: mesa.tableId || "",
          preparationNumber: mesa.preparationNumber || null,
          phase: mesa.phase,
          items: mesa.items || [],
          pedido: getMesaOrderSummary(mesa),
          falta: getMesaMissingItems(mesa),
          paymentOperationId: mesa.paymentOperationId || "",
          requestedPaymentMethod: mesa.requestedPaymentMethod || "",
          selectedPaymentMethod: mesa.selectedPaymentMethod || "",
          paymentStatus: mesa.paymentStatus || "",
          kitchenModeSnapshot: mesa.kitchenModeSnapshot || "manual",
          kitchenDispatchStatus: mesa.kitchenDispatchStatus || "",
          requiresKitchen: orderNeedsKitchen(mesa),
          requestedAt: mesa.orderedAt,
        }))
        .sort((a, b) => new Date(a.requestedAt) - new Date(b.requestedAt)),
    [mesasActivas]
  );

  const manualKitchenSummary = useMemo(() => {
    const summary = {
      pendingNotice: 0,
      confirmed: 0,
      ready: 0,
    };

    pendientesSalida.forEach((mesa) => {
      if (!orderNeedsKitchen(mesa) || (mesa.kitchenModeSnapshot || "manual") !== "manual") {
        return;
      }

      const workflow = getKitchenDispatchWorkflowState(mesa);

      if (workflow.step === "manual_pending") {
        summary.pendingNotice += 1;
        return;
      }

      if (workflow.step === "manual_confirmed") {
        summary.confirmed += 1;
        return;
      }

      if (workflow.step === "ready") {
        summary.ready += 1;
      }
    });

    return summary;
  }, [pendientesSalida]);

  const todosLosPedidos = useMemo(
    () =>
      [
        ...pedidosHistoricos,
        ...mesasActivas.map((mesa) => ({
          ...mesa,
          id: mesa.id,
          mesa: mesa.mesa,
          canal: mesa.canal,
          pedido: getMesaOrderSummary(mesa),
          estado: getMesaStatus(mesa),
          requestedAt: mesa.orderedAt,
        })),
      ].sort(sortOrdersByRequestedAtDesc),
    [mesasActivas, pedidosHistoricos]
  );
  const todayRange = useMemo(
    () => ({
      fromDate: getStartOfDay(new Date(currentTime)),
      toDate: getEndOfDay(new Date(currentTime)),
    }),
    [currentTime]
  );
  const pedidosDelDia = useMemo(
    () => todosLosPedidos.filter((pedido) => isOrderWithinRange(pedido, todayRange)),
    [todayRange, todosLosPedidos]
  );
  const orderReportRange = useMemo(
    () => buildOrderDateRange(orderReportFilters),
    [orderReportFilters]
  );
  const availableOrderReportStatuses = useMemo(
    () =>
      Array.from(
        new Set(todosLosPedidos.map((pedido) => normalizeOrderStatusLabel(pedido.estado)))
      ).sort(compareOrderStatuses),
    [todosLosPedidos]
  );
  const selectedOrderReportStatuses = useMemo(() => {
    if (orderReportFilters.statusMode === "all") {
      return availableOrderReportStatuses;
    }

    return orderReportFilters.statuses.filter((status) =>
      availableOrderReportStatuses.includes(status)
    );
  }, [availableOrderReportStatuses, orderReportFilters]);
  const pedidosReportePdf = useMemo(
    () =>
      todosLosPedidos
        .filter((pedido) => isOrderWithinRange(pedido, orderReportRange))
        .filter((pedido) =>
          selectedOrderReportStatuses.includes(normalizeOrderStatusLabel(pedido.estado))
        )
        .sort(sortOrdersByRequestedAtDesc),
    [orderReportRange, selectedOrderReportStatuses, todosLosPedidos]
  );
  const todayLabel = useMemo(() => formatShortDate(new Date(currentTime)), [currentTime]);

  const salonTables = useMemo(() => {
    const normalizeLabel = (value) => String(value || "").trim().toLowerCase();
    const activeByTableId = new Map();
    const activeByLabel = new Map();
    const assignmentsByTableId = new Map();
    const assignmentsByLabel = new Map();
    const normalizedReservations = normalizeSalonReservations(salonReservations);

    (waiterOps.tableAssignments || []).forEach((assignment) => {
      if (
        assignment?.branchId &&
        currentBranch?.id &&
        assignment.branchId !== currentBranch.id
      ) {
        return;
      }

      const tableId = String(assignment?.tableId || "").trim();
      const label = normalizeLabel(assignment?.tableLabel);

      if (tableId) assignmentsByTableId.set(tableId, assignment);
      if (label) assignmentsByLabel.set(label, assignment);
    });

    mesasActivas.forEach((mesa) => {
      const tableId = String(mesa.tableId || "").trim();
      const label = normalizeLabel(mesa.mesa);

      if (tableId) {
        activeByTableId.set(tableId, [...(activeByTableId.get(tableId) || []), mesa]);
      }

      if (label) {
        activeByLabel.set(label, [...(activeByLabel.get(label) || []), mesa]);
      }
    });

    const rows = availableTables.map((table) => {
      const tableId = String(table.id || "").trim();
      const tableLabel = table.label || (table.tableNumber ? `Mesa ${table.tableNumber}` : "Mesa");
      const activeMesas =
        (tableId && activeByTableId.get(tableId)) ||
        activeByLabel.get(normalizeLabel(tableLabel)) ||
        [];
      const activeMesa = activeMesas[0] || null;
      const tableSession =
        (tableId && assignmentsByTableId.get(tableId)) ||
        assignmentsByLabel.get(normalizeLabel(tableLabel)) ||
        null;
      const dinerNames = Array.from(
        new Set(
          activeMesas
            .map((mesa) => getMesaCustomerName(mesa))
            .filter(Boolean)
        )
      );
      const reservations = getCurrentSalonReservations(
        normalizedReservations[tableId || tableLabel] || [],
        currentTime
      );
      const activeReservation =
        reservations.find((reservation) =>
          isSalonReservationBlockingTable(reservation, currentTime)
        ) || null;

      return {
        id: tableId || tableLabel,
        label: tableLabel,
        tableNumber: Number(table.tableNumber || 0),
        activeMesas,
        activeMesa,
        tableSession,
        operational: activeMesas.length > 0 || Boolean(tableSession),
        reservation: activeReservation,
        reservations,
        nextReservation: reservations[0] || null,
        totalAmount: activeMesas.reduce(
          (sum, mesa) => sum + Number(mesa.totalAmount || mesa.pricing?.finalTotal || 0),
          0
        ),
        dinerNames,
      };
    });

    const knownIds = new Set(rows.map((table) => table.id).filter(Boolean));
    const knownLabels = new Set(rows.map((table) => normalizeLabel(table.label)).filter(Boolean));

    const unknownGroups = new Map();

    mesasActivas.forEach((mesa) => {
      const tableId = String(mesa.tableId || "").trim();
      const label = mesa.mesa || "Mesa sin numero";
      const normalizedLabel = normalizeLabel(label);

      if ((tableId && knownIds.has(tableId)) || (normalizedLabel && knownLabels.has(normalizedLabel))) {
        return;
      }

      const groupId = tableId || normalizedLabel || mesa.id;
      unknownGroups.set(groupId, [...(unknownGroups.get(groupId) || []), mesa]);
    });

    unknownGroups.forEach((activeMesas, groupId) => {
      const activeMesa = activeMesas[0];
      const label = activeMesa?.mesa || "Mesa sin numero";
      const dinerNames = Array.from(
        new Set(activeMesas.map((mesa) => getMesaCustomerName(mesa)).filter(Boolean))
      );
      const reservations = getCurrentSalonReservations(
        normalizedReservations[groupId] || [],
        currentTime
      );

      rows.push({
        id: groupId,
        label,
        tableNumber: 0,
        activeMesas,
        activeMesa,
        operational: true,
        reservation:
          reservations.find((reservation) =>
            isSalonReservationBlockingTable(reservation, currentTime)
          ) || null,
        reservations,
        nextReservation: reservations[0] || null,
        totalAmount: activeMesas.reduce(
          (sum, mesa) => sum + Number(mesa.totalAmount || mesa.pricing?.finalTotal || 0),
          0
        ),
        dinerNames,
      });
    });

    return rows.sort((first, second) => {
      if (first.tableNumber && second.tableNumber) {
        return first.tableNumber - second.tableNumber;
      }

      if (first.tableNumber) {
        return -1;
      }

      if (second.tableNumber) {
        return 1;
      }

      return first.label.localeCompare(second.label, "es");
    });
  }, [
    availableTables,
    currentBranch?.id,
    currentTime,
    mesasActivas,
    salonReservations,
    waiterOps.tableAssignments,
  ]);

  const normalizedSalonRooms = useMemo(
    () => normalizeSalonRoomsForUi(salonRooms),
    [salonRooms]
  );
  const salonKnownRoomIds = useMemo(
    () => new Set(normalizedSalonRooms.map((room) => room.id)),
    [normalizedSalonRooms]
  );
  const resolvedSalonActiveRoomId = salonKnownRoomIds.has(salonActiveRoomId)
    ? salonActiveRoomId
    : normalizedSalonRooms[0]?.id || DEFAULT_SALON_ROOM_ID;
  const activeSalonRoom =
    normalizedSalonRooms.find((room) => room.id === resolvedSalonActiveRoomId) ||
    DEFAULT_SALON_ROOM;
  const salonResolvedTableRooms = useMemo(() => {
    const fallbackRoomId = normalizedSalonRooms[0]?.id || DEFAULT_SALON_ROOM_ID;

    return Object.fromEntries(
      salonTables.map((table) => {
        const assignedRoomId = String(salonTableRooms?.[table.id] || "").trim();
        return [table.id, salonKnownRoomIds.has(assignedRoomId) ? assignedRoomId : fallbackRoomId];
      })
    );
  }, [normalizedSalonRooms, salonKnownRoomIds, salonTableRooms, salonTables]);
  const salonVisibleTables = useMemo(
    () =>
      salonTables.filter(
        (table) => salonResolvedTableRooms[table.id] === resolvedSalonActiveRoomId
      ),
    [resolvedSalonActiveRoomId, salonResolvedTableRooms, salonTables]
  );
  const salonServiceRequestsByTableId = useMemo(() => {
    const requestsByTableId = new Map();

    waiterServiceNotifications.forEach((request) => {
      const matchedTable = salonTables.find((table) =>
        doesWaiterServiceRequestMatchTable(request, table)
      );

      if (!matchedTable) {
        return;
      }

      requestsByTableId.set(matchedTable.id, [
        ...(requestsByTableId.get(matchedTable.id) || []),
        request,
      ]);
    });

    return requestsByTableId;
  }, [salonTables, waiterServiceNotifications]);
  const firstCallingSalonTable = useMemo(
    () =>
      salonTables.find((table) =>
        salonServiceRequestsByTableId.has(table.id)
      ) || null,
    [salonServiceRequestsByTableId, salonTables]
  );
  const activeSalonFixtures = useMemo(
    () => resolveSalonFixturesForRoom(salonFixtures, resolvedSalonActiveRoomId),
    [resolvedSalonActiveRoomId, salonFixtures]
  );
  const selectedSalonFixture = useMemo(
    () =>
      activeSalonFixtures.find((fixture) => fixture.id === salonSelectedFixtureId) ||
      null,
    [activeSalonFixtures, salonSelectedFixtureId]
  );
  const selectedSalonFixtureIsDoor = isSalonDoorFixtureType(
    selectedSalonFixture?.type
  );
  const selectedSalonFixtureIsVertical = isVerticalSalonDoorFixture(
    selectedSalonFixture
  );
  const salonRoomStats = useMemo(
    () =>
      normalizedSalonRooms.map((room) => {
        const roomTables = salonTables.filter(
          (table) => salonResolvedTableRooms[table.id] === room.id
        );

        return {
          ...room,
          tableCount: roomTables.length,
          operationalCount: roomTables.filter((table) => table.operational).length,
        };
      }),
    [normalizedSalonRooms, salonResolvedTableRooms, salonTables]
  );
  const salonReservationAgenda = useMemo(() => {
    const tablesById = new Map(salonTables.map((table) => [table.id, table]));
    const roomsById = new Map(normalizedSalonRooms.map((room) => [room.id, room]));
    const fallbackRoomId = normalizedSalonRooms[0]?.id || DEFAULT_SALON_ROOM_ID;

    return Object.entries(normalizeSalonReservations(salonReservations))
      .flatMap(([storedTableId, reservations]) => {
        const table =
          tablesById.get(storedTableId) ||
          salonTables.find(
            (candidate) =>
              candidate.label === reservations?.[0]?.tableLabel ||
              candidate.id === reservations?.[0]?.tableId
          ) ||
          null;
        const roomId = table
          ? salonResolvedTableRooms[table.id] || fallbackRoomId
          : fallbackRoomId;

        return getCurrentSalonReservations(reservations, currentTime).map(
          (reservation) => ({
            reservation,
            table,
            tableLabel:
              table?.label || reservation.tableLabel || `Mesa ${storedTableId}`,
            roomId,
            roomName: roomsById.get(roomId)?.name || "Salon principal",
          })
        );
      })
      .sort(
        (first, second) =>
          getSalonReservationTimestamp(first.reservation) -
          getSalonReservationTimestamp(second.reservation)
      );
  }, [
    currentTime,
    normalizedSalonRooms,
    salonReservations,
    salonResolvedTableRooms,
    salonTables,
  ]);
  const salonOperationalCount = useMemo(
    () => salonVisibleTables.filter((table) => table.operational).length,
    [salonVisibleTables]
  );

  const selectedSalonTable = useMemo(
    () =>
      salonVisibleTables.find((table) => table.id === salonSelectedTableId) ||
      salonVisibleTables.find((table) => table.operational) ||
      salonVisibleTables[0] ||
      null,
    [salonSelectedTableId, salonVisibleTables]
  );
  const selectedSalonTableLayout = useMemo(() => {
    if (!selectedSalonTable) return null;

    const tableIndex = salonVisibleTables.findIndex(
      (table) => table.id === selectedSalonTable.id
    );
    return resolveSalonTableLayout(
      salonLayout[selectedSalonTable.id],
      Math.max(0, tableIndex),
      salonVisibleTables.length
    );
  }, [salonLayout, salonVisibleTables, selectedSalonTable]);
  const selectedSalonMesas = selectedSalonTable?.activeMesas || [];
  const selectedSalonServiceRequests = selectedSalonTable
    ? salonServiceRequestsByTableId.get(selectedSalonTable.id) || []
    : [];

  useEffect(() => {
    if (salonActiveRoomId !== resolvedSalonActiveRoomId) {
      setSalonActiveRoomId(resolvedSalonActiveRoomId);
    }

    if (
      salonSelectedTableId &&
      !salonVisibleTables.some((table) => table.id === salonSelectedTableId)
    ) {
      setSalonSelectedTableId("");
    }

    if (
      salonSelectedFixtureId &&
      !activeSalonFixtures.some((fixture) => fixture.id === salonSelectedFixtureId)
    ) {
      setSalonSelectedFixtureId("");
    }
  }, [
    activeSalonFixtures,
    resolvedSalonActiveRoomId,
    salonActiveRoomId,
    salonSelectedFixtureId,
    salonSelectedTableId,
    salonVisibleTables,
  ]);

  const metricasBase = useMemo(
    () => [
      {
        id: "pedidos-hoy",
        label: "Pedidos de hoy",
        value: String(pedidosDelDia.length),
        detail: "Solo pedidos creados en la fecha actual",
        linkLabel: `${pendientesSalida.length} pendientes de salida`,
        detailActionId: "pedidos-pendientes",
        interactive: true,
        liveAccent: "sky",
      },
      {
        id: "mesas-activas",
        label: "Mesas activas",
        value: String(mesasActivas.length),
        detail: "Seguimiento del salon y la barra",
        linkLabel: `${mesasListasParaCerrar.length} esperando cerrar cuenta`,
        detailActionId: "esperando-cierre",
        interactive: true,
        liveAccent: "emerald",
      },
      {
        id: "salon",
        label: "Salon",
        value: `${salonOperationalCount}/${salonTables.length || 0}`,
        detail: waiterServiceNotifications.length
          ? `${waiterServiceNotifications.length} ${
              waiterServiceNotifications.length === 1
                ? "mesa esta llamando"
                : "mesas estan llamando"
            }`
          : salonTables.length
            ? "Mesas operativas / total del salon"
            : "Mapa de mesas y pedidos",
        linkLabel: waiterServiceNotifications.length ? "Atender llamados" : "Abrir salon",
        detailActionId: "salon",
        interactive: true,
        liveAccent: "emerald",
        waiterCallCount: waiterServiceNotifications.length,
      },
      {
        id: "recaudacion",
        label: "Recaudacion",
        value: "$ 424.300",
        detail: "Turno actual en marcha",
      },
    ],
    [
      mesasActivas.length,
      mesasListasParaCerrar.length,
      pedidosDelDia.length,
      pendientesSalida.length,
      salonOperationalCount,
      salonTables.length,
      waiterServiceNotifications.length,
    ]
  );

  const pedidosSeguimiento = useMemo(
    () =>
      mesasActivas.slice(0, 3).map((mesa) => ({
        mesa: mesa.mesa,
        estado: getMesaStatus(mesa),
        nota: getMesaMissingItems(mesa),
      })),
    [mesasActivas]
  );

  const menuCategories = useMemo(() => menuCatalog.categories || [], [menuCatalog.categories]);
  const menuSubcategories = useMemo(
    () => menuCatalog.subcategories || [],
    [menuCatalog.subcategories]
  );
  const menuProducts = useMemo(() => menuCatalog.products || [], [menuCatalog.products]);
  const menuProductStockAlerts = useMemo(
    () =>
      !stockFeatureEnabled
        ? []
        : menuProducts
            .map((product) => ({
              product,
              stockState: getOperationalProductStockState(
                operationalRestaurant,
                product,
                {
                  ingredientes,
                  stockMap: ingredientStockSnapshot,
                  stockEnabled: stockFeatureEnabled,
                  fallbackMessage:
                    "Este producto quedo fuera de venta hasta que cargues o repongas stock.",
                }
              ),
            }))
            .filter(
              ({ product, stockState }) =>
                product?.active !== false && stockState.available === false
            )
            .sort((left, right) =>
              String(left?.product?.nombre || left?.product?.name || "").localeCompare(
                String(right?.product?.nombre || right?.product?.name || ""),
                "es"
              )
            ),
    [
      ingredientStockSnapshot,
      ingredientes,
      menuProducts,
      operationalRestaurant,
      stockFeatureEnabled,
    ]
  );

  const manualOrderProducts = useMemo(
    () =>
      menuProducts.filter((product) => {
        if (!manualOrderForm.menuCategoryId) {
          return true;
        }

        return (
          normalizeInicioEntityId(
            product.categoria || product.categoryId || product.category_id
          ) === manualOrderForm.menuCategoryId
        );
      }),
    [manualOrderForm.menuCategoryId, menuProducts]
  );

  const filteredManualOrderProducts = useMemo(() => {
    const query = manualOrderSearch.trim().toLowerCase();

    if (!query) {
      return manualOrderProducts;
    }

    return manualOrderProducts.filter((product) =>
      `${product.nombre || product.name || ""} ${
        product.subcategoria || product.subcategoryName || ""
      }`
        .toLowerCase()
        .includes(query)
    );
  }, [manualOrderProducts, manualOrderSearch]);

  const filteredManualKnownCustomers = useMemo(() => {
    const query = manualCustomerSearch.trim().toLowerCase();
    const customers = Array.isArray(manualKnownCustomers) ? manualKnownCustomers : [];

    if (!query) {
      return customers.slice(0, 6);
    }

    return customers
      .filter((customer) => buildManualKnownCustomerSearchText(customer).includes(query))
      .slice(0, 8);
  }, [manualCustomerSearch, manualKnownCustomers]);

  const applyManualKnownCustomer = useCallback(
    (customer) => {
      const defaultAddress = getManualKnownCustomerDefaultAddress(customer);
      const nextName = getManualKnownCustomerName(customer);
      const nextPhone = getManualKnownCustomerPhone(customer);

      setManualOrderForm((prev) => ({
        ...prev,
        customerFirstName: customer?.first_name || "",
        customerLastName: customer?.last_name || "",
        customerPhone: nextPhone,
        ...(manualOrderDestinationType === "delivery" && defaultAddress
          ? {
              deliveryZoneId:
                defaultAddress.delivery_zone_id ||
                defaultAddress.deliveryZoneId ||
                prev.deliveryZoneId,
              addressStreet: defaultAddress.street || "",
              addressStreetNumber:
                defaultAddress.street_number || defaultAddress.streetNumber || "",
              addressNeighborhood: defaultAddress.neighborhood || "",
              addressCity: defaultAddress.city || "",
              addressReference: defaultAddress.reference || "",
            }
          : {}),
      }));
      setManualCustomerSearch(
        [nextName, formatExternalPhone(nextPhone)].filter(Boolean).join(" - ")
      );
      setManualOrderMessage({
        type: "success",
        text:
          manualOrderDestinationType === "delivery" && defaultAddress
            ? `Cargamos los datos y la direccion de ${nextName}.`
            : `Cargamos los datos de ${nextName}.`,
      });
    },
    [manualOrderDestinationType]
  );

  const manualOrderPricing = useMemo(
    () => buildPromotionPricing(manualOrderItems, promotions, menuProducts, manualOrderForm.paymentMethod),
    [manualOrderItems, menuProducts, promotions, manualOrderForm.paymentMethod]
  );

  const manualOrderFinalTotal = useMemo(
    () => manualOrderPricing.finalTotal,
    [manualOrderPricing.finalTotal]
  );

  const selectedManualOrderProduct = useMemo(() => {
    const product =
      menuProducts.find((menuProduct) => menuProduct.id === manualOrderForm.menuProductId) ||
      null;

    if (!product) {
      return null;
    }

    return {
      ...product,
      ingredients: (product.ingredientes || []).map((ingredientConfig) => {
        const normalizedIngredient = normalizeManualCatalogIngredient(ingredientConfig);
        const ingredientData =
          ingredientes.find((ingredient) => ingredient.id === ingredientConfig.id) || null;
        const consumptionUnit =
          normalizedIngredient.consumptionUnit ||
          normalizedIngredient.unit ||
          normalizedIngredient.unidad ||
          "un";

        return {
          id: normalizedIngredient.id,
          imageDeferred: ingredientData?.imageDeferred,
          imageCompanyId: ingredientData?.imageCompanyId,
          imageScope: ingredientData?.imageScope,
          imageVersion: ingredientData?.imageVersion,
          name: ingredientData?.nombre || normalizedIngredient.name || "Ingrediente",
          imageUrl:
            ingredientData?.imagen ||
            ingredientData?.imageUrl ||
            ingredientData?.image_url ||
            normalizedIngredient.imageUrl ||
            "",
          price: Number(ingredientData?.precio ?? normalizedIngredient.price ?? 0),
          type: ingredientData?.tipo || normalizedIngredient.type || "opcional",
          baseQuantity: normalizedIngredient.baseQuantity,
          menuBaseQuantity: normalizedIngredient.menuBaseQuantity,
          consumptionQuantity: normalizedIngredient.consumptionQuantity,
          stockQuantity: normalizedIngredient.stockQuantity,
          unit: consumptionUnit,
          unidad: consumptionUnit,
          consumptionUnit,
          canRemove: normalizedIngredient.canRemove === true,
          canAddMore:
            normalizedIngredient.canAddMore === true,
          maxExtraQuantity: Number(normalizedIngredient.maxExtraQuantity),
        };
      }),
    };
  }, [ingredientes, manualOrderForm.menuProductId, menuProducts]);

  const selectedManualTable = useMemo(
    () => availableTables.find((table) => table.id === manualOrderForm.tableId) || null,
    [availableTables, manualOrderForm.tableId]
  );

  const selectedManualProductSummary = useMemo(
    () => getManualCustomizationSummary(selectedManualOrderProduct, manualOrderSelection),
    [manualOrderSelection, selectedManualOrderProduct]
  );
  const selectedManualPizzaSizeOptions = useMemo(
    () => getPizzaSizeOptions(selectedManualOrderProduct),
    [selectedManualOrderProduct]
  );
  const selectedManualPizzaSizeOption = useMemo(
    () =>
      selectedManualPizzaSizeOptions.find(
        (option) => option.id === manualOrderPizzaSizeOptionId
      ) ||
      selectedManualPizzaSizeOptions[0] ||
      null,
    [manualOrderPizzaSizeOptionId, selectedManualPizzaSizeOptions]
  );
  const selectedManualPizzaHalfOptions = useMemo(
    () =>
      buildPizzaHalfAndHalfOptions(
        selectedManualOrderProduct,
        menuProducts,
        menuCategories,
        {
          sizeOptionId: selectedManualPizzaSizeOption?.id || "",
          subcategories: menuSubcategories,
        }
      ),
    [
      menuCategories,
      menuSubcategories,
      menuProducts,
      selectedManualOrderProduct,
      selectedManualPizzaSizeOption?.id,
    ]
  );
  const selectedManualPizzaHalfProduct = useMemo(
    () =>
      selectedManualPizzaHalfOptions.find(
        (product) => product.id === manualOrderPizzaHalfProductId
      ) || null,
    [manualOrderPizzaHalfProductId, selectedManualPizzaHalfOptions]
  );
  const selectedManualPizzaHalfSelection = useMemo(
    () =>
      createPizzaHalfAndHalfSelection(
        selectedManualOrderProduct,
        selectedManualPizzaHalfProduct,
        { sizeOptionId: selectedManualPizzaSizeOption?.id || "" }
      ),
    [
      selectedManualOrderProduct,
      selectedManualPizzaHalfProduct,
      selectedManualPizzaSizeOption?.id,
    ]
  );

  const selectedManualProductTotal = useMemo(() => {
    if (!selectedManualOrderProduct) {
      return 0;
    }

    const quantity = Math.max(1, Number(manualOrderForm.menuProductQuantity || 1));
    const baseUnitPrice = selectedManualPizzaSizeOption
      ? resolvePizzaProductPrice(selectedManualOrderProduct, {
          sizeOption: selectedManualPizzaSizeOption,
          portionMode: selectedManualPizzaHalfSelection ? "half_and_half" : manualOrderPizzaPortionMode,
        })
      : resolveMenuProductPrice(selectedManualOrderProduct);

    return (
      (baseUnitPrice +
        Number(selectedManualPizzaHalfSelection?.unitPrice || 0) +
        Number(selectedManualProductSummary?.extraCost || 0)) *
      quantity
    );
  }, [
    manualOrderForm.menuProductQuantity,
    selectedManualOrderProduct,
    selectedManualPizzaHalfSelection,
    selectedManualPizzaSizeOption,
    manualOrderPizzaPortionMode,
    selectedManualProductSummary,
  ]);

  useEffect(() => {
    if (!availableTables.length) {
      return;
    }

    setManualOrderForm((prev) => {
      if (prev.tableId && availableTables.some((table) => table.id === prev.tableId)) {
        return prev;
      }

      return {
        ...prev,
        tableId: availableTables[0].id,
      };
    });
  }, [availableTables]);

  useEffect(() => {
    setManualOrderSelection(createManualSelectionDraft(selectedManualOrderProduct));
    setManualOrderPizzaPortionMode(getDefaultPizzaSaleMode(getPizzaSizeOptions(selectedManualOrderProduct)[0]));
    setManualOrderPizzaSizeOptionId(
      getPizzaSizeOptions(selectedManualOrderProduct)[0]?.id || ""
    );
    setManualOrderPizzaHalfProductId("");
  }, [selectedManualOrderProduct]);

  useEffect(() => {
    if (!selectedManualOrderProduct) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeManualProductConfigurator();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedManualOrderProduct]);

  const activePromotionsCount = useMemo(
    () => promotions.filter((promotion) => promotion.active !== false).length,
    [promotions]
  );

  const activeComboCount = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false && getPromotionRecordType(promotion) === "combo"
      ).length,
    [promotions]
  );

  const activePromotionRulesCount = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false && getPromotionRecordType(promotion) === "promotion"
      ).length,
    [promotions]
  );

  const availablePromotionNowCount = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false && isPromotionAvailableNow(promotion, new Date(currentTime))
      ).length,
    [currentTime, promotions]
  );

  const deliveryAssignmentMesa = useMemo(() => {
    if (!deliveryAssignmentMesaId) {
      return null;
    }

    return mesasActivas.find((mesa) => mesa.id === deliveryAssignmentMesaId) || null;
  }, [deliveryAssignmentMesaId, mesasActivas]);
  const deliveryAssignmentItems = useMemo(() => {
    if (!deliveryAssignmentMesa || !deliveryAssignmentItemIds.length) {
      return [];
    }

    const selectedIds = new Set(deliveryAssignmentItemIds);
    return (deliveryAssignmentMesa.items || []).filter((item) => selectedIds.has(item.id));
  }, [deliveryAssignmentItemIds, deliveryAssignmentMesa]);
  const isDirectItemDeliveryAssignment = deliveryAssignmentItems.length > 0;
  const waiterRequestAssignment = useMemo(() => {
    if (!waiterRequestAssignmentId) {
      return null;
    }

    return (
      waiterServiceNotifications.find((request) => request.id === waiterRequestAssignmentId) || null
    );
  }, [waiterRequestAssignmentId, waiterServiceNotifications]);

  const {
    waiters: deliveryAvailableWaiters,
    loading: loadingConnectedWaiters,
    error: connectedWaitersError,
    refresh: refreshConnectedWaiters,
    assign: assignConnectedWaiter,
    assigning: assigningConnectedWaiter,
    cancelAssignment: cancelConnectedWaiterAssignment,
  } = useConnectedWaiters(
    currentBranch?.id,
    Boolean(currentCompany?.id && !isCreatorMode && isSupabaseConfigured)
  );
  useEffect(() => {
    const onlineIds = new Set(deliveryAvailableWaiters.map((waiter) => waiter.userId));
    setDeliveryAssignmentWaiterId((selected) => onlineIds.has(selected) ? selected : "");
    setWaiterRequestAssignmentWaiterId((selected) => onlineIds.has(selected) ? selected : "");
  }, [deliveryAvailableWaiters]);

  const manualAvailableCombos = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          promotion.active !== false &&
          getPromotionRecordType(promotion) === "combo" &&
          isPromotionAvailableNow(promotion, new Date(currentTime)) &&
          Array.isArray(promotion.items) &&
          promotion.items.length > 0
      ),
    [currentTime, promotions]
  );

  const promotionSummaryItems = useMemo(
    () =>
      [...promotions]
        .sort((left, right) => {
          const leftActive = left.active === false ? 1 : 0;
          const rightActive = right.active === false ? 1 : 0;

          if (leftActive !== rightActive) {
            return leftActive - rightActive;
          }

          return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
        })
        .slice(0, 4),
    [promotions]
  );

  const notificationDetailMesa = useMemo(() => {
    if (!notificationDetailMesaId) {
      return null;
    }

    return mesasActivas.find((mesa) => mesa.id === notificationDetailMesaId) || null;
  }, [notificationDetailMesaId, mesasActivas]);

  const openMesaDetail = (mesaId) => {
    setDetalleMesaId(mesaId || "");
    setDetalleActivo("mesas-activas");
  };

  const closeManualProductConfigurator = () => {
    setManualOrderForm((prev) => ({
      ...prev,
      menuProductId: "",
      menuProductQuantity: "1",
    }));
    setManualOrderSelection({});
    setManualOrderPizzaSizeOptionId("");
    setManualOrderPizzaHalfProductId("");
  };

  const addManualOrderItemFromMenu = () => {
    if (!ensureInicioPermission(canTakeOrders, "armar pedidos manuales", "manual")) {
      return;
    }

    const selectedProduct = selectedManualOrderProduct;

    if (!selectedProduct) {
      setManualOrderMessage({
        type: "error",
        text: "Selecciona un plato del menu para agregarlo al pedido manual.",
      });
      return;
    }

    const quantity = Math.max(1, Number(manualOrderForm.menuProductQuantity || 1));
    const customizationSummary = getManualCustomizationSummary(
      selectedProduct,
      manualOrderSelection
    );
    const pizzaHalfSelection = selectedManualPizzaHalfSelection;
    const pizzaHalfProduct = selectedManualPizzaHalfProduct;
    const pizzaSizeOption = selectedManualPizzaSizeOption;
    const pizzaMode = pizzaHalfSelection ? "half_and_half" : manualOrderPizzaPortionMode;
    if (pizzaSizeOption && (!isPizzaSaleModeEnabled(pizzaSizeOption, pizzaMode) ||
        (pizzaMode === "half_and_half" && !pizzaHalfSelection))) {
      setManualOrderMessage({ type: "error", text: "Elegi una modalidad habilitada y, si corresponde, la otra mitad." });
      return;
    }
    const baseUnitPrice = pizzaSizeOption
      ? resolvePizzaProductPrice(selectedProduct, { sizeOption: pizzaSizeOption, portionMode: pizzaMode })
      : resolveMenuProductPrice(selectedProduct);
    const pizzaHalfExtraUnitPrice = Number(pizzaHalfSelection?.unitPrice || 0);
    const extraUnitCost = Number(customizationSummary?.extraCost || 0);
    const unitPrice = baseUnitPrice + pizzaHalfExtraUnitPrice + extraUnitCost;
    const selectionDetails = [
      ...(pizzaMode === "half" ? ["Formato: Media pizza"] : pizzaMode === "slice" ? ["Formato: Porcion"] : []),
      ...(!pizzaHalfSelection
        ? [createPizzaSizeSelectionDetail(pizzaSizeOption)].filter(Boolean)
        : []),
      ...buildManualSelectionDetails(selectedProduct, manualOrderSelection),
      ...(pizzaHalfSelection
        ? buildPizzaHalfAndHalfSelectionDetails(selectedProduct, pizzaHalfSelection)
        : []),
    ];
    const linkedPizzaSizeOption = pizzaHalfSelection
      ? findPizzaSizeOption(
          pizzaHalfProduct,
          pizzaHalfSelection.pizzaSizeOptionId || pizzaSizeOption?.id || ""
        )
      : null;
    const consumos = pizzaHalfSelection
      ? [
          ...buildOperationalConsumptions(selectedProduct, {
            selection: manualOrderSelection || {},
            quantity,
            quantityScale: 0.5 * Number(pizzaSizeOption?.recipeScale || 1),
          }),
          ...buildOperationalConsumptions(pizzaHalfProduct, {
            quantity,
            quantityScale: 0.5 * Number(linkedPizzaSizeOption?.recipeScale || 1),
          }),
        ]
      : buildOperationalConsumptions(selectedProduct, {
          selection: manualOrderSelection || {},
          quantity,
          quantityScale: getPizzaPortionScale(pizzaSizeOption, pizzaMode) * Number(pizzaSizeOption?.recipeScale || 1),
        });
    const nextManualItems = [
      ...manualOrderItems,
      {
        id: `manual-item-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
        productId: selectedProduct.id,
        name: selectedProduct.nombre,
        quantity,
        baseUnitPrice,
        extraUnitCost,
        linkedProductId: pizzaHalfProduct?.id || "",
        linkedProductName: pizzaHalfProduct
          ? getPizzaProductName(pizzaHalfProduct)
          : "",
        linkedSelections: pizzaHalfSelection ? [pizzaHalfSelection] : [],
        linkedUnitPrice: pizzaHalfExtraUnitPrice,
        pizzaSizeOptionId: pizzaSizeOption?.id || "",
        pizzaPortionMode: pizzaMode,
        pizzaSizeLabel: pizzaSizeOption?.label || "",
        pizzaSizeFullUnitPrice: Number(pizzaSizeOption?.fullPrice || 0),
        pizzaSizeHalfUnitPrice: Number(pizzaSizeOption?.halfPrice || 0),
        pizzaSizeSliceUnitPrice: Number(pizzaSizeOption?.slicePrice || 0),
        pizzaSlices: Number(pizzaSizeOption?.slices || 0),
        pizzaSizeRecipeScale: Number(pizzaSizeOption?.recipeScale || 1),
        unitPrice,
        categoryId: selectedProduct.categoria || "",
        categoryName:
          selectedProduct.categoryName ||
          menuCategories.find((category) => category.id === selectedProduct.categoria)?.nombre ||
          "",
        subcategoryId: selectedProduct.subcategoriaId || "",
        subcategoryName: selectedProduct.subcategoryName || selectedProduct.subcategoria || "",
        selection: manualOrderSelection || {},
        selectionDetails,
        consumos,
      },
    ];
    const stockValidation = validateIngredientStock(
      operationalRestaurant,
      nextManualItems.reduce(
        (acc, item) => acc.concat(Array.isArray(item.consumos) ? item.consumos : []),
        []
      ),
      {
        ingredientes,
        stockMap: ingredientStockSnapshot,
        allowMissingStockRecords: false,
      }
    );

    if (!stockValidation.ok) {
      setManualOrderMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para agregar ese plato al pedido manual.",
        }),
      });
      return;
    }

    setManualOrderItems(nextManualItems);

    setManualOrderForm((prev) => ({
      ...prev,
      menuProductId: "",
      menuProductQuantity: "1",
    }));
    closeManualProductConfigurator();
    setManualOrderSearch("");
    setManualOrderMessage({
      type: "success",
      text: `${selectedProduct.nombre} se agrego al pedido manual.`,
    });
  };

  const addManualComboToOrder = (combo) => {
    if (!ensureInicioPermission(canTakeOrders, "sumar combos al pedido manual", "manual")) {
      return;
    }

    const comboItems = Array.isArray(combo?.items) ? combo.items : [];

    if (!comboItems.length) {
      setManualOrderMessage({
        type: "error",
        text: "Ese combo todavia no tiene productos configurados.",
      });
      return;
    }

    const nextItems = comboItems
      .map((comboItem) => {
        const product = menuProducts.find((item) => item.id === comboItem.productId);

        if (!product) {
          return null;
        }

        const quantity = Math.max(1, Number(comboItem.quantity || 1));
        const pizzaSizeOption = productHasPizzaSizeOptions(product)
          ? findPizzaSizeOption(product)
          : null;
        const resolvedProductPrice = pizzaSizeOption
          ? resolvePizzaProductPrice(product, { sizeOption: pizzaSizeOption })
          : resolveMenuProductPrice(product);
        const baseUnitPrice =
          comboItem.unitPrice !== undefined
            ? Number(comboItem.unitPrice || 0)
            : resolvedProductPrice;
        const consumos = buildOperationalConsumptions(product, {
          quantity,
          quantityScale: Number(pizzaSizeOption?.recipeScale || 1),
        });

        return {
          id: `manual-combo-item-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
          productId: product.id,
          name: product.nombre,
          quantity,
          baseUnitPrice,
          extraUnitCost: 0,
          pizzaSizeOptionId: pizzaSizeOption?.id || "",
          pizzaSizeLabel: pizzaSizeOption?.label || "",
          pizzaSizeFullUnitPrice: Number(pizzaSizeOption?.fullPrice || 0),
          pizzaSizeHalfUnitPrice: Number(pizzaSizeOption?.halfPrice || 0),
          pizzaSizeSliceUnitPrice: Number(pizzaSizeOption?.slicePrice || 0),
          pizzaSlices: Number(pizzaSizeOption?.slices || 0),
          pizzaSizeRecipeScale: Number(pizzaSizeOption?.recipeScale || 1),
          unitPrice: baseUnitPrice,
          categoryId: product.categoria || "",
          categoryName:
            product.categoryName ||
            menuCategories.find((category) => category.id === product.categoria)?.nombre ||
            "",
          subcategoryId: product.subcategoriaId || "",
          subcategoryName: product.subcategoryName || product.subcategoria || "",
          selectionDetails: [
            `Combo: ${combo.title}`,
            createPizzaSizeSelectionDetail(pizzaSizeOption),
          ].filter(Boolean),
          consumos,
        };
      })
      .filter(Boolean);

    if (!nextItems.length) {
      setManualOrderMessage({
        type: "error",
        text: "No pudimos resolver los productos reales de ese combo en el menu actual.",
      });
      return;
    }

    const nextManualItems = [...manualOrderItems, ...nextItems];
    const stockValidation = validateIngredientStock(
      operationalRestaurant,
      nextManualItems.reduce(
        (acc, item) => acc.concat(Array.isArray(item.consumos) ? item.consumos : []),
        []
      ),
      {
        ingredientes,
        stockMap: ingredientStockSnapshot,
        allowMissingStockRecords: false,
      }
    );

    if (!stockValidation.ok) {
      setManualOrderMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para agregar ese combo al pedido manual.",
        }),
      });
      return;
    }

    setManualOrderItems(nextManualItems);
    setManualOrderMessage({
      type: "success",
      text: `Se agrego el combo "${combo.title}" al pedido manual.`,
    });
  };

  const removeManualOrderItem = (itemId) => {
    if (!ensureInicioPermission(canTakeOrders, "editar el borrador del pedido", "manual")) {
      return;
    }

    setManualOrderItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateManualOrderIngredientQuantity = (ingredient, change) => {
    if (!ensureInicioPermission(canTakeOrders, "ajustar ingredientes del pedido", "manual")) {
      return;
    }

    setManualOrderSelection((prev) => {
      const baseQuantity = Number(ingredient.baseQuantity || 0);
      const currentQuantity = Number(prev?.[ingredient.id] ?? baseQuantity);
      const minValue = ingredient.canRemove ? 0 : baseQuantity;
      const maxValue = ingredient.canAddMore
        ? baseQuantity +
          Math.max(
            1,
            Number(ingredient.maxExtraQuantity || DEFAULT_MANUAL_EXTRA_QUANTITY)
          )
        : baseQuantity;
      const nextValue = Math.min(maxValue, Math.max(minValue, currentQuantity + change));

      return {
        ...prev,
        [ingredient.id]: nextValue,
      };
    });
  };

  const resetManualCustomization = () => {
    if (!ensureInicioPermission(canTakeOrders, "restablecer la personalizacion", "manual")) {
      return;
    }

    setManualOrderSelection(createManualSelectionDraft(selectedManualOrderProduct));
    setManualOrderPizzaSizeOptionId(selectedManualPizzaSizeOptions[0]?.id || "");
    setManualOrderPizzaPortionMode(getDefaultPizzaSaleMode(selectedManualPizzaSizeOptions[0]));
    setManualOrderPizzaHalfProductId("");
  };

  const createManualPaymentOperation = ({
    operationId,
    orderId,
    tableId,
    mesa,
    customerName = "",
    preparationNumber = null,
    serviceType = "",
    canal,
    monto,
    paymentFlow,
    paymentMethod,
    itemsSummary,
    notes,
    pricing,
  }) => {
    const now = new Date().toISOString();
    const operations = getPaymentOperations(currentRestaurant);
    const isDeepLink = paymentMethod === "deep_link";
    const methodLabel = paymentMethod;
    const status =
      paymentFlow === "paid_now" ? (isDeepLink ? "link_sent" : "paid") : "pending";
    const history = [
      createPaymentHistoryEntry("Operacion manual creada desde Inicio."),
      createPaymentHistoryEntry(
        paymentFlow === "paid_now"
          ? isDeepLink
            ? "Se marco pago inmediato por deep link."
            : `Cobro manual registrado por ${methodLabel}.`
          : `Cobro pendiente con medio previsto: ${methodLabel}.`
      ),
    ];

    const operation = {
      id: operationId || buildManualPaymentOperationId(),
      orderId: orderId || "",
      tableId: tableId || "",
      mesa,
      customerName,
      preparationNumber,
      serviceType,
      canal,
      source: "manual",
      requestedMethod: null,
      selectedMethod: paymentMethod,
      status,
      amount: Number(monto),
      pricing,
      orderedAt: now,
      paidAt: paymentFlow === "paid_now" && !isDeepLink ? now : null,
      closedAt: null,
      itemsSummary,
      notes,
      history,
    };

    savePaymentOperations(currentRestaurant, [operation, ...operations]);
    if (paymentFlow === "paid_now" && !isDeepLink) {
      void syncManualCommissionDebtSnapshot(
        operation,
        "Cobro manual inmediato registrado desde Inicio."
      );
    }
    return operation.id;
  };

  const printManualCustomerPickupTicket = async (order, paymentMethod = "") => {
    const restaurantName =
      perfil?.nombre || currentBranch?.name || currentRestaurant?.name || "MozzoApp";
    const branchName = currentBranch?.name || "";
    const paperWidth = perfil?.kitchenTicketPaper || "80mm";
    const paymentMethodLabel = resolveManualPaymentMethodLabel(paymentMethod);
    const paperWidthMm = paperWidth === "58mm" ? 58 : 80;

    if (isMozzoWindowsBridgeAvailable()) {
      await printTicketWithMozzoWindows(
        buildMozzoWindowsCustomerTicketPayload({
          order,
          restaurantName,
          paymentMethodLabel,
          footer: "Retire cuando aparezca su numero en pantalla",
          paperWidthMm,
        })
      );
      return;
    }

    if (isMozzoAndroidBridgeAvailable()) {
      await printTicketWithMozzoAndroid(
        buildMozzoAndroidCustomerTicketPayload({
          order,
          restaurantName,
          paymentMethodLabel,
          footer: "Retire cuando aparezca su numero en pantalla",
          paperWidthMm,
        })
      );
      return;
    }

    await printCustomerPickupTicket({
      restaurantName,
      branchName,
      order,
      paperWidth,
      preferCurrentDocumentPrint: true,
      cleanupDelayMs: 1200,
    });
  };

  const resolveActiveKitchenMode = (order = null) =>
    resolveOrderKitchenModeAtDispatch({
      order,
      profile: perfil,
      branch: currentBranch,
      restaurant: currentRestaurant,
    });

  const printKitchenTicketToConfiguredDevice = async (order) => {
    if (!orderRequiresKitchen(order)) {
      return { skipped: true };
    }

    const restaurantName =
      perfil?.nombre || currentBranch?.name || currentRestaurant?.name || "MozzoApp";
    const branchName = currentBranch?.name || "";
    const paperWidth = perfil?.kitchenTicketPaper || "80mm";
    const paperWidthMm = paperWidth === "58mm" ? 58 : 80;

    if (isMozzoWindowsBridgeAvailable()) {
      await printTicketWithMozzoWindows(
        buildMozzoWindowsKitchenTicketPayload({
          order,
          restaurantName,
          paperWidthMm,
        })
      );
      return;
    }

    if (isMozzoAndroidBridgeAvailable()) {
      await printTicketWithMozzoAndroid(
        buildMozzoAndroidKitchenTicketPayload({
          order,
          restaurantName,
          paperWidthMm,
        })
      );
      return;
    }

    await printKitchenTicket({
      restaurantName,
      branchName,
      order,
      paperWidth,
      preferCurrentDocumentPrint: true,
    });
  };

  const markKitchenTicketAsPrinted = (mesaId, mesa) => {
    const printedAt = new Date().toISOString();

    rememberMesaMutationOverride(mesaId, {
      phase: mesa.phase === "new" ? "preparation" : mesa.phase,
      kitchenDispatchStatus: "printer_printed",
      kitchenDispatchedAt: mesa.kitchenDispatchedAt || printedAt,
      kitchenDispatchedBy: mesa.kitchenDispatchedBy || user?.id || "",
    });

    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesaId
          ? {
              ...item,
              phase: item.phase === "new" ? "preparation" : item.phase,
              kitchenDispatchStatus: "printer_printed",
              kitchenDispatchedAt: item.kitchenDispatchedAt || printedAt,
              kitchenDispatchedBy: item.kitchenDispatchedBy || user?.id || "",
              kitchenPrintedAt: printedAt,
              kitchenPrintedBy: user?.id || "",
              kitchenPrintCount: Math.max(0, Number(item.kitchenPrintCount || 0)) + 1,
            }
          : item
      )
    );
  };

  const handleCreateManualOrder = async (event) => {
    event.preventDefault();

    const pizzaValidation = validatePizzaCartItems(manualOrderItems, menuProducts);
    if (!pizzaValidation.ok) {
      setManualOrderMessage({ type: "error", text: pizzaValidation.message });
      return;
    }

    if (!ensureInicioPermission(canTakeOrders, "crear pedidos manuales", "manual")) {
      return;
    }

    const acquireManualOrderSubmission = () => {
      if (manualOrderSubmissionLockRef.current) {
        setManualOrderMessage({
          type: "error",
          text: "El pedido ya se esta registrando. Espera la confirmacion antes de volver a enviarlo.",
        });
        return false;
      }

      manualOrderSubmissionLockRef.current = true;
      setIsManualOrderSaving(true);
      return true;
    };

    const releaseManualOrderSubmission = () => {
      manualOrderSubmissionLockRef.current = false;
      setIsManualOrderSaving(false);
    };

    const destinationType = isManualOrderKioskMode
      ? "pickup"
      : manualOrderForm.destinationType || "table";
    const isPickupDestination = destinationType === "pickup";
    const isDeliveryDestination = destinationType === "delivery";
    const isExternalDestination = isDeliveryDestination;
    const manualCustomerName = buildManualCustomerName(manualOrderForm);
    const salonDinerName = String(manualOrderForm.dinerName || "").trim();
    const mesa = isExternalDestination
      ? "Envio"
      : isPickupDestination
        ? "Para llevar"
      : selectedManualTable?.label || `Mesa ${selectedManualTable?.tableNumber || ""}`;
    const monto = Number(manualOrderFinalTotal || 0);

    if (!isPickupDestination && !isExternalDestination && !selectedManualTable?.id) {
      setManualOrderMessage({
        type: "error",
        text: "Selecciona una mesa real generada desde Mesas QR antes de cargar el pedido.",
      });
      return;
    }

    if (!manualOrderItems.length) {
      setManualOrderMessage({
        type: "error",
        text: "Agrega al menos un plato del menu al pedido manual.",
      });
      return;
    }

    if (monto <= 0) {
      setManualOrderMessage({
        type: "error",
        text: "El pedido necesita un total valido para pasar a cobro.",
      });
      return;
    }

    const stockValidation = validateIngredientStock(
      operationalRestaurant,
      manualOrderItems.reduce(
        (acc, item) => acc.concat(Array.isArray(item.consumos) ? item.consumos : []),
        []
      ),
      {
        ingredientes,
        stockMap: ingredientStockSnapshot,
        allowMissingStockRecords: false,
      }
    );

    if (!stockValidation.ok) {
      setManualOrderMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para registrar este pedido manual.",
        }),
      });
      return;
    }

    if (isPickupDestination && !manualCustomerName) {
      setManualOrderMessage({
        type: "error",
        text: "Ingresa el nombre del cliente para imprimir el ticket de retiro.",
      });
      return;
    }

    if (isExternalDestination) {
      if (!currentBranch?.id || !isSupabaseConfigured || isCreatorMode) {
        setManualOrderMessage({
          type: "error",
          text: "Para cargar pedidos para llevar o con envio necesitas una sucursal conectada a Supabase.",
        });
        return;
      }

      if (!manualOrderForm.customerFirstName.trim() || !manualOrderForm.customerPhone.trim()) {
        setManualOrderMessage({
          type: "error",
          text: "Completa el nombre y el telefono del cliente.",
        });
        return;
      }

      if (destinationType === "delivery" && !manualOrderForm.deliveryZoneId) {
        setManualOrderMessage({
          type: "error",
          text: "Selecciona una zona habilitada para calcular el envio.",
        });
        return;
      }

      if (destinationType === "delivery" && !manualOrderForm.addressStreet.trim()) {
        setManualOrderMessage({
          type: "error",
          text: "Completa la calle de entrega antes de confirmar el envio.",
        });
        return;
      }

      if (manualOrderPricing.paymentPrices) {
        const zone = destinationType === "delivery"
          ? manualDeliveryZones.find((item) => String(item.id) === String(manualOrderForm.deliveryZoneId)) : null;
        const quote = calculateExternalDeliveryQuote({ subtotal: manualOrderPricing.finalTotal, zone });
        if (!window.confirm(`${promotionPaymentNotice(manualOrderPricing, formatCurrency)}\n\nEnvio: ${formatCurrency(quote.deliveryFee)}. Total a confirmar: ${formatCurrency(quote.total)}.`)) return;
      }

      if (!acquireManualOrderSubmission()) {
        return;
      }

      setManualOrderMessage({ type: "", text: "" });

      try {
        const created = await createExternalOrder({
          branchId: currentBranch.id,
          customer: {
            firstName: manualOrderForm.customerFirstName.trim(),
            lastName: manualOrderForm.customerLastName.trim(),
            phone: manualOrderForm.customerPhone.trim(),
          },
          address: {
            label: "Entrega",
            street: manualOrderForm.addressStreet.trim(),
            streetNumber: manualOrderForm.addressStreetNumber.trim(),
            neighborhood: manualOrderForm.addressNeighborhood.trim(),
            city: manualOrderForm.addressCity.trim(),
            reference: manualOrderForm.addressReference.trim(),
          },
          deliveryZoneId:
            destinationType === "delivery" ? manualOrderForm.deliveryZoneId : null,
          fulfillmentType: destinationType === "delivery" ? "delivery" : "pickup",
          paymentMethod: resolveManualExternalPaymentMethod(
            manualOrderForm.paymentMethod,
            destinationType
          ),
          customerNotes: manualOrderForm.notes.trim(),
          internalNotes: "Pedido cargado manualmente desde Inicio.",
          promotionPricing: manualOrderPricing,
          items: manualOrderItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            categoryName: item.categoryName || "",
            subcategoryName: item.subcategoryName || "",
            selectionDetails: item.selectionDetails || [],
            stockConsumptions: item.consumos || [],
            pizzaSizeOptionId: item.pizzaSizeOptionId || "",
            pizzaPortionMode: item.pizzaPortionMode || "full",
            linkedSelections: item.linkedSelections || [],
            notes: "",
          })),
          sourceChannel: "manual",
          idempotencyKey: `inicio-${destinationType}-${Date.now()}`,
        });
        const selectedZone =
          manualDeliveryZones.find(
            (zone) => String(zone.id) === String(manualOrderForm.deliveryZoneId)
          ) || null;
        const createdOrder = {
          id: created.id,
          display_number: created.displayNumber,
          fulfillment_type: destinationType === "delivery" ? "delivery" : "pickup",
          payment_method: resolveManualExternalPaymentMethod(
            manualOrderForm.paymentMethod,
            destinationType
          ),
          payment_status: created.paymentStatus,
          status: created.status,
          subtotal: created.subtotal,
          delivery_fee: created.deliveryFee,
          total_amount: created.totalAmount,
          change_due: created.changeDue,
          customer: {
            first_name: manualOrderForm.customerFirstName.trim(),
            last_name: manualOrderForm.customerLastName.trim(),
            phone_original: manualOrderForm.customerPhone.trim(),
          },
          address:
            destinationType === "delivery"
              ? {
                  street: manualOrderForm.addressStreet.trim(),
                  street_number: manualOrderForm.addressStreetNumber.trim(),
                  neighborhood: manualOrderForm.addressNeighborhood.trim(),
                  city: manualOrderForm.addressCity.trim(),
                  reference: manualOrderForm.addressReference.trim(),
                }
              : null,
          zone: selectedZone,
          customer_notes: manualOrderForm.notes.trim(),
          internal_notes: "Pedido cargado manualmente desde Inicio.",
          created_at: new Date().toISOString(),
          items: manualOrderItems.map((item, index) => ({
            id: `${created.id}-${index}`,
            product_id: item.productId,
            product_name: item.name,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            line_total: item.unitPrice * item.quantity,
            categoryName: item.categoryName || "",
            subcategoryName: item.subcategoryName || "",
            selection_details: item.selectionDetails || [],
            stock_consumptions: item.consumos || [],
            linkedProductId: item.linkedProductId || "",
            linkedProductName: item.linkedProductName || "",
            linkedSelections: item.linkedSelections || [],
            linkedUnitPrice: item.linkedUnitPrice || 0,
            notes: "",
          })),
        };

        let dispatchWarning = "";
        try {
          await dispatchExternalOrderToKitchen({ branch: currentBranch, order: createdOrder });
        } catch (dispatchError) {
          dispatchWarning = ` El pedido quedo guardado, pero necesita reintentar el envio a cocina: ${dispatchError.message}`;
        }

        setManualOrderForm(initialManualOrderForm);
        setManualOrderItems([]);
        setManualOrderSelection({});
        setManualOrderPizzaSizeOptionId("");
        setManualOrderPizzaHalfProductId("");
        setManualOrderSearch("");
        setManualCustomerSearch("");
        setManualOrderMessage({
          type: "success",
          text: `${created.displayNumber || "El pedido"} quedo cargado en Delivery.${dispatchWarning || " Tambien ingreso a cocina."}`,
        });
        setIsManualOrderExpanded(false);
        return;
      } catch (error) {
        setManualOrderMessage({
          type: "error",
          text: error?.message || "No pudimos registrar el pedido para llevar o con envio.",
        });
        return;
      } finally {
        releaseManualOrderSubmission();
      }
    }

    const now = new Date().toISOString();
    const orderId = `manual-order-${Date.now()}`;
    const paymentOperationId = buildManualPaymentOperationId();
    const isDeepLinkPayment = manualOrderForm.paymentMethod === "deep_link";
    const kitchenModeSnapshot = resolveActiveKitchenMode();
    const manualPreparationNumber = isPickupDestination
      ? resolveNextManualPreparationNumber({ mesasActivas, pedidosHistoricos })
      : null;
    const items = manualOrderItems.map((item, index) =>
      createOperationalOrderItem({
        id: `${orderId}-item-${index + 1}`,
        productId: item.productId,
        productName: item.name,
        quantity: item.quantity,
        baseUnitPrice: item.baseUnitPrice,
        extraUnitCost: item.extraUnitCost,
        unitPrice: item.unitPrice,
        linkedProductId: item.linkedProductId || "",
        linkedProductName: item.linkedProductName || "",
        linkedSelections: item.linkedSelections || [],
        linkedUnitPrice: item.linkedUnitPrice || 0,
        selectionDetails: item.selectionDetails,
        delivered: false,
        consumos: item.consumos || [],
        categoryName: item.categoryName || "",
        subcategoryName: item.subcategoryName || "",
      })
    );
    const itemsSummary = buildCatalogItemSummary(manualOrderItems);
    const pricing = {
      ...compactPromotionPaymentFields(manualOrderPricing),
      listTotal: Number(manualOrderPricing.listTotal || 0),
      comboDiscountTotal: Number(manualOrderPricing.comboDiscountTotal || 0),
      promotionDiscountTotal: Number(manualOrderPricing.promotionDiscountTotal || 0),
      totalDiscount: Number(manualOrderPricing.totalDiscount || 0),
      finalTotal: monto,
      adjustments: Array.isArray(manualOrderPricing.adjustments)
        ? manualOrderPricing.adjustments.map((adjustment) => ({
            id: adjustment.id,
            type: adjustment.type,
            title: adjustment.title,
            amount: Number(adjustment.amount || 0),
            summary: adjustment.summary || adjustment.title || "",
          }))
        : [],
    };

    const promotionNotice = promotionPaymentNotice(pricing, formatCurrency);
    if (promotionNotice && !window.confirm(`${promotionNotice}\n\nConfirmar pedido por ${formatCurrency(pricing.finalTotal)}?`)) return;

    if (!acquireManualOrderSubmission()) {
      return;
    }

    const createOrderAfterMzCheck = async () => {
      if (!isCreatorMode && isSupabaseConfigured) {
        try { await assertAcceptingNewOrders(operationalRestaurant.id); }
        catch (error) {
          setManualOrderMessage({ type: "error", text: error.message });
          releaseManualOrderSubmission();
          return;
        }
      }
      try {
      if (!isDeepLinkPayment) {
        try {
          const mzResult = await registerManualOperationalMzUsage({
            orderId,
            paymentOperationId,
            sourceChannel: "manual",
            sourceMode: manualOrderForm.paymentMethod || "manual",
            metadata: {
              canal: manualOrderForm.canal || "",
              customerName: manualCustomerName,
              destinationType,
              paymentFlow: manualOrderForm.paymentFlow || "",
              paymentMethod: manualOrderForm.paymentMethod || "",
              mesa,
              preparationNumber: manualPreparationNumber,
              grossAmount: monto,
              totalAmount: monto,
            },
          });

          if (mzResult?.skipped && mzResult.reason === "insufficient_balance") {
            setManualOrderMessage({
              type: "error",
              text: `Se agotó el saldo operativo manual de la cuenta. Necesitas recargar MZ o volver a cobrar por deeplink para restaurar el cupo base de ${MANUAL_OPERATIONAL_MZ_RESET_UNITS} MZ.`,
            });
            return;
          }
        } catch (error) {
          console.error(
            "No pudimos validar el saldo operativo MZ antes de registrar el pedido manual:",
            error
          );
          setManualOrderMessage({
            type: "error",
            text:
              error?.message ||
              "No pudimos validar el saldo operativo MZ para este pedido manual.",
          });
          return;
        }
      }

      createManualPaymentOperation({
        operationId: paymentOperationId,
        orderId,
        tableId: isPickupDestination ? "" : selectedManualTable.id,
        mesa,
        customerName: isPickupDestination ? manualCustomerName : salonDinerName,
        preparationNumber: manualPreparationNumber,
        serviceType: isPickupDestination ? "takeaway" : "",
        canal: isPickupDestination ? "Retiro" : manualOrderForm.canal,
        monto,
        paymentFlow: manualOrderForm.paymentFlow,
        paymentMethod: manualOrderForm.paymentMethod,
        itemsSummary,
        notes: manualOrderForm.notes.trim(),
        pricing,
      });

      const nextMesa = {
        id: orderId,
        mesa,
        tableId: isPickupDestination ? "" : selectedManualTable.id,
        comensales: isPickupDestination ? 1 : Number(manualOrderForm.comensales || 1),
        canal: isPickupDestination ? "Retiro" : manualOrderForm.canal,
        phase: "new",
        source: "manual",
        serviceType: isPickupDestination ? "takeaway" : "",
        customerName: isPickupDestination ? manualCustomerName : salonDinerName,
        dinerName: isPickupDestination ? "" : salonDinerName,
        preparationNumber: manualPreparationNumber,
        kitchenModeSnapshot,
        stockDiscounted: false,
        orderedAt: now,
        items,
        notes: manualOrderForm.notes.trim(),
        totalAmount: monto,
        pricing,
        requestedPaymentMethod: manualOrderForm.paymentMethod,
        selectedPaymentMethod: manualOrderForm.paymentMethod,
        paymentStatus:
          manualOrderForm.paymentFlow === "paid_now"
            ? isDeepLinkPayment
              ? "link_sent"
              : "paid"
            : "pending",
        paymentMethod: manualOrderForm.paymentMethod,
        paymentMethodLabel: resolveManualPaymentMethodLabel(manualOrderForm.paymentMethod),
        paymentOperationId,
      };

      setMesasActivas((prev) => [nextMesa, ...prev]);

      // El pedido manual se imprime en el mismo instante en que queda registrado.
      // No depende del sincronizador posterior, que puede llegar tarde desde Supabase.
      let kitchenTicketWarning = "";
      if (kitchenModeSnapshot === "printer" && orderRequiresKitchen(nextMesa)) {
        autoKitchenPrintKnownIdsRef.current.add(orderId);
        autoKitchenPrintInFlightIdsRef.current.add(orderId);

        try {
          await printKitchenTicketToConfiguredDevice(nextMesa);
          markKitchenTicketAsPrinted(orderId, nextMesa);
        } catch (ticketError) {
          kitchenTicketWarning = ` La comanda no pudo imprimirse: ${
            ticketError?.message || "revisa la impresora configurada."
          }`;
          setServiceAlert({
            id: `printer-kitchen-error-${Date.now()}`,
            title: `No pudimos imprimir ${nextMesa.mesa}`,
            text: kitchenTicketWarning.trim(),
          });
        } finally {
          autoKitchenPrintInFlightIdsRef.current.delete(orderId);
        }
      }

      if (!isPickupDestination && selectedManualTable?.id) {
        setSalonReservations((prev) => {
          const next = { ...prev };
          delete next[selectedManualTable.id];
          return next;
        });
      }

      let ticketWarning = "";
      if (isPickupDestination) {
        try {
          await printManualCustomerPickupTicket(nextMesa, manualOrderForm.paymentMethod);
        } catch (ticketError) {
          ticketWarning = ` No pudimos imprimir el ticket: ${
            ticketError?.message || "revisa la impresora configurada."
          }`;
        }
      }

      setManualOrderForm(initialManualOrderForm);
      setManualOrderItems([]);
      setManualOrderSelection({});
      setManualOrderPizzaSizeOptionId("");
      setManualOrderPizzaHalfProductId("");
      setManualOrderSearch("");
      setManualCustomerSearch("");
      setIsManualOrderExpanded(false);
      openMesaDetail(orderId);
      const manualOrderSuccessText = isPickupDestination
        ? `Pedido para llevar cargado${
            manualPreparationNumber
              ? ` con numero #${formatPreparationNumber(manualPreparationNumber)}`
              : ""
          }.${ticketWarning || " Ticket emitido."}`
        : manualOrderForm.paymentFlow === "paid_now"
          ? "Pedido manual cargado. El cobro ya quedo registrado en Pagos."
          : "Pedido manual cargado. El cobro quedo pendiente en Pagos.";
      setManualOrderMessage({
        type: "success",
        text: `${manualOrderSuccessText}${
          kitchenTicketWarning ||
          (kitchenModeSnapshot === "printer" && orderNeedsKitchen(nextMesa)
            ? " Comanda enviada a cocina."
            : "")
        }`,
      });
      } catch (error) {
        console.error("No pudimos registrar el pedido manual:", error);
        setManualOrderMessage({
          type: "error",
          text: error?.message || "No pudimos registrar el pedido manual.",
        });
      } finally {
        releaseManualOrderSubmission();
      }
    };

    void createOrderAfterMzCheck();
  };

  const closeNotificationDetailModal = () => {
    setNotificationDetailMesaId("");
  };

  const detalleConfig = useMemo(() => {
    if (detalleActivo === "pedidos-hoy") {
      return {
        title: "Pedidos del dia",
        subtitle:
          `Aqui ves solo los pedidos creados hoy (${todayLabel}). Debajo puedes generar un PDF del historial por fecha y estado.`,
        rows: pedidosDelDia,
        mode: "pedidos",
      };
    }

    if (detalleActivo === "mesas-activas") {
      const selectedMesa =
        detalleMesaId ? mesasActivas.find((mesa) => mesa.id === detalleMesaId) || null : null;
      const rows = selectedMesa ? [selectedMesa] : mesasActivas;

      return {
        title: selectedMesa ? `Detalle de ${selectedMesa.mesa}` : "Mesas activas en detalle",
        subtitle: selectedMesa
          ? "Aqui tienes el pedido puntual, sus items, el total final y las acciones operativas de esta mesa."
          : "Aqui puedes ver que mesas estan ocupadas, pasar pedidos a preparacion y registrar entregas parciales o completas.",
        rows,
        mode: "mesas",
      };
    }

    if (detalleActivo === "pedidos-pendientes") {
      return {
        title: "Cocina y salida",
        subtitle:
          "Aqui ves rapido que pedidos estan en marcha, como se despachan a cocina y que mesa conviene priorizar primero.",
        rows: pendientesSalida,
        mode: "pendientes",
      };
    }

    if (detalleActivo === "esperando-cierre") {
      return {
        title: "Mesas esperando cerrar cuenta",
        subtitle:
          "Aqui quedan las mesas con toda la entrega registrada y listas para pasar a cobro o cierre.",
        rows: mesasListasParaCerrar,
        mode: "cierres",
      };
    }

    return null;
  }, [
    detalleActivo,
    detalleMesaId,
    mesasActivas,
    mesasListasParaCerrar,
    pedidosDelDia,
    pendientesSalida,
    todayLabel,
  ]);

  const handleOrderReportDateChange = (field, value) => {
    setOrderReportFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleOrderReportStatus = (status) => {
    const baseStatuses =
      orderReportFilters.statusMode === "all"
        ? availableOrderReportStatuses
        : selectedOrderReportStatuses;
    const isSelected = baseStatuses.includes(status);
    const nextStatuses = isSelected
      ? baseStatuses.filter((item) => item !== status)
      : [...baseStatuses, status].sort(compareOrderStatuses);

    if (nextStatuses.length === availableOrderReportStatuses.length) {
      setOrderReportFilters((prev) => ({
        ...prev,
        statusMode: "all",
        statuses: [],
      }));
      return;
    }

    setOrderReportFilters((prev) => ({
      ...prev,
      statusMode: "custom",
      statuses: nextStatuses,
    }));
  };

  const resetOrderReportFilters = () => {
    const today = formatDateInputValue(new Date(currentTime));
    setOrderReportFilters({
      from: today,
      to: today,
      statusMode: "all",
      statuses: [],
    });
  };

  const exportOrdersReportPdf = () => {
    if (
      !ensureInicioPermission(
        canExportOperationalReports,
        "exportar reportes de pedidos"
      )
    ) {
      return;
    }

    if (!pedidosReportePdf.length) {
      setServiceAlert({
        id: `orders-report-empty-${Date.now()}`,
        title: "No hay pedidos para exportar",
        text:
          "Ajusta el rango de fechas o los estados para incluir al menos un pedido en el PDF.",
      });
      return;
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 14;
    const usableWidth = pageWidth - margin * 2;
    const reportRangeLabel = `${orderReportRange.fromDate ? formatShortDate(orderReportRange.fromDate) : "Inicio"} a ${orderReportRange.toDate ? formatShortDate(orderReportRange.toDate) : "Hoy"}`;
    const reportStatusesLabel = selectedOrderReportStatuses.length
      ? selectedOrderReportStatuses.join(", ")
      : "Sin estados seleccionados";
    const branchLabel = operationalRestaurant?.name || "MozzoApp";
    const generatedAtLabel = formatDateTime(new Date());
    let y = margin;

    const ensurePageSpace = (requiredHeight = 10) => {
      if (y + requiredHeight <= pageHeight - margin) {
        return;
      }

      pdf.addPage();
      y = margin;
    };

    const writeWrappedText = ({
      text,
      fontSize = 10,
      fontStyle = "normal",
      color = [51, 65, 85],
      gapAfter = 5,
    }) => {
      const safeText = String(text || "").trim() || "-";
      pdf.setFont("helvetica", fontStyle);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(safeText, usableWidth);
      const blockHeight = Math.max(lines.length, 1) * (fontSize <= 10 ? 4.8 : 5.4);
      ensurePageSpace(blockHeight + gapAfter);
      pdf.text(lines, margin, y);
      y += blockHeight + gapAfter;
    };

    writeWrappedText({
      text: "Reporte de pedidos",
      fontSize: 18,
      fontStyle: "bold",
      color: [15, 23, 42],
      gapAfter: 2,
    });
    writeWrappedText({
      text: branchLabel,
      fontSize: 12,
      fontStyle: "bold",
      color: [37, 99, 235],
      gapAfter: 4,
    });
    writeWrappedText({
      text: `Periodo: ${reportRangeLabel}`,
      fontSize: 10,
      color: [71, 85, 105],
      gapAfter: 1,
    });
    writeWrappedText({
      text: `Estados: ${reportStatusesLabel}`,
      fontSize: 10,
      color: [71, 85, 105],
      gapAfter: 1,
    });
    writeWrappedText({
      text: `Generado: ${generatedAtLabel} - Total pedidos: ${pedidosReportePdf.length}`,
      fontSize: 10,
      color: [71, 85, 105],
      gapAfter: 6,
    });

    pedidosReportePdf.forEach((pedido, index) => {
      const orderStatus = normalizeOrderStatusLabel(pedido.estado);
      const amount = Number(pedido.totalAmount ?? pedido?.pricing?.finalTotal ?? 0);
      const detailLines = [
        `Canal: ${pedido.canal || "Sin canal"} - Pedido: ${formatDateTime(pedido.requestedAt)}`,
        `Detalle: ${pedido.pedido || "-"}`,
      ];

      if (amount > 0) {
        detailLines.push(`Total: ${formatCurrency(amount)}`);
      }

      if (pedido.closedAt) {
        detailLines.push(`Cerrado: ${formatDateTime(pedido.closedAt)}`);
      }

      if (pedido.cancelledAt) {
        detailLines.push(`Cancelado: ${formatDateTime(pedido.cancelledAt)}`);
      }

      const estimatedHeight = 12 + detailLines.length * 6 + 6;
      ensurePageSpace(estimatedHeight);
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, y - 1, pageWidth - margin, y - 1);
      y += 4;

      writeWrappedText({
        text: `${index + 1}. ${pedido.mesa || "Pedido"} - ${orderStatus}`,
        fontSize: 12,
        fontStyle: "bold",
        color: [15, 23, 42],
        gapAfter: 2,
      });

      detailLines.forEach((line, lineIndex) => {
        writeWrappedText({
          text: line,
          fontSize: 10,
          color: lineIndex === 1 ? [30, 41, 59] : [71, 85, 105],
          gapAfter: lineIndex === detailLines.length - 1 ? 4 : 1,
        });
      });
    });

    pdf.save(buildOrdersReportFileName(orderReportRange));
  };

  const ensureMesaStockDiscounted = (mesaId) => {
    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa || mesa.stockDiscounted) {
      return { ok: true };
    }

    const consumos = mesa.items.flatMap((item) => item.consumos || []);
    if (consumos.length === 0) {
      return { ok: true };
    }

    const stockValidation = validateIngredientStock(
      operationalRestaurant,
      consumos,
      {
        ingredientes,
        stockMap: ingredientStockSnapshot,
        allowMissingStockRecords: false,
      }
    );

    if (!stockValidation.ok) {
      return stockValidation;
    }

    consumeIngredientStock(operationalRestaurant, ingredientes, consumos);
    return { ok: true };
  };
  const restoreMesaStockIfNeeded = (mesa) => {
    if (!mesa || mesa.stockDiscounted !== true) {
      return { ok: true, restored: false };
    }

    const consumos = (Array.isArray(mesa.items) ? mesa.items : []).flatMap(
      (item) => item.consumos || []
    );

    if (consumos.length === 0) {
      return { ok: true, restored: false };
    }

    restoreIngredientStock(operationalRestaurant, ingredientes, consumos);
    return { ok: true, restored: true };
  };

  const notifyInsufficientMesaStock = (mesa, validation) => {
    setServiceAlert({
      id: `stock-alert-${Date.now()}`,
      title: `Stock insuficiente en ${mesa?.mesa || "la mesa"}`,
      text: getIngredientStockValidationMessage(validation, {
        fallbackMessage:
          "No hay stock suficiente para enviar este pedido a cocina.",
      }),
    });
  };

  const getWaiterDisplayName = (waiter) =>
    waiter?.profileFullName?.trim() || "Mozo activo";

  const getWaiterOpenTableCount = (waiterUserId, excludedTableId = "") => {
    if (!waiterUserId) {
      return 0;
    }

    const activeTableIds = new Set(
      mesasActivas
        .filter(
          (mesa) =>
            mesa.phase !== "cancelled" &&
            mesa.phase !== "closed" &&
            mesa.tableId &&
            mesa.tableId !== excludedTableId
        )
        .map((mesa) => mesa.tableId)
    );
    const waiterOpsState = getWaiterOperationsState(operationalRestaurant);

    return (waiterOpsState.tableAssignments || []).filter(
      (assignment) =>
        assignment.branchId === currentBranch?.id &&
        assignment.waiterUserId === waiterUserId &&
        activeTableIds.has(assignment.tableId)
    ).length;
  };
  const getWaiterPendingRequestCount = useCallback(
    (waiterUserId, excludedRequestId = "") => {
      if (!waiterUserId) {
        return 0;
      }

      return (waiterOps.serviceRequests || []).filter(
        (request) =>
          request.id !== excludedRequestId &&
          request.branchId === currentBranch?.id &&
          request.status === "assigned" &&
          request.assignedWaiterUserId === waiterUserId
      ).length;
    },
    [currentBranch?.id, waiterOps.serviceRequests]
  );

  const closeDeliveryAssignmentModal = () => {
    cancelConnectedWaiterAssignment();
    setDeliveryAssignmentMesaId("");
    setDeliveryAssignmentWaiterId("");
    setDeliveryAssignmentItemIds([]);
  };

  const closeWaiterRequestAssignmentModal = () => {
    cancelConnectedWaiterAssignment();
    setWaiterRequestAssignmentId("");
    setWaiterRequestAssignmentWaiterId("");
  };

  const openDeliveryAssignmentModal = (mesa, options = {}) => {
    if (!ensureInicioPermission(canModifyOrders, "asignar entregas")) {
      return;
    }

    if (!mesa) {
      return;
    }

    setDeliveryAssignmentMesaId(mesa.id);
    void refreshConnectedWaiters();
    setDeliveryAssignmentWaiterId(deliveryAvailableWaiters[0]?.userId || "");
    setDeliveryAssignmentItemIds(
      Array.from(
        new Set(
          (Array.isArray(options?.itemIds) ? options.itemIds : [])
            .map((itemId) => String(itemId || "").trim())
            .filter(Boolean)
        )
      )
    );
  };
  const markDirectItemsReadyForWaiter = (mesa, waiter, itemIds = []) => {
    if (!ensureInicioPermission(canModifyOrders, "asignar entregas directas")) {
      return;
    }

    if (!mesa) {
      return;
    }

    const selectedIds = new Set(itemIds);
    const directItems = getMesaPendingDirectDeliveryItems(mesa).filter((item) =>
      selectedIds.size ? selectedIds.has(item.id) : true
    );
    const waiterUserId = waiter?.userId || "";
    const waiterName = getWaiterDisplayName(waiter);

    if (!directItems.length || !waiterUserId) {
      openDeliveryAssignmentModal(mesa, {
        itemIds: directItems.map((item) => item.id),
      });
      return;
    }

    const stockValidation = ensureMesaStockDiscounted(mesa.id);
    if (!stockValidation.ok) {
      notifyInsufficientMesaStock(mesa, stockValidation);
      return;
    }

    const now = new Date().toISOString();
    const directItemIds = directItems.map((item) => item.id);
    const directAlertKey = directItemIds.slice().sort().join("-");

    if (mesa.tableId) {
      assignWaiterToTable(operationalRestaurant, {
        tableId: mesa.tableId,
        tableLabel: mesa.mesa,
        branchId: currentBranch?.id || "",
        waiterUserId,
        waiterName,
      });
    }

    createDeliveryAlert(operationalRestaurant, {
      id: `delivery-direct-${mesa.id}-${directAlertKey}`,
      orderId: mesa.id,
      tableId: mesa.tableId || "",
      branchId: currentBranch?.id || "",
      mesa: mesa.mesa,
      type: "direct_item_delivery",
      summary: `Entregar: ${getDirectDeliveryItemsSummary(directItems)}`,
      itemIds: directItemIds,
      source: mesa.source || "",
      requestedAt: now,
      broadcast: false,
      targetWaiterIds: [waiterUserId],
      targetWaiterNames: [waiterName],
    });

    const assignedIds = new Set(directItemIds);
    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesa.id
          ? {
              ...item,
              phase: orderNeedsKitchen(item) ? item.phase : "ready",
              kitchenDispatchStatus: orderNeedsKitchen(item)
                ? item.kitchenDispatchStatus
                : "not_required",
              stockDiscounted: true,
              deliveryMode: "waiter",
              assignedWaiterUserId: waiterUserId,
              assignedWaiterName: waiterName,
              items: (item.items || []).map((orderItem) =>
                assignedIds.has(orderItem.id)
                  ? {
                      ...orderItem,
                      deliveryStatus: "assigned",
                      deliveryAssignedAt: now,
                      assignedWaiterUserId: waiterUserId,
                      assignedWaiterName: waiterName,
                    }
                  : orderItem
              ),
            }
          : item
      )
    );

    setServiceAlert({
      id: `direct-delivery-${Date.now()}`,
      title: `Entrega asignada a ${waiterName}`,
      text: `${getDirectDeliveryItemsSummary(directItems)} puede salir ahora, sin esperar el pedido de cocina.`,
    });

    closeDeliveryAssignmentModal();
  };

  const markDirectItemsReadyForManualDelivery = (mesa, itemIds = []) => {
    if (!ensureInicioPermission(canModifyOrders, "registrar entregas directas")) {
      return;
    }

    if (!mesa) {
      return;
    }

    const selectedIds = new Set(itemIds);
    const directItems = getMesaPendingDirectDeliveryItems(mesa).filter((item) =>
      selectedIds.size ? selectedIds.has(item.id) : true
    );

    if (!directItems.length) {
      closeDeliveryAssignmentModal();
      return;
    }

    const stockValidation = ensureMesaStockDiscounted(mesa.id);
    if (!stockValidation.ok) {
      notifyInsufficientMesaStock(mesa, stockValidation);
      return;
    }

    const now = new Date().toISOString();
    const directItemIds = new Set(directItems.map((item) => item.id));
    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesa.id
          ? {
              ...item,
              phase: orderNeedsKitchen(item) ? item.phase : "ready",
              kitchenDispatchStatus: orderNeedsKitchen(item)
                ? item.kitchenDispatchStatus
                : "not_required",
              stockDiscounted: true,
              deliveryMode: "manual",
              items: (item.items || []).map((orderItem) =>
                directItemIds.has(orderItem.id)
                  ? {
                      ...orderItem,
                      deliveryStatus: "ready",
                      deliveryAssignedAt: now,
                      assignedWaiterUserId: "",
                      assignedWaiterName: "",
                    }
                  : orderItem
              ),
            }
          : item
      )
    );

    setServiceAlert({
      id: `direct-delivery-manual-${Date.now()}`,
      title: "Entrega directa lista",
      text: `${getDirectDeliveryItemsSummary(directItems)} puede entregarse desde salon.`,
    });

    closeDeliveryAssignmentModal();
  };
  const markOrderReadyForWaiter = (mesa, waiter) => {
    if (!ensureInicioPermission(canModifyOrders, "marcar pedidos como listos")) {
      return;
    }

    if (!mesa) {
      return;
    }

    const waiterUserId = waiter?.userId || "";
    const waiterName = getWaiterDisplayName(waiter);
    const readyItems = getMesaKitchenItems(mesa).filter((item) => !item.delivered);

    if (!waiterUserId) {
      openDeliveryAssignmentModal(mesa);
      return;
    }

    if (mesa.tableId) {
      assignWaiterToTable(operationalRestaurant, {
        tableId: mesa.tableId,
        tableLabel: mesa.mesa,
        branchId: currentBranch?.id || "",
        waiterUserId,
        waiterName,
      });
    }

    createDeliveryAlert(operationalRestaurant, {
      id: `delivery-ready-${mesa.id}`,
      orderId: mesa.id,
      tableId: mesa.tableId || "",
      branchId: currentBranch?.id || "",
      mesa: mesa.mesa,
      type: "delivery",
      summary: readyItems.length
        ? `Entregar: ${getDirectDeliveryItemsSummary(readyItems)}`
        : "Entregar pedido listo.",
      itemIds: readyItems.map((item) => item.id),
      source: mesa.source || "",
      requestedAt: new Date().toISOString(),
      broadcast: false,
      targetWaiterIds: [waiterUserId],
      targetWaiterNames: [waiterName],
    });

    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesa.id
          ? {
              ...item,
              phase: "ready",
              kitchenDispatchStatus:
                !orderNeedsKitchen(item)
                  ? "not_required"
                  : item.kitchenModeSnapshot === "printer"
                    ? "printer_ready"
                    : item.kitchenDispatchStatus,
              deliveryMode: "waiter",
              assignedWaiterUserId: waiterUserId,
              assignedWaiterName: waiterName,
            }
          : item
      )
    );

    setServiceAlert({
      id: `delivery-toast-${Date.now()}`,
      title: `Aviso enviado a ${waiterName}`,
      text: `El pedido de ${mesa.mesa} quedo listo para entregar y ya se asigno a ${waiterName}.`,
    });

    closeDeliveryAssignmentModal();
  };

  const markOrderReadyForManualDelivery = (mesa) => {
    if (!ensureInicioPermission(canModifyOrders, "registrar entregas manuales")) {
      return;
    }

    if (!mesa) {
      return;
    }

    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesa.id
          ? {
              ...item,
              phase: "ready",
              kitchenDispatchStatus: !orderNeedsKitchen(item)
                ? "not_required"
                : item.kitchenDispatchStatus,
              deliveryMode: "manual",
              assignedWaiterUserId: "",
              assignedWaiterName: "",
            }
          : item
      )
    );

    setServiceAlert({
      id: `delivery-manual-${Date.now()}`,
      title: "Entrega manual registrada",
      text: `El pedido de ${mesa.mesa} quedo listo para entregar sin mozo asignado.`,
    });

    closeDeliveryAssignmentModal();
  };

  const handleAutoAssignReadyOrder = () => {
    if (!ensureInicioPermission(canModifyOrders, "asignar mozos automaticamente")) {
      return;
    }

    if (!deliveryAssignmentMesa) {
      return;
    }

    void assignConnectedWaiter((waiters) => {
      const load = (waiter) => getWaiterOpenTableCount(waiter.userId, deliveryAssignmentMesa.tableId || "");
      const lowestLoad = Math.min(...waiters.map(load));
      const tiedWaiters = waiters.filter((waiter) => load(waiter) === lowestLoad);
      return tiedWaiters[Math.floor(Math.random() * tiedWaiters.length)] || null;
    }, (selectedWaiter) => {
      if (isDirectItemDeliveryAssignment) {
        markDirectItemsReadyForWaiter(
          deliveryAssignmentMesa, selectedWaiter, deliveryAssignmentItems.map((item) => item.id)
        );
      } else {
        markOrderReadyForWaiter(deliveryAssignmentMesa, selectedWaiter);
      }
    });
  };

  const handleManualAssignReadyOrder = () => {
    if (!ensureInicioPermission(canModifyOrders, "asignar mozos manualmente")) {
      return;
    }

    if (!deliveryAssignmentMesa || !deliveryAssignmentWaiterId) {
      return;
    }

    void assignConnectedWaiter(
      (waiters) => waiters.find((waiter) => waiter.userId === deliveryAssignmentWaiterId),
      (selectedWaiter) => {
        if (isDirectItemDeliveryAssignment) {
          markDirectItemsReadyForWaiter(
            deliveryAssignmentMesa, selectedWaiter, deliveryAssignmentItems.map((item) => item.id)
          );
        } else {
          markOrderReadyForWaiter(deliveryAssignmentMesa, selectedWaiter);
        }
      }
    );
  };
  const assignWaiterToHelpRequest = (request, waiter) => {
      if (!ensureInicioPermission(canModifyOrders, "derivar llamados al mozo")) {
        return;
      }

      if (!request || !waiter?.userId) {
        return;
      }

      const waiterName = getWaiterDisplayName(waiter);

      if (request.tableId) {
        assignWaiterToTable(operationalRestaurant, {
          tableId: request.tableId,
          tableLabel: request.mesa,
          branchId: currentBranch?.id || "",
          waiterUserId: waiter.userId,
          waiterName,
        });
      }

      assignWaiterServiceRequest(operationalRestaurant, request.id, {
        status: "assigned",
        assignedWaiterUserId: waiter.userId,
        assignedWaiterName: waiterName,
        assignedAt: new Date().toISOString(),
      });

      createDeliveryAlert(operationalRestaurant, {
        id: `waiter-help-${request.id}`,
        orderId: request.id,
        tableId: request.tableId || "",
        mesa: request.mesa,
        type: "service_request",
        summary: request.summary || "Cliente pide ayuda del mozo.",
        source: request.source || "qr",
        requestedAt: new Date().toISOString(),
        broadcast: false,
        targetWaiterIds: [waiter.userId],
        targetWaiterNames: [waiterName],
      });

      setServiceAlert({
        id: `waiter-help-toast-${Date.now()}`,
        title: `Llamado asignado a ${waiterName}`,
        text: `${request.mesa} ya quedo derivada a ${waiterName}.`,
      });

      closeWaiterRequestAssignmentModal();
    };
  const handleAutoAssignWaiterRequest = () => {
    if (!ensureInicioPermission(canModifyOrders, "asignar llamados automaticamente")) {
      return;
    }

    if (!waiterRequestAssignment) {
      return;
    }

    void assignConnectedWaiter((waiters) => {
      const load = (waiter) => getWaiterPendingRequestCount(waiter.userId, waiterRequestAssignment.id);
      const lowestLoad = Math.min(...waiters.map(load));
      const tiedWaiters = waiters.filter((waiter) => load(waiter) === lowestLoad);
      return tiedWaiters[Math.floor(Math.random() * tiedWaiters.length)] || null;
    }, (selectedWaiter) => assignWaiterToHelpRequest(waiterRequestAssignment, selectedWaiter));
  };
  const handleManualAssignWaiterRequest = () => {
    if (!ensureInicioPermission(canModifyOrders, "asignar llamados manualmente")) {
      return;
    }

    if (!waiterRequestAssignment || !waiterRequestAssignmentWaiterId) {
      return;
    }

    void assignConnectedWaiter(
      (waiters) => waiters.find((waiter) => waiter.userId === waiterRequestAssignmentWaiterId),
      (selectedWaiter) => assignWaiterToHelpRequest(waiterRequestAssignment, selectedWaiter)
    );
  };
  const handleCompleteWaiterRequestFromInicio = (request) => {
    if (!ensureInicioPermission(canModifyOrders, "cerrar llamados del mozo")) {
      return;
    }

    if (!request?.id) {
      return;
    }

    completeWaiterServiceRequest(operationalRestaurant, request.id);
    setServiceAlert({
      id: `waiter-help-complete-${Date.now()}`,
      title: `Llamado cerrado en ${request.mesa}`,
      text: "El pedido al mozo ya quedo marcado como atendido.",
    });
  };

  // eslint-disable-next-line no-unused-vars
  const notifyWaitersOrderReady = (mesaId) => {
    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return;
    }

    const assignment = mesa.tableId
      ? getWaiterAssignmentForTable(operationalRestaurant, mesa.tableId)
      : null;
    const isManualOrder = mesa.source === "manual";
    const shouldBroadcast = isManualOrder || !assignment?.waiterUserId;
    const activeWaiterCount = activeWaiters.length;

    createDeliveryAlert(operationalRestaurant, {
      id: `delivery-ready-${mesa.id}`,
      orderId: mesa.id,
      tableId: mesa.tableId || "",
      mesa: mesa.mesa,
      source: mesa.source || "",
      requestedAt: new Date().toISOString(),
      broadcast: shouldBroadcast,
      targetWaiterIds: shouldBroadcast ? [] : [assignment.waiterUserId],
      targetWaiterNames:
        shouldBroadcast || !assignment?.waiterName ? [] : [assignment.waiterName],
    });

    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesaId
          ? {
              ...item,
              phase: "ready",
            }
          : item
      )
    );

    setServiceAlert({
      id: `delivery-toast-${Date.now()}`,
      title: shouldBroadcast
        ? `Aviso enviado para ${mesa.mesa}`
        : `Aviso enviado a ${assignment.waiterName || "su mozo"}`,
      text: shouldBroadcast
        ? `El pedido quedó listo para entregar y se avisó al equipo activo${activeWaiterCount > 0 ? ` (${activeWaiterCount})` : ""}.`
        : `El pedido de ${mesa.mesa} ya quedó marcado como listo para entregar.`,
    });
  };

  const printKitchenOrderTicket = async (mesaId, options = {}) => {
    if (!ensureInicioPermission(canModifyOrders, "imprimir comandas de cocina")) {
      return false;
    }

    const mesa =
      options.orderOverride ||
      mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return false;
    }

    if (!orderRequiresKitchen(mesa)) {
      setServiceAlert({
        id: `printer-kitchen-skipped-${Date.now()}`,
        title: `Sin comanda de cocina para ${mesa.mesa}`,
        text: "El pedido solo tiene bebidas, por eso no se envia a cocina.",
      });
      return true;
    }

    try {
      await printKitchenTicketToConfiguredDevice(mesa);
      markKitchenTicketAsPrinted(mesaId, mesa);

      setServiceAlert({
        id: `printer-kitchen-${Date.now()}`,
        title: `Comanda lista para ${mesa.mesa}`,
        text:
          Number(mesa.kitchenPrintCount || 0) > 0
            ? "La comanda se reenvio a impresion para cocina."
            : "La comanda se envio a impresion para cocina.",
      });

      return true;
    } catch (error) {
      setServiceAlert({
        id: `printer-kitchen-error-${Date.now()}`,
        title: `No pudimos imprimir ${mesa.mesa}`,
        text:
          error instanceof Error
            ? error.message
            : "Revisa la impresora o vuelve a intentarlo desde la card del pedido.",
      });
      return false;
    }
  };

  useEffect(() => {
    printKitchenOrderTicketRef.current = printKitchenOrderTicket;
  });

  useEffect(() => {
    if (!autoKitchenPrintHydratedRef.current) {
      autoKitchenPrintKnownIdsRef.current = new Set(
        (Array.isArray(mesasActivas) ? mesasActivas : [])
          .map((mesa) => String(mesa?.id || "").trim())
          .filter(Boolean)
      );
      autoKitchenPrintHydratedRef.current = true;
      return;
    }

    if (!canModifyOrders || !Array.isArray(mesasActivas) || !mesasActivas.length) {
      return;
    }

    const now = Date.now();
    const candidate = mesasActivas.find((mesa) => {
      const mesaId = String(mesa?.id || "").trim();

      if (!mesaId || autoKitchenPrintInFlightIdsRef.current.has(mesaId)) {
        return false;
      }

      if (!orderRequiresKitchen(mesa)) {
        autoKitchenPrintKnownIdsRef.current.add(mesaId);
        return false;
      }

      const mode = String(mesa?.kitchenModeSnapshot || perfil?.kitchenMode || "manual")
        .trim()
        .toLowerCase();
      const status = String(mesa?.kitchenDispatchStatus || "").trim().toLowerCase();
      const phase = String(mesa?.phase || "new").trim().toLowerCase();
      const orderedAtMs = mesa?.orderedAt ? new Date(mesa.orderedAt).getTime() : now;
      const isRecentOrder =
        Number.isFinite(orderedAtMs) && now - orderedAtMs <= 10 * 60 * 1000;

      if (autoKitchenPrintKnownIdsRef.current.has(mesaId)) {
        return false;
      }

      if (mode !== "printer" || status === "printer_printed") {
        autoKitchenPrintKnownIdsRef.current.add(mesaId);
        return false;
      }

      if (["new", "pending"].includes(phase)) {
        return false;
      }

      if (phase !== "preparation") {
        autoKitchenPrintKnownIdsRef.current.add(mesaId);
        return false;
      }

      if (!isRecentOrder) {
        autoKitchenPrintKnownIdsRef.current.add(mesaId);
        return false;
      }

      return true;
    });

    if (!candidate?.id) {
      return;
    }

    autoKitchenPrintKnownIdsRef.current.add(candidate.id);
    autoKitchenPrintInFlightIdsRef.current.add(candidate.id);

    const printPromise = printKitchenOrderTicketRef.current?.(candidate.id, {
      orderOverride: candidate,
      auto: true,
    });

    if (!printPromise || typeof printPromise.finally !== "function") {
      autoKitchenPrintInFlightIdsRef.current.delete(candidate.id);
      return;
    }

    void printPromise.finally(() => {
      autoKitchenPrintInFlightIdsRef.current.delete(candidate.id);
    });
  }, [canModifyOrders, mesasActivas, perfil?.kitchenMode]);

  const requestDirectItemDelivery = (mesaId, requestedItemIds = []) => {
    if (!ensureInicioPermission(canModifyOrders, "asignar entregas directas")) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return;
    }

    const requestedIds = new Set(requestedItemIds);
    const directItems = getMesaPendingDirectDeliveryItems(mesa).filter((item) =>
      requestedIds.size ? requestedIds.has(item.id) : true
    );

    if (!directItems.length) {
      setServiceAlert({
        id: `direct-delivery-empty-${Date.now()}`,
        title: "Entrega directa completa",
        text: "No quedan bebidas ni productos de salida directa pendientes.",
      });
      return;
    }

    const assignment = mesa.tableId
      ? getWaiterAssignmentForTable(operationalRestaurant, mesa.tableId)
      : null;
    const assignedWaiter = assignment?.waiterUserId
      ? deliveryAvailableWaiters.find(
          (waiter) => waiter.userId === assignment.waiterUserId
        ) || null
      : null;

    if (assignedWaiter) {
      void assignConnectedWaiter(
        (waiters) => waiters.find((waiter) => waiter.userId === assignedWaiter.userId),
        (connectedWaiter) => markDirectItemsReadyForWaiter(mesa, connectedWaiter, directItems.map((item) => item.id)),
        () => openDeliveryAssignmentModal(mesa, { itemIds: directItems.map((item) => item.id) })
      );
      return;
    }

    openDeliveryAssignmentModal(mesa, {
      itemIds: directItems.map((item) => item.id),
    });
  };

  const requestOrderReadyDelivery = (mesaId) => {
    if (!ensureInicioPermission(canModifyOrders, "pasar pedidos a entrega")) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return;
    }

    if (!orderNeedsKitchen(mesa)) {
      requestDirectItemDelivery(mesaId);
      return;
    }

    if (!canRequestReadyDelivery(mesa)) {
      const requiresKitchen = orderNeedsKitchen(mesa);
      setServiceAlert({
        id: `kitchen-block-${Date.now()}`,
        title: requiresKitchen
          ? `Falta sacar la comanda de ${mesa.mesa}`
          : `Falta confirmar ${mesa.mesa}`,
        text:
          !requiresKitchen
            ? "Este pedido no requiere cocina. Confirma el cobro o dejalo listo para habilitar la entrega."
            : mesa.kitchenModeSnapshot === "printer"
            ? "Imprime la comanda antes de pasar este pedido a entrega."
            : mesa.kitchenModeSnapshot === "screen"
              ? "Este pedido sigue en cocina. El jefe de cocina debe marcarlo como listo desde la pantalla de cocina."
            : "Primero confirma el paso por cocina antes de seguir con la entrega.",
      });
      return;
    }

    const assignment = mesa.tableId
      ? getWaiterAssignmentForTable(operationalRestaurant, mesa.tableId)
      : null;
    const assignedWaiter =
      assignment?.waiterUserId
        ? deliveryAvailableWaiters.find((waiter) => waiter.userId === assignment.waiterUserId) ||
          null
        : null;

    if (!assignment?.waiterUserId || !assignedWaiter) {
      openDeliveryAssignmentModal(mesa);
      return;
    }

    void assignConnectedWaiter(
      (waiters) => waiters.find((waiter) => waiter.userId === assignedWaiter.userId),
      (connectedWaiter) => markOrderReadyForWaiter(mesa, connectedWaiter),
      () => openDeliveryAssignmentModal(mesa)
    );
  };

  const requiresWaiterDeliveryAssignment = (mesa) =>
    Boolean(mesa?.tableId) &&
    String(mesa?.serviceType || "").trim().toLowerCase() !== "takeaway";

  const hasWaiterDeliveryAssignment = (mesa) => {
    if (!requiresWaiterDeliveryAssignment(mesa) || mesa?.deliveryMode === "manual") {
      return true;
    }

    const assignment = getWaiterAssignmentForTable(operationalRestaurant, mesa.tableId);
    return Boolean(mesa?.assignedWaiterUserId || assignment?.waiterUserId);
  };

  // Ninguna mesa de salon puede marcarse entregada o cerrarse sin una salida clara:
  // un mozo asignado o la entrega manual confirmada desde el modal.
  const ensureWaiterDeliveryAssignment = (mesa) => {
    if (requiresWaiterDeliveryAssignment(mesa) && mesa?.phase !== "ready") {
      requestOrderReadyDelivery(mesa.id);
      return false;
    }

    if (hasWaiterDeliveryAssignment(mesa)) {
      return true;
    }

    requestOrderReadyDelivery(mesa.id);
    return false;
  };

  const collectMesaCashAndSendToKitchen = async (mesaId) => {
    if (!ensureInicioPermission(canChargeOrders, "confirmar cobros en caja")) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return;
    }

    if (mesa.phase !== "new") {
      return;
    }

    const currentPayments = getPaymentOperations(operationalRestaurant);
    const currentPaymentState = resolveOperationalOrderPaymentState(
      mesa,
      currentPayments,
      mesa?.paymentOperationId
    );

    if (currentPaymentState.isPaid) {
      setServiceAlert({
        id: `cash-collect-already-paid-${Date.now()}`,
        title: "La cuenta ya fue cobrada",
        text: "El cobro fue confirmado desde Caja o por un mozo. Actualiza el pedido para continuar solo con su preparacion o entrega.",
      });
      return;
    }

    if (currentPaymentState.hasPendingDigitalPayment) {
      setServiceAlert({
        id: `cash-collect-pending-digital-${Date.now()}`,
        title: "Hay un QR de cobro activo",
        text: "Espera su confirmacion o cancelalo desde el panel que genero el QR antes de registrar efectivo.",
      });
      return;
    }

    const now = new Date().toISOString();
    const paidMesaSnapshot = {
      ...mesa,
      paymentStatus: "paid",
      cashPaymentPending: false,
      cashCollectedAt: mesa?.cashCollectedAt || now,
      cashCollectedBy: mesa?.cashCollectedBy || user?.id || "",
    };
    let confirmedPaymentOperationId =
      String(mesa?.paymentOperationId || "").trim() || `counter-payment-${mesa.id}`;

    {
      const operations = currentPayments;
      const targetOperation =
        currentPaymentState.paymentOperation ||
        buildFallbackPaymentOperationFromMesa({
          ...mesa,
          paymentOperationId: confirmedPaymentOperationId,
        });
      let settledOperation = null;

      if (targetOperation && targetOperation.status !== "closed") {
        const methodLabel =
          targetOperation.selectedMethod === "deep_link" ? "deep link" : "caja";
        const mzResult = await registerManualOperationalMzUsage({
          orderId: mesa.id,
          paymentOperationId: targetOperation.id,
          sourceChannel:
            mesa.source === "kiosk"
              ? "kiosk_counter"
              : mesa.source === "app" || mesa.source === "qr"
                ? "menu_qr_counter"
                : "manual_counter",
          sourceMode:
            String(targetOperation.selectedMethod || targetOperation.requestedMethod || "")
              .trim()
              .toLowerCase() || "pay_at_counter",
          metadata: {
            mesa: mesa.mesa || "",
            customerName: getMesaCustomerName(mesa) || "",
            flow: "collect_cash_and_send_to_kitchen",
            grossAmount: Number(
              targetOperation?.grossAmount ??
                targetOperation?.amount ??
                mesa?.pricing?.finalTotal ??
                mesa?.totalAmount ??
                0
            ),
          },
        }).catch((error) => {
          console.error(
            "No pudimos validar el saldo operativo MZ al confirmar el cobro en caja:",
            error
          );
          setServiceAlert({
            id: `manual-mz-cash-${Date.now()}`,
            title: "No pudimos validar el saldo operativo",
            text:
              error?.message ||
              "Intenta nuevamente o recarga la cuenta antes de seguir operando en caja.",
          });
          return { skipped: true, reason: "runtime_error" };
        });

        if (mzResult?.skipped && mzResult.reason === "insufficient_balance") {
          setServiceAlert({
            id: `manual-mz-cash-insufficient-${Date.now()}`,
            title: "Saldo operativo agotado",
            text: `La cuenta ya consumio su cupo manual. Necesitas recargar MZ o cobrar por deeplink para restaurar el piso operativo de ${MANUAL_OPERATIONAL_MZ_RESET_UNITS} MZ.`,
          });
          return;
        }

        if (mzResult?.skipped && mzResult.reason === "runtime_error") {
          return;
        }

        settledOperation = {
          ...targetOperation,
          status: "paid",
          paidAt: targetOperation.paidAt || now,
          updatedAt: now,
          history: [
            createPaymentHistoryEntry(`Cobro registrado por ${methodLabel} desde Inicio.`),
            ...(targetOperation.history || []),
          ].slice(0, 12),
        };
        confirmedPaymentOperationId = settledOperation.id;

        const orderPaymentPatch = {
          paymentStatus: "paid",
          paymentMethod: "counter",
          selectedPaymentMethod: "counter",
          requestedPaymentMethod: "counter",
          cashPaymentPending: false,
          paymentOperationId: settledOperation.id,
          paidAt: settledOperation.paidAt,
          cashCollectedAt: paidMesaSnapshot.cashCollectedAt,
          cashCollectedBy: paidMesaSnapshot.cashCollectedBy,
          paymentClaimSource: "inicio_cash",
          paymentClaimedBy: user?.id || "",
        };

        try {
          const paymentClaim = await claimSharedOperationalOrderPayment({
            restaurant: operationalRestaurant,
            orderId: mesa.id,
            paymentOperation: settledOperation,
            orderPatch: orderPaymentPatch,
            fallbackFactory: createEmptyOperationalState,
          });

          if (paymentClaim.supported) {
            applySharedPaymentClaimPayload(paymentClaim.payload);

            if (!paymentClaim.claimed) {
              setServiceAlert({
                id: `cash-collect-claimed-${Date.now()}`,
                title:
                  paymentClaim.reason === "payment_in_progress"
                    ? "Hay un QR de cobro activo"
                    : "La cuenta ya fue cobrada",
                text:
                  paymentClaim.reason === "payment_in_progress"
                    ? "Espera su confirmacion o cancelalo antes de registrar efectivo."
                    : "El cobro fue confirmado desde Caja o por un mozo. No se registrara una segunda vez.",
              });
              return;
            }
          } else {
            // Compatibilidad temporal para sucursales donde aun no se aplico el SQL.
            savePaymentOperations(
              operationalRestaurant,
              [
                settledOperation,
                ...operations.filter((operation) => operation?.id !== settledOperation.id),
              ]
            );
          }
        } catch (error) {
          setServiceAlert({
            id: `cash-collect-sync-${Date.now()}`,
            title: "No pudimos confirmar el cobro",
            text:
              error?.message ||
              "La base no confirmo el cobro. Revisa el estado antes de volver a intentarlo.",
          });
          return;
        }

        if (settledOperation) {
          await syncManualCommissionDebtSnapshot(
            settledOperation,
            "Cobro en caja confirmado desde Inicio."
          );
        }
      }
    }

    const movedToKitchen = await setMesaPhase(mesaId, "preparation", {
      permissionGranted: canChargeOrders,
      orderPatch: {
        paymentStatus: "paid",
        paymentMethod: "counter",
        selectedPaymentMethod: "counter",
        requestedPaymentMethod: "counter",
        cashPaymentPending: false,
        paymentOperationId: confirmedPaymentOperationId,
        paidAt: paidMesaSnapshot.cashCollectedAt,
        cashCollectedAt: paidMesaSnapshot.cashCollectedAt,
        cashCollectedBy: paidMesaSnapshot.cashCollectedBy,
      },
    });

    if (!movedToKitchen) {
      return;
    }

    setServiceAlert({
      id: `cash-collect-${Date.now()}`,
      title: "Cobro confirmado en caja",
      text: `${mesa.mesa}${getMesaCustomerName(mesa) ? ` · ${getMesaCustomerName(mesa)}` : ""} ${
        orderNeedsKitchen(mesa) ? "ya puede pasar a cocina." : "ya quedo listo para entregar."
      }`,
    });
  };

  const collectAndCloseMesaWithLocalPayment = async (mesaId, requestedMethod) => {
    if (!ensureInicioPermission(canChargeOrders, "confirmar cobros")) {
      return false;
    }

    if (!ensureInicioPermission(canModifyOrders, "cerrar pedidos")) {
      return false;
    }

    const localPaymentMethod = requestedMethod === "card" ? "card" : "counter";
    const isCardPayment = localPaymentMethod === "card";
    const actionKey = `collect-close:${localPaymentMethod}`;

    if (!acquireMesaActionLock(mesaId, actionKey, 30000)) {
      return false;
    }

    try {
      const latestOperationalState = getOperationalOrdersState(
        operationalRestaurant,
        createEmptyOperationalState
      );
      const mesa = resolveOrderPromotionPayment(
        (latestOperationalState.mesasActivas || []).find((item) => item.id === mesaId) ||
        mesasActivas.find((item) => item.id === mesaId), localPaymentMethod);

      if (!mesa) {
        return false;
      }

      if ((mesa.items || []).some((item) => !item.delivered)) {
        setServiceAlert({
          id: `local-payment-delivery-pending-${Date.now()}`,
          title: "Falta completar la entrega",
          text: "La cuenta puede cobrarse, pero solo se cierra cuando todos los productos figuran como entregados.",
        });
        return false;
      }

      const currentPayments = getPaymentOperations(operationalRestaurant);
      const paymentState = resolveOperationalOrderPaymentState(
        mesa,
        currentPayments,
        mesa?.paymentOperationId
      );

      if (paymentState.isPaid) {
        setServiceAlert({
          id: `local-payment-already-paid-${Date.now()}`,
          title: "La cuenta ya fue cobrada",
          text: "No se registrara un segundo cobro. Actualiza el pedido y usa Cerrar pedido.",
        });
        return false;
      }

      if (paymentState.hasPendingDigitalPayment) {
        setServiceAlert({
          id: `local-payment-digital-pending-${Date.now()}`,
          title: "Hay un cobro digital activo",
          text: "Espera su confirmacion o cancelalo desde el panel que genero el QR antes de cobrar por otro medio.",
        });
        return false;
      }

      const totalAmount = Number(mesa?.pricing?.finalTotal ?? mesa?.totalAmount ?? 0);

      if (totalAmount <= 0) {
        setServiceAlert({
          id: `local-payment-empty-${Date.now()}`,
          title: "La cuenta no tiene saldo pendiente",
          text: "Revisa el total del pedido antes de registrar el cobro.",
        });
        return false;
      }

      const customerName = getMesaCustomerName(mesa) || "el comensal";
      const confirmationMessage = isCardPayment
        ? `Confirma que la tarjeta de ${customerName} ya fue aprobada por ${formatCurrency(
            totalAmount
          )} en la terminal del restaurante. Al aceptar, la cuenta quedara pagada y cerrada.`
        : `Confirma que recibiste ${formatCurrency(
            totalAmount
          )} en efectivo de ${customerName}. Al aceptar, la cuenta quedara pagada y cerrada.`;

      if (!window.confirm([promotionPaymentNotice(mesa.pricing, formatCurrency), confirmationMessage].filter(Boolean).join("\n\n"))) {
        return false;
      }

      setLocalPaymentCloseAction({
        mesaId,
        method: localPaymentMethod,
      });

      const now = new Date().toISOString();
      const paymentOperationId =
        String(paymentState.paymentOperation?.id || mesa?.paymentOperationId || "").trim() ||
        `inicio-payment-${mesa.id}`;
      const source = String(mesa?.source || "manual").trim().toLowerCase();
      const branchPaymentContext = currentBranch || currentRestaurant || {};
      const commissionChannel =
        source === "kiosk"
          ? "kiosk"
          : source === "app" || source === "qr"
            ? "menu_qr"
            : "manual";
      const paymentRail = buildPaymentRailSnapshot({
        source: "inicio",
        checkoutMethod: localPaymentMethod,
        paymentProvider: isCardPayment ? "external_card_terminal" : "cash",
        splitMode: "manual_debt",
        isKioskExperience: false,
      });
      const paymentOperation = {
        ...(paymentState.paymentOperation || {}),
        id: paymentOperationId,
        orderId: mesa.id,
        tableId: mesa.tableId || "",
        mesa: mesa.mesa || "",
        customerName: getMesaCustomerName(mesa) || "",
        canal: mesa.canal || "Salon",
        source: "inicio",
        requestedMethod: localPaymentMethod,
        selectedMethod: localPaymentMethod,
        paymentMethod: localPaymentMethod,
        paymentRail: paymentRail.rail,
        paymentRailProvider: paymentRail.provider,
        paymentRailLabel: isCardPayment ? "Cobro con tarjeta" : paymentRail.label,
        chargePresentation: isCardPayment
          ? "external_card_terminal"
          : paymentRail.chargePresentation,
        commissionCollectionMode: paymentRail.commissionCollectionMode,
        splitExecutionMode: paymentRail.splitExecutionMode,
        interoperabilityObjective: paymentRail.interoperabilityObjective,
        paymentSplitMode: paymentRail.splitMode,
        commissionRate: resolveBranchCommissionRateByChannel(
          branchPaymentContext,
          commissionChannel
        ),
        status: "paid",
        grossAmount: totalAmount,
        amount: totalAmount,
        pricing: mesa.pricing || { finalTotal: totalAmount },
        itemsSummary: getMesaOrderSummary(mesa),
        orderedAt: mesa.orderedAt || now,
        paidAt: now,
        closedAt: null,
        updatedAt: now,
        history: [
          createPaymentHistoryEntry(
            isCardPayment
              ? "Cobro con tarjeta externa confirmado desde Inicio."
              : "Cobro en efectivo confirmado desde Inicio."
          ),
          ...(paymentState.paymentOperation?.history || []),
        ].slice(0, 12),
      };
      const orderPaymentPatch = {
        paymentStatus: "paid",
        paymentMethod: localPaymentMethod,
        selectedPaymentMethod: localPaymentMethod,
        requestedPaymentMethod: localPaymentMethod,
        cashPaymentPending: false,
        paymentOperationId,
        paidAt: now,
        paymentClaimSource: isCardPayment ? "inicio_card" : "inicio_cash",
        pricing: mesa.pricing,
        totalAmount,
        paymentClaimedBy: user?.id || "",
        updatedAt: now,
        ...(isCardPayment
          ? {
              cardApprovedAt: now,
              cardRecordedBy: user?.id || "",
            }
          : {
              cashCollectedAt: now,
              cashCollectedBy: user?.id || "",
            }),
      };
      const mzResult = await registerManualOperationalMzUsage({
        orderId: mesa.id,
        paymentOperationId,
        sourceChannel:
          source === "kiosk"
            ? isCardPayment
              ? "kiosk_card"
              : "kiosk_counter"
            : source === "app" || source === "qr"
              ? isCardPayment
                ? "menu_qr_card"
                : "menu_qr_counter"
              : isCardPayment
                ? "manual_card"
                : "manual_counter",
        sourceMode: localPaymentMethod,
        metadata: {
          mesa: mesa.mesa || "",
          customerName: getMesaCustomerName(mesa) || "",
          flow: "collect_local_payment_and_close",
          grossAmount: totalAmount,
        },
      }).catch((error) => {
        console.error(
          "No pudimos validar el saldo operativo MZ al cobrar y cerrar el pedido:",
          error
        );
        setServiceAlert({
          id: `local-payment-mz-${Date.now()}`,
          title: "No pudimos validar el saldo operativo",
          text:
            error?.message ||
            "Intenta nuevamente o recarga la cuenta antes de registrar el cobro.",
        });
        return { skipped: true, reason: "runtime_error" };
      });

      if (mzResult?.skipped && mzResult.reason === "insufficient_balance") {
        setServiceAlert({
          id: `local-payment-mz-insufficient-${Date.now()}`,
          title: "Saldo operativo agotado",
          text: `La cuenta ya consumio su cupo manual. Necesitas recargar MZ o cobrar por deeplink para restaurar el piso operativo de ${MANUAL_OPERATIONAL_MZ_RESET_UNITS} MZ.`,
        });
        return false;
      }

      if (mzResult?.skipped && mzResult.reason === "runtime_error") {
        return false;
      }

      let settledOperation = paymentOperation;

      try {
        const paymentClaim = await claimSharedOperationalOrderPayment({
          restaurant: operationalRestaurant,
          orderId: mesa.id,
          paymentOperation,
          orderPatch: orderPaymentPatch,
          fallbackFactory: createEmptyOperationalState,
        });

        if (paymentClaim.supported) {
          applySharedPaymentClaimPayload(paymentClaim.payload);

          if (!paymentClaim.claimed) {
            setServiceAlert({
              id: `local-payment-claimed-${Date.now()}`,
              title:
                paymentClaim.reason === "payment_in_progress"
                  ? "Hay un cobro digital activo"
                  : "La cuenta ya fue cobrada",
              text:
                paymentClaim.reason === "payment_in_progress"
                  ? "Espera su confirmacion o cancelalo antes de registrar otro medio de pago."
                  : "El cobro fue confirmado desde Caja o por un mozo. No se registrara una segunda vez.",
            });
            return false;
          }

          settledOperation = {
            ...paymentOperation,
            ...(paymentClaim.paymentOperation || {}),
          };
        } else {
          savePaymentOperations(operationalRestaurant, [
            paymentOperation,
            ...currentPayments.filter((operation) => operation?.id !== paymentOperationId),
          ]);
        }
      } catch (error) {
        setServiceAlert({
          id: `local-payment-sync-${Date.now()}`,
          title: "No pudimos confirmar el cobro",
          text:
            error?.message ||
            "La base no confirmo el cobro. Revisa el estado antes de volver a intentarlo.",
        });
        return false;
      }

      await syncManualCommissionDebtSnapshot(
        settledOperation,
        isCardPayment
          ? "Cobro con tarjeta externa confirmado desde Inicio."
          : "Cobro en efectivo confirmado desde Inicio."
      );

      const closedAt = new Date().toISOString();
      const closedPaymentOperation = {
        ...settledOperation,
        status: "closed",
        paidAt: settledOperation.paidAt || now,
        closedAt,
        updatedAt: closedAt,
        history: [
          createPaymentHistoryEntry("Operacion cerrada junto con el cobro desde Inicio."),
          ...(settledOperation.history || []),
        ].slice(0, 12),
      };
      const paidMesaSnapshot = {
        ...mesa,
        ...orderPaymentPatch,
      };
      const historyEntry = {
        ...paidMesaSnapshot,
        id: `cerrado-${mesa.id}-${Date.now()}`,
        originalOrderId: mesa.id,
        pedido: getMesaOrderSummary(mesa),
        estado: "Cerrado",
        phase: "closed",
        paymentStatus: "closed",
        paidAt: orderPaymentPatch.paidAt,
        closedAt,
        updatedAt: closedAt,
        requestedAt: mesa.orderedAt,
      };
      let sharedCloseResult = null;

      if (
        operationalRestaurant?.id &&
        !isCreatorMode &&
        isSupabaseConfigured
      ) {
        try {
          sharedCloseResult = await patchSharedOperationalOrder({
            restaurant: operationalRestaurant,
            orderId: mesa.id,
            patch: historyEntry,
            moveToHistory: true,
            fallbackFactory: createEmptyOperationalState,
          });
        } catch (error) {
          setServiceAlert({
            id: `local-payment-close-sync-${Date.now()}`,
            title: "Cobro confirmado; falta cerrar el pedido",
            text:
              error?.message ||
              "El pago ya quedo registrado. Actualiza la pantalla y usa Cerrar pedido para completar el cierre.",
          });
          return false;
        }
      }

      rememberMesaMutationOverride(mesa.id, {
        phase: "closed",
        paymentStatus: "closed",
      });

      if (sharedCloseResult?.supported) {
        applySharedOrderMutationPayload(sharedCloseResult.payload);
      } else {
        const localState = getOperationalOrdersState(
          operationalRestaurant,
          createEmptyOperationalState
        );
        const nextState = {
          ...localState,
          mesasActivas: (localState.mesasActivas || []).filter(
            (item) => item.id !== mesa.id
          ),
          pedidosHistoricos: [
            historyEntry,
            ...(localState.pedidosHistoricos || []).filter(
              (item) => item.id !== historyEntry.id && item.originalOrderId !== mesa.id
            ),
          ].slice(0, 120),
        };

        beginOperationalRemoteSyncHold();
        syncingOperationalStateRef.current = true;
        saveOperationalOrdersState(
          operationalRestaurant,
          nextState,
          createEmptyOperationalState
        );
        setMesasActivas(nextState.mesasActivas);
        setPedidosHistoricos(nextState.pedidosHistoricos);

        if (operationalRestaurant?.id && !isCreatorMode && isSupabaseConfigured) {
          void pushSharedOperationalState(
            operationalRestaurant,
            nextState,
            createEmptyOperationalState
          ).catch((error) => {
            console.error(
              "El pedido se cerro localmente, pero no pudimos sincronizarlo:",
              error
            );
          });
        }
      }

      const savedClosedPayments = savePaymentOperations(operationalRestaurant, [
        closedPaymentOperation,
        ...getPaymentOperations(operationalRestaurant).filter(
          (operation) => operation?.id !== closedPaymentOperation.id
        ),
      ]);

      if (operationalRestaurant?.id && !isCreatorMode && isSupabaseConfigured) {
        try {
          await pushSharedPaymentOperationsState(
            operationalRestaurant,
            savedClosedPayments
          );
        } catch (error) {
          console.warn(
            "El pedido se cerro, pero el estado final del cobro quedo pendiente de sincronizacion:",
            error
          );
        }
      }

      if (detalleMesaId === mesa.id) {
        setDetalleMesaId("");
      }

      if (notificationDetailMesaId === mesa.id) {
        setNotificationDetailMesaId("");
      }

      setServiceAlert({
        id: `local-payment-closed-${Date.now()}`,
        title: isCardPayment
          ? "Tarjeta registrada y pedido cerrado"
          : "Efectivo recibido y pedido cerrado",
        text: `${mesa.mesa}${
          getMesaCustomerName(mesa) ? ` · ${getMesaCustomerName(mesa)}` : ""
        } quedo cobrada por ${formatCurrency(totalAmount)}.`,
      });
      return true;
    } finally {
      setLocalPaymentCloseAction({ mesaId: "", method: "" });
      releaseMesaActionLock(mesaId, actionKey);
    }
  };

  const progressInitialOrder = (mesa) => {
    if (!mesa) {
      return;
    }

    if (isMesaCashPendingForKitchen(mesa)) {
      collectMesaCashAndSendToKitchen(mesa.id);
      return;
    }

    if (isMesaDigitalPaymentPendingForKitchen(mesa)) {
      setServiceAlert({
        id: `digital-payment-wait-${Date.now()}`,
        title: `Esperando pago en ${mesa.mesa}`,
        text: orderNeedsKitchen(mesa)
          ? "Este pedido del totem todavia no confirmo el pago QR. Quedara listo para cocina cuando la transaccion figure como pagada."
          : "Este pedido del totem todavia no confirmo el pago QR. Quedara listo para entregar cuando la transaccion figure como pagada.",
      });
      return;
    }

    if (!orderNeedsKitchen(mesa)) {
      requestDirectItemDelivery(mesa.id);
      return;
    }

    setMesaPhase(mesa.id, "preparation");
  };

  const getInitialOrderProgressLabel = (mesa) => {
    if (isMesaCashPendingForKitchen(mesa)) {
      return orderNeedsKitchen(mesa)
        ? "Cobrar en caja y pasar a preparacion"
        : "Cobrar en caja y dejar listo para entregar";
    }

    if (isMesaDigitalPaymentPendingForKitchen(mesa)) {
      return "Esperando pago QR";
    }

    if (!orderNeedsKitchen(mesa)) {
      return "Entregar";
    }

    return hasMixedKitchenAndDirectDelivery(mesa)
      ? "Pasar comida a preparacion"
      : "Pasar a preparacion";
  };

  const isInitialOrderProgressDisabled = (mesa) =>
    isMesaCashPendingForKitchen(mesa) ? !canChargeOrders : !canModifyOrders;

  const renderPendingPaymentCloseActions = (mesa, paymentState) => {
    const activeMethod =
      localPaymentCloseAction.mesaId === mesa.id
        ? localPaymentCloseAction.method
        : "";
    const isBusy = Boolean(activeMethod);
    const actionsDisabled = isBusy || !canChargeOrders || !canModifyOrders;

    if (paymentState?.hasPendingDigitalPayment) {
      return (
        <div style={styles.localPaymentClosePanel}>
          <span style={styles.paymentPendingCloseNotice}>
            Hay un cobro digital pendiente. Espera su confirmacion o cancelalo antes de
            cobrar por otro medio.
          </span>
        </div>
      );
    }

    return (
      <div style={styles.localPaymentClosePanel}>
        <span style={styles.paymentPendingCloseNotice}>
          Pago pendiente: confirma como pago el comensal para cerrar la cuenta.
        </span>
        <div style={styles.localPaymentCloseButtons}>
          <button
            type="button"
            style={{
              ...styles.localPaymentCloseButton,
              ...styles.cashPaymentCloseButton,
              ...(actionsDisabled ? styles.localPaymentCloseButtonDisabled : {}),
            }}
            onClick={() => collectAndCloseMesaWithLocalPayment(mesa.id, "counter")}
            disabled={actionsDisabled}
          >
            {activeMethod === "counter"
              ? "Registrando efectivo..."
              : "Cobrar y cerrar en efectivo"}
          </button>
          <button
            type="button"
            style={{
              ...styles.localPaymentCloseButton,
              ...styles.cardPaymentCloseButton,
              ...(actionsDisabled ? styles.localPaymentCloseButtonDisabled : {}),
            }}
            onClick={() => collectAndCloseMesaWithLocalPayment(mesa.id, "card")}
            disabled={actionsDisabled}
          >
            {activeMethod === "card"
              ? "Registrando tarjeta..."
              : "Registrar tarjeta y cerrar"}
          </button>
        </div>
        <span style={styles.localPaymentCloseHint}>
          Tarjeta: confirma solo despues de que la terminal del restaurante indique pago
          aprobado.
        </span>
      </div>
    );
  };

  const saveOrderBillDiscount = async (order, discount, settleZero = false) => {
    if (!canPerformAction("apply_order_discount")) throw new Error("Solo el administrador puede aplicar descuentos.");
    const paymentState = resolveOperationalOrderPaymentState(order, getPaymentOperations(operationalRestaurant), order.paymentOperationId);
    const reason = getOrderDiscountBlockReason(order, paymentState);
    if (reason) throw new Error(reason);
    const requestId = window.crypto.randomUUID();
    if (isCreatorMode) {
      const pricing = settleZero ? order.pricing : { ...previewOrderDiscount(order, discount), discountRevision: requestId };
      const nextOrder = { ...order, pricing, totalAmount: pricing.finalTotal, updatedAt: new Date().toISOString(),
        ...(settleZero ? { paymentStatus: "paid", paymentMethod: "bonified", paidAt: new Date().toISOString() } : {}) };
      const state = getOperationalOrdersState(operationalRestaurant, createEmptyOperationalState);
      applySharedOrderMutationPayload({ ...state, mesasActivas: state.mesasActivas.map((item) => item.id === order.id ? nextOrder : item) });
      return;
    }
    const result = await setSharedOrderDiscount({ restaurant: operationalRestaurant, order, discount, settleZero, requestId });
    applySharedPaymentClaimPayload(result.payload);
  };

  const printOrderBill = async (order, paperWidth) => {
    const currentOrder = isCreatorMode ? order : await getSharedOrderBill(operationalRestaurant, order.id);
    return printCustomerBill({
      order: currentOrder, paperWidth,
      restaurantName: perfil?.nombre || currentCompany?.name || operationalRestaurant.name,
      branchName: operationalRestaurant.name,
    });
  };

  const renderMesaDetailCard = (mesa, options = {}) => {
    const { compact = false } = options;
    const requiresKitchen = orderNeedsKitchen(mesa);
    const kitchenWorkflow = getKitchenDispatchWorkflowState(mesa);
    const kitchenProgress = getKitchenProgressDetail(mesa);
    const canMoveToDelivery = canRequestReadyDelivery(mesa);
    const kitchenItems = getMesaKitchenItems(mesa);
    const directDeliveryItems = getMesaDirectDeliveryItems(mesa);
    const pendingDirectDeliveryItems = getMesaPendingDirectDeliveryItems(mesa);
    const unassignedDirectDeliveryItems = pendingDirectDeliveryItems.filter(
      (item) => !["assigned", "ready"].includes(String(item.deliveryStatus || ""))
    );
    const paymentState = resolveOperationalOrderPaymentState(
      mesa,
      getPaymentOperations(operationalRestaurant),
      mesa?.paymentOperationId
    );
    const isPaymentVerified = paymentState.isPaid;
    const isManualDelivery = mesa.deliveryMode === "manual";
    const waiterAssignment = mesa.tableId
      ? getWaiterAssignmentForTable(operationalRestaurant, mesa.tableId)
      : null;
    const assignedWaiterName =
      isManualDelivery
        ? ""
        : mesa.assignedWaiterName || waiterAssignment?.waiterName || "";

    return (
      <article
        key={mesa.id}
        style={{
          ...styles.detailCard,
          ...(compact ? styles.mesaDetailCompactCard : {}),
        }}
      >
        <div style={styles.detailCardHeader}>
          <div>
            <strong style={styles.detailTitle}>{mesa.mesa}</strong>
            <div style={styles.detailMeta}>
              {getMesaOperationalLabel(mesa)}
            </div>
          </div>
          <span
            style={{
              ...styles.listBadge,
              ...(getStatusBadgeStyle(getMesaStatus(mesa)) || {}),
            }}
          >
            {getMesaStatus(mesa)}
          </span>
        </div>

        <div style={styles.progressRow}>
          <span style={styles.detailLabel}>Entrega registrada</span>
          <strong style={styles.progressValue}>
            {getDeliveredCount(mesa)}/{mesa.items.length} items
          </strong>
        </div>

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>Pedido</span>
          {kitchenItems.length ? (
            <div style={styles.itemWorkflowGroup}>
              <div style={styles.itemWorkflowHeader}>
                <strong>Preparacion en cocina</strong>
                <span>{kitchenItems.length} item(s)</span>
              </div>
              <div style={styles.itemList}>
                {kitchenItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    style={{
                      ...styles.itemToggle,
                      ...(item.delivered
                        ? styles.itemToggleDelivered
                        : styles.itemTogglePending),
                    }}
                    onClick={() => toggleItemDelivered(mesa.id, item.id)}
                    disabled={!canModifyOrders || (!item.delivered && mesa.phase !== "ready")}
                  >
                    <span>{item.nombre}</span>
                    <strong>
                      {item.delivered
                        ? "Entregado"
                        : mesa.phase === "ready"
                          ? "Confirmar entrega"
                          : "En circuito de cocina"}
                    </strong>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {directDeliveryItems.length ? (
            <div style={styles.itemWorkflowGroup}>
              <div style={styles.itemWorkflowHeader}>
                <strong>Entrega directa</strong>
                <span>No espera a cocina</span>
              </div>
              <div style={styles.itemList}>
                {directDeliveryItems.map((item) => {
                  const deliveryStatus = String(item.deliveryStatus || "pending");
                  const itemWaiterName = item.assignedWaiterName || assignedWaiterName;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      style={{
                        ...styles.itemToggle,
                        ...(item.delivered
                          ? styles.itemToggleDelivered
                          : styles.itemTogglePending),
                      }}
                      onClick={() => toggleItemDelivered(mesa.id, item.id)}
                      disabled={!canModifyOrders}
                    >
                      <span>{item.nombre}</span>
                      <strong>
                        {item.delivered
                          ? "Entregado"
                          : deliveryStatus === "assigned"
                            ? `Confirmar entrega${itemWaiterName ? ` - ${itemWaiterName}` : ""}`
                            : deliveryStatus === "ready"
                              ? "Confirmar entrega manual"
                              : "Entregar"}
                      </strong>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>Total final</span>
          <div style={styles.detailText}>
            {typeof mesa.totalAmount === "number" && mesa.totalAmount >= 0
              ? formatCurrency(mesa.totalAmount)
              : "Todavia no hay un total registrado para este pedido."}
          </div>
        </div>

        {mesa.pricing ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Desglose comercial</span>
            <div style={styles.pricingBreakdown}>
              <div style={styles.pricingBreakdownRow}>
                <span>Precio de lista</span>
                <strong>{formatCurrency(mesa.pricing.listTotal)}</strong>
              </div>
              <div style={styles.pricingBreakdownRow}>
                <span>Descuento aplicado</span>
                <strong>
                  {mesa.pricing.totalDiscount > 0
                    ? `- ${formatCurrency(mesa.pricing.totalDiscount)}`
                    : "Sin descuento"}
                </strong>
              </div>
              {Array.isArray(mesa.pricing.adjustments) && mesa.pricing.adjustments.length > 0 ? (
                <div style={styles.pricingAdjustmentList}>
                  {mesa.pricing.adjustments.map((adjustment) => (
                    <div
                      key={`${mesa.id}-${adjustment.id}`}
                      style={styles.pricingAdjustmentItem}
                    >
                      <span>{adjustment.title || adjustment.summary || "Ajuste comercial"}</span>
                      <strong>{`- ${formatCurrency(adjustment.amount)}`}</strong>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <OrderBillActions
          order={mesa}
          canDiscount={canPerformAction("apply_order_discount")}
          blockReason={getOrderDiscountBlockReason(mesa, paymentState)}
          onSaveDiscount={(discount) => saveOrderBillDiscount(mesa, discount)}
          onSettleZero={() => saveOrderBillDiscount(mesa, { type: "none", value: 0 }, true)}
          onPrint={(paperWidth) => printOrderBill(mesa, paperWidth)}
        />

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>Falta entregar</span>
          <div style={styles.detailText}>{getMesaMissingItems(mesa)}</div>
        </div>

        {getMesaCustomerName(mesa) ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Cliente</span>
            <div style={styles.detailText}>{getMesaCustomerName(mesa)}</div>
          </div>
        ) : null}

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>Forma de pago</span>
          <div style={styles.detailText}>{getMesaPaymentMethodLabel(mesa)}</div>
          <div style={styles.detailMeta}>{getMesaPaymentStatusLabel(mesa)}</div>
        </div>

        {getMesaPreparationNumber(mesa) ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Numero de preparacion</span>
            <div style={styles.detailText}>#{getMesaPreparationNumber(mesa)}</div>
          </div>
        ) : null}

        {isManualDelivery ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Tipo de entrega</span>
            <div style={styles.detailText}>Manual desde salon</div>
          </div>
        ) : assignedWaiterName ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Mozo asignado</span>
            <div style={styles.detailText}>{assignedWaiterName}</div>
          </div>
        ) : null}

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>
            {requiresKitchen ? "Operacion de cocina" : "Preparacion"}
          </span>
          <div style={styles.detailText}>{getKitchenDispatchStatusLabelSafe(mesa)}</div>
        </div>

        <div style={styles.detailBlock}>
          <span style={styles.detailLabel}>
            {requiresKitchen ? "Estado en cocina" : "Estado de salida"}
          </span>
          <div style={styles.detailText}>{kitchenProgress.label}</div>
          <div style={styles.detailMeta}>{kitchenProgress.detail}</div>
        </div>

        {isMesaDigitalPaymentPendingForKitchen(mesa) ? (
          <div style={styles.detailBlock}>
            <span style={styles.detailLabel}>Cobro digital</span>
            <div style={styles.detailText}>
              El pedido espera confirmacion del pago QR antes de pasar a preparacion.
            </div>
          </div>
        ) : null}

        <div style={styles.actionRow}>
          {mesa.phase === "new" && mesa.items.every((item) => !item.delivered) && (
            <>
              <button
                type="button"
                style={styles.primaryInlineButton}
                onClick={() => progressInitialOrder(mesa)}
                disabled={isInitialOrderProgressDisabled(mesa)}
              >
                {getInitialOrderProgressLabel(mesa)}
              </button>
              <button
                type="button"
                style={styles.dangerInlineButton}
                onClick={() => requestCancelOrder(mesa.id)}
                disabled={!canCancelOrders}
              >
                Cancelar pedido
              </button>
            </>
          )}
          {mesa.items.some((item) => !item.delivered) ? (
            <>
              {requiresKitchen && unassignedDirectDeliveryItems.length > 0 ? (
                <button
                  type="button"
                  style={styles.directDeliveryActionButton}
                  onClick={() =>
                    requestDirectItemDelivery(
                      mesa.id,
                      unassignedDirectDeliveryItems.map((item) => item.id)
                    )
                  }
                  disabled={!canModifyOrders}
                >
                  Entregar bebidas
                </button>
              ) : null}
              {mesa.phase === "preparation" &&
              mesa.kitchenModeSnapshot === "manual" &&
              kitchenWorkflow.step === "manual_pending" ? (
                <button
                  type="button"
                  style={styles.manualKitchenActionButton}
                  onClick={() => markManualKitchenNotice(mesa.id)}
                  disabled={!canModifyOrders}
                >
                  Avisar a cocina
                </button>
              ) : null}
              {requiresKitchen &&
              mesa.phase === "preparation" &&
              mesa.kitchenModeSnapshot === "printer" ? (
                <button
                  type="button"
                  style={styles.printerKitchenActionButton}
                  onClick={() => printKitchenOrderTicket(mesa.id)}
                  disabled={!canModifyOrders}
                >
                  {kitchenWorkflow.step === "printer_printed"
                    ? "Reimprimir comanda"
                    : "Imprimir comanda"}
                </button>
              ) : null}
              {canMoveToDelivery ? (
                <button
                  type="button"
                  style={styles.secondaryInlineButton}
                  onClick={() => requestOrderReadyDelivery(mesa.id)}
                  disabled={!canModifyOrders}
                >
                  {mesa.phase === "ready"
                    ? isManualDelivery
                      ? "Asignar mozo"
                      : "Reavisar entrega"
                    : kitchenWorkflow.step === "manual_confirmed" ||
                        kitchenWorkflow.step === "screen_confirmed"
                      ? "Marcar listo"
                      : kitchenWorkflow.step === "printer_printed"
                        ? "Comanda lista: asignar mozo"
                      : "Listo para entregar"}
                </button>
              ) : null}
              {mesa.phase === "ready" ? (
                <button
                  type="button"
                  style={styles.secondaryInlineButton}
                  onClick={() => deliverAllItems(mesa.id)}
                  disabled={!canModifyOrders}
                >
                  {isManualDelivery
                    ? "Confirmar entrega manual"
                    : "Marcar entrega completa"}
                </button>
              ) : null}
              {mesa.phase === "ready" ? (
                <span style={styles.readyBadge}>
                  {isManualDelivery
                    ? "Esperando entrega manual"
                    : assignedWaiterName
                    ? `Esperando entrega por ${assignedWaiterName}`
                    : "Esperando asignacion de mozo"}
                </span>
              ) : null}
            </>
          ) : isPaymentVerified ? (
            <button
              type="button"
              style={styles.closeOrderButton}
              onClick={() => requestCloseOrder(mesa.id)}
              disabled={!canModifyOrders}
            >
              Cerrar pedido
            </button>
          ) : (
            renderPendingPaymentCloseActions(mesa, paymentState)
          )}
        </div>
      </article>
    );
  };

  const setMesaPhase = async (mesaId, nextPhase, options = {}) => {
    const {
      orderPatch = {},
      permissionGranted = false,
    } = options;

    if (
      !permissionGranted &&
      !ensureInicioPermission(canModifyOrders, "mover pedidos entre estados")
    ) {
      return false;
    }

    const actionKey = `phase:${nextPhase}`;
    if (!acquireMesaActionLock(mesaId, actionKey, 15000)) {
      return false;
    }

    const mesaActual = mesasActivas.find((item) => item.id === mesaId);

    if (!mesaActual) {
      releaseMesaActionLock(mesaId, actionKey);
      return false;
    }

    if (mesaActual.phase === nextPhase) {
      releaseMesaActionLock(mesaId, actionKey);
      return false;
    }

    if (nextPhase === "preparation" && mesaActual.phase !== "new") {
      releaseMesaActionLock(mesaId, actionKey);
      return false;
    }

    const stockWasAlreadyDiscounted = mesaActual.stockDiscounted === true;

    if (nextPhase === "preparation") {
      const stockValidation = ensureMesaStockDiscounted(mesaId);
      if (!stockValidation.ok) {
        releaseMesaActionLock(mesaId, actionKey);
        notifyInsufficientMesaStock(mesaActual, stockValidation);
        return false;
      }
    }

    const isExternalOrder = Boolean(mesaActual.externalOrderId);
    let orderAtDispatch = mesaActual;
    let kitchenModeAtDispatch =
      nextPhase === "preparation"
        ? resolveActiveKitchenMode(mesaActual)
        : mesaActual.kitchenModeSnapshot || "manual";

    if (nextPhase === "preparation" && isExternalOrder) {
      const dispatchContext = await resolveExternalKitchenDispatchContext({
        branch: currentBranch || operationalRestaurant,
        order: mesaActual,
      });
      kitchenModeAtDispatch = dispatchContext.kitchenMode;
      orderAtDispatch = {
        ...mesaActual,
        items: enrichExternalOperationalItemsWithMenu(
          mesaActual.items,
          dispatchContext.menuSnapshot
        ),
      };
    }

    const requiresKitchen = orderNeedsKitchen(orderAtDispatch);
    const resolvedNextPhase =
      nextPhase === "preparation" && !requiresKitchen ? "ready" : nextPhase;
    const transitionTimestamp = new Date().toISOString();
    const nextKitchenDispatchStatus =
      nextPhase === "preparation"
        ? requiresKitchen
          ? kitchenModeAtDispatch === "printer"
            ? "printer_pending"
            : kitchenModeAtDispatch === "screen"
              ? "screen_pending"
              : "manual_confirmed"
          : "not_required"
        : "";
    const nextOrderPatch = {
      ...(orderPatch && typeof orderPatch === "object" ? orderPatch : {}),
      phase: resolvedNextPhase,
      updatedAt: transitionTimestamp,
       ...(nextPhase === "preparation"
         ? {
            kitchenModeSnapshot: kitchenModeAtDispatch,
            kitchenDispatchStatus: nextKitchenDispatchStatus,
            kitchenDispatchedAt: transitionTimestamp,
            kitchenDispatchedBy: user?.id || "",
            stockDiscounted: true,
            ...(isExternalOrder ? { items: orderAtDispatch.items } : {}),
          }
        : {}),
    };

    const shouldPatchSharedOrder = Boolean(
      operationalRestaurant?.id &&
        isSupabaseConfigured &&
        (!isCreatorMode || isExternalOrder)
    );
    const optimisticMesasActivas = mesasActivas.map((mesa) =>
      mesa.id === mesaId
        ? {
            ...mesa,
            ...nextOrderPatch,
          }
        : mesa
    );

    rememberMesaMutationOverride(mesaId, nextOrderPatch);
    beginOperationalRemoteSyncHold();
    if (shouldPatchSharedOrder) {
      syncingOperationalStateRef.current = true;
    }
    setMesasActivas(optimisticMesasActivas);

    let sharedMutationResult = null;

    if (shouldPatchSharedOrder) {
      try {
        sharedMutationResult = await patchSharedOperationalOrder({
          restaurant: operationalRestaurant,
          orderId: mesaId,
          patch: nextOrderPatch,
          fallbackFactory: createEmptyOperationalState,
        });
      } catch (error) {
        mesaMutationOverridesRef.current.delete(String(mesaId || "").trim());
        syncingOperationalStateRef.current = true;
        setMesasActivas((prev) =>
          prev.map((mesa) =>
            mesa.id === mesaId && mesa.updatedAt === transitionTimestamp
              ? mesaActual
              : mesa
          )
        );

        if (nextPhase === "preparation" && !stockWasAlreadyDiscounted) {
          restoreMesaStockIfNeeded({
            ...mesaActual,
            stockDiscounted: true,
          });
        }

        setServiceAlert({
          id: `order-phase-sync-${Date.now()}`,
          title: "No pudimos actualizar el pedido",
          text:
            error?.message ||
            "La base no confirmo el cambio. El pedido conserva su estado anterior.",
        });
        releaseMesaActionLock(mesaId, actionKey);
        return false;
      }
    }

    if (sharedMutationResult?.supported) {
      applySharedOrderMutationPayload(sharedMutationResult.payload);
    } else if (shouldPatchSharedOrder) {
      saveInicioStoredState(operationalRestaurant, {
        mesasActivas: optimisticMesasActivas,
        pedidosHistoricos,
        ...(sharedSalonTables.length ? { salonTables: sharedSalonTables } : {}),
        salonLayout,
        salonReservations,
        salonRooms,
        salonTableRooms,
        salonFixtures,
      });
    }

    if (nextPhase === "preparation" && isExternalOrder) {
      try {
        await transitionExternalOrderStatus({
          orderId: mesaActual.externalOrderId,
          nextStatus: "preparing",
          note: "Pedido enviado a Cocina desde Inicio.",
        });

        if (!requiresKitchen) {
          await transitionExternalOrderStatus({
            orderId: mesaActual.externalOrderId,
            nextStatus:
              String(mesaActual.canal || "").toLowerCase() === "delivery"
                ? "ready_for_delivery"
                : "ready_for_pickup",
            note: "Pedido sin productos que requieran preparación en Cocina.",
          });
        }
      } catch (error) {
        setServiceAlert({
          id: `external-order-tracking-${Date.now()}`,
          title: "El pedido llegó a Cocina",
          text:
            error?.message ||
            "No pudimos actualizar el seguimiento del cliente. Reintenta desde Pedidos.",
        });
      }
    }

    if (
      resolvedNextPhase === "preparation" &&
      kitchenModeAtDispatch === "printer" &&
      requiresKitchen
    ) {
      autoKitchenPrintKnownIdsRef.current.add(mesaId);
      void printKitchenOrderTicket(mesaId, {
        orderOverride: {
          ...mesaActual,
          ...nextOrderPatch,
        },
      });
    }

    scheduleMesaActionLockRelease(mesaId, actionKey);
    return true;
  };

  const markManualKitchenNotice = (mesaId) => {
    if (!ensureInicioPermission(canModifyOrders, "avisar pedidos a cocina")) {
      return;
    }

    const actionKey = "manual-kitchen";
    if (!acquireMesaActionLock(mesaId, actionKey)) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const stockValidation = ensureMesaStockDiscounted(mesaId);
    if (!stockValidation.ok) {
      releaseMesaActionLock(mesaId, actionKey);
      notifyInsufficientMesaStock(mesa, stockValidation);
      return;
    }

    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((item) =>
        item.id === mesaId
          ? {
              ...item,
              phase: "preparation",
              kitchenDispatchStatus: "manual_confirmed",
              kitchenDispatchedAt: item.kitchenDispatchedAt || new Date().toISOString(),
              kitchenDispatchedBy: item.kitchenDispatchedBy || user?.id || "",
              stockDiscounted: true,
            }
          : item
      )
    );

    setServiceAlert({
      id: `manual-kitchen-${Date.now()}`,
      title: `Cocina avisada en ${mesa.mesa}`,
      text: "El pedido quedo marcado como avisado para seguimiento manual.",
    });

    scheduleMesaActionLockRelease(mesaId, actionKey);
  };

  const toggleItemDelivered = (mesaId, itemId) => {
    if (!ensureInicioPermission(canModifyOrders, "marcar items entregados")) {
      return;
    }

    const actionKey = `deliver-item:${itemId}`;
    if (!acquireMesaActionLock(mesaId, actionKey, 900)) {
      return;
    }

    const mesaActual = mesasActivas.find((item) => item.id === mesaId);

    if (!mesaActual) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const targetItem = (mesaActual.items || []).find((item) => item.id === itemId);

    if (!targetItem) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const isDirectDeliveryItem = getMesaDirectDeliveryItems(mesaActual).some(
      (item) => item.id === itemId
    );

    if (
      isDirectDeliveryItem &&
      !targetItem.delivered &&
      !["assigned", "ready"].includes(String(targetItem.deliveryStatus || ""))
    ) {
      releaseMesaActionLock(mesaId, actionKey);
      requestDirectItemDelivery(mesaId, [itemId]);
      return;
    }

    if (!isDirectDeliveryItem && mesaActual.phase !== "ready") {
      setServiceAlert({
        id: `kitchen-item-not-ready-${Date.now()}`,
        title: "La comida todavia no esta lista",
        text: "Primero cocina debe marcar el pedido como listo para entregar.",
      });
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if (!isDirectDeliveryItem && !ensureWaiterDeliveryAssignment(mesaActual)) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const stockValidation = ensureMesaStockDiscounted(mesaId);

    if (!stockValidation.ok) {
      releaseMesaActionLock(mesaId, actionKey);
      notifyInsufficientMesaStock(mesaActual, stockValidation);
      return;
    }

    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((mesa) => {
        if (mesa.id !== mesaId) {
          return mesa;
        }

        const currentItem = (mesa.items || []).find((item) => item.id === itemId);
        const nextDelivered = !currentItem?.delivered;

        return {
          ...mesa,
          phase: isDirectDeliveryItem
            ? mesa.phase
            : mesa.phase === "ready"
              ? "ready"
              : "preparation",
          kitchenDispatchStatus:
            isDirectDeliveryItem
              ? mesa.kitchenDispatchStatus
              : mesa.kitchenDispatchStatus ||
                (mesa.kitchenModeSnapshot === "printer"
                  ? "printer_pending"
                  : mesa.kitchenModeSnapshot === "screen"
                    ? "screen_pending"
                    : "manual_confirmed"),
          kitchenDispatchedAt: isDirectDeliveryItem
            ? mesa.kitchenDispatchedAt
            : mesa.kitchenDispatchedAt || new Date().toISOString(),
          kitchenDispatchedBy: isDirectDeliveryItem
            ? mesa.kitchenDispatchedBy
            : mesa.kitchenDispatchedBy || user?.id || "",
          stockDiscounted: true,
          items: mesa.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  delivered: nextDelivered,
                  deliveryStatus: nextDelivered
                    ? "delivered"
                    : item.assignedWaiterUserId
                      ? "assigned"
                      : "ready",
                  deliveredAt: nextDelivered ? new Date().toISOString() : "",
                  deliveredBy: nextDelivered ? user?.id || "" : "",
                }
              : item
          ),
        };
      })
    );

    scheduleMesaActionLockRelease(mesaId, actionKey, 1200);
  };

  const deliverAllItems = (mesaId) => {
    if (!ensureInicioPermission(canModifyOrders, "registrar entregas completas")) {
      return;
    }

    const actionKey = "deliver-all";
    if (!acquireMesaActionLock(mesaId, actionKey, 1000)) {
      return;
    }

    const mesaActual = mesasActivas.find((item) => item.id === mesaId);

    if (!mesaActual) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if (orderNeedsKitchen(mesaActual) && mesaActual.phase !== "ready") {
      setServiceAlert({
        id: `deliver-all-not-ready-${Date.now()}`,
        title: "El pedido todavia no esta listo",
        text: "Puedes entregar las bebidas por separado, pero la comida debe quedar lista en cocina antes de completar toda la entrega.",
      });
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if (!ensureWaiterDeliveryAssignment(mesaActual)) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const stockValidation = ensureMesaStockDiscounted(mesaId);

    if (!stockValidation.ok) {
      releaseMesaActionLock(mesaId, actionKey);
      notifyInsufficientMesaStock(mesaActual, stockValidation);
      return;
    }

    beginOperationalRemoteSyncHold();
    setMesasActivas((prev) =>
      prev.map((mesa) => {
        if (mesa.id !== mesaId) {
          return mesa;
        }

        return {
          ...mesa,
          phase: mesa.phase === "ready" ? "ready" : "preparation",
          kitchenDispatchStatus:
            mesa.kitchenDispatchStatus ||
            (mesa.kitchenModeSnapshot === "printer"
              ? "printer_pending"
              : mesa.kitchenModeSnapshot === "screen"
                ? "screen_pending"
                : "manual_confirmed"),
          kitchenDispatchedAt: mesa.kitchenDispatchedAt || new Date().toISOString(),
          kitchenDispatchedBy: mesa.kitchenDispatchedBy || user?.id || "",
          stockDiscounted: true,
          items: mesa.items.map((item) => ({
            ...item,
            delivered: true,
            deliveryStatus: "delivered",
            deliveredAt: item.deliveredAt || new Date().toISOString(),
            deliveredBy: item.deliveredBy || user?.id || "",
          })),
        };
      })
    );

    scheduleMesaActionLockRelease(mesaId, actionKey, 1200);
  };

  const closeOrder = async (mesaId) => {
    if (!ensureInicioPermission(canModifyOrders, "cerrar pedidos")) {
      return;
    }

    const actionKey = "close";
    if (!acquireMesaActionLock(mesaId, actionKey, 1800)) {
      return;
    }

    const mesaToClose = mesasActivas.find((mesa) => mesa.id === mesaId);

    if (!mesaToClose) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if (mesaToClose.phase === "closed") {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if ((mesaToClose.items || []).some((item) => !item.delivered)) {
      setServiceAlert({
        id: `close-order-delivery-pending-${Date.now()}`,
        title: "Falta completar la entrega",
        text: "Antes de cerrar el pedido, confirma que todos los productos fueron entregados.",
      });
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    if (!ensureWaiterDeliveryAssignment(mesaToClose)) {
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const closePaymentState = resolveOperationalOrderPaymentState(
      mesaToClose,
      getPaymentOperations(operationalRestaurant),
      mesaToClose?.paymentOperationId
    );

    if (!closePaymentState.isPaid) {
      setServiceAlert({
        id: `close-order-payment-pending-${Date.now()}`,
        title: "Falta confirmar el cobro",
        text: "Antes de cerrar el pedido, confirma el pago desde Caja o desde el panel del mozo. Asi evitamos registrar el mismo cobro dos veces.",
      });
      releaseMesaActionLock(mesaId, actionKey);
      return;
    }

    const closedAt = new Date().toISOString();

    if (mesaToClose.paymentOperationId) {
      const operations = getPaymentOperations(operationalRestaurant);
      const targetOperation =
        closePaymentState.paymentOperation ||
        buildFallbackPaymentOperationFromMesa(mesaToClose);
      let settledOperation = null;

      if (targetOperation) {
        const method = String(
          targetOperation.selectedMethod || targetOperation.requestedMethod || ""
        )
          .trim()
          .toLowerCase();
        const paymentRail = buildPaymentRailSnapshot({
          source: targetOperation.source || mesaToClose.source || "",
          checkoutMethod: method,
          paymentProvider:
            targetOperation.paymentRailProvider ||
            currentBranch?.paymentProvider ||
            currentRestaurant?.paymentProvider ||
            "",
          splitMode:
            targetOperation.paymentSplitMode ||
            currentBranch?.paymentSplitMode ||
            currentRestaurant?.paymentSplitMode ||
            "",
          isKioskExperience:
            targetOperation.source === "kiosk" || mesaToClose.source === "kiosk",
        }).rail;
        const isDigitalOperation = isDigitalPaymentRail(
          targetOperation.paymentRail || paymentRail
        );
        const isAlreadyClosed = targetOperation.status === "closed";
        const isAlreadyConfirmed = isPaymentOperationConfirmed(targetOperation);
        const shouldAutoSettleLocal = !isDigitalOperation && !isAlreadyConfirmed;
        const shouldCloseDigital = isDigitalOperation && Boolean(targetOperation.paidAt);
        const shouldFinalizePayment =
          !isAlreadyClosed &&
          (shouldAutoSettleLocal || shouldCloseDigital || isAlreadyConfirmed);

        if (shouldFinalizePayment) {
          if (shouldAutoSettleLocal) {
            const mzResult = await registerManualOperationalMzUsage({
              orderId: mesaToClose.id,
              paymentOperationId: targetOperation.id,
              sourceChannel:
                mesaToClose.source === "kiosk"
                  ? "kiosk_counter"
                  : mesaToClose.source === "app" || mesaToClose.source === "qr"
                    ? "menu_qr_counter"
                    : "manual_counter",
              sourceMode: method || "counter",
              metadata: {
                mesa: mesaToClose.mesa || "",
                customerName: getMesaCustomerName(mesaToClose) || "",
                flow: "close_mesa_local_payment",
                grossAmount: Number(
                  targetOperation?.grossAmount ??
                    targetOperation?.amount ??
                    mesaToClose?.pricing?.finalTotal ??
                    mesaToClose?.totalAmount ??
                    0
                ),
              },
            }).catch((error) => {
              console.error(
                "No pudimos validar el saldo operativo MZ al cerrar el pedido:",
                error
              );
              setServiceAlert({
                id: `manual-mz-close-${Date.now()}`,
                title: "No pudimos validar el saldo operativo",
                text:
                  error?.message ||
                  "Intenta nuevamente o recarga la cuenta antes de cerrar el pedido.",
              });
              return { skipped: true, reason: "runtime_error" };
            });

            if (mzResult?.skipped && mzResult.reason === "insufficient_balance") {
              setServiceAlert({
                id: `manual-mz-close-insufficient-${Date.now()}`,
                title: "Saldo operativo agotado",
                text: `La cuenta ya consumio su cupo manual. Necesitas recargar MZ o cobrar por deeplink para restaurar el piso operativo de ${MANUAL_OPERATIONAL_MZ_RESET_UNITS} MZ.`,
              });
              return;
            }

            if (mzResult?.skipped && mzResult.reason === "runtime_error") {
              return;
            }
          }

          const nextHistory = [...(targetOperation.history || [])];

          if (shouldAutoSettleLocal && !targetOperation.paidAt) {
            nextHistory.unshift(
              createPaymentHistoryEntry(
                `Cobro local confirmado al cerrar el pedido (${method || "medio local"}).`
              )
            );
          }

          if (!isAlreadyClosed) {
            nextHistory.unshift(
              createPaymentHistoryEntry("Operacion cerrada junto con el pedido.")
            );
          }

          savePaymentOperations(
            operationalRestaurant,
            (operations.some((operation) => operation.id === targetOperation.id)
              ? operations
              : [targetOperation, ...operations]
            ).map((operation) => {
              if (operation.id !== targetOperation.id) {
                return operation;
              }

              settledOperation = {
                ...operation,
                status: "closed",
                paidAt:
                  shouldAutoSettleLocal && !operation.paidAt
                    ? closedAt
                    : operation.paidAt || closedAt,
                closedAt,
                history: nextHistory.slice(0, 12),
              };

              return settledOperation;
            })
          );

          if (settledOperation && shouldAutoSettleLocal) {
            await syncManualCommissionDebtSnapshot(
              settledOperation,
              "Operacion local cerrada junto con el pedido desde Inicio."
            );
          }
        }
      }
    }

    rememberMesaMutationOverride(mesaId, {
      phase: "closed",
      paymentStatus: "closed",
    });
    beginOperationalRemoteSyncHold();
    setPedidosHistoricos((historicos) => [
      {
        ...mesaToClose,
        id: `cerrado-${mesaToClose.id}-${Date.now()}`,
        originalOrderId: mesaToClose.id,
        mesa: mesaToClose.mesa,
        canal: mesaToClose.canal,
        pedido: getMesaOrderSummary(mesaToClose),
        estado: "Cerrado",
        phase: "closed",
        paymentStatus: "closed",
        paidAt:
          closePaymentState.paymentOperation?.paidAt || mesaToClose.paidAt || closedAt,
        closedAt,
        updatedAt: closedAt,
        requestedAt: mesaToClose.orderedAt,
      },
      ...historicos,
    ]);

    // Cerrar una cuenta no significa que los comensales dejaron la mesa.
    // Conservamos una sesion de salon hasta que se use "Cerrar mesa".
    if (
      mesaToClose.tableId &&
      mesaToClose.source !== "kiosk" &&
      !isManualOrderKioskMode &&
      !getWaiterAssignmentForTable(operationalRestaurant, mesaToClose.tableId)
    ) {
      assignWaiterToTable(operationalRestaurant, {
        tableId: mesaToClose.tableId,
        tableLabel: mesaToClose.mesa || "Mesa",
        branchId: currentBranch?.id || mesaToClose.branchId || "",
        waiterUserId: mesaToClose.assignedWaiterUserId || "",
        waiterName: mesaToClose.assignedWaiterName || "",
        assignedAt: mesaToClose.assignedAt || closedAt,
      });
    }

    setMesasActivas((prev) => prev.filter((mesa) => mesa.id !== mesaId));
    scheduleMesaActionLockRelease(mesaId, actionKey, 2000);
  };

  const cancelOrder = async (mesaId, { refundReason = "" } = {}) => {
    if (!ensureInicioPermission(canCancelOrders, "cancelar pedidos")) {
      return false;
    }

    const actionKey = "cancel";
    if (!acquireMesaActionLock(mesaId, actionKey, 15000)) {
      return false;
    }

    const mesaToCancel = mesasActivas.find((mesa) => mesa.id === mesaId);

    if (!mesaToCancel || mesaToCancel.phase !== "new") {
      releaseMesaActionLock(mesaId, actionKey);
      return false;
    }

    const paymentStatus = String(mesaToCancel.paymentStatus || "")
      .trim()
      .toLowerCase();
    const hasApprovedDigitalPayment =
      ["paid", "approved", "closed", "completed", "accredited"].includes(
        paymentStatus
      ) && Boolean(mesaToCancel.paymentIntentId);

    if (hasApprovedDigitalPayment) {
      if (!ensureInicioPermission(canRefundOrders, "reintegrar cobros")) {
        releaseMesaActionLock(mesaId, actionKey);
        return false;
      }

      try {
        await refundApprovedDigitalPayment({
          branchId: operationalRestaurant?.id || currentBranch?.id,
          intentId: mesaToCancel.paymentIntentId,
          orderId: mesaToCancel.id,
          reason: refundReason,
        });
      } catch (refundError) {
        setServiceAlert({
          id: `refund-order-sync-${Date.now()}`,
          title: "No pudimos reintegrar el pago",
          text:
            refundError?.message ||
            "El pedido sigue activo porque Mercado Pago no confirmo la devolucion.",
        });
        releaseMesaActionLock(mesaId, actionKey);
        return false;
      }
    }

    const cancelledAt = new Date().toISOString();
    const historyEntry = {
      ...mesaToCancel,
      id: `cancelado-${mesaToCancel.id}-${Date.now()}`,
      originalOrderId: mesaToCancel.id,
      mesa: mesaToCancel.mesa,
      canal: mesaToCancel.canal,
      pedido: getMesaOrderSummary(mesaToCancel),
      estado: "Cancelado",
      phase: "cancelled",
      paymentStatus: hasApprovedDigitalPayment
        ? "refunded"
        : mesaToCancel.paymentStatus,
      refundedAt: hasApprovedDigitalPayment ? cancelledAt : mesaToCancel.refundedAt,
      refundReason: hasApprovedDigitalPayment
        ? String(refundReason || "").trim()
        : mesaToCancel.refundReason,
      cancelledAt,
      updatedAt: cancelledAt,
      requestedAt: mesaToCancel.orderedAt,
    };
    let sharedMutationResult = null;

    if (
      operationalRestaurant?.id &&
      !isCreatorMode &&
      isSupabaseConfigured
    ) {
      try {
        sharedMutationResult = await patchSharedOperationalOrder({
          restaurant: operationalRestaurant,
          orderId: mesaId,
          patch: historyEntry,
          moveToHistory: true,
          fallbackFactory: createEmptyOperationalState,
        });
      } catch (error) {
        setServiceAlert({
          id: `cancel-order-sync-${Date.now()}`,
          title: "No pudimos cancelar el pedido",
          text:
            error?.message ||
            "La base no confirmo la cancelacion. El pedido sigue activo para evitar inconsistencias.",
        });
        releaseMesaActionLock(mesaId, actionKey);
        return false;
      }
    }

    restoreMesaStockIfNeeded(mesaToCancel);

    rememberMesaMutationOverride(mesaId, {
      phase: "cancelled",
    });

    if (sharedMutationResult?.supported) {
      applySharedOrderMutationPayload(sharedMutationResult.payload);
    } else {
      beginOperationalRemoteSyncHold();
      setPedidosHistoricos((historicos) => [historyEntry, ...historicos].slice(0, 120));
      setMesasActivas((prev) => prev.filter((mesa) => mesa.id !== mesaId));
    }

    if (detalleMesaId === mesaId) {
      setDetalleMesaId("");
    }

    if (notificationDetailMesaId === mesaId) {
      setNotificationDetailMesaId("");
    }

    scheduleMesaActionLockRelease(mesaId, actionKey, 1800);
    return true;
  };

  const requestCloseOrder = (mesaId) => {
    if (!ensureInicioPermission(canModifyOrders, "cerrar pedidos")) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa) {
      return;
    }

    if ((mesa.items || []).some((item) => !item.delivered)) {
      setServiceAlert({
        id: `close-order-delivery-pending-${Date.now()}`,
        title: "Falta completar la entrega",
        text: "El pedido se puede cerrar cuando todos sus productos figuren como entregados.",
      });
      return;
    }

    const paymentState = resolveOperationalOrderPaymentState(
      mesa,
      getPaymentOperations(operationalRestaurant),
      mesa?.paymentOperationId
    );

    if (!paymentState.isPaid) {
      setServiceAlert({
        id: `close-order-payment-pending-${Date.now()}`,
        title: "Pago pendiente",
        text: "Confirma el cobro desde Caja o desde el panel del mozo antes de cerrar el pedido.",
      });
      return;
    }

    const confirmed = window.confirm(
      `Confirma que ${mesa.mesa} ya recibio toda la entrega y deseas cerrar el pedido.`
    );

    if (!confirmed) {
      return;
    }

    closeOrder(mesaId);
  };

  const openManualOrderForSalonTable = (table, dinerName = "") => {
    if (!table || !ensureInicioPermission(canTakeOrders, "crear pedidos de salon", "manual")) {
      return;
    }

    const tableId = String(table.activeMesa?.tableId || table.id || "").trim();

    setManualOrderForm({
      ...initialManualOrderForm,
      destinationType: "table",
      tableId,
      comensales: "1",
      dinerName: String(dinerName || "").trim(),
      canal: "Salon",
    });
    setManualOrderItems([]);
    setManualOrderSelection({});
    setManualOrderPizzaSizeOptionId("");
    setManualOrderPizzaHalfProductId("");
    setManualOrderSearch("");
    setManualCustomerSearch("");
    setManualOrderMessage({ type: "", text: "" });
    setIsManualOrderExpanded(true);
    setSalonEditMode(false);
    setSalonReservationTableId("");
    setSalonReservationEditId("");
    setDetalleActivo(null);

    window.setTimeout(() => {
      document
        .getElementById("inicio-manual-order")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const openSalonReservationForm = (table, reservation = null) => {
    if (!table || !ensureInicioPermission(canTakeOrders, "reservar mesas", "manual")) {
      return;
    }

    const selectedReservation = reservation || {};
    setSalonSelectedTableId(table.id);
    setSalonReservationTableId(table.id);
    setSalonReservationEditId(selectedReservation.id || "");
    setSalonReservationForm({
      customerName: selectedReservation.customerName || "",
      partySize: String(selectedReservation.partySize || 2),
      reservedFor: selectedReservation.reservedFor || "",
      blockMinutesBefore: String(selectedReservation.blockMinutesBefore ?? 120),
      durationMinutes: String(selectedReservation.durationMinutes ?? 120),
      notes: selectedReservation.notes || "",
    });
  };

  const saveSalonReservation = () => {
    if (!ensureInicioPermission(canTakeOrders, "reservar mesas", "manual")) {
      return;
    }

    const table = salonTables.find((item) => item.id === salonReservationTableId);
    const customerName = String(salonReservationForm.customerName || "").trim();
    const reservedFor = String(salonReservationForm.reservedFor || "").trim();
    const reservedAt = new Date(reservedFor).getTime();

    if (!table || !customerName || !reservedFor || !Number.isFinite(reservedAt)) {
      setServiceAlert({
        id: `salon-reservation-${Date.now()}`,
        title: "Faltan datos de la reserva",
        text: "Completa el nombre, la fecha y la hora de la reserva.",
      });
      return;
    }

    const blockMinutesBefore = Math.max(
      0,
      Number(salonReservationForm.blockMinutesBefore || 120)
    );
    const durationMinutes = Math.max(
      30,
      Number(salonReservationForm.durationMinutes || 120)
    );
    const currentTableReservations = getCurrentSalonReservations(
      normalizeSalonReservations(salonReservations)[table.id] || [],
      Date.now() - 24 * 60 * 60 * 1000
    );
    const candidateWindow = getSalonReservationWindow({
      reservedFor,
      blockMinutesBefore,
      durationMinutes,
    });
    const overlappingReservation = currentTableReservations.find((reservation) => {
      if (reservation.id === salonReservationEditId) {
        return false;
      }

      const existingWindow = getSalonReservationWindow(reservation);
      return (
        candidateWindow.startsAt < existingWindow.endsAt &&
        candidateWindow.endsAt > existingWindow.startsAt
      );
    });

    if (overlappingReservation) {
      setServiceAlert({
        id: `salon-reservation-overlap-${Date.now()}`,
        title: "La mesa ya tiene una reserva cercana",
        text: `Se superpone con ${overlappingReservation.customerName} (${formatSalonReservationDateTime(
          overlappingReservation.reservedFor
        )}). Ajusta el horario o los tiempos de bloqueo.`,
      });
      return;
    }

    setSalonReservations((prev) => {
      const normalized = normalizeSalonReservations(prev);
      const previousReservations = normalized[table.id] || [];
      const existingReservation = previousReservations.find(
        (reservation) => reservation.id === salonReservationEditId
      );
      const nextReservation = {
        id: existingReservation?.id || `reservation-${table.id}-${Date.now()}`,
        tableId: table.id,
        tableLabel: table.label,
        customerName,
        partySize: Math.max(1, Number(salonReservationForm.partySize || 1)),
        reservedFor,
        blockMinutesBefore,
        durationMinutes,
        notes: String(salonReservationForm.notes || "").trim(),
        createdAt: existingReservation?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return normalizeSalonReservations({
        ...normalized,
        [table.id]: [
          ...previousReservations.filter(
            (reservation) => reservation.id !== nextReservation.id
          ),
          nextReservation,
        ],
      });
    });
    setSalonReservationTableId("");
    setSalonReservationEditId("");
    setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
  };

  const cancelSalonReservation = (table, reservation) => {
    if (!table || !reservation?.id || !ensureInicioPermission(canModifyOrders, "cancelar reservas")) {
      return;
    }

    if (
      !window.confirm(
        `Confirma que deseas cancelar la reserva de ${reservation.customerName} en ${table.label}.`
      )
    ) {
      return;
    }

    setSalonReservations((prev) => {
      const normalized = normalizeSalonReservations(prev);
      const remainingReservations = (normalized[table.id] || []).filter(
        (item) => item.id !== reservation.id
      );
      const next = { ...normalized };

      if (remainingReservations.length) {
        next[table.id] = remainingReservations;
      } else {
        delete next[table.id];
      }

      return next;
    });
    setSalonReservationTableId("");
    setSalonReservationEditId("");
    setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
  };

  const closeSalonTable = async (table) => {
    if (!table?.operational || !ensureInicioPermission(canModifyOrders, "cerrar mesas")) {
      return;
    }

    for (const mesa of table.activeMesas || []) {
      if ((mesa.items || []).some((item) => !item.delivered)) {
        setServiceAlert({
          id: `close-table-delivery-pending-${Date.now()}`,
          title: "La mesa todavia tiene entregas pendientes",
          text: `Completa la entrega de ${getMesaCustomerName(mesa) || "una cuenta"} antes de liberar ${table.label}.`,
        });
        return;
      }

      const paymentState = resolveOperationalOrderPaymentState(
        mesa,
        getPaymentOperations(operationalRestaurant),
        mesa.paymentOperationId
      );

      if (!paymentState.isPaid) {
        setServiceAlert({
          id: `close-table-payment-pending-${Date.now()}`,
          title: "La mesa todavia tiene pagos pendientes",
          text: `Confirma el cobro de ${getMesaCustomerName(mesa) || "una cuenta"} antes de liberar ${table.label}.`,
        });
        return;
      }
    }

    const openAccounts = table.activeMesas?.length || 0;
    const confirmationText = openAccounts
      ? `Se cerraran ${openAccounts} ${openAccounts === 1 ? "cuenta" : "cuentas"} y ${table.label} quedara libre. Deseas continuar?`
      : `${table.label} no tiene cuentas abiertas. Confirma que los comensales se retiraron y deseas liberarla.`;

    if (!window.confirm(confirmationText)) {
      return;
    }

    for (const mesa of table.activeMesas || []) {
      await closeOrder(mesa.id);
    }

    releaseWaiterTableAssignment(operationalRestaurant, table.id);
    setServiceAlert({
      id: `table-released-${Date.now()}`,
      title: `${table.label} liberada`,
      text: "La mesa ya esta disponible para recibir nuevos comensales.",
    });
  };

  const saveSalonStructure = ({
    rooms = normalizedSalonRooms,
    tableRooms = salonTableRooms,
    layout = salonLayout,
    fixtures = salonFixtures,
  } = {}) => {
    beginOperationalRemoteSyncHold(12000);
    salonStructureSyncPendingRef.current += 1;
    const remoteSync = saveInicioStoredState(operationalRestaurant, {
      mesasActivas,
      pedidosHistoricos,
      ...(sharedSalonTables.length ? { salonTables: sharedSalonTables } : {}),
      salonLayout: layout,
      salonReservations,
      salonRooms: rooms,
      salonTableRooms: tableRooms,
      salonFixtures: fixtures,
    });

    void Promise.resolve(remoteSync)
      .then(() => beginOperationalRemoteSyncHold(1500))
      .catch((error) => {
        console.error("No pudimos confirmar la estructura del salon:", error);
        beginOperationalRemoteSyncHold(15000);
        setServiceAlert({
          id: `salon-sync-${Date.now()}`,
          title: "No pudimos guardar el salon",
          text: "Los cambios siguen en este equipo. Reintenta la modificacion cuando vuelva la conexion.",
        });
      })
      .finally(() => {
        salonStructureSyncPendingRef.current = Math.max(
          0,
          salonStructureSyncPendingRef.current - 1
        );
      });
  };

  const addSalonRoom = () => {
    if (!ensureInicioPermission(canModifyOrders, "administrar los salones")) {
      return;
    }

    const name = String(salonNewRoomName || "").trim().replace(/\s+/g, " ");

    if (!name) {
      window.alert("Escribe un nombre para el nuevo salon o patio.");
      return;
    }

    if (normalizedSalonRooms.some((room) => room.name.toLowerCase() === name.toLowerCase())) {
      window.alert("Ya existe un ambiente con ese nombre.");
      return;
    }

    const nextRoom = {
      id: buildSalonRoomId(name),
      name: name.slice(0, 60),
      createdAt: new Date().toISOString(),
    };
    const nextRooms = [...normalizedSalonRooms, nextRoom];
    const nextFixtures = {
      ...salonFixtures,
      [nextRoom.id]: buildDefaultSalonFixtures(nextRoom.id),
    };

    setSalonRooms(nextRooms);
    setSalonFixtures(nextFixtures);
    setSalonActiveRoomId(nextRoom.id);
    setSalonSelectedTableId("");
    setSalonSelectedFixtureId("");
    setSalonNewRoomName("");
    saveSalonStructure({ rooms: nextRooms, fixtures: nextFixtures });
  };

  const removeSalonRoom = (room) => {
    if (!room || !ensureInicioPermission(canModifyOrders, "eliminar salones")) {
      return;
    }

    if (normalizedSalonRooms.length <= 1) {
      window.alert("Debe quedar al menos un salon o patio configurado.");
      return;
    }

    const fallbackRoom = normalizedSalonRooms.find((item) => item.id !== room.id);
    const assignedTables = salonTables.filter(
      (table) => salonResolvedTableRooms[table.id] === room.id
    );
    const moveMessage = assignedTables.length
      ? ` Sus ${assignedTables.length} mesas se moveran a ${fallbackRoom.name}.`
      : "";

    if (!window.confirm(`Eliminar ${room.name}?${moveMessage}`)) {
      return;
    }

    const nextRooms = normalizedSalonRooms.filter((item) => item.id !== room.id);
    const nextTableRooms = { ...salonTableRooms };
    const nextFixtures = { ...salonFixtures };
    delete nextFixtures[room.id];

    assignedTables.forEach((table) => {
      nextTableRooms[table.id] = fallbackRoom.id;
    });

    setSalonRooms(nextRooms);
    setSalonTableRooms(nextTableRooms);
    setSalonFixtures(nextFixtures);

    if (resolvedSalonActiveRoomId === room.id) {
      setSalonActiveRoomId(fallbackRoom.id);
      setSalonSelectedTableId("");
      setSalonSelectedFixtureId("");
    }

    saveSalonStructure({
      rooms: nextRooms,
      tableRooms: nextTableRooms,
      fixtures: nextFixtures,
    });
  };

  const assignTableToSalon = (tableId, roomId) => {
    if (!ensureInicioPermission(canModifyOrders, "asignar mesas a salones")) {
      return;
    }

    if (!tableId || !salonKnownRoomIds.has(roomId)) {
      return;
    }

    const nextTableRooms = {
      ...salonTableRooms,
      [tableId]: roomId,
    };

    setSalonTableRooms(nextTableRooms);

    if (salonSelectedTableId === tableId && roomId !== resolvedSalonActiveRoomId) {
      setSalonSelectedTableId("");
    }

    saveSalonStructure({ tableRooms: nextTableRooms });
  };

  const resetSalonLayout = () => {
    if (!ensureInicioPermission(canModifyOrders, "organizar el salon")) {
      return;
    }

    if (window.confirm(`Restablecer la distribucion automatica de ${activeSalonRoom.name}.`)) {
      const activeTableIds = new Set(salonVisibleTables.map((table) => table.id));
      const nextLayout = Object.fromEntries(
        Object.entries(salonLayout).filter(([tableId]) => !activeTableIds.has(tableId))
      );

      setSalonLayout(nextLayout);
      saveSalonStructure({ layout: nextLayout });
    }
  };

  const updateSalonTableGeometry = (tableId, patch, { persist = true } = {}) => {
    if (!ensureInicioPermission(canModifyOrders, "cambiar el formato de las mesas")) {
      return;
    }

    const tableIndex = salonVisibleTables.findIndex((table) => table.id === tableId);
    if (tableIndex < 0) return;

    const currentLayout = resolveSalonTableLayout(
      salonLayout[tableId],
      tableIndex,
      salonVisibleTables.length
    );
    const nextShape = String(patch?.shape || currentLayout.shape);
    const shapeBase =
      nextShape !== currentLayout.shape
        ? buildSalonTableShapeGeometry(currentLayout, nextShape)
        : currentLayout;
    const geometry = normalizeSalonTableGeometry({ ...shapeBase, ...patch });
    const floorRect = salonFloorRef.current?.getBoundingClientRect();
    const nextEntry = {
      ...currentLayout,
      ...geometry,
      x: clampSalonTablePosition(
        currentLayout.x,
        geometry.width,
        floorRect?.width
      ),
      y: clampSalonTablePosition(
        currentLayout.y,
        geometry.height,
        floorRect?.height
      ),
    };
    const nextLayout = {
      ...salonLayout,
      [tableId]: nextEntry,
    };

    setSalonLayout(nextLayout);
    if (persist) {
      saveSalonStructure({ layout: nextLayout });
    }
  };

  const applySalonTableFormat = ({ allRooms = false } = {}) => {
    if (!ensureInicioPermission(canModifyOrders, "aplicar el formato general de mesas")) {
      return;
    }

    const targetTables = allRooms ? salonTables : salonVisibleTables;
    if (!targetTables.length) return;

    const geometry = normalizeSalonTableGeometry(salonTableFormatDraft);
    const floorRect = salonFloorRef.current?.getBoundingClientRect();
    const nextLayout = targetTables.reduce((layout, table) => {
      const roomId = salonResolvedTableRooms[table.id];
      const roomTables = salonTables.filter(
        (candidate) => salonResolvedTableRooms[candidate.id] === roomId
      );
      const tableIndex = Math.max(
        0,
        roomTables.findIndex((candidate) => candidate.id === table.id)
      );
      const currentLayout = resolveSalonTableLayout(
        layout[table.id],
        tableIndex,
        roomTables.length
      );

      return {
        ...layout,
        [table.id]: {
          ...currentLayout,
          ...geometry,
          x: clampSalonTablePosition(
            currentLayout.x,
            geometry.width,
            floorRect?.width
          ),
          y: clampSalonTablePosition(
            currentLayout.y,
            geometry.height,
            floorRect?.height
          ),
        },
      };
    }, { ...salonLayout });

    setSalonLayout(nextLayout);
    saveSalonStructure({ layout: nextLayout });
  };

  const saveActiveSalonFixtures = (fixtures) => {
    const normalizedFixtures = normalizeSalonFixtureListForUi(fixtures);
    const nextFixtures = {
      ...salonFixtures,
      [resolvedSalonActiveRoomId]: normalizedFixtures,
    };

    setSalonFixtures(nextFixtures);
    saveSalonStructure({ fixtures: nextFixtures });
    return nextFixtures;
  };

  const addSalonFixture = (type) => {
    if (!ensureInicioPermission(canModifyOrders, "organizar el plano del salon")) {
      return;
    }

    const definition = SALON_FIXTURE_BY_TYPE[type];
    if (!definition) return;

    const offset = activeSalonFixtures.length % 6;
    const fixture = normalizeSalonFixtureListForUi([
      {
        id: buildSalonFixtureId(type),
        type,
        label: definition.label,
        x: 28 + offset * 8,
        y: 24 + (offset % 3) * 14,
        width: definition.width,
        height: definition.height,
      },
    ])[0];

    if (!fixture) return;

    saveActiveSalonFixtures([...activeSalonFixtures, fixture]);
    setSalonSelectedFixtureId(fixture.id);
    setSalonSelectedTableId("");
  };

  const updateSalonFixture = (fixtureId, patch, { persist = true } = {}) => {
    if (!ensureInicioPermission(canModifyOrders, "organizar el plano del salon")) {
      return;
    }

    const normalizedFixtures = normalizeSalonFixtureListForUi(
      activeSalonFixtures.map((fixture) =>
        fixture.id === fixtureId ? { ...fixture, ...patch } : fixture
      )
    );
    const nextFixtures = {
      ...salonFixtures,
      [resolvedSalonActiveRoomId]: normalizedFixtures,
    };

    setSalonFixtures(nextFixtures);
    if (persist) {
      saveSalonStructure({ fixtures: nextFixtures });
    }
  };

  const setSalonDoorFixtureOrientation = (fixture, orientation) => {
    if (!fixture || !isSalonDoorFixtureType(fixture.type)) return;

    const isVertical = orientation === "vertical";
    const width = isVertical ? 5 : 18;
    const height = isVertical ? 30 : 7;

    updateSalonFixture(fixture.id, {
      width,
      height,
      x: clampSalonFixturePosition(fixture.x, width),
      y: clampSalonFixturePosition(fixture.y, height),
    });
  };

  const removeSalonFixture = (fixture) => {
    if (
      !fixture ||
      !ensureInicioPermission(canModifyOrders, "eliminar elementos del plano")
    ) {
      return;
    }

    if (!window.confirm(`Eliminar ${fixture.label} de ${activeSalonRoom.name}?`)) {
      return;
    }

    saveActiveSalonFixtures(
      activeSalonFixtures.filter((item) => item.id !== fixture.id)
    );
    setSalonSelectedFixtureId("");
  };

  const handleSalonFixturePointerDown = (event, fixture) => {
    if (!salonEditMode || !salonFloorRef.current) return;

    const rect = salonFloorRef.current.getBoundingClientRect();
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSalonSelectedFixtureId(fixture.id);
    setSalonSelectedTableId("");
    salonFixtureDragRef.current = {
      fixtureId: fixture.id,
      rect,
      width: fixture.width,
      height: fixture.height,
      position: null,
    };
  };

  const handleSalonFixturePointerMove = (event, fixtureId) => {
    const drag = salonFixtureDragRef.current;
    if (!salonEditMode || !drag || drag.fixtureId !== fixtureId) return;

    const position = {
      x: clampSalonFixturePosition(
        ((event.clientX - drag.rect.left) / drag.rect.width) * 100,
        drag.width
      ),
      y: clampSalonFixturePosition(
        ((event.clientY - drag.rect.top) / drag.rect.height) * 100,
        drag.height
      ),
    };

    drag.position = position;
    event.currentTarget.style.left = `${position.x}%`;
    event.currentTarget.style.top = `${position.y}%`;
  };

  const finishSalonFixtureDrag = (event, fixtureId) => {
    const drag = salonFixtureDragRef.current;
    if (!drag || drag.fixtureId !== fixtureId) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag.position) {
      updateSalonFixture(fixtureId, drag.position);
    }
    salonFixtureDragRef.current = null;
  };

  const handleSalonTablePointerDown = (event, tableId) => {
    if (!salonEditMode || !salonFloorRef.current) {
      return;
    }

    const rect = salonFloorRef.current.getBoundingClientRect();
    const tableRect = event.currentTarget.getBoundingClientRect();
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSalonSelectedTableId(tableId);
    setSalonSelectedFixtureId("");
    salonDragRef.current = {
      tableId,
      rect,
      width: tableRect.width,
      height: tableRect.height,
      position: null,
    };
  };

  const handleSalonTablePointerMove = (event, tableId) => {
    const drag = salonDragRef.current;
    if (!salonEditMode || !drag || drag.tableId !== tableId) {
      return;
    }

    const position = {
      x: clampSalonTablePosition(
        ((event.clientX - drag.rect.left) / drag.rect.width) * 100,
        drag.width,
        drag.rect.width
      ),
      y: clampSalonTablePosition(
        ((event.clientY - drag.rect.top) / drag.rect.height) * 100,
        drag.height,
        drag.rect.height
      ),
    };

    drag.position = position;
    event.currentTarget.style.left = `${position.x}%`;
    event.currentTarget.style.top = `${position.y}%`;
  };

  const finishSalonTableDrag = (event, tableId) => {
    const drag = salonDragRef.current;
    if (!drag || drag.tableId !== tableId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag.position) {
      const tableIndex = salonVisibleTables.findIndex((table) => table.id === tableId);
      const currentLayout = resolveSalonTableLayout(
        salonLayout[tableId],
        Math.max(0, tableIndex),
        salonVisibleTables.length
      );
      const nextLayout = {
        ...salonLayout,
        [tableId]: {
          ...currentLayout,
          ...drag.position,
        },
      };

      setSalonLayout(nextLayout);
      saveSalonStructure({ layout: nextLayout });
    }
    salonDragRef.current = null;
  };

  const requestCancelOrder = (mesaId) => {
    if (!ensureInicioPermission(canCancelOrders, "cancelar pedidos")) {
      return;
    }

    const mesa = mesasActivas.find((item) => item.id === mesaId);

    if (!mesa || mesa.phase !== "new") {
      return;
    }

    const paymentStatus = String(mesa.paymentStatus || "").trim().toLowerCase();
    const hasApprovedDigitalPayment =
      ["paid", "approved", "closed", "completed", "accredited"].includes(
        paymentStatus
      ) && Boolean(mesa.paymentIntentId);
    let refundReason = "";

    if (hasApprovedDigitalPayment) {
      if (!ensureInicioPermission(canRefundOrders, "reintegrar cobros")) {
        return;
      }
      const requestedReason = window.prompt(
        `Motivo del reintegro del pedido de ${mesa.mesa}:`,
        "Pedido cancelado por el restaurante"
      );
      if (requestedReason === null) return;
      refundReason = String(requestedReason).trim();
      if (refundReason.length < 5) {
        setServiceAlert({
          id: `refund-reason-${Date.now()}`,
          title: "Falta el motivo",
          text: "Indica brevemente por que se reintegra el cobro.",
        });
        return;
      }
    }

    const confirmed = window.confirm(
      hasApprovedDigitalPayment
        ? `Se devolverá ${formatCurrency(
            Number(mesa?.pricing?.finalTotal ?? mesa?.totalAmount ?? 0)
          )} por Mercado Pago y se cancelará el pedido. ¿Continuar?`
        : `Confirma que deseas cancelar el pedido de ${mesa.mesa} antes de pasar a preparacion.`
    );

    if (!confirmed) {
      return;
    }

    void cancelOrder(mesaId, { refundReason });
  };

  const toggleDashboardDetail = (targetId) => {
    setDetalleMesaId("");

    if (
      targetId === "salon" &&
      detalleActivo !== "salon" &&
      firstCallingSalonTable
    ) {
      setSalonSelectedTableId(firstCallingSalonTable.id);
      setSalonActiveRoomId(
        salonResolvedTableRooms[firstCallingSalonTable.id] ||
          normalizedSalonRooms[0]?.id ||
          DEFAULT_SALON_ROOM_ID
      );
    }

    setDetalleActivo((previous) =>
      previous === targetId ? null : targetId
    );
  };

  return (
    <div style={styles.page}>
      <OperationalWorkspaceSwitcher activeWorkspace="salon" />
      <style>
        {`
          @keyframes inicioMetricFlowHorizontal {
            0% { transform: translateX(-120%); opacity: 0.12; }
            18% { opacity: 0.85; }
            50% { opacity: 1; }
            82% { opacity: 0.85; }
            100% { transform: translateX(120%); opacity: 0.12; }
          }

          @keyframes inicioMetricFlowVertical {
            0% { transform: translateY(-120%); opacity: 0.12; }
            18% { opacity: 0.85; }
            50% { opacity: 1; }
            82% { opacity: 0.85; }
            100% { transform: translateY(120%); opacity: 0.12; }
          }

          @keyframes inicioSalonCallCardPulse {
            0%, 100% {
              box-shadow: 0 0 0 1px rgba(220,38,38,0.18), 0 14px 34px rgba(220,38,38,0.16), inset 0 1px 0 rgba(255,255,255,0.92);
            }
            50% {
              box-shadow: 0 0 0 4px rgba(239,68,68,0.2), 0 16px 44px rgba(220,38,38,0.38), inset 0 1px 0 rgba(255,255,255,0.92);
            }
          }

          @keyframes inicioSalonTableCallPulse {
            0%, 100% {
              background: linear-gradient(145deg, #22c55e 0%, #15803d 100%);
              border-color: #16a34a;
              box-shadow: 0 0 0 5px rgba(34,197,94,0.22), 0 16px 34px rgba(22,163,74,0.34);
            }
            50% {
              background: linear-gradient(145deg, #ef4444 0%, #b91c1c 100%);
              border-color: #991b1b;
              box-shadow: 0 0 0 7px rgba(239,68,68,0.24), 0 18px 42px rgba(185,28,28,0.46);
            }
          }

          .inicio-manual-order-modal {
            left: 118px !important;
            right: 24px !important;
            width: auto !important;
            transform: none !important;
          }

          .inicio-salon-modal {
            width: min(1320px, calc(100vw - 144px)) !important;
          }

          .inicio-operations-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 16px !important;
            align-items: stretch !important;
          }

          .inicio-operations-grid-two {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .inicio-quick-operation-card {
            min-height: 226px;
            box-sizing: border-box;
          }

          .inicio-operation-expanded {
            grid-column: 1 / -1 !important;
            min-height: auto;
          }

          @media (max-width: 1260px) {
            .inicio-operations-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .inicio-service-operation-card {
              grid-column: 1 / -1;
            }

            .inicio-salon-modal-body {
              grid-template-columns: minmax(0, 1fr) !important;
            }

            .inicio-salon-detail-panel {
              max-height: none !important;
            }
          }

          @media (max-width: 760px) {
            .inicio-operations-grid {
              grid-template-columns: minmax(0, 1fr) !important;
            }

            .inicio-service-operation-card,
            .inicio-operation-expanded {
              grid-column: auto !important;
            }

            .inicio-quick-operation-card {
              min-height: auto;
            }

            .inicio-manual-order-modal {
              top: 12px !important;
              left: 12px !important;
              right: 12px !important;
              max-height: calc(100vh - 24px) !important;
              padding: 16px !important;
              border-radius: 20px !important;
            }

            .inicio-salon-modal {
              width: calc(100vw - 24px) !important;
              max-height: calc(100vh - 24px) !important;
              padding: 16px !important;
              border-radius: 20px !important;
            }

            .inicio-salon-floor {
              min-height: 440px !important;
            }
          }

        `}
      </style>
      {false ? (
      <section style={styles.hero}>
        <div style={styles.heroIntroCard}>
          <div style={styles.overline}>Actividad del turno</div>
          <div style={styles.heroNotificationHeader}>
            <div style={styles.heroNotificationHeaderText}>
              <h1 style={styles.title}>Notificaciones</h1>
              <p style={styles.subtitle}>
                {serviceNotificationItems.length > 0
                  ? "Aqui ves primero lo urgente del servicio: pedidos nuevos y llamados al mozo en tiempo real."
                  : "Cuando entren pedidos o llamados de salon, esta bandeja te los va a dejar arriba de todo para reaccionar rapido."}
              </p>
            </div>
            <div style={styles.heroNotificationTotalBadge}>
              {serviceNotificationItems.length}
            </div>
          </div>

          <div style={styles.heroChipRow}>
            <span style={styles.heroChip}>{currentBranch?.name || "Sucursal activa"}</span>
            <span style={styles.heroChip}>{heroOrderNotificationCount} pedidos nuevos</span>
            <span style={styles.heroChip}>{heroWaiterRequestCount} llamados al mozo</span>
          </div>

          {heroNotificationPreviewItems.length > 0 ? (
            <div style={styles.heroNotificationList}>
              {heroNotificationPreviewItems.map((item) => {
                const isOrder = item.kind === "order";

                return (
                  <article key={item.id} style={styles.heroNotificationItem}>
                    <div style={styles.heroNotificationItemTop}>
                      <span
                        style={{
                          ...styles.heroNotificationKind,
                          ...(isOrder
                            ? styles.heroNotificationKindOrder
                            : styles.heroNotificationKindWaiter),
                        }}
                      >
                        {isOrder ? "Pedido nuevo" : "Llamado mozo"}
                      </span>
                      <span
                        style={{
                          ...styles.elapsedBadge,
                          ...getElapsedTone(item.requestedAt),
                        }}
                      >
                        {formatElapsed(item.requestedAt)}
                      </span>
                    </div>

                    <strong style={styles.heroNotificationMesa}>{item.mesa}</strong>
                    <div style={styles.heroNotificationSummary}>{item.summary}</div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div style={styles.heroNotificationEmpty}>
              <div style={styles.heroNotificationEmptyTitle}>Sin alertas nuevas</div>
              <div style={styles.heroNotificationEmptyText}>
                El turno esta tranquilo por ahora. Cuando entre movimiento, lo vas a ver aqui.
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            ...styles.statusCard,
            ...(operationalState.isOpen ? {} : styles.statusCardClosed),
          }}
        >
          <div style={styles.statusHeader}>
            <span
              style={{
                ...styles.statusDot,
                ...(operationalState.isOpen ? {} : styles.statusDotClosed),
              }}
            />
            <span
              style={{
                ...styles.statusText,
                ...(operationalState.isOpen ? {} : styles.statusTextClosed),
              }}
            >
              {operationalState.title}
            </span>
          </div>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Horario visible</span>
            <strong>{perfil.horario}</strong>
          </div>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Recepcion de pedidos</span>
            <strong>{operationalState.intakeText}</strong>
          </div>
          <div style={styles.statusRow}>
            <span style={styles.statusLabel}>Contacto</span>
            <strong>{perfil.telefono || "Pendiente de configurar"}</strong>
          </div>
          <div
            style={{
              ...styles.statusNote,
              ...(operationalState.isOpen ? styles.statusNoteOpen : styles.statusNoteClosed),
            }}
          >
            {operationalState.serviceText}
          </div>
        </div>
      </section>
      ) : null}

      <div
        className={`inicio-operations-grid ${
          stockFeatureEnabled && menuProductStockAlerts.length > 0
            ? "inicio-operations-grid-three"
            : "inicio-operations-grid-two"
        }`}
        style={styles.operationsGrid}
      >
        {isManualOrderExpanded ? (
          <button
            type="button"
            aria-label="Cerrar nuevo pedido"
            style={styles.manualOrderWorkspaceBackdrop}
            onClick={() => setIsManualOrderExpanded(false)}
          />
        ) : null}
        <section
          id="inicio-manual-order"
          className={
            isManualOrderExpanded
              ? "inicio-manual-order-modal"
              : "inicio-quick-operation-card inicio-order-operation-card"
          }
          style={{
            ...styles.panel,
            ...styles.operationPanel,
            ...(isManualOrderExpanded
              ? styles.manualOrderWorkspaceModal
              : styles.manualOrderLauncherPanel),
          }}
        >
          <div style={styles.panelHeader}>
            <div>
              <span style={styles.eyebrow}>VENTA ASISTIDA</span>
              <h2 style={styles.panelTitle}>
                Nuevo pedido
              </h2>
              <p style={styles.detailSubtitle}>
                {isManualOrderKioskMode
                  ? "Mostrador kiosk con ticket de retiro e impresion local."
                  : "Mesa, retiro o envio con el menu completo y todas sus personalizaciones."}
              </p>
            </div>
            <div style={styles.headerActions}>
              <button
                type="button"
                style={
                  isManualOrderExpanded
                    ? styles.secondaryInlineButton
                    : styles.manualOrderLauncherButton
                }
                onClick={() => setIsManualOrderExpanded((prev) => !prev)}
                aria-expanded={isManualOrderExpanded}
              >
                {isManualOrderExpanded ? "Cerrar" : "+ Nuevo pedido"}
              </button>
            </div>
          </div>

          {!isManualOrderExpanded ? (
            <div style={styles.manualOrderLauncherSummary}>
              {!isManualOrderKioskMode ? (
                <span style={styles.metricChip}>
                  {availableTables.length > 0
                    ? `${availableTables.length} mesas disponibles`
                    : "Sin mesas disponibles"}
                </span>
              ) : null}
              <span style={styles.metricChip}>Para llevar</span>
              {!isManualOrderKioskMode ? <span style={styles.metricChip}>Envio</span> : null}
            </div>
          ) : null}

          {isManualOrderExpanded ? (
          <>
          {manualOrderMessage.text ? (
            <div
              style={{
                ...styles.manualOrderNotice,
                ...(manualOrderMessage.type === "success"
                  ? styles.manualOrderNoticeSuccess
                  : styles.manualOrderNoticeError),
              }}
            >
              {manualOrderMessage.text}
            </div>
          ) : null}

          {!canTakeOrders ? (
            <div style={{ ...styles.manualOrderNotice, ...styles.manualOrderNoticeError }}>
              {currentAccessRoleLabel} puede consultar esta carga rapida, pero no crear pedidos
              manuales desde Inicio.
            </div>
          ) : null}

          <form onSubmit={handleCreateManualOrder} style={styles.manualOrderForm}>
            <div style={{ ...styles.catalogPanel, gridColumn: "1 / -1" }}>
              <div style={styles.catalogPanelHeader}>
                <strong style={styles.detailTitle}>
                  {isManualOrderKioskMode ? "Tomar pedido" : "Tipo de pedido"}
                </strong>
                <span style={styles.detailMeta}>
                  {isManualOrderKioskMode
                    ? "Pedido de mostrador con ticket de retiro, nombre y numero."
                    : "Elegí si el pedido va a mesa, retiro o envío."}
                </span>
              </div>
              {!isManualOrderKioskMode ? (
              <div style={styles.manualDestinationGrid}>
                {[
                  {
                    id: "table",
                    title: "Cargar a una mesa",
                    description: "Pedido para consumir en el salon.",
                  },
                  {
                    id: "pickup",
                    title: "Tomar pedido",
                    description: "Pedido de mostrador con ticket para retirar.",
                  },
                  {
                    id: "delivery",
                    title: "Enviar a domicilio",
                    description: "Pedido con cliente, direccion y zona de envio.",
                  },
                ].map((destination) => (
                  <button
                    key={destination.id}
                    type="button"
                    style={{
                      ...styles.manualDestinationButton,
                      ...(manualOrderDestinationType === destination.id
                        ? styles.manualDestinationButtonActive
                        : {}),
                    }}
                    onClick={() =>
                      setManualOrderForm((prev) => ({
                        ...prev,
                        destinationType: destination.id,
                        canal:
                          destination.id === "table"
                            ? "Salon"
                            : destination.id === "delivery"
                              ? "Delivery"
                              : "Retiro",
                      }))
                    }
                  >
                    <strong style={styles.manualDestinationTitle}>{destination.title}</strong>
                    <span style={styles.manualDestinationDescription}>
                      {destination.description}
                    </span>
                  </button>
                ))}
              </div>
              ) : null}

              {manualOrderDestinationType !== "table" ? (
                <div style={styles.manualDeliveryFields}>
                  {!isManualOrderKioskMode && manualKnownCustomers.length ? (
                    <div style={styles.manualKnownCustomerBox}>
                      <div style={styles.manualKnownCustomerHeader}>
                        <div>
                          <strong>Cliente registrado</strong>
                          <span>Busca por nombre, telefono o direccion guardada.</span>
                        </div>
                        <span style={styles.manualKnownCustomerCount}>
                          {manualKnownCustomers.length} guardados
                        </span>
                      </div>
                      <input
                        type="search"
                        value={manualCustomerSearch}
                        onChange={(event) => setManualCustomerSearch(event.target.value)}
                        style={styles.manualInput}
                        placeholder="Ej: Diego, 1133445566 o calle"
                      />
                      {filteredManualKnownCustomers.length ? (
                        <div style={styles.manualKnownCustomerList}>
                          {filteredManualKnownCustomers.map((customer) => {
                            const customerName = getManualKnownCustomerName(customer);
                            const customerPhone = getManualKnownCustomerPhone(customer);
                            const address = getManualKnownCustomerDefaultAddress(customer);

                            return (
                              <button
                                key={customer.id}
                                type="button"
                                style={styles.manualKnownCustomerButton}
                                onClick={() => applyManualKnownCustomer(customer)}
                              >
                                <span style={styles.manualKnownCustomerMain}>
                                  <strong>{customerName}</strong>
                                  <small>{formatExternalPhone(customerPhone) || "Sin telefono"}</small>
                                </span>
                                <span style={styles.manualKnownCustomerAddress}>
                                  {formatManualKnownCustomerAddress(address)}
                                </span>
                                <span style={styles.manualKnownCustomerUse}>Usar cliente</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : manualCustomerSearch.trim() ? (
                        <div style={styles.manualKnownCustomerEmpty}>
                          No encontramos ese cliente. Cargalo manualmente abajo.
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <label style={styles.manualField}>
                    <span style={styles.detailLabel}>
                      {manualOrderDestinationType === "pickup"
                        ? "Nombre para el ticket"
                        : "Nombre"}
                    </span>
                    <input
                      type="text"
                      value={manualOrderForm.customerFirstName}
                      onChange={(event) =>
                        setManualOrderForm((prev) => ({
                          ...prev,
                          customerFirstName: event.target.value,
                        }))
                      }
                      style={styles.manualInput}
                      placeholder={
                        manualOrderDestinationType === "pickup"
                          ? "Ej: Diego"
                          : "Nombre del cliente"
                      }
                    />
                  </label>

                  {manualOrderDestinationType === "delivery" ? (
                    <>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Apellido</span>
                        <input
                          type="text"
                          value={manualOrderForm.customerLastName}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              customerLastName: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Apellido"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Telefono</span>
                        <input
                          type="tel"
                          value={manualOrderForm.customerPhone}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              customerPhone: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Telefono de contacto"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Zona de envio</span>
                        <select
                          value={manualOrderForm.deliveryZoneId}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              deliveryZoneId: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                        >
                          <option value="">Seleccionar zona</option>
                          {manualDeliveryZones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name || zone.zone_name || "Zona de reparto"}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Calle</span>
                        <input
                          type="text"
                          value={manualOrderForm.addressStreet}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              addressStreet: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Calle"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Numero</span>
                        <input
                          type="text"
                          value={manualOrderForm.addressStreetNumber}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              addressStreetNumber: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Altura"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Barrio</span>
                        <input
                          type="text"
                          value={manualOrderForm.addressNeighborhood}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              addressNeighborhood: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Barrio"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Ciudad</span>
                        <input
                          type="text"
                          value={manualOrderForm.addressCity}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              addressCity: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Ciudad"
                        />
                      </label>
                      <label style={styles.manualField}>
                        <span style={styles.detailLabel}>Referencia</span>
                        <input
                          type="text"
                          value={manualOrderForm.addressReference}
                          onChange={(event) =>
                            setManualOrderForm((prev) => ({
                              ...prev,
                              addressReference: event.target.value,
                            }))
                          }
                          style={styles.manualInput}
                          placeholder="Piso, departamento o referencia"
                        />
                      </label>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={{ ...styles.catalogPanel, gridColumn: "1 / -1" }}>
              <div style={styles.catalogPanelHeader}>
                <strong style={styles.detailTitle}>Armado del pedido</strong>
                <span style={styles.detailMeta}>
                  {isManualOrderKioskMode
                    ? "Busca platos del menu kiosk y agregalos al ticket de retiro."
                    : "Primero elegi mesa o destino, despues busca platos y agregalos al pedido."}
                </span>
              </div>

              <div style={styles.catalogSelectGrid}>
                {manualOrderDestinationType === "table" ? (
                  <>
                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Mesa</span>
                  <select
                    value={manualOrderForm.tableId}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({ ...prev, tableId: event.target.value }))
                    }
                    style={styles.manualInput}
                  >
                    <option value="">Seleccionar mesa</option>
                    {availableTables.map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.label || `Mesa ${table.tableNumber || ""}`}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Comensales</span>
                  <input
                    type="number"
                    min="1"
                    value={manualOrderForm.comensales}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({ ...prev, comensales: event.target.value }))
                    }
                    style={styles.manualInput}
                  />
                </label>

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Canal</span>
                  <select
                    value={manualOrderForm.canal}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({ ...prev, canal: event.target.value }))
                    }
                    style={styles.manualInput}
                  >
                    <option value="Salon">Salon</option>
                    <option value="Barra">Barra</option>
                    <option value="Retiro">Retiro</option>
                    <option value="Delivery">Delivery</option>
                  </select>
                </label>
                  </>
                ) : null}

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Categoria</span>
                  <select
                    value={manualOrderForm.menuCategoryId}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({
                        ...prev,
                        menuCategoryId: event.target.value,
                        menuProductId: "",
                      }))
                    }
                    style={styles.manualInput}
                  >
                    <option value="">Todas</option>
                    {menuCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.nombre}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {manualAvailableCombos.length > 0 ? (
                <div style={styles.searchResultsPanel}>
                  <div style={styles.searchResultsHeader}>
                    <strong style={styles.searchResultsTitle}>Combos directos</strong>
                    <span style={styles.searchResultsMeta}>
                      {manualAvailableCombos.length} disponibles ahora
                    </span>
                  </div>
                  <div style={styles.manualComboGrid}>
                    {manualAvailableCombos.map((combo) => (
                      <article key={combo.id} style={styles.manualComboCard}>
                        <div style={styles.manualComboCardMain}>
                          <div style={styles.manualComboBadgeRow}>
                            <span style={styles.promoSummaryTypeChip}>Combo</span>
                            <span style={styles.metricChip}>
                              {combo.pricingSummary || combo.hook || "Precio promo"}
                            </span>
                          </div>
                          <strong style={styles.detailTitle}>{combo.title}</strong>
                          <div style={styles.detailMeta}>
                            {(combo.items || [])
                              .map((item) => `${item.quantity}x ${item.name}`)
                              .join(" · ")}
                          </div>
                        </div>
                        <button
                          type="button"
                          style={styles.primaryInlineButton}
                          onClick={() => addManualComboToOrder(combo)}
                          disabled={!canTakeOrders}
                        >
                          Agregar combo
                        </button>
                      </article>
                    ))}
                  </div>
                  <div style={styles.detailMeta}>
                    Si cargas productos sueltos y coinciden con un combo o promocion, el
                    descuento se aplica solo al total.
                  </div>
                </div>
              ) : null}

              <div style={styles.searchResultsPanel}>
                <div style={styles.searchResultsHeader}>
                  <strong style={styles.searchResultsTitle}>Buscador de platos</strong>
                  <span style={styles.searchResultsMeta}>
                    {filteredManualOrderProducts.length} resultados
                  </span>
                </div>
                <input
                  type="text"
                  value={manualOrderSearch}
                  onChange={(event) => setManualOrderSearch(event.target.value)}
                  placeholder="Buscar por nombre o subcategoria"
                  style={styles.manualInput}
                />
                <div style={styles.searchResultsGrid}>
                  {filteredManualOrderProducts.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() =>
                        {
                          setManualOrderForm((prev) => ({
                            ...prev,
                            menuProductId: product.id,
                            menuProductQuantity: "1",
                          }));
                          setManualOrderPizzaSizeOptionId(
                            getPizzaSizeOptions(product)[0]?.id || ""
                          );
                          setManualOrderPizzaHalfProductId("");
                        }
                      }
                      style={{
                        ...styles.searchResultCard,
                        ...(manualOrderForm.menuProductId === product.id
                          ? styles.searchResultCardActive
                          : {}),
                      }}
                    >
                      {product.imagen || product.imageUrl || product.image_url ? (
                        <img
                          src={product.imagen || product.imageUrl || product.image_url}
                          alt=""
                          style={styles.searchResultImage}
                        />
                      ) : (
                        <span style={styles.searchResultPlaceholder}>
                          {String(product.nombre || "P").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                      <strong style={styles.searchResultName}>{product.nombre}</strong>
                      <span style={styles.searchResultMeta}>
                        {product.subcategoria || product.categoria || "Menu"}
                      </span>
                      <span style={styles.searchResultPrice}>
                        {productHasPizzaSizeOptions(product) ? "Desde " : ""}
                        {formatCurrency(resolveMenuProductPrice(product))}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedManualOrderProduct ? (
                <>
                  <div style={styles.selectionResume}>
                    Configurando {selectedManualOrderProduct.nombre}. Cada variante se agrega por
                    separado, asi despues podes volver a abrir el mismo plato y cargar otra
                    distinta.
                  </div>

                </>
              ) : null}
            </div>

            <div style={{ ...styles.catalogPanel, gridColumn: "1 / -1" }}>
              <div style={styles.catalogPanelHeader}>
                <strong style={styles.detailTitle}>Pedido armado</strong>
                <span style={styles.detailMeta}>
                  {manualOrderItems.length > 0
                    ? `${manualOrderItems.length} items listos para cobrar`
                    : "Todavia no agregaste productos"}
                </span>
              </div>

              {manualOrderItems.length > 0 ? (
                <div style={styles.selectedItemsList}>
                  {manualOrderItems.map((item) => (
                    <article key={item.id} style={styles.selectedItemCard}>
                      <div>
                        <strong style={styles.detailTitle}>
                          {item.quantity}x {item.name}
                        </strong>
                        {item.selectionDetails?.length ? (
                          <div style={styles.detailMeta}>{item.selectionDetails.join(", ")}</div>
                        ) : (
                          <div style={styles.detailMeta}>Sin ajustes</div>
                        )}
                      </div>
                      <div style={styles.actionRow}>
                        <span style={styles.metricChip}>{formatCurrency(item.unitPrice * item.quantity)}</span>
                        <button
                          type="button"
                          style={styles.dangerInlineButton}
                          onClick={() => removeManualOrderItem(item.id)}
                          disabled={!canTakeOrders}
                        >
                          Quitar
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyNotice}>
                  Agregá platos desde el menu para armar el pedido manual.
                </div>
              )}
            </div>

            <div style={{ ...styles.manualPaymentPanel, gridColumn: "1 / -1" }}>
              <div style={styles.manualPaymentHeader}>
                <div>
                  <h3 style={styles.manualPaymentTitle}>Cobro asociado</h3>
                  <p style={styles.manualPaymentHint}>
                    El total siempre sale del precio real del pedido, con descuento o sin
                    descuento segun combos y promociones activas.
                  </p>
                </div>
              </div>

              <div style={styles.manualPaymentGrid}>
                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Precio de lista</span>
                  <strong style={styles.detailTitle}>
                    {formatCurrency(manualOrderPricing.listTotal)}
                  </strong>
                </div>
                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Descuentos</span>
                  <strong style={styles.detailTitle}>
                    {manualOrderPricing.totalDiscount > 0
                      ? `- ${formatCurrency(manualOrderPricing.totalDiscount)}`
                      : "Sin descuento"}
                  </strong>
                </div>
                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Total que ira a Pagos</span>
                  <strong style={styles.detailTitle}>
                    {formatCurrency(manualOrderFinalTotal)}
                  </strong>
                </div>
              </div>

              {manualOrderPricing.adjustments?.length ? (
                <div style={styles.pricingAdjustmentList}>
                  {manualOrderPricing.adjustments.map((adjustment) => (
                    <div key={adjustment.id} style={styles.pricingAdjustmentItem}>
                      <span>{adjustment.title || adjustment.summary || "Ajuste comercial"}</span>
                      <strong>{`- ${formatCurrency(adjustment.amount)}`}</strong>
                    </div>
                  ))}
                </div>
              ) : null}

              <div style={styles.manualOrderForm}>
                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Flujo de cobro</span>
                  <select
                    value={manualOrderForm.paymentFlow}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({
                        ...prev,
                        paymentFlow: event.target.value,
                      }))
                    }
                    style={styles.manualInput}
                  >
                    <option value="later">Cobrar despues</option>
                    <option value="paid_now">Cobrar ahora</option>
                  </select>
                </label>

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Medio de pago</span>
                  <select
                    value={manualOrderForm.paymentMethod}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({
                        ...prev,
                        paymentMethod: event.target.value,
                      }))
                    }
                    style={styles.manualInput}
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="deep_link">Deep link</option>
                  </select>
                </label>
              </div>

              <label style={{ ...styles.manualField, gridColumn: "1 / -1" }}>
                <span style={styles.detailLabel}>Notas internas</span>
                <textarea
                  value={manualOrderForm.notes}
                  onChange={(event) =>
                    setManualOrderForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  style={styles.manualTextarea}
                  placeholder="Observaciones del pedido o del cobro"
                />
              </label>

              <div style={styles.manualFormActions}>
                <button
                  type="submit"
                  style={styles.primaryInlineButton}
                  disabled={!canTakeOrders || isManualOrderSaving}
                >
                  {isManualOrderSaving
                    ? "Guardando pedido..."
                    : manualOrderDestinationType === "delivery"
                      ? "Crear pedido con envio"
                      : manualOrderDestinationType === "pickup"
                        ? "Crear pedido para llevar"
                        : "Cargar pedido en mesa"}
                </button>
              </div>
            </div>
          </form>
          </>
          ) : null}
        </section>

        {false ? (
        <section style={{ ...styles.panel, ...styles.operationPanel }}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Promos y combos</h2>
              <p style={styles.detailSubtitle}>
                La configuracion comercial vive en el modulo Promos. Desde Inicio solo
                dejamos el pulso de lo que esta activo y un acceso rapido para ajustar.
              </p>
            </div>
            <div style={styles.headerActions}>
              <span style={styles.panelPillMuted}>{availablePromotionNowCount} visibles ahora</span>
              <button
                type="button"
                style={styles.collapseToggleButton}
                onClick={() => setIsPromosExpanded((prev) => !prev)}
                aria-expanded={isPromosExpanded}
              >
                <span>{isPromosExpanded ? "Ocultar" : "Usar ahora"}</span>
                <span
                  style={{
                    ...styles.collapseToggleIcon,
                    transform: isPromosExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▾
                </span>
              </button>
            </div>
          </div>

          {!isPromosExpanded ? (
            <div style={styles.collapsedPreviewCard}>
              <div style={styles.collapsedPreviewRow}>
                <strong style={styles.detailTitle}>Resumen comercial cerrado</strong>
                <span style={styles.metricChip}>
                  {activePromotionsCount} activo{activePromotionsCount === 1 ? "" : "s"}
                </span>
              </div>
              <div style={styles.detailMeta}>
                Combos: {activeComboCount} · Promociones: {activePromotionRulesCount}. Abrilo solo
                cuando necesites revisar o entrar al modulo.
              </div>
            </div>
          ) : (
            <>
              <div style={styles.catalogInlineActions}>
                <span style={styles.panelPill}>{availablePromotionNowCount} visibles ahora</span>
                <button
                  type="button"
                  style={styles.primaryInlineButton}
                  onClick={handleOpenPromosModule}
                  disabled={!canOpenPromosModule}
                >
                  Abrir modulo
                </button>
              </div>

              <div style={styles.promoSummaryStats}>
                <article style={styles.promoSummaryStatCard}>
                  <span style={styles.detailLabel}>Activas</span>
                  <strong style={styles.promoSummaryValue}>{activePromotionsCount}</strong>
                  <span style={styles.detailMeta}>Promos o combos habilitados en esta sucursal.</span>
                </article>
                <article style={styles.promoSummaryStatCard}>
                  <span style={styles.detailLabel}>Combos</span>
                  <strong style={styles.promoSummaryValue}>{activeComboCount}</strong>
                  <span style={styles.detailMeta}>Paquetes cerrados con precio propio.</span>
                </article>
                <article style={styles.promoSummaryStatCard}>
                  <span style={styles.detailLabel}>Promociones</span>
                  <strong style={styles.promoSummaryValue}>{activePromotionRulesCount}</strong>
                  <span style={styles.detailMeta}>Reglas por categoria, subcategoria o producto.</span>
                </article>
              </div>

              {promotionSummaryItems.length > 0 ? (
                <div style={styles.promoSummaryList}>
                  {promotionSummaryItems.map((promotion) => {
                    const promotionTypeLabel =
                      getPromotionRecordType(promotion) === 'combo' ? 'Combo' : 'Promocion';
                    const isAvailable =
                      promotion.active !== false && isPromotionAvailableNow(promotion, new Date(currentTime));

                    return (
                      <article key={promotion.id} style={styles.promoSummaryItem}>
                        <div style={styles.promoSummaryItemMain}>
                          <div style={styles.promoSummaryItemBadges}>
                            <span style={styles.promoSummaryTypeChip}>{promotionTypeLabel}</span>
                            <span
                              style={{
                                ...styles.promoSummaryStateChip,
                                ...(isAvailable
                                  ? styles.promoSummaryStateChipLive
                                  : styles.promoSummaryStateChipMuted),
                              }}
                            >
                              {isAvailable ? 'Visible ahora' : 'Programada'}
                            </span>
                          </div>
                          <strong style={styles.detailTitle}>{promotion.title}</strong>
                          <div style={styles.detailMeta}>
                            {promotion.pricingSummary || promotion.hook || 'Sin resumen comercial'}
                          </div>
                          <div style={styles.promoSummaryMetaRow}>
                            <span style={styles.metricChip}>
                              {promotion.targetLabel || promotion.categoryName || 'Sucursal'}
                            </span>
                            <span style={styles.metricChip}>
                              {promotion.scheduleSummary || buildPromotionScheduleSummary(promotion.schedule)}
                            </span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.emptyNotice}>
                  Todavia no hay promos ni combos creados para esta sucursal. Cargalos desde el
                  modulo Promos para que despues impacten igual en QR, carga manual y pagos.
                </div>
              )}
            </>
          )}
        </section>
        ) : null}
      {stockFeatureEnabled && menuProductStockAlerts.length > 0 ? (
        <section
          className={`inicio-quick-operation-card inicio-stock-operation-card${
            isStockAlertExpanded ? " inicio-operation-expanded" : ""
          }`}
          style={{
            ...styles.panel,
            ...styles.stockAlertPanel,
            ...(isStockAlertExpanded ? styles.operationExpandedPanel : {}),
          }}
        >
          <div style={styles.panelHeader}>
            <div>
              <span style={{ ...styles.eyebrow, ...styles.stockEyebrow }}>
                CONTROL DE INSUMOS
              </span>
              <h2 style={styles.panelTitle}>Stock a reponer</h2>
            </div>
            <div style={styles.headerActions}>
              <span style={{ ...styles.panelPill, ...styles.stockAlertPill }}>
                {menuProductStockAlerts.length} productos bloqueados
              </span>
              <button
                type="button"
                style={styles.collapseToggleButton}
                onClick={() => setIsStockAlertExpanded((prev) => !prev)}
                aria-expanded={isStockAlertExpanded}
              >
                <span>{isStockAlertExpanded ? "Achicar" : "Ver stock"}</span>
                <span
                  style={{
                    ...styles.collapseToggleIcon,
                    transform: isStockAlertExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▾
                </span>
              </button>
            </div>
          </div>

          {!isStockAlertExpanded ? (
            <div style={{ ...styles.collapsedPreviewCard, ...styles.stockAlertCollapsedPreview }}>
              <div style={styles.collapsedPreviewRow}>
                <strong style={styles.detailTitle}>Productos ocultos por stock</strong>
                <span style={{ ...styles.metricChip, ...styles.stockAlertCollapsedChip }}>
                  Revisar reposicion
                </span>
              </div>
              <div style={styles.stockAlertCollapsedText}>
                {menuProductStockAlerts[0]?.product?.nombre ||
                  menuProductStockAlerts[0]?.product?.name ||
                  "Hay productos bloqueados"}
                {menuProductStockAlerts.length > 1
                  ? ` y ${menuProductStockAlerts.length - 1} mas necesitan insumos.`
                  : " necesita insumos."}
              </div>
            </div>
          ) : null}

          {isStockAlertExpanded ? (
            <>
              <div style={styles.stockAlertLead}>
                Estos productos quedaron deshabilitados para vender porque su receta no tiene stock
                suficiente o le falta carga de insumos. Reponlos desde Stock para volver a mostrarlos
                operativos.
              </div>

              <div style={styles.stockAlertGrid}>
                {menuProductStockAlerts.slice(0, 6).map(({ product, stockState }) => (
                  <article key={product.id} style={styles.stockAlertCard}>
                    <strong style={styles.stockAlertProductName}>
                      {product?.nombre || product?.name || "Producto"}
                    </strong>
                    <span style={styles.stockAlertProductMeta}>
                      {product?.subcategoria || product?.subcategoryName || "Sin subcategoria"}
                    </span>
                    <span style={styles.stockAlertProductReason}>
                      {stockState.reason ||
                        "No hay stock suficiente para seguir vendiendo este producto."}
                    </span>
                  </article>
                ))}
              </div>

              {menuProductStockAlerts.length > 6 ? (
                <div style={styles.stockAlertMore}>
                  Hay {menuProductStockAlerts.length - 6} productos mas bloqueados por stock en esta
                  sucursal.
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
      <section
        className={`inicio-quick-operation-card inicio-service-operation-card${
          notificaciones.length + waiterServiceNotifications.length > 0
            ? " inicio-operation-expanded"
            : ""
        }`}
        style={{
          ...styles.panel,
          ...styles.serviceNotificationPanel,
          ...(notificaciones.length + waiterServiceNotifications.length > 0
            ? styles.operationExpandedPanel
            : {}),
          border: `1px solid ${SERVICE_NOTIFICATION_ACCENT.border}`,
          boxShadow: `0 18px 40px rgba(15, 23, 42, 0.05), 0 0 0 1px ${SERVICE_NOTIFICATION_ACCENT.glow}`,
        }}
      >
        <div style={styles.serviceNotificationFrame} aria-hidden="true">
          <span
            style={{
              ...styles.serviceNotificationLine,
              ...styles.serviceNotificationLineTop,
              background: SERVICE_NOTIFICATION_ACCENT.horizontal,
            }}
          />
          <span
            style={{
              ...styles.serviceNotificationLine,
              ...styles.serviceNotificationLineBottom,
              background: SERVICE_NOTIFICATION_ACCENT.horizontal,
              animationDuration: "4.8s",
              animationDelay: "-1.6s",
            }}
          />
          <span
            style={{
              ...styles.serviceNotificationLine,
              ...styles.serviceNotificationLineLeft,
              background: SERVICE_NOTIFICATION_ACCENT.vertical,
            }}
          />
          <span
            style={{
              ...styles.serviceNotificationLine,
              ...styles.serviceNotificationLineRight,
              background: SERVICE_NOTIFICATION_ACCENT.vertical,
              animationDuration: "4.8s",
              animationDelay: "-1.6s",
            }}
          />
        </div>
        <div style={styles.panelHeader}>
          <div>
            <span style={{ ...styles.eyebrow, ...styles.serviceEyebrow }}>
              ACTIVIDAD EN VIVO
            </span>
            <h2 style={styles.panelTitle}>Pedidos</h2>
          </div>
          <span
            style={{
              ...styles.panelPill,
              ...(operationalState.isOpen ? {} : styles.panelPillClosed),
            }}
          >
            {operationalState.isOpen
              ? notificaciones.length === 1
                ? "1 nuevo"
                : `${notificaciones.length} nuevos`
              : "Recepcion pausada"}
          </span>
        </div>

        {!operationalState.isOpen && (
          <div style={styles.servicePausedNotice}>
            Fuera de horario no se toman pedidos nuevos. Desde aqui solo puedes terminar de
            preparar, entregar y cerrar los pedidos que quedaron en curso.
          </div>
        )}

        {notificaciones.length > 0 ? (
          <div style={styles.notificationGrid}>
            {notificaciones.map((mesa) => (
              <article key={mesa.id} style={styles.notificationCard}>
                <div style={styles.detailCardHeader}>
                  <div>
                    <strong style={styles.detailTitle}>{mesa.mesa}</strong>
                    <div style={styles.detailMeta}>
                      {getMesaOperationalLabel(mesa)} · nuevo pedido hace {formatElapsed(mesa.orderedAt)}
                    </div>
                  </div>
                  <span style={{ ...styles.elapsedBadge, ...getElapsedTone(mesa.orderedAt) }}>
                    {formatElapsed(mesa.orderedAt)}
                  </span>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Pedido ingresado</span>
                  <div style={styles.detailText}>{getMesaOrderSummary(mesa)}</div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Forma de pago</span>
                  <div style={styles.detailText}>{getMesaPaymentMethodLabel(mesa)}</div>
                  <div style={styles.detailMeta}>{getMesaPaymentStatusLabel(mesa)}</div>
                </div>

                {isMesaDigitalPaymentPendingForKitchen(mesa) ? (
                  <div style={styles.detailBlock}>
                    <span style={styles.detailLabel}>Cobro digital</span>
                    <div style={styles.detailText}>
                      Esperando confirmacion del pago QR antes de mandarlo a cocina.
                    </div>
                  </div>
                ) : null}

                <div style={styles.actionRow}>
                  <button
                    type="button"
                    style={styles.primaryInlineButton}
                    onClick={() => progressInitialOrder(mesa)}
                    disabled={isInitialOrderProgressDisabled(mesa)}
                  >
                    {getInitialOrderProgressLabel(mesa)}
                  </button>
                  {hasMixedKitchenAndDirectDelivery(mesa) &&
                  getMesaPendingDirectDeliveryItems(mesa).some(
                    (item) =>
                      !["assigned", "ready"].includes(String(item.deliveryStatus || ""))
                  ) ? (
                    <button
                      type="button"
                      style={styles.directDeliveryActionButton}
                      onClick={() => requestDirectItemDelivery(mesa.id)}
                      disabled={!canModifyOrders}
                    >
                      Entregar bebidas
                    </button>
                  ) : null}
                  <button
                    type="button"
                    style={styles.secondaryInlineButton}
                    onClick={() => setNotificationDetailMesaId(mesa.id)}
                  >
                    Ver detalle
                  </button>
                  <button
                    type="button"
                    style={styles.dangerInlineButton}
                    onClick={() => requestCancelOrder(mesa.id)}
                    disabled={!canCancelOrders}
                  >
                    Cancelar
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div style={styles.emptyNotice}>No hay nuevos pedidos por revisar ahora mismo.</div>
        )}
      </section>

      {selectedManualOrderProduct ? (
        <div
          style={styles.manualProductModalOverlay}
          onClick={closeManualProductConfigurator}
        >
          <div
            style={styles.manualProductModalCard}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Configurar ${selectedManualOrderProduct.nombre}`}
          >
            <div style={styles.mesaDetailModalHeader}>
              <div>
                <h2 style={styles.panelTitle}>{selectedManualOrderProduct.nombre}</h2>
                <p style={styles.mesaDetailModalSubtitle}>
                  Armá esta variante por separado. Si necesitás otra distinta, la volvés a cargar
                  después como una nueva línea.
                </p>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                onClick={closeManualProductConfigurator}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.manualProductModalBody}>
              <div style={styles.catalogInlineActions}>
                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Cantidad de esta variante</span>
                  <input
                    type="number"
                    min="1"
                    value={manualOrderForm.menuProductQuantity}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({
                        ...prev,
                        menuProductQuantity: event.target.value,
                      }))
                    }
                    style={styles.manualInput}
                  />
                </label>
                <div style={styles.manualCustomizationSummary}>
                  {selectedManualProductSummary ? (
                    <span style={styles.manualCustomizationChipStrong}>
                      {formatCurrency(selectedManualProductSummary.extraCost || 0)} extra por unidad
                    </span>
                  ) : (
                    <span style={styles.manualCustomizationChip}>Version base</span>
                  )}
                  <span style={styles.metricChip}>
                    Total variante: {formatCurrency(selectedManualProductTotal)}
                  </span>
                  <button
                    type="button"
                    style={styles.secondaryInlineButton}
                    onClick={resetManualCustomization}
                    disabled={!canTakeOrders}
                  >
                    Restablecer
                  </button>
                </div>
              </div>

              {selectedManualOrderProduct.ingredients?.length > 0 ? (
                <div style={styles.manualCustomizationPanel}>
                  <div style={styles.manualCustomizationHeader}>
                    <div>
                      <strong style={styles.detailTitle}>Personalizacion</strong>
                      <div style={styles.detailMeta}>
                        Ajusta ingredientes igual que en el QR del comensal.
                      </div>
                    </div>
                  </div>

                  <div style={styles.manualIngredientList}>
                    {selectedManualOrderProduct.ingredients.map((ingredient) => {
                      const quantity = Number(
                        manualOrderSelection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
                      );

                      return (
                        <div key={ingredient.id} style={styles.manualIngredientRow}>
                          {ingredient.imageUrl || ingredient.imageDeferred ? (
                            <IngredientImage ingredient={ingredient} style={styles.manualIngredientImage} />
                          ) : null}
                          <div style={styles.manualIngredientInfo}>
                            <div style={styles.manualIngredientNameRow}>
                              <strong style={styles.searchResultName}>{ingredient.name}</strong>
                              <span style={styles.manualIngredientState}>
                                {getManualIngredientStateLabel(ingredient, quantity)}
                              </span>
                            </div>
                            <span style={styles.detailMeta}>
                              Base {ingredient.baseQuantity || 0}
                              {ingredient.price > 0
                                ? ` · Extra ${formatCurrency(ingredient.price)}`
                                : ""}
                            </span>
                          </div>

                          {ingredient.canRemove || ingredient.canAddMore ? (
                            <div style={styles.manualIngredientControls}>
                              <button
                                type="button"
                                style={styles.manualQuantityButton}
                                onClick={() =>
                                  updateManualOrderIngredientQuantity(ingredient, -1)
                                }
                                disabled={!canTakeOrders}
                              >
                                -
                              </button>
                              <span style={styles.manualQuantityValue}>{quantity}</span>
                              <button
                                type="button"
                                style={styles.manualQuantityButton}
                                onClick={() =>
                                  updateManualOrderIngredientQuantity(ingredient, 1)
                                }
                                disabled={!canTakeOrders}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <span style={styles.fixedChip}>Fijo</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={styles.emptyNotice}>
                  Este plato no tiene ingredientes configurables cargados.
                </div>
              )}

              {selectedManualPizzaSizeOptions.length > 0 ? (
                <div style={styles.manualCustomizationPanel}>
                  <div style={styles.manualCustomizationHeader}>
                    <div>
                      <strong style={styles.detailTitle}>Tamano de pizza</strong>
                      <div style={styles.detailMeta}>
                        Primero elegi la presentacion. Luego, si corresponde,
                        combina mitad y mitad.
                      </div>
                    </div>
                    <span style={styles.manualCustomizationChipStrong}>
                      Obligatorio
                    </span>
                  </div>

                  <div style={styles.manualIngredientList}>
                    {selectedManualPizzaSizeOptions.map((option) => {
                      const isActive = selectedManualPizzaSizeOption?.id === option.id;

                      return (
                        <button
                          key={`manual-pizza-size:${option.id}`}
                          type="button"
                          style={{
                            ...styles.manualIngredientRow,
                            borderColor: isActive ? "#2563eb" : "#e2e8f0",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setManualOrderPizzaSizeOptionId(option.id);
                            setManualOrderPizzaPortionMode(getDefaultPizzaSaleMode(option));
                            setManualOrderPizzaHalfProductId("");
                          }}
                          disabled={!canTakeOrders}
                        >
                          <div style={styles.manualIngredientInfo}>
                            <strong style={styles.searchResultName}>
                              {option.label}
                            </strong>
                            <span style={styles.detailMeta}>
                              {getPizzaSizePriceSummary(option, formatCurrency)}
                            </span>
                          </div>
                          <span style={styles.metricChip}>
                            {formatCurrency(getPizzaStartingPrice(option))}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {selectedManualPizzaSizeOption && <PizzaSaleModeSelector option={selectedManualPizzaSizeOption}
                value={manualOrderPizzaPortionMode} disabled={!canTakeOrders}
                canCombine={selectedManualPizzaHalfOptions.length > 0} onChange={(mode) => {
                  setManualOrderPizzaPortionMode(mode); setManualOrderPizzaHalfProductId("");
                }} />}
              {manualOrderPizzaPortionMode === "half_and_half" && selectedManualPizzaHalfOptions.length > 0 ? (
                <div style={styles.manualCustomizationPanel}>
                  <div style={styles.manualCustomizationHeader}>
                    <div>
                      <strong style={styles.detailTitle}>Mitad y mitad</strong>
                      <div style={styles.detailMeta}>
                        Solo se ofrecen sabores con mitad y mitad habilitada para
                        este tamano. Se suma el precio de cada mitad.
                      </div>
                    </div>
                    <span style={styles.manualCustomizationChipStrong}>Opcional</span>
                  </div>

                  <div style={styles.manualIngredientList}>

                    {selectedManualPizzaHalfOptions.map((option) => {
                      const isActive = manualOrderPizzaHalfProductId === option.id;
                      const optionPricing = getPizzaHalfAndHalfPricing(
                        selectedManualOrderProduct,
                        option,
                        { sizeOptionId: selectedManualPizzaSizeOption?.id || "" }
                      );
                      const optionExtraPrice = optionPricing.extraUnitPrice;

                      return (
                        <button
                          key={`manual-pizza-half:${option.id}`}
                          type="button"
                          style={{
                            ...styles.manualIngredientRow,
                            borderColor: isActive ? "#2563eb" : "#e2e8f0",
                            cursor: "pointer",
                          }}
                          onClick={() => setManualOrderPizzaHalfProductId(option.id)}
                          disabled={!canTakeOrders}
                        >
                          <div style={styles.manualIngredientInfo}>
                            <strong style={styles.searchResultName}>
                              1/2 {getPizzaProductName(option)}
                            </strong>
                            <span style={styles.detailMeta}>
                              {option.descripcion ||
                                option.description ||
                                `Total mitad y mitad: ${formatCurrency(optionPricing.totalUnitPrice)}`}
                            </span>
                          </div>
                          <span style={styles.metricChip}>
                            {optionExtraPrice > 0
                              ? `+ ${formatCurrency(optionExtraPrice)}`
                              : "Sin adicional"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div style={styles.manualProductModalFooter}>
                <button
                  type="button"
                  style={styles.secondaryInlineButton}
                  onClick={closeManualProductConfigurator}
                >
                  Seguir buscando
                </button>
                <button
                  type="button"
                  style={styles.primaryInlineButton}
                  onClick={addManualOrderItemFromMenu}
                  disabled={!canTakeOrders}
                >
                  Agregar esta variante
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deliveryAssignmentMesa ? (
        <div style={styles.mesaDetailModalOverlay} onClick={closeDeliveryAssignmentModal}>
          <div
            style={styles.assignmentModalCard}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Asignar mozo a ${deliveryAssignmentMesa.mesa}`}
          >
            <div style={styles.mesaDetailModalHeader}>
              <div>
                <h2 style={styles.panelTitle}>
                  {isDirectItemDeliveryAssignment
                    ? `Asignar entrega directa para ${deliveryAssignmentMesa.mesa}`
                    : `Asignar entrega para ${deliveryAssignmentMesa.mesa}`}
                </h2>
                <p style={styles.mesaDetailModalSubtitle}>
                  {isDirectItemDeliveryAssignment
                    ? deliveryAvailableWaiters.length
                      ? "Estas bebidas o productos pueden salir ahora, sin esperar la comida. Asignalos a un mozo para registrar quien los entrega."
                      : "No hay mozos conectados ahora. Puedes dejar estos productos listos y confirmar su entrega manualmente desde salon."
                    : deliveryAvailableWaiters.length
                      ? "El pedido ya esta listo. Elige si quieres asignar automaticamente al mozo con menos mesas activas o decidirlo manualmente."
                      : "No hay mozos conectados ahora. Puedes dejar este pedido listo y resolver la entrega manualmente desde salon."}
                </p>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                onClick={closeDeliveryAssignmentModal}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.mesaDetailModalBody}>
              {loadingConnectedWaiters || assigningConnectedWaiter ? (
                <p role="status" style={styles.detailText}>Verificando mozos conectados...</p>
              ) : null}
              {connectedWaitersError ? (
                <div role="alert" style={styles.assignmentManualDeliveryNotice}>
                  {connectedWaitersError}
                  <button type="button" style={styles.secondaryInlineButton} onClick={refreshConnectedWaiters} disabled={assigningConnectedWaiter}>Reintentar</button>
                </div>
              ) : null}
              {isDirectItemDeliveryAssignment ? (
                <div style={styles.directDeliveryAssignmentSummary}>
                  <span style={styles.detailLabel}>Entrega inmediata</span>
                  <strong>{getDirectDeliveryItemsSummary(deliveryAssignmentItems)}</strong>
                </div>
              ) : null}
              <div style={styles.assignmentPanel}>
                {deliveryAvailableWaiters.length ? (
                  <>
                    <div style={styles.assignmentAutoCard}>
                      <strong style={styles.detailTitle}>Asignacion automatica</strong>
                      <div style={styles.detailText}>
                        Se toma al mozo con menos mesas activas. Si hay empate, se elige al azar.
                      </div>
                      <button
                        type="button"
                        style={styles.primaryInlineButton}
                        onClick={handleAutoAssignReadyOrder}
                        disabled={!canModifyOrders || assigningConnectedWaiter}
                      >
                        Asignar automaticamente
                      </button>
                    </div>

                    <div style={styles.assignmentManualCard}>
                      <strong style={styles.detailTitle}>Asignacion manual</strong>
                      <div style={styles.assignmentWaiterList}>
                        {deliveryAvailableWaiters.map((waiter) => {
                          const waiterName = getWaiterDisplayName(waiter);
                          const tableCount = getWaiterOpenTableCount(
                            waiter.userId,
                            deliveryAssignmentMesa.tableId || ""
                          );

                          return (
                            <label
                              key={waiter.userId}
                              style={{
                                ...styles.assignmentWaiterRow,
                                ...(deliveryAssignmentWaiterId === waiter.userId
                                  ? styles.assignmentWaiterRowActive
                                  : {}),
                              }}
                            >
                              <input
                                type="radio"
                                name="delivery-waiter"
                                value={waiter.userId}
                                checked={deliveryAssignmentWaiterId === waiter.userId}
                                onChange={(event) =>
                                  setDeliveryAssignmentWaiterId(event.target.value)
                                }
                                disabled={!canModifyOrders || assigningConnectedWaiter}
                              />
                              <div style={styles.assignmentWaiterInfo}>
                                <strong>{waiterName}</strong>
                                <span style={styles.detailMeta}>
                                  {tableCount} mesa{tableCount === 1 ? "" : "s"} activa
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <div style={styles.formInlineActions}>
                        <button
                          type="button"
                          style={styles.secondaryInlineButton}
                          onClick={closeDeliveryAssignmentModal}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          style={styles.primaryInlineButton}
                          onClick={handleManualAssignReadyOrder}
                          disabled={!deliveryAssignmentWaiterId || !canModifyOrders || assigningConnectedWaiter}
                        >
                          Asignar mozo
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={styles.assignmentManualDeliveryCard}>
                    <strong style={styles.detailTitle}>Entrega manual</strong>
                    <div style={styles.detailText}>
                      {isDirectItemDeliveryAssignment
                        ? "Los productos seleccionados quedaran listos para entregar, sin alterar el avance de la comida en cocina."
                        : "El pedido quedara marcado como listo para entregar, sin mozo asignado. Luego podras entregarlo manualmente o volver a asignar un mozo cuando haya uno disponible."}
                    </div>
                    <div style={styles.assignmentManualDeliveryNotice}>
                      {isDirectItemDeliveryAssignment
                        ? "Usa esta opcion cuando salon vaya a entregar las bebidas directamente."
                        : "Usa esta opcion cuando cocina o caja vayan a resolver la entrega desde el salon."}
                    </div>
                    <div style={styles.formInlineActions}>
                      <button
                        type="button"
                        style={styles.secondaryInlineButton}
                        onClick={closeDeliveryAssignmentModal}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        style={styles.primaryInlineButton}
                        onClick={() =>
                          isDirectItemDeliveryAssignment
                            ? markDirectItemsReadyForManualDelivery(
                                deliveryAssignmentMesa,
                                deliveryAssignmentItems.map((item) => item.id)
                              )
                            : markOrderReadyForManualDelivery(deliveryAssignmentMesa)
                        }
                        disabled={!canModifyOrders || assigningConnectedWaiter}
                      >
                        {isDirectItemDeliveryAssignment
                          ? "Dejar listo para entrega manual"
                          : "Registrar entrega manual"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {waiterRequestAssignment ? (
        <div style={styles.mesaDetailModalOverlay} onClick={closeWaiterRequestAssignmentModal}>
          <div
            style={styles.assignmentModalCard}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Asignar mozo a ${waiterRequestAssignment.mesa}`}
          >
            <div style={styles.mesaDetailModalHeader}>
              <div>
                <h2 style={styles.panelTitle}>Asignar mozo para {waiterRequestAssignment.mesa}</h2>
                <p style={styles.mesaDetailModalSubtitle}>
                  El cliente pidio ayuda desde el QR. Elige si quieres asignar automaticamente al
                  mozo con menos llamados activos o decidirlo manualmente.
                </p>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                onClick={closeWaiterRequestAssignmentModal}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.mesaDetailModalBody}>
              {loadingConnectedWaiters || assigningConnectedWaiter ? (
                <p role="status" style={styles.detailText}>Verificando mozos conectados...</p>
              ) : null}
              {connectedWaitersError ? <p role="alert" style={styles.assignmentManualDeliveryNotice}>{connectedWaitersError}</p> : null}
              {!loadingConnectedWaiters && !deliveryAvailableWaiters.length ? <p style={styles.detailText}>No hay mozos conectados ahora.</p> : null}
              <div style={styles.detailBlock}>
                <span style={styles.detailLabel}>Cliente solicita</span>
                <div style={styles.detailText}>
                  {waiterRequestAssignment.summary || "Cliente pide ayuda del mozo."}
                </div>
              </div>

              <div style={styles.assignmentPanel}>
                <div style={styles.assignmentAutoCard}>
                  <strong style={styles.detailTitle}>Asignacion automatica</strong>
                  <div style={styles.detailText}>
                    Se toma al mozo con menos llamados activos. Si hay empate, se elige al azar.
                  </div>
                  <button
                    type="button"
                    style={styles.primaryInlineButton}
                    onClick={handleAutoAssignWaiterRequest}
                    disabled={!canModifyOrders || !deliveryAvailableWaiters.length || assigningConnectedWaiter}
                  >
                    Asignar automaticamente
                  </button>
                </div>

                <div style={styles.assignmentManualCard}>
                  <strong style={styles.detailTitle}>Asignacion manual</strong>
                  <div style={styles.assignmentWaiterList}>
                    {deliveryAvailableWaiters.map((waiter) => {
                      const waiterName = getWaiterDisplayName(waiter);
                      const requestCount = getWaiterPendingRequestCount(
                        waiter.userId,
                        waiterRequestAssignment.id
                      );

                      return (
                        <label
                          key={waiter.userId}
                          style={{
                            ...styles.assignmentWaiterRow,
                            ...(waiterRequestAssignmentWaiterId === waiter.userId
                              ? styles.assignmentWaiterRowActive
                              : {}),
                          }}
                        >
                          <input
                            type="radio"
                            name="waiter-help-waiter"
                            value={waiter.userId}
                            checked={waiterRequestAssignmentWaiterId === waiter.userId}
                            onChange={(event) =>
                              setWaiterRequestAssignmentWaiterId(event.target.value)
                            }
                            disabled={!canModifyOrders || assigningConnectedWaiter}
                          />
                          <div style={styles.assignmentWaiterInfo}>
                            <strong>{waiterName}</strong>
                            <span style={styles.detailMeta}>
                              {requestCount} llamado{requestCount === 1 ? "" : "s"} activo
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div style={styles.formInlineActions}>
                    <button
                      type="button"
                      style={styles.secondaryInlineButton}
                      onClick={closeWaiterRequestAssignmentModal}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      style={styles.primaryInlineButton}
                      onClick={handleManualAssignWaiterRequest}
                      disabled={!waiterRequestAssignmentWaiterId || !canModifyOrders || assigningConnectedWaiter}
                    >
                      Asignar mozo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {notificationDetailMesa ? (
        <div style={styles.mesaDetailModalOverlay} onClick={closeNotificationDetailModal}>
          <div
            style={styles.mesaDetailModalCard}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Detalle de ${notificationDetailMesa.mesa}`}
          >
            <div style={styles.mesaDetailModalHeader}>
              <div>
                <h2 style={styles.panelTitle}>Detalle de {notificationDetailMesa.mesa}</h2>
                <p style={styles.mesaDetailModalSubtitle}>
                  Aqui puedes revisar el pedido puntual, su total final y avanzar el servicio sin
                  salir de notificaciones.
                </p>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                onClick={closeNotificationDetailModal}
              >
                Cerrar
              </button>
            </div>

            <div style={styles.mesaDetailModalBody}>
              {renderMesaDetailCard(notificationDetailMesa, { compact: true })}
            </div>
          </div>
        </div>
      ) : null}

      {detalleActivo === "salon" ? (
        <div
          style={styles.mesaDetailModalOverlay}
          onClick={() => {
            setDetalleActivo(null);
            setSalonSelectedTableId("");
            setSalonSelectedFixtureId("");
            setSalonEditMode(false);
            setSalonRoomsManagerOpen(false);
            setSalonNewRoomName("");
            setSalonReservationsOpen(false);
            setSalonReservationTableId("");
            setSalonReservationEditId("");
            setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
          }}
        >
          <div
            className="inicio-salon-modal"
            style={{ ...styles.mesaDetailModalCard, ...styles.salonModalCard }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Plano operativo del salon"
          >
            <div style={styles.salonModalHeader}>
              <div>
                <span style={styles.salonEyebrow}>GESTION DEL SALON</span>
                <h2 style={styles.salonModalTitle}>Plano operativo</h2>
                <p style={styles.mesaDetailModalSubtitle}>
                  {salonOperationalCount} ocupadas de {salonVisibleTables.length} mesas en {activeSalonRoom.name}.
                  {" "}{normalizedSalonRooms.length} {normalizedSalonRooms.length === 1 ? "ambiente" : "ambientes"} en total.
                </p>
                <div style={styles.salonLegend}>
                  <span style={styles.salonLegendItem}>
                    <i style={{ ...styles.salonLegendDot, ...styles.salonLegendDotFree }} />
                    Libre
                  </span>
                  <span style={styles.salonLegendItem}>
                    <i style={{ ...styles.salonLegendDot, ...styles.salonLegendDotOperational }} />
                    Ocupada
                  </span>
                  <span style={styles.salonLegendItem}>
                    <i style={{ ...styles.salonLegendDot, ...styles.salonLegendDotReserved }} />
                    Reservada
                  </span>
                </div>
              </div>
              <div style={styles.salonHeaderActions}>
                <button
                  type="button"
                  style={{
                    ...styles.salonToolbarButton,
                    ...(salonReservationsOpen ? styles.salonToolbarButtonActive : {}),
                  }}
                  onClick={() => setSalonReservationsOpen((current) => !current)}
                >
                  Agenda de reservas ({salonReservationAgenda.length})
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.salonToolbarButton,
                    ...(salonRoomsManagerOpen ? styles.salonToolbarButtonActive : {}),
                  }}
                  onClick={() => setSalonRoomsManagerOpen((current) => !current)}
                >
                  {salonRoomsManagerOpen ? "Cerrar ambientes" : "Gestionar ambientes"}
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.salonToolbarButton,
                    ...(salonEditMode ? styles.salonToolbarButtonActive : {}),
                  }}
                  onClick={() =>
                    setSalonEditMode((current) => {
                      if (current) {
                        setSalonSelectedFixtureId("");
                      }
                      return !current;
                    })
                  }
                >
                  {salonEditMode ? "Terminar organizacion" : "Organizar plano"}
                </button>
                {salonEditMode ? (
                  <button
                    type="button"
                    style={styles.salonToolbarButton}
                    onClick={resetSalonLayout}
                  >
                    Restablecer este plano
                  </button>
                ) : null}
                <button
                  type="button"
                  style={styles.closeButton}
                  onClick={() => {
                    setDetalleActivo(null);
                    setSalonSelectedTableId("");
                    setSalonSelectedFixtureId("");
                    setSalonEditMode(false);
                    setSalonRoomsManagerOpen(false);
                    setSalonNewRoomName("");
                    setSalonReservationsOpen(false);
                    setSalonReservationTableId("");
                    setSalonReservationEditId("");
                    setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>

            <nav style={styles.salonRoomTabs} aria-label="Salones y patios del restaurante">
              {salonRoomStats.map((room) => (
                <button
                  key={room.id}
                  type="button"
                  style={{
                    ...styles.salonRoomTab,
                    ...(room.id === resolvedSalonActiveRoomId ? styles.salonRoomTabActive : {}),
                  }}
                  onClick={() => {
                    setSalonActiveRoomId(room.id);
                    setSalonSelectedTableId("");
                    setSalonSelectedFixtureId("");
                    setSalonReservationTableId("");
                    setSalonReservationEditId("");
                    setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
                  }}
                >
                  <strong style={styles.salonRoomTabName}>{room.name}</strong>
                  <span style={styles.salonRoomTabMeta}>
                    {room.operationalCount}/{room.tableCount} ocupadas
                  </span>
                </button>
              ))}
            </nav>

            {salonReservationsOpen ? (
              <section style={styles.salonReservationAgenda}>
                <div style={styles.salonReservationAgendaHeader}>
                  <div>
                    <span style={styles.salonDetailEyebrow}>AGENDA OPERATIVA</span>
                    <strong style={styles.salonReservationAgendaTitle}>
                      Reservas proximas
                    </strong>
                  </div>
                  <span style={styles.salonReservationAgendaCount}>
                    {salonReservationAgenda.length}
                  </span>
                </div>

                {salonReservationAgenda.length ? (
                  <div style={styles.salonReservationAgendaList}>
                    {salonReservationAgenda.map((item) => {
                      const isBlocking = isSalonReservationBlockingTable(
                        item.reservation,
                        currentTime
                      );
                      const reservationWindow = getSalonReservationWindow(
                        item.reservation
                      );

                      return (
                        <article
                          key={item.reservation.id}
                          style={{
                            ...styles.salonReservationAgendaItem,
                            ...(isBlocking
                              ? styles.salonReservationAgendaItemBlocking
                              : {}),
                          }}
                        >
                          <div style={styles.salonReservationAgendaItemHeader}>
                            <div>
                              <span style={styles.salonDetailEyebrow}>
                                {isBlocking ? "MESA BLOQUEADA" : "PROGRAMADA"}
                              </span>
                              <strong style={styles.salonReservationName}>
                                {item.reservation.customerName}
                              </strong>
                            </div>
                            <strong style={styles.salonReservationDate}>
                              {formatSalonReservationDateTime(
                                item.reservation.reservedFor
                              )}
                            </strong>
                          </div>
                          <span style={styles.salonReservationAgendaMeta}>
                            {item.tableLabel} - {item.roomName} - {item.reservation.partySize} personas
                          </span>
                          <span style={styles.salonReservationAgendaMeta}>
                            Bloquea desde {formatSalonReservationDateTime(reservationWindow.startsAt)}
                            {" "}({formatSalonReservationBlockLead(
                              item.reservation.blockMinutesBefore
                            )}) - Duracion {Math.max(
                              30,
                              Number(item.reservation.durationMinutes || 120)
                            )} min
                          </span>
                          {item.reservation.notes ? (
                            <p style={styles.salonReservationNotes}>
                              {item.reservation.notes}
                            </p>
                          ) : null}
                          {item.table ? (
                            <div style={styles.salonReservationActions}>
                              <button
                                type="button"
                                style={styles.salonReservationEditButton}
                                onClick={() => {
                                  setSalonActiveRoomId(item.roomId);
                                  setSalonSelectedTableId(item.table.id);
                                  setSalonReservationsOpen(false);
                                }}
                              >
                                Ver mesa
                              </button>
                              <button
                                type="button"
                                style={styles.salonReservationEditButton}
                                onClick={() => {
                                  setSalonActiveRoomId(item.roomId);
                                  setSalonReservationsOpen(false);
                                  openSalonReservationForm(item.table, item.reservation);
                                }}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                style={styles.salonReservationDeleteButton}
                                onClick={() =>
                                  cancelSalonReservation(item.table, item.reservation)
                                }
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div style={styles.salonReservationAgendaEmpty}>
                    No hay reservas futuras para este restaurante.
                  </div>
                )}
              </section>
            ) : null}

            {salonRoomsManagerOpen ? (
              <section style={styles.salonRoomsManager}>
                <div style={styles.salonRoomsManagerHeader}>
                  <div>
                    <strong style={styles.salonRoomsManagerTitle}>Ambientes del restaurante</strong>
                    <span style={styles.salonRoomsManagerHint}>
                      Agrega salones o patios y asigna cada mesa al ambiente correspondiente.
                    </span>
                  </div>
                  <div style={styles.salonRoomCreateForm}>
                    <input
                      type="text"
                      value={salonNewRoomName}
                      maxLength={60}
                      placeholder="Ejemplo: Patio interno"
                      style={styles.salonRoomInput}
                      onChange={(event) => setSalonNewRoomName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addSalonRoom();
                        }
                      }}
                    />
                    <button
                      type="button"
                      style={styles.salonRoomAddButton}
                      onClick={addSalonRoom}
                    >
                      + Agregar ambiente
                    </button>
                  </div>
                </div>

                <div style={styles.salonRoomList}>
                  {salonRoomStats.map((room) => (
                    <article key={room.id} style={styles.salonRoomListItem}>
                      <div style={styles.salonRoomListInfo}>
                        <strong>{room.name}</strong>
                        <span>
                          {room.tableCount} {room.tableCount === 1 ? "mesa" : "mesas"} · {room.operationalCount} ocupadas
                        </span>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.salonRoomDeleteButton,
                          ...(normalizedSalonRooms.length <= 1
                            ? styles.salonRoomDeleteButtonDisabled
                            : {}),
                        }}
                        disabled={normalizedSalonRooms.length <= 1}
                        onClick={() => removeSalonRoom(room)}
                      >
                        Eliminar
                      </button>
                    </article>
                  ))}
                </div>

                <div style={styles.salonTableAssignments}>
                  {salonTables.length ? (
                    salonTables.map((table) => (
                      <label key={table.id} style={styles.salonTableAssignmentCard}>
                        <span style={styles.salonTableAssignmentLabel}>{table.label}</span>
                        <select
                          value={salonResolvedTableRooms[table.id] || resolvedSalonActiveRoomId}
                          style={styles.salonTableAssignmentSelect}
                          onChange={(event) => assignTableToSalon(table.id, event.target.value)}
                        >
                          {normalizedSalonRooms.map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))
                  ) : (
                    <div style={styles.salonEmptyState}>
                      Primero configura las mesas de esta sucursal.
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            <div className="inicio-salon-modal-body" style={styles.salonModalBody}>
              <section style={styles.salonFloorSection}>
                <div style={styles.salonFloorToolbar}>
                  <div>
                    <strong style={styles.salonFloorTitle}>Plano de {activeSalonRoom.name}</strong>
                    <span style={styles.salonFloorHint}>
                      {salonEditMode
                        ? "Arrastra mesas y elementos hasta su ubicacion real. El plano se guarda automaticamente."
                        : "Selecciona una mesa para ver sus acciones y cuentas activas."}
                    </span>
                  </div>
                  {salonEditMode ? (
                    <span style={styles.salonEditBadge}>MODO ORGANIZACION</span>
                  ) : null}
                </div>

                {salonEditMode ? (
                  <div style={styles.salonFixtureWorkspace}>
                    <div style={styles.salonFixtureToolbar}>
                      <span style={styles.salonFixtureToolbarLabel}>Agregar al plano</span>
                      {SALON_FIXTURE_DEFINITIONS.map((definition) => (
                        <button
                          key={definition.type}
                          type="button"
                          style={styles.salonFixtureAddButton}
                          onClick={() => addSalonFixture(definition.type)}
                        >
                          + {definition.label}
                        </button>
                      ))}
                    </div>

                    {selectedSalonFixture ? (
                      <div style={styles.salonFixtureEditor}>
                        <div style={styles.salonFixtureEditorIdentity}>
                          <span style={styles.salonFixtureEditorEyebrow}>ELEMENTO SELECCIONADO</span>
                          <strong style={styles.salonFixtureEditorTitle}>
                            {selectedSalonFixture.label}
                          </strong>
                        </div>
                        {selectedSalonFixtureIsDoor ? (
                          <div style={styles.salonFixtureOrientationField}>
                            <span style={styles.salonFixtureOrientationLabel}>Orientacion</span>
                            <div style={styles.salonFixtureOrientationButtons}>
                              <button
                                type="button"
                                style={{
                                  ...styles.salonFixtureOrientationButton,
                                  ...(!selectedSalonFixtureIsVertical
                                    ? styles.salonFixtureOrientationButtonActive
                                    : {}),
                                }}
                                onClick={() =>
                                  setSalonDoorFixtureOrientation(
                                    selectedSalonFixture,
                                    "horizontal"
                                  )
                                }
                              >
                                Horizontal
                              </button>
                              <button
                                type="button"
                                style={{
                                  ...styles.salonFixtureOrientationButton,
                                  ...(selectedSalonFixtureIsVertical
                                    ? styles.salonFixtureOrientationButtonActive
                                    : {}),
                                }}
                                onClick={() =>
                                  setSalonDoorFixtureOrientation(
                                    selectedSalonFixture,
                                    "vertical"
                                  )
                                }
                              >
                                Vertical
                              </button>
                            </div>
                          </div>
                        ) : null}
                        <label style={styles.salonFixtureSizeField}>
                          <span>Ancho</span>
                          <input
                            type="range"
                            min={selectedSalonFixtureIsDoor ? "4" : "8"}
                            max={selectedSalonFixtureIsVertical ? "5" : "65"}
                            step="1"
                            value={selectedSalonFixture.width}
                            style={styles.salonFixtureRange}
                            onChange={(event) => {
                              const width = clampSalonFixtureSize(
                                event.target.value,
                                "width",
                                selectedSalonFixture.type
                              );
                              updateSalonFixture(
                                selectedSalonFixture.id,
                                {
                                  width,
                                  x: clampSalonFixturePosition(selectedSalonFixture.x, width),
                                },
                                { persist: false }
                              );
                            }}
                            onPointerUp={() =>
                              updateSalonFixture(selectedSalonFixture.id, {})
                            }
                            onKeyUp={() => updateSalonFixture(selectedSalonFixture.id, {})}
                            onBlur={() => updateSalonFixture(selectedSalonFixture.id, {})}
                          />
                          <strong>{Math.round(selectedSalonFixture.width)}%</strong>
                        </label>
                        <label style={styles.salonFixtureSizeField}>
                          <span>Alto</span>
                          <input
                            type="range"
                            min="5"
                            max="40"
                            step="1"
                            value={selectedSalonFixture.height}
                            style={styles.salonFixtureRange}
                            onChange={(event) => {
                              const height = clampSalonFixtureSize(
                                event.target.value,
                                "height",
                                selectedSalonFixture.type
                              );
                              updateSalonFixture(
                                selectedSalonFixture.id,
                                {
                                  height,
                                  y: clampSalonFixturePosition(selectedSalonFixture.y, height),
                                },
                                { persist: false }
                              );
                            }}
                            onPointerUp={() =>
                              updateSalonFixture(selectedSalonFixture.id, {})
                            }
                            onKeyUp={() => updateSalonFixture(selectedSalonFixture.id, {})}
                            onBlur={() => updateSalonFixture(selectedSalonFixture.id, {})}
                          />
                          <strong>{Math.round(selectedSalonFixture.height)}%</strong>
                        </label>
                        <button
                          type="button"
                          style={styles.salonFixtureDeleteButton}
                          onClick={() => removeSalonFixture(selectedSalonFixture)}
                        >
                          Eliminar del plano
                        </button>
                      </div>
                    ) : (
                      <span style={styles.salonFixtureHelper}>
                        Agrega un elemento o selecciona uno del plano para ajustar su tamano.
                      </span>
                    )}

                    <div style={styles.salonTableFormatPanel}>
                      <div style={styles.salonTableFormatHeader}>
                        <div>
                          <span style={styles.salonTableFormatEyebrow}>
                            FORMATO GENERAL DE MESAS
                          </span>
                          <strong style={styles.salonTableFormatTitle}>
                            Forma y tamano base
                          </strong>
                          <span style={styles.salonTableFormatDescription}>
                            Aplica el mismo formato al ambiente actual o a todas las mesas del restaurante.
                          </span>
                        </div>
                      </div>

                      <div style={styles.salonTableShapeButtons}>
                        {SALON_TABLE_SHAPES.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            style={{
                              ...styles.salonTableShapeButton,
                              ...(salonTableFormatDraft.shape === option.id
                                ? styles.salonTableShapeButtonActive
                                : {}),
                            }}
                            onClick={() =>
                              setSalonTableFormatDraft((current) =>
                                buildSalonTableShapeGeometry(current, option.id)
                              )
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>

                      <div style={styles.salonTableDimensionGrid}>
                        <label style={styles.salonTableDimensionField}>
                          <span style={styles.salonTableDimensionHeader}>
                            <span>
                              {salonTableFormatDraft.shape === "rectangle"
                                ? "Ancho"
                                : "Tamano"}
                            </span>
                            <strong>{Math.round(salonTableFormatDraft.width)} px</strong>
                          </span>
                          <input
                            type="range"
                            min={salonTableFormatDraft.shape === "rectangle" ? "80" : "64"}
                            max={salonTableFormatDraft.shape === "rectangle" ? "180" : "140"}
                            step="2"
                            value={salonTableFormatDraft.width}
                            style={styles.salonTableDimensionRange}
                            onChange={(event) =>
                              setSalonTableFormatDraft((current) =>
                                normalizeSalonTableGeometry({
                                  ...current,
                                  width: event.target.value,
                                })
                              )
                            }
                          />
                        </label>

                        {salonTableFormatDraft.shape === "rectangle" ? (
                          <label style={styles.salonTableDimensionField}>
                            <span style={styles.salonTableDimensionHeader}>
                              <span>Alto</span>
                              <strong>{Math.round(salonTableFormatDraft.height)} px</strong>
                            </span>
                            <input
                              type="range"
                              min="52"
                              max="140"
                              step="2"
                              value={salonTableFormatDraft.height}
                              style={styles.salonTableDimensionRange}
                              onChange={(event) =>
                                setSalonTableFormatDraft((current) =>
                                  normalizeSalonTableGeometry({
                                    ...current,
                                    height: event.target.value,
                                  })
                                )
                              }
                            />
                          </label>
                        ) : null}
                      </div>

                      {salonTableFormatDraft.shape === "rectangle" ? (
                        <button
                          type="button"
                          style={styles.salonTableRotationButton}
                          onClick={() =>
                            setSalonTableFormatDraft((current) =>
                              rotateSalonRectangleGeometry(current)
                            )
                          }
                        >
                          Rotar mesa:{" "}
                          {salonTableFormatDraft.rotation === 90
                            ? "vertical"
                            : "horizontal"}
                        </button>
                      ) : null}

                      <div style={styles.salonTableFormatActions}>
                        <button
                          type="button"
                          style={styles.salonTableFormatApplyButton}
                          onClick={() => applySalonTableFormat()}
                        >
                          Aplicar a {activeSalonRoom.name}
                        </button>
                        <button
                          type="button"
                          style={styles.salonTableFormatApplySecondary}
                          onClick={() => applySalonTableFormat({ allRooms: true })}
                        >
                          Aplicar a todo el restaurante
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div
                  ref={salonFloorRef}
                  className="inicio-salon-floor"
                  style={styles.salonFloor}
                >
                {activeSalonFixtures.map((fixture) => {
                  const isSelected = selectedSalonFixture?.id === fixture.id;
                  const isVerticalDoorFixture = isVerticalSalonDoorFixture(fixture);
                  const fixtureTypeStyle =
                    fixture.type === "bar"
                      ? styles.salonFixtureBar
                      : fixture.type === "counter"
                        ? styles.salonFixtureCounter
                        : fixture.type === "entrance"
                          ? styles.salonFixtureEntrance
                          : fixture.type === "exit"
                            ? styles.salonFixtureExit
                            : styles.salonFixtureKitchen;

                  return (
                    <button
                      key={fixture.id}
                      type="button"
                      style={{
                        ...styles.salonFixture,
                        ...fixtureTypeStyle,
                        ...(isVerticalDoorFixture
                          ? styles.salonFixtureVerticalDoor
                          : {}),
                        ...(isSelected ? styles.salonFixtureSelected : {}),
                        left: `${fixture.x}%`,
                        top: `${fixture.y}%`,
                        width: `${fixture.width}%`,
                        height: `${fixture.height}%`,
                        cursor: salonEditMode ? "grab" : "default",
                        pointerEvents: salonEditMode ? "auto" : "none",
                      }}
                      tabIndex={salonEditMode ? 0 : -1}
                      onClick={(event) => {
                        if (!salonEditMode) return;
                        event.stopPropagation();
                        setSalonSelectedFixtureId(fixture.id);
                        setSalonSelectedTableId("");
                      }}
                      onPointerDown={(event) =>
                        handleSalonFixturePointerDown(event, fixture)
                      }
                      onPointerMove={(event) =>
                        handleSalonFixturePointerMove(event, fixture.id)
                      }
                      onPointerUp={(event) =>
                        finishSalonFixtureDrag(event, fixture.id)
                      }
                      onPointerCancel={(event) =>
                        finishSalonFixtureDrag(event, fixture.id)
                      }
                      aria-label={`${fixture.label}, elemento representativo del plano`}
                    >
                      <span
                        style={{
                          ...styles.salonFixtureLabel,
                          ...(isVerticalDoorFixture
                            ? styles.salonFixtureLabelVertical
                            : {}),
                        }}
                      >
                        {fixture.label}
                      </span>
                      {salonEditMode && isSelected && !isVerticalDoorFixture ? (
                        <span style={styles.salonFixtureMoveHint}>ARRASTRAR</span>
                      ) : null}
                    </button>
                  );
                })}
                {salonVisibleTables.length ? (
                  salonVisibleTables.map((table, index) => {
                    const isSelected = selectedSalonTable?.id === table.id;
                    const isReserved = !table.operational && Boolean(table.reservation);
                    const tableServiceRequests =
                      salonServiceRequestsByTableId.get(table.id) || [];
                    const hasPendingWaiterCall = tableServiceRequests.length > 0;
                    const tableLayout = resolveSalonTableLayout(
                      salonLayout[table.id],
                      index,
                      salonVisibleTables.length
                    );
                    const primaryDiner = table.dinerNames?.[0] || "";

                    return (
                      <button
                        key={table.id}
                        type="button"
                        data-table-id={table.id}
                        style={{
                          ...styles.salonTableCircle,
                          ...(table.operational
                            ? styles.salonTableCircleOperational
                            : isReserved
                              ? styles.salonTableCircleReserved
                              : styles.salonTableCircleFree),
                          ...(isSelected ? styles.salonTableCircleSelected : {}),
                          left: `${tableLayout.x}%`,
                          top: `${tableLayout.y}%`,
                          width: `${tableLayout.width}px`,
                          height: `${tableLayout.height}px`,
                          borderRadius: getSalonTableBorderRadius(tableLayout.shape),
                          cursor: salonEditMode ? "grab" : "pointer",
                          ...(hasPendingWaiterCall
                            ? {
                                animation:
                                  "inicioSalonTableCallPulse 1.05s ease-in-out infinite",
                              }
                            : {}),
                        }}
                        onClick={() => {
                          setSalonSelectedTableId(table.id);
                          setSalonSelectedFixtureId("");
                          if (salonReservationTableId !== table.id) {
                            setSalonReservationTableId("");
                            setSalonReservationEditId("");
                            setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
                          }
                        }}
                        onPointerDown={(event) => handleSalonTablePointerDown(event, table.id)}
                        onPointerMove={(event) => handleSalonTablePointerMove(event, table.id)}
                        onPointerUp={(event) => finishSalonTableDrag(event, table.id)}
                        onPointerCancel={(event) => finishSalonTableDrag(event, table.id)}
                        aria-label={`${table.label}, ${
                          table.operational ? "ocupada" : isReserved ? "reservada" : "libre"
                        }`}
                      >
                        <strong style={styles.salonCircleName}>{table.label}</strong>
                        {hasPendingWaiterCall ? (
                          <span style={styles.salonCallBadge}>
                            LLAMADO{tableServiceRequests.length > 1 ? ` (${tableServiceRequests.length})` : ""}
                          </span>
                        ) : null}
                        <span style={styles.salonCircleState}>
                          {table.operational
                            ? `${table.activeMesas.length} ${
                                table.activeMesas.length === 1 ? "cuenta" : "cuentas"
                              }`
                            : isReserved
                              ? "Reservada"
                              : "Libre"}
                        </span>
                        {table.operational ? (
                          <span
                            style={{
                              ...styles.salonCircleMeta,
                              maxWidth: `${Math.max(48, tableLayout.width - 20)}px`,
                            }}
                          >
                            {primaryDiner || formatCurrency(table.totalAmount)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                ) : (
                  <div style={styles.salonEmptyState}>
                    Todavia no hay mesas asignadas a {activeSalonRoom.name}. Abre Gestionar ambientes para agregar mesas a este plano.
                  </div>
                )}
                </div>
              </section>

              <aside className="inicio-salon-detail-panel" style={styles.salonDetailPanel}>
                {selectedSalonTable ? (
                  <>
                    <div style={styles.salonDetailHeader}>
                      <div>
                        <span style={styles.salonDetailEyebrow}>MESA SELECCIONADA</span>
                        <h3 style={styles.salonDetailTitle}>{selectedSalonTable.label}</h3>
                      </div>
                      <span
                        style={{
                          ...styles.salonStatusBadge,
                          ...(selectedSalonTable.operational
                            ? styles.salonStatusBadgeOperational
                            : selectedSalonTable.reservation
                              ? styles.salonStatusBadgeReserved
                              : styles.salonStatusBadgeFree),
                        }}
                      >
                        {selectedSalonTable.operational
                          ? "Ocupada"
                          : selectedSalonTable.reservation
                            ? "Reservada"
                            : "Libre"}
                      </span>
                    </div>

                    {selectedSalonServiceRequests.length > 0 ? (
                      <div style={styles.salonCallPanel}>
                        <div style={styles.salonCallPanelHeader}>
                          <div>
                            <span style={styles.salonCallPanelEyebrow}>LLAMADO ACTIVO</span>
                            <strong style={styles.salonCallPanelTitle}>
                              La mesa necesita atencion
                            </strong>
                          </div>
                          <span style={styles.salonCallPanelCount}>
                            {selectedSalonServiceRequests.length}
                          </span>
                        </div>
                        {selectedSalonServiceRequests.map((request) => (
                          <article key={request.id} style={styles.salonCallRequest}>
                            <div style={styles.salonCallRequestCopy}>
                              <strong>
                                {request.summary || "Cliente llama al mozo."}
                              </strong>
                              <span>
                                Hace {formatElapsed(request.requestedAt)}
                                {request.assignedWaiterName
                                  ? ` - ${request.assignedWaiterName}`
                                  : " - Sin mozo asignado"}
                              </span>
                            </div>
                            <button
                              type="button"
                              style={styles.salonCallCompleteButton}
                              onClick={() => handleCompleteWaiterRequestFromInicio(request)}
                              disabled={!canModifyOrders}
                            >
                              Marcar atendido
                            </button>
                          </article>
                        ))}
                      </div>
                    ) : null}

                    {salonEditMode && selectedSalonTableLayout ? (
                      <div style={styles.salonTableIndividualEditor}>
                        <div>
                          <span style={styles.salonTableFormatEyebrow}>
                            FORMATO DE ESTA MESA
                          </span>
                          <strong style={styles.salonTableFormatTitle}>
                            Ajuste individual
                          </strong>
                          <span style={styles.salonTableFormatDescription}>
                            Este cambio solo afecta a {selectedSalonTable.label}.
                          </span>
                        </div>

                        <div style={styles.salonTableShapeButtons}>
                          {SALON_TABLE_SHAPES.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              style={{
                                ...styles.salonTableShapeButton,
                                ...(selectedSalonTableLayout.shape === option.id
                                  ? styles.salonTableShapeButtonActive
                                  : {}),
                              }}
                              onClick={() =>
                                updateSalonTableGeometry(selectedSalonTable.id, {
                                  shape: option.id,
                                })
                              }
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>

                        <label style={styles.salonTableDimensionField}>
                          <span style={styles.salonTableDimensionHeader}>
                            <span>
                              {selectedSalonTableLayout.shape === "rectangle"
                                ? "Ancho"
                                : "Tamano"}
                            </span>
                            <strong>{Math.round(selectedSalonTableLayout.width)} px</strong>
                          </span>
                          <input
                            type="range"
                            min={selectedSalonTableLayout.shape === "rectangle" ? "80" : "64"}
                            max={selectedSalonTableLayout.shape === "rectangle" ? "180" : "140"}
                            step="2"
                            value={selectedSalonTableLayout.width}
                            style={styles.salonTableDimensionRange}
                            onChange={(event) =>
                              updateSalonTableGeometry(
                                selectedSalonTable.id,
                                { width: event.target.value },
                                { persist: false }
                              )
                            }
                            onPointerUp={() =>
                              updateSalonTableGeometry(selectedSalonTable.id, {})
                            }
                            onKeyUp={() =>
                              updateSalonTableGeometry(selectedSalonTable.id, {})
                            }
                            onBlur={() =>
                              updateSalonTableGeometry(selectedSalonTable.id, {})
                            }
                          />
                        </label>

                        {selectedSalonTableLayout.shape === "rectangle" ? (
                          <label style={styles.salonTableDimensionField}>
                            <span style={styles.salonTableDimensionHeader}>
                              <span>Alto</span>
                              <strong>{Math.round(selectedSalonTableLayout.height)} px</strong>
                            </span>
                            <input
                              type="range"
                              min="52"
                              max="140"
                              step="2"
                              value={selectedSalonTableLayout.height}
                              style={styles.salonTableDimensionRange}
                              onChange={(event) =>
                                updateSalonTableGeometry(
                                  selectedSalonTable.id,
                                  { height: event.target.value },
                                  { persist: false }
                                )
                              }
                              onPointerUp={() =>
                                updateSalonTableGeometry(selectedSalonTable.id, {})
                              }
                              onKeyUp={() =>
                                updateSalonTableGeometry(selectedSalonTable.id, {})
                              }
                              onBlur={() =>
                                updateSalonTableGeometry(selectedSalonTable.id, {})
                              }
                            />
                          </label>
                        ) : null}

                        {selectedSalonTableLayout.shape === "rectangle" ? (
                          <button
                            type="button"
                            style={styles.salonTableRotationButton}
                            onClick={() =>
                              updateSalonTableGeometry(
                                selectedSalonTable.id,
                                rotateSalonRectangleGeometry(selectedSalonTableLayout)
                              )
                            }
                          >
                            Rotar mesa:{" "}
                            {selectedSalonTableLayout.rotation === 90
                              ? "vertical"
                              : "horizontal"}
                          </button>
                        ) : null}
                      </div>
                    ) : null}

                    {selectedSalonTable.operational ? (
                      <>
                        <div style={styles.salonOperationalSummary}>
                          <span>
                            {selectedSalonMesas.length} {selectedSalonMesas.length === 1 ? "cuenta" : "cuentas"}
                          </span>
                          <strong>{formatCurrency(selectedSalonTable.totalAmount)}</strong>
                        </div>

                        <div style={styles.salonAccountList}>
                          {selectedSalonMesas.map((mesa, index) => {
                            const dinerName = getMesaCustomerName(mesa) || `Cuenta ${index + 1}`;
                            return (
                              <article key={mesa.id} style={styles.salonAccountCard}>
                                <div style={styles.salonAccountHeader}>
                                  <div>
                                    <strong style={styles.salonAccountName}>{dinerName}</strong>
                                    <span style={styles.salonAccountMeta}>
                                      {getMesaOrderSummary(mesa)}
                                    </span>
                                  </div>
                                  <strong style={styles.salonAccountAmount}>
                                    {formatCurrency(mesa.totalAmount || 0)}
                                  </strong>
                                </div>
                                <div style={styles.salonAccountActions}>
                                  <button
                                    type="button"
                                    style={styles.salonSecondaryAction}
                                    onClick={() => openMesaDetail(mesa.id)}
                                  >
                                    Ver detalle
                                  </button>
                                  <button
                                    type="button"
                                    style={styles.salonPrimaryAction}
                                    onClick={() => openManualOrderForSalonTable(selectedSalonTable, dinerName)}
                                  >
                                    Agregar pedido
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>

                        <div style={styles.salonActionGrid}>
                          <button
                            type="button"
                            style={styles.salonPrimaryActionLarge}
                            onClick={() => openManualOrderForSalonTable(selectedSalonTable)}
                          >
                            + Nuevo comensal o cuenta
                          </button>
                          <button
                            type="button"
                            style={styles.salonDangerAction}
                            onClick={() => closeSalonTable(selectedSalonTable)}
                          >
                            Cerrar mesa
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {selectedSalonTable.reservation ? (
                          <article style={styles.salonReservationCard}>
                            <span style={styles.salonDetailEyebrow}>RESERVA ACTIVA</span>
                            <strong style={styles.salonReservationName}>
                              {selectedSalonTable.reservation.customerName || "Sin nombre"}
                            </strong>
                            <span style={styles.salonReservationMeta}>
                              {selectedSalonTable.reservation.partySize || 1} personas
                              {selectedSalonTable.reservation.reservedFor
                                ? ` · ${String(selectedSalonTable.reservation.reservedFor).replace("T", " ")}`
                                : ""}
                            </span>
                            {selectedSalonTable.reservation.notes ? (
                              <p style={styles.salonReservationNotes}>
                                {selectedSalonTable.reservation.notes}
                              </p>
                            ) : null}
                          </article>
                        ) : (
                          <div style={styles.salonFreeMessage}>
                            {selectedSalonTable.nextReservation
                              ? `Mesa disponible. La proxima reserva es ${formatSalonReservationDateTime(
                                  selectedSalonTable.nextReservation.reservedFor
                                )}.`
                              : "Esta mesa esta disponible y no tiene reservas proximas."}
                          </div>
                        )}

                        {selectedSalonTable.reservations?.length ? (
                          <section style={styles.salonReservationSchedule}>
                            <div style={styles.salonReservationScheduleHeader}>
                              <strong>Proximas reservas</strong>
                              <span>{selectedSalonTable.reservations.length}</span>
                            </div>
                            <div style={styles.salonReservationList}>
                              {selectedSalonTable.reservations.map((reservation) => {
                                const isBlockingNow =
                                  selectedSalonTable.reservation?.id === reservation.id;

                                return (
                                  <article
                                    key={reservation.id}
                                    style={{
                                      ...styles.salonReservationCard,
                                      ...(isBlockingNow
                                        ? styles.salonReservationCardActive
                                        : {}),
                                    }}
                                  >
                                    <div style={styles.salonReservationCardHeader}>
                                      <div>
                                        <span style={styles.salonDetailEyebrow}>
                                          {isBlockingNow ? "BLOQUEANDO MESA" : "PROGRAMADA"}
                                        </span>
                                        <strong style={styles.salonReservationName}>
                                          {reservation.customerName || "Sin nombre"}
                                        </strong>
                                      </div>
                                      <strong style={styles.salonReservationDate}>
                                        {formatSalonReservationDateTime(reservation.reservedFor)}
                                      </strong>
                                    </div>
                                    <span style={styles.salonReservationMeta}>
                                      {reservation.partySize || 1} personas · Bloquea {formatSalonReservationBlockLead(
                                        reservation.blockMinutesBefore
                                      )} · Duracion {Math.max(
                                        30,
                                        Number(reservation.durationMinutes || 120)
                                      )} min
                                    </span>
                                    {reservation.notes ? (
                                      <p style={styles.salonReservationNotes}>
                                        {reservation.notes}
                                      </p>
                                    ) : null}
                                    <div style={styles.salonReservationActions}>
                                      <button
                                        type="button"
                                        style={styles.salonReservationEditButton}
                                        onClick={() =>
                                          openSalonReservationForm(
                                            selectedSalonTable,
                                            reservation
                                          )
                                        }
                                      >
                                        Editar
                                      </button>
                                      <button
                                        type="button"
                                        style={styles.salonReservationDeleteButton}
                                        onClick={() =>
                                          cancelSalonReservation(
                                            selectedSalonTable,
                                            reservation
                                          )
                                        }
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </article>
                                );
                              })}
                            </div>
                          </section>
                        ) : null}

                        <div style={styles.salonActionGrid}>
                          <button
                            type="button"
                            style={styles.salonPrimaryActionLarge}
                            onClick={() => openManualOrderForSalonTable(selectedSalonTable)}
                          >
                            Cargar pedido
                          </button>
                          <button
                            type="button"
                            style={styles.salonSecondaryActionLarge}
                            onClick={() => openSalonReservationForm(selectedSalonTable)}
                          >
                            Nueva reserva
                          </button>
                        </div>

                        {salonReservationTableId === selectedSalonTable.id ? (
                          <div style={styles.salonReservationForm}>
                            <strong style={styles.salonReservationFormTitle}>
                              {salonReservationEditId ? "Editar" : "Nueva"} reserva de {selectedSalonTable.label}
                            </strong>
                            <label style={styles.salonReservationField}>
                              <span>Nombre</span>
                              <input
                                type="text"
                                value={salonReservationForm.customerName}
                                onChange={(event) =>
                                  setSalonReservationForm((current) => ({
                                    ...current,
                                    customerName: event.target.value,
                                  }))
                                }
                                style={styles.salonReservationInput}
                                placeholder="Nombre de la reserva"
                              />
                            </label>
                            <div style={styles.salonReservationColumns}>
                              <label style={styles.salonReservationField}>
                                <span>Personas</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={salonReservationForm.partySize}
                                  onChange={(event) =>
                                    setSalonReservationForm((current) => ({
                                      ...current,
                                      partySize: event.target.value,
                                    }))
                                  }
                                  style={styles.salonReservationInput}
                                />
                              </label>
                              <label style={styles.salonReservationField}>
                                <span>Fecha y hora</span>
                                <input
                                  type="datetime-local"
                                  value={salonReservationForm.reservedFor}
                                  onChange={(event) =>
                                    setSalonReservationForm((current) => ({
                                      ...current,
                                      reservedFor: event.target.value,
                                    }))
                                  }
                                  style={styles.salonReservationInput}
                                />
                              </label>
                            </div>
                            <div style={styles.salonReservationColumns}>
                              <label style={styles.salonReservationField}>
                                <span>Bloquear mesa desde</span>
                                <select
                                  value={salonReservationForm.blockMinutesBefore}
                                  onChange={(event) =>
                                    setSalonReservationForm((current) => ({
                                      ...current,
                                      blockMinutesBefore: event.target.value,
                                    }))
                                  }
                                  style={styles.salonReservationInput}
                                >
                                  {SALON_RESERVATION_BLOCK_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label style={styles.salonReservationField}>
                                <span>Duracion estimada</span>
                                <select
                                  value={salonReservationForm.durationMinutes}
                                  onChange={(event) =>
                                    setSalonReservationForm((current) => ({
                                      ...current,
                                      durationMinutes: event.target.value,
                                    }))
                                  }
                                  style={styles.salonReservationInput}
                                >
                                  {SALON_RESERVATION_DURATION_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                            <label style={styles.salonReservationField}>
                              <span>Nota</span>
                              <textarea
                                value={salonReservationForm.notes}
                                onChange={(event) =>
                                  setSalonReservationForm((current) => ({
                                    ...current,
                                    notes: event.target.value,
                                  }))
                                }
                                style={{ ...styles.salonReservationInput, minHeight: "72px" }}
                                placeholder="Ej: cerca de la ventana"
                              />
                            </label>
                            <div style={styles.salonAccountActions}>
                              <button
                                type="button"
                                style={styles.salonPrimaryAction}
                                onClick={saveSalonReservation}
                              >
                                Guardar reserva
                              </button>
                              <button
                                type="button"
                                style={styles.salonSecondaryAction}
                                onClick={() => {
                                  setSalonReservationTableId("");
                                  setSalonReservationEditId("");
                                  setSalonReservationForm(EMPTY_SALON_RESERVATION_FORM);
                                }}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : null}

                      </>
                    )}
                  </>
                ) : (
                  <div style={styles.salonEmptyDetail}>
                    <strong>Selecciona una mesa</strong>
                    <span>Desde aqui podras cargar pedidos, reservar o consultar cada comensal.</span>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
      ) : null}

      {serviceAlert ? (
        <div style={styles.serviceAlertToast}>
          <div style={styles.serviceAlertDot} />
          <div style={styles.serviceAlertContent}>
            <strong style={styles.serviceAlertTitle}>{serviceAlert.title}</strong>
            <span style={styles.serviceAlertText}>{serviceAlert.text}</span>
          </div>
          <button
            type="button"
            style={styles.serviceAlertClose}
            onClick={() => setServiceAlert(null)}
            aria-label="Cerrar alerta"
          >
            ×
          </button>
        </div>
      ) : null}

      </div>

      <section style={styles.metricGrid}>
        {metricasBase.map((metrica) => {
          const isSalonCallAlert =
            metrica.id === "salon" &&
            metrica.waiterCallCount > 0 &&
            detalleActivo !== "salon";
          const accentConfig = isSalonCallAlert
            ? {
                ...SERVICE_NOTIFICATION_ACCENT,
                shadow: "rgba(185, 28, 28, 0.3)",
                wash:
                  "linear-gradient(135deg, rgba(255,255,255,0.99) 0%, rgba(254,242,242,0.94) 48%, rgba(254,226,226,0.96) 100%)",
              }
            : metrica.liveAccent
              ? LIVE_METRIC_ACCENTS[metrica.liveAccent] || null
              : null;
          const metricStyles = {
            ...styles.metricCard,
            ...(metrica.interactive ? styles.metricCardInteractive : {}),
            ...(accentConfig
              ? {
                  ...styles.metricCardLive,
                  border: `1px solid ${accentConfig.border}`,
                  boxShadow: `0 18px 38px rgba(15, 23, 42, 0.07), 0 0 0 1px ${accentConfig.glow}, 0 14px 34px ${accentConfig.shadow}, 0 0 34px ${accentConfig.glow}, inset 0 1px 0 rgba(255,255,255,0.92)`,
                }
              : {}),
            ...(detalleActivo === metrica.id || detalleActivo === metrica.detailActionId
              ? styles.metricCardActive
              : {}),
            ...(isSalonCallAlert
              ? {
                  animation: "inicioSalonCallCardPulse 1.15s ease-in-out infinite",
                }
              : {}),
          };

          const detailNode = metrica.detailActionId ? (
            <>
              <span style={styles.metricDetail}>{metrica.detail}</span>
              <button
                type="button"
                style={styles.metricLinkButton}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDashboardDetail(metrica.detailActionId);
                }}
              >
                {metrica.linkLabel}
              </button>
            </>
          ) : (
            <span style={styles.metricDetail}>{metrica.detail}</span>
          );

          return metrica.interactive ? (
            <article
              key={metrica.id}
              style={metricStyles}
              onClick={() => {
                toggleDashboardDetail(metrica.id);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleDashboardDetail(metrica.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {accentConfig ? (
                <div style={styles.metricLiveFrame} aria-hidden="true">
                  <span
                    style={{
                      ...styles.metricLiveWash,
                      background: accentConfig.wash,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowTop,
                      background: accentConfig.horizontal,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowBottom,
                      background: accentConfig.horizontal,
                      animationDuration: "6.8s",
                      animationDelay: "-2.6s",
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowLeft,
                      background: accentConfig.vertical,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowRight,
                      background: accentConfig.vertical,
                      animationDuration: "6.8s",
                      animationDelay: "-2.6s",
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveSheen,
                      background:
                        "linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 48%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                </div>
              ) : null}
              {isSalonCallAlert ? (
                <span style={styles.metricWaiterCallBadge}>
                  {metrica.waiterCallCount} {metrica.waiterCallCount === 1 ? "LLAMADO" : "LLAMADOS"}
                </span>
              ) : null}
              <span style={styles.metricLabel}>{metrica.label}</span>
              <strong style={styles.metricValue}>{metrica.value}</strong>
              {detailNode}
            </article>
          ) : (
            <article key={metrica.id} style={metricStyles}>
              {accentConfig ? (
                <div style={styles.metricLiveFrame} aria-hidden="true">
                  <span
                    style={{
                      ...styles.metricLiveWash,
                      background: accentConfig.wash,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowTop,
                      background: accentConfig.horizontal,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowBottom,
                      background: accentConfig.horizontal,
                      animationDuration: "6.8s",
                      animationDelay: "-2.6s",
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowLeft,
                      background: accentConfig.vertical,
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveGlow,
                      ...styles.metricLiveGlowRight,
                      background: accentConfig.vertical,
                      animationDuration: "6.8s",
                      animationDelay: "-2.6s",
                    }}
                  />
                  <span
                    style={{
                      ...styles.metricLiveSheen,
                      background:
                        "linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 48%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                </div>
              ) : null}
              <span style={styles.metricLabel}>{metrica.label}</span>
              <strong style={styles.metricValue}>{metrica.value}</strong>
              {detailNode}
            </article>
          );
        })}
      </section>

      {detalleConfig && (
        <section
          style={{
            ...styles.detailPanel,
            ...(detalleConfig.mode === "mesas" ? styles.detailPanelPrimary : {}),
          }}
        >
          <div style={styles.panelHeader}>
            <div>
              <h2
                style={{
                  ...styles.panelTitle,
                  ...(detalleConfig.mode === "mesas" ? styles.detailPanelPrimaryTitle : {}),
                }}
              >
                {detalleConfig.title}
              </h2>
              <p
                style={{
                  ...styles.detailSubtitle,
                  ...(detalleConfig.mode === "mesas"
                    ? styles.detailPanelPrimarySubtitle
                    : {}),
                }}
              >
                {detalleConfig.subtitle}
              </p>
            </div>
            <button
              type="button"
              style={{
                ...styles.closeButton,
                ...(detalleConfig.mode === "mesas" ? styles.closeButtonPrimary : {}),
              }}
              onClick={() => {
                setDetalleActivo(null);
                setDetalleMesaId("");
              }}
            >
              Cerrar
            </button>
          </div>

          {detalleConfig.mode === "pendientes" ? (
            <div style={styles.manualKitchenSummaryStrip}>
              <article
                style={{
                  ...styles.manualKitchenSummaryCard,
                  ...styles.manualKitchenSummaryPending,
                }}
              >
                <span style={styles.manualKitchenSummaryLabel}>Por avisar</span>
                <strong style={styles.manualKitchenSummaryValue}>
                  {manualKitchenSummary.pendingNotice}
                </strong>
                <span style={styles.manualKitchenSummaryHint}>Manual · cocina todavia no avisada</span>
              </article>
              <article
                style={{
                  ...styles.manualKitchenSummaryCard,
                  ...styles.manualKitchenSummaryConfirmed,
                }}
              >
                <span style={styles.manualKitchenSummaryLabel}>Avisados</span>
                <strong style={styles.manualKitchenSummaryValue}>
                  {manualKitchenSummary.confirmed}
                </strong>
                <span style={styles.manualKitchenSummaryHint}>Manual · ya informado desde Inicio</span>
              </article>
              <article
                style={{
                  ...styles.manualKitchenSummaryCard,
                  ...styles.manualKitchenSummaryReady,
                }}
              >
                <span style={styles.manualKitchenSummaryLabel}>Listos</span>
                <strong style={styles.manualKitchenSummaryValue}>{manualKitchenSummary.ready}</strong>
                <span style={styles.manualKitchenSummaryHint}>Esperando entrega o cierre</span>
              </article>
            </div>
          ) : null}

          {detalleConfig.mode === "pedidos" ? (
            <div style={styles.orderReportPanel}>
              <div style={styles.orderReportHeader}>
                <div>
                  <strong style={styles.orderReportTitle}>Generador de PDF</strong>
                  <div style={styles.orderReportHint}>
                    Elige rango de fechas y estados para exportar pedidos cerrados,
                    cancelados o cualquier otro estado operativo guardado.
                  </div>
                </div>
                <div style={styles.orderReportSummary}>
                  {pedidosReportePdf.length} pedidos en el filtro actual
                </div>
              </div>

              <div style={styles.orderReportFiltersGrid}>
                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Desde</span>
                  <input
                    type="date"
                    value={orderReportFilters.from}
                    onChange={(event) =>
                      handleOrderReportDateChange("from", event.target.value)
                    }
                    style={styles.manualInput}
                  />
                </label>

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Comensal o cuenta</span>
                  <input
                    type="text"
                    value={manualOrderForm.dinerName}
                    onChange={(event) =>
                      setManualOrderForm((prev) => ({ ...prev, dinerName: event.target.value }))
                    }
                    style={styles.manualInput}
                    placeholder="Ej: Fulano (opcional)"
                  />
                </label>

                <label style={styles.manualField}>
                  <span style={styles.detailLabel}>Hasta</span>
                  <input
                    type="date"
                    value={orderReportFilters.to}
                    onChange={(event) =>
                      handleOrderReportDateChange("to", event.target.value)
                    }
                    style={styles.manualInput}
                  />
                </label>
              </div>

              <div style={styles.orderReportStatusHeader}>
                <span style={styles.detailLabel}>Estados a incluir</span>
                <div style={styles.orderReportStatusActions}>
                  <button
                    type="button"
                    style={styles.secondaryInlineButton}
                    onClick={resetOrderReportFilters}
                  >
                    Volver a hoy
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryInlineButton}
                    onClick={() =>
                      setOrderReportFilters((prev) => ({
                        ...prev,
                        statusMode: "all",
                        statuses: [],
                      }))
                    }
                  >
                    Todos los estados
                  </button>
                </div>
              </div>

              <div style={styles.orderReportStatusGrid}>
                {availableOrderReportStatuses.length ? (
                  availableOrderReportStatuses.map((status) => {
                    const isSelected = selectedOrderReportStatuses.includes(status);

                    return (
                      <label
                        key={status}
                        style={{
                          ...styles.orderReportStatusChip,
                          ...(isSelected ? styles.orderReportStatusChipActive : {}),
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleOrderReportStatus(status)}
                        />
                        <span>{status}</span>
                      </label>
                    );
                  })
                ) : (
                  <div style={styles.orderReportEmptyHint}>
                    Todavia no hay estados guardados para exportar.
                  </div>
                )}
              </div>

              <div style={styles.orderReportFooter}>
                <div style={styles.orderReportHint}>
                  Periodo listo para exportar:{" "}
                  {orderReportRange.fromDate
                    ? formatShortDate(orderReportRange.fromDate)
                    : "inicio"}{" "}
                  a{" "}
                  {orderReportRange.toDate
                    ? formatShortDate(orderReportRange.toDate)
                    : "hoy"}.
                </div>
                <button
                  type="button"
                  style={styles.primaryInlineButton}
                  onClick={exportOrdersReportPdf}
                  disabled={!canExportOperationalReports}
                >
                  Descargar PDF
                </button>
              </div>
            </div>
          ) : null}

          <div style={styles.detailList}>
            {!detalleConfig.rows.length ? (
              <article style={styles.detailEmptyState}>
                No hay pedidos para mostrar con el criterio actual.
              </article>
            ) : detalleConfig.mode === "mesas"
              ? detalleConfig.rows.map((mesa) => renderMesaDetailCard(mesa))
              : detalleConfig.mode === "cierres"
                ? detalleConfig.rows.map((mesa) => {
                    const paymentState = resolveOperationalOrderPaymentState(
                      mesa,
                      getPaymentOperations(operationalRestaurant),
                      mesa?.paymentOperationId
                    );

                    return (
                    <article key={mesa.id} style={styles.detailCard}>
                      <div style={styles.detailCardHeader}>
                        <div>
                          <strong style={styles.detailTitle}>{mesa.mesa}</strong>
                          <div style={styles.detailMeta}>
                            {getMesaOperationalLabel(mesa)}
                          </div>
                        </div>
                        <span style={styles.readyBadge}>Entrega completa</span>
                      </div>

                      <div style={styles.detailBlock}>
                        <span style={styles.detailLabel}>Pedido completo</span>
                        <div style={styles.detailText}>{getMesaOrderSummary(mesa)}</div>
                      </div>

                      <div style={styles.detailBlock}>
                        <span style={styles.detailLabel}>Tiempo desde el pedido</span>
                        <div style={styles.detailText}>{formatElapsed(mesa.orderedAt)}</div>
                      </div>

                      <div style={styles.detailBlock}>
                        <span style={styles.detailLabel}>Forma de pago</span>
                        <div style={styles.detailText}>{getMesaPaymentMethodLabel(mesa)}</div>
                        <div style={styles.detailMeta}>{getMesaPaymentStatusLabel(mesa)}</div>
                      </div>

                      <div style={styles.actionRow}>
                        {paymentState.isPaid ? (
                          <button
                            type="button"
                            style={styles.closeOrderButton}
                            onClick={() => requestCloseOrder(mesa.id)}
                            disabled={!canModifyOrders}
                          >
                            Cerrar pedido
                          </button>
                        ) : (
                          renderPendingPaymentCloseActions(mesa, paymentState)
                        )}
                      </div>
                    </article>
                    );
                  })
                : detalleConfig.mode === "pendientes"
                  ? detalleConfig.rows.map((row) => (
                      <article
                        key={row.id}
                        style={{
                          ...styles.detailCard,
                          ...styles.pendingKitchenCard,
                          ...getKitchenDispatchVisualSafe(row).cardStyle,
                        }}
                      >
                        <div
                          style={{
                            ...styles.pendingKitchenAccent,
                            background: getKitchenDispatchVisualSafe(row).accent,
                          }}
                          aria-hidden="true"
                        />
                        <div style={styles.detailCardHeader}>
                          <div>
                            <strong style={styles.detailTitle}>{row.mesa}</strong>
                            <div style={styles.detailMeta}>
                              {getMesaOperationalLabel(row)}
                            </div>
                          </div>
                          <div style={styles.pendingKitchenHeaderMeta}>
                            <span
                              style={{
                                ...styles.pendingKitchenStatusBadge,
                                ...getKitchenDispatchVisualSafe(row).badgeStyle,
                              }}
                            >
                              {getKitchenDispatchVisualSafe(row).badgeLabel}
                            </span>
                            <span
                              style={{ ...styles.elapsedBadge, ...getElapsedTone(row.requestedAt) }}
                            >
                              {formatElapsed(row.requestedAt)}
                            </span>
                          </div>
                        </div>

                        <div style={styles.pendingKitchenSummaryRow}>
                          <div style={styles.pendingKitchenInfoCard}>
                            <span style={styles.detailLabel}>
                              {orderNeedsKitchen(row) ? "Operacion de cocina" : "Preparacion"}
                            </span>
                            <div style={styles.pendingKitchenInfoText}>
                              {getKitchenDispatchStatusLabelSafe(row)}
                            </div>
                          </div>
                          <div style={styles.pendingKitchenInfoCard}>
                            <span style={styles.detailLabel}>
                              {orderNeedsKitchen(row) ? "Estado en cocina" : "Estado de salida"}
                            </span>
                            <div style={styles.pendingKitchenInfoText}>
                              {getKitchenProgressDetail(row).label}
                            </div>
                          </div>
                          <div style={styles.pendingKitchenInfoCard}>
                            <span style={styles.detailLabel}>Modo</span>
                            <div style={styles.pendingKitchenModeValue}>
                              {orderNeedsKitchen(row)
                                ? getKitchenModeLabel(row.kitchenModeSnapshot)
                                : "No aplica"}
                            </div>
                          </div>
                          <div style={styles.pendingKitchenInfoCard}>
                            <span style={styles.detailLabel}>Pago</span>
                            <div style={styles.pendingKitchenInfoText}>
                              {getMesaPaymentMethodLabel(row)}
                            </div>
                            <div style={styles.detailMeta}>
                              {getMesaPaymentStatusLabel(row)}
                            </div>
                          </div>
                        </div>

                        <div style={styles.pendingKitchenHighlightBox}>
                          <span style={styles.pendingKitchenHighlightLabel}>Pedido</span>
                          <div style={styles.pendingKitchenHighlightText}>{row.pedido}</div>
                        </div>

                        <div style={styles.pendingKitchenAlertBox}>
                          <span style={styles.pendingKitchenHighlightLabel}>
                            {orderNeedsKitchen(row) ? "Falta sacar" : "Falta entregar"}
                          </span>
                          <div style={styles.pendingKitchenAlertText}>{row.falta}</div>
                        </div>

                        <div style={styles.pendingKitchenNextStepBox}>
                          <span style={styles.pendingKitchenHighlightLabel}>Siguiente paso</span>
                          <div style={styles.pendingKitchenNextStepText}>
                            {getKitchenNextActionLabel(row)}
                          </div>
                        </div>

                        {isMesaDigitalPaymentPendingForKitchen(row) ? (
                          <div style={styles.pendingKitchenAlertBox}>
                            <span style={styles.pendingKitchenHighlightLabel}>
                              Cobro digital
                            </span>
                            <div style={styles.pendingKitchenAlertText}>
                              Esperando confirmacion del pago QR antes de mandarlo a
                              preparacion.
                            </div>
                          </div>
                        ) : null}

                        <div style={styles.pendingKitchenFooter}>
                          {row.phase === "new" ? (
                            <button
                              type="button"
                              style={styles.primaryInlineButton}
                              onClick={() => progressInitialOrder(row)}
                              disabled={isInitialOrderProgressDisabled(row)}
                            >
                              {getInitialOrderProgressLabel(row)}
                            </button>
                          ) : null}
                          {hasMixedKitchenAndDirectDelivery(row) &&
                          getMesaPendingDirectDeliveryItems(row).some(
                            (item) =>
                              !["assigned", "ready"].includes(
                                String(item.deliveryStatus || "")
                              )
                          ) ? (
                            <button
                              type="button"
                              style={styles.directDeliveryActionButton}
                              onClick={() => requestDirectItemDelivery(row.id)}
                              disabled={!canModifyOrders}
                            >
                              Entregar bebidas
                            </button>
                          ) : null}
                          {row.phase === "preparation" &&
                          row.kitchenModeSnapshot === "manual" &&
                          getKitchenDispatchWorkflowState(row).step === "manual_pending" ? (
                            <button
                              type="button"
                              style={styles.manualKitchenActionButton}
                              onClick={() => markManualKitchenNotice(row.id)}
                              disabled={!canModifyOrders}
                            >
                              Avisar a cocina
                            </button>
                          ) : null}
                          {orderNeedsKitchen(row) &&
                          row.phase === "preparation" &&
                          row.kitchenModeSnapshot === "printer" ? (
                            <button
                              type="button"
                              style={styles.printerKitchenActionButton}
                              onClick={() => printKitchenOrderTicket(row.id)}
                              disabled={!canModifyOrders}
                            >
                              {getKitchenDispatchWorkflowState(row).step === "printer_printed"
                                ? "Reimprimir comanda"
                                : "Imprimir comanda"}
                            </button>
                          ) : null}
                          {row.phase === "preparation" &&
                          canRequestReadyDelivery(row) ? (
                            <button
                              type="button"
                              style={styles.primaryInlineButton}
                            onClick={() => requestOrderReadyDelivery(row.id)}
                            disabled={!canModifyOrders}
                          >
                              {getKitchenDispatchWorkflowState(row).step === "printer_printed"
                                ? "Comanda lista: asignar mozo"
                                : "Marcar listo"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            style={styles.secondaryInlineButton}
                            onClick={() => {
                              setDetalleMesaId(row.id);
                              setDetalleActivo("mesas-activas");
                            }}
                          >
                            Abrir seguimiento
                          </button>
                        </div>
                      </article>
                    ))
                  : detalleConfig.rows.map((row) => (
                      <article key={row.id} style={styles.detailCard}>
                        <div style={styles.detailCardHeader}>
                          <div>
                            <strong style={styles.detailTitle}>{row.mesa}</strong>
                            <div style={styles.detailMeta}>{row.canal}</div>
                          </div>
                          <span
                            style={{
                              ...styles.listBadge,
                              ...(getStatusBadgeStyle(row.estado) || {}),
                            }}
                          >
                            {row.estado}
                          </span>
                        </div>

                        <div style={styles.detailBlock}>
                          <span style={styles.detailLabel}>Pedido</span>
                          <div style={styles.detailText}>{row.pedido}</div>
                        </div>

                        <div style={styles.detailBlock}>
                          <span style={styles.detailLabel}>Tiempo transcurrido</span>
                          <div style={styles.detailText}>{formatElapsed(row.requestedAt)}</div>
                        </div>
                      </article>
                    ))}
          </div>
        </section>
      )}

      <section style={styles.contentGrid}>
        <article style={styles.panel}>
          <div style={styles.panelHeader}>
            <h2 style={styles.panelTitle}>Seguimiento del servicio</h2>
            <span style={styles.panelPill}>Turno actual</span>
          </div>

          <div style={styles.list}>
            {pedidosSeguimiento.map((pedido) => (
              <div key={`${pedido.mesa}-${pedido.estado}`} style={styles.listItem}>
                <div>
                  <strong style={styles.listTitle}>{pedido.mesa}</strong>
                  <div style={styles.listSubtitle}>{pedido.nota}</div>
                </div>
                <span
                  style={{
                    ...styles.listBadge,
                    ...(getStatusBadgeStyle(pedido.estado) || {}),
                  }}
                >
                  {pedido.estado}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article style={styles.panel}>
          <div style={styles.panelHeader}>
            <h2 style={styles.panelTitle}>Identidad visible hoy</h2>
            <span style={styles.panelPillMuted}>Mi Restaurante</span>
          </div>

          <div style={styles.identityBlock}>
            <div style={styles.identityRow}>
              <span style={styles.identityLabel}>Direccion</span>
              <strong>{perfil.direccion || "Pendiente de configurar"}</strong>
            </div>
            <div style={styles.identityRow}>
              <span style={styles.identityLabel}>Mensaje</span>
              <strong>{perfil.mensajeBienvenida}</strong>
            </div>
          </div>

          <div style={styles.tagWrap}>
            {caracteristicas.length > 0 ? (
              caracteristicas.map((item) => (
                <span key={item} style={styles.tag}>
                  {item}
                </span>
              ))
            ) : (
              <span style={styles.emptyText}>
                Aun no hay caracteristicas cargadas para el restaurante.
              </span>
            )}
          </div>
        </article>
      </section>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <h2 style={styles.panelTitle}>Alertas y recordatorios</h2>
          <span style={styles.panelPillMuted}>Vista general</span>
        </div>

        <div style={styles.alertList}>
          {alertasBase.map((alerta) => (
            <div key={alerta} style={styles.alertItem}>
              <span style={styles.alertMark}>*</span>
              <span>{alerta}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    maxWidth: "1480px",
    margin: "0 auto",
    padding: "12px 10px 28px",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "20px",
    alignItems: "stretch",
  },
  heroIntroCard: {
    padding: "28px",
    borderRadius: "24px",
    border: "1px solid var(--brand-border, rgba(191, 219, 254, 0.85))",
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, var(--brand-soft, rgba(239,246,255,0.96)) 42%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 24px 50px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gap: "18px",
    position: "relative",
    overflow: "hidden",
  },
  overline: {
    color: "var(--brand-primary, #1d4ed8)",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "2px",
  },
  title: {
    marginTop: 0,
    marginBottom: "4px",
    color: "#0f172a",
    fontSize: "36px",
    lineHeight: 1.05,
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.7,
    maxWidth: "780px",
    fontSize: "15px",
  },
  heroChipRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  heroNotificationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  heroNotificationHeaderText: {
    display: "grid",
    gap: "6px",
    minWidth: 0,
    flex: "1 1 320px",
  },
  heroNotificationTotalBadge: {
    minWidth: "52px",
    height: "52px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.92) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.95)",
    color: "var(--brand-primary, #1d4ed8)",
    fontSize: "22px",
    fontWeight: 800,
    boxShadow: "0 14px 28px rgba(59, 130, 246, 0.08)",
  },
  heroNotificationList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
  },
  heroNotificationItem: {
    display: "grid",
    gap: "10px",
    padding: "16px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(219, 234, 254, 0.95)",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.06)",
    minWidth: 0,
  },
  heroNotificationItemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
  },
  heroNotificationKind: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 9px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
  },
  heroNotificationKindOrder: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },
  heroNotificationKindWaiter: {
    backgroundColor: "#ede9fe",
    color: "#6d28d9",
    border: "1px solid #ddd6fe",
  },
  heroNotificationMesa: {
    color: "#0f172a",
    fontSize: "19px",
    lineHeight: 1.15,
  },
  heroNotificationSummary: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.55,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  heroNotificationEmpty: {
    display: "grid",
    gap: "6px",
    padding: "18px 20px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.86)",
    border: "1px dashed rgba(191, 219, 254, 0.95)",
  },
  heroNotificationEmptyTitle: {
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: 800,
  },
  heroNotificationEmptyText: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.55,
  },
  heroChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "32px",
    padding: "0 12px",
    borderRadius: "999px",
    backgroundColor: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(191, 219, 254, 0.95)",
    color: "#1e3a8a",
    fontSize: "12px",
    fontWeight: 800,
    boxShadow: "0 10px 24px rgba(59, 130, 246, 0.08)",
  },
  heroStatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
  },
  heroStatCard: {
    display: "grid",
    gap: "6px",
    padding: "16px 16px 15px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(219, 234, 254, 0.95)",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.06)",
  },
  heroStatLabel: {
    color: "#64748b",
    fontSize: "11px",
    textTransform: "uppercase",
    fontWeight: 800,
    letterSpacing: "0.06em",
  },
  heroStatValue: {
    color: "#0f172a",
    fontSize: "24px",
    lineHeight: 1.1,
  },
  heroStatHint: {
    color: "#475569",
    fontSize: "13px",
  },
  statusCard: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    borderRadius: "24px",
    padding: "24px",
    border: "1px solid rgba(219, 226, 234, 0.95)",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.07)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    justifyContent: "center",
  },
  statusCardClosed: {
    border: "1px solid #fecaca",
    background:
      "linear-gradient(180deg, rgba(255,247,247,0.98) 0%, rgba(255,241,242,0.98) 100%)",
    boxShadow: "0 24px 48px rgba(127, 29, 29, 0.08)",
  },
  statusHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  statusDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    boxShadow: "0 0 0 6px rgba(34,197,94,0.12)",
  },
  statusDotClosed: {
    backgroundColor: "#ef4444",
    boxShadow: "0 0 0 6px rgba(239,68,68,0.12)",
  },
  statusText: {
    fontWeight: 700,
    color: "#166534",
  },
  statusTextClosed: {
    color: "#b91c1c",
  },
  statusRow: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    color: "#0f172a",
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "rgba(248,250,252,0.88)",
    border: "1px solid rgba(226,232,240,0.9)",
  },
  statusLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },
  statusNote: {
    borderRadius: "16px",
    padding: "14px 16px",
    fontSize: "13px",
    lineHeight: 1.5,
    fontWeight: 600,
  },
  statusNoteOpen: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  statusNoteClosed: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },
  operationsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
    gap: "20px",
    alignItems: "start",
  },
  panel: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(251,253,255,0.98) 100%)",
    borderRadius: "22px",
    border: "1px solid rgba(219, 226, 234, 0.92)",
    padding: "24px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)",
  },
  serviceNotificationPanel: {
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
    padding: "22px",
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.99) 0%, rgba(239,246,255,0.96) 100%)",
  },
  stockAlertPanel: {
    padding: "22px",
    border: "1px solid #fecaca",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(254,242,242,0.98) 100%)",
    boxShadow: "0 18px 40px rgba(185, 28, 28, 0.08)",
  },
  operationExpandedPanel: {
    gridColumn: "1 / -1",
    minHeight: "auto",
  },
  stockEyebrow: {
    color: "#dc2626",
  },
  serviceEyebrow: {
    color: "#2563eb",
  },
  stockAlertPill: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
  },
  stockAlertCollapsedPreview: {
    borderColor: "#fecaca",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(254,242,242,0.92) 100%)",
  },
  stockAlertCollapsedChip: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
  },
  stockAlertCollapsedText: {
    color: "#7f1d1d",
    fontSize: "13px",
    lineHeight: 1.55,
    fontWeight: 700,
  },
  stockAlertLead: {
    color: "#7f1d1d",
    lineHeight: 1.65,
    marginTop: "-4px",
    marginBottom: "18px",
  },
  stockAlertGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "12px",
  },
  stockAlertCard: {
    display: "grid",
    gap: "8px",
    padding: "16px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.95)",
    border: "1px solid #fecaca",
    boxShadow: "0 12px 24px rgba(185, 28, 28, 0.06)",
  },
  stockAlertProductName: {
    color: "#7f1d1d",
    fontSize: "17px",
    lineHeight: 1.2,
  },
  stockAlertProductMeta: {
    color: "#9f1239",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  stockAlertProductReason: {
    color: "#991b1b",
    fontSize: "13px",
    lineHeight: 1.55,
  },
  stockAlertMore: {
    marginTop: "14px",
    color: "#991b1b",
    fontSize: "13px",
    fontWeight: 700,
  },
  serviceNotificationFrame: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 0,
  },
  serviceNotificationLine: {
    position: "absolute",
    borderRadius: "999px",
    filter: "blur(0.2px)",
  },
  serviceNotificationLineTop: {
    top: 0,
    left: 12,
    right: 12,
    height: 2,
    animation: "inicioMetricFlowHorizontal 5.2s linear infinite",
  },
  serviceNotificationLineBottom: {
    bottom: 0,
    left: 18,
    right: 18,
    height: 2,
    animation: "inicioMetricFlowHorizontal 5.2s linear infinite reverse",
  },
  serviceNotificationLineLeft: {
    top: 12,
    bottom: 12,
    left: 0,
    width: 2,
    animation: "inicioMetricFlowVertical 5.2s linear infinite",
  },
  serviceNotificationLineRight: {
    top: 18,
    bottom: 18,
    right: 0,
    width: 2,
    animation: "inicioMetricFlowVertical 5.2s linear infinite reverse",
  },
  operationPanel: {
    minWidth: 0,
    alignSelf: "start",
  },
  eyebrow: {
    display: "inline-block",
    marginBottom: "8px",
    color: "#f97316",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.12em",
  },
  manualOrderWorkspaceBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1190,
    border: 0,
    padding: 0,
    backgroundColor: "rgba(15, 23, 42, 0.62)",
    backdropFilter: "blur(6px)",
    cursor: "default",
  },
  manualOrderWorkspaceModal: {
    position: "fixed",
    top: "3vh",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1200,
    width: "min(1480px, 96vw)",
    maxHeight: "94vh",
    overflowY: "auto",
    boxSizing: "border-box",
    borderRadius: "28px",
    padding: "24px",
    boxShadow: "0 34px 110px rgba(15, 23, 42, 0.32)",
  },
  manualOrderLauncherPanel: {
    gridColumn: "auto",
    display: "grid",
    alignContent: "space-between",
    gap: "16px",
    padding: "22px",
    background:
      "linear-gradient(145deg, rgba(255,247,237,0.99) 0%, rgba(255,255,255,0.99) 54%, rgba(239,246,255,0.96) 100%)",
    border: "1px solid rgba(251, 146, 60, 0.34)",
  },
  manualOrderLauncherButton: {
    minHeight: "48px",
    padding: "0 20px",
    borderRadius: "14px",
    border: "1px solid #ea580c",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(234, 88, 12, 0.22)",
  },
  manualOrderLauncherSummary: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  metricChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "30px",
    padding: "0 11px",
    borderRadius: "999px",
    border: "1px solid #dbe2ea",
    backgroundColor: "rgba(255,255,255,0.88)",
    color: "#475569",
    fontSize: "12px",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  manualDestinationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "12px",
    marginTop: "14px",
  },
  manualDestinationButton: {
    display: "grid",
    gap: "7px",
    minHeight: "110px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  },
  manualDestinationButtonActive: {
    border: "2px solid #f97316",
    background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
    boxShadow: "0 14px 30px rgba(249, 115, 22, 0.15)",
  },
  manualDestinationTitle: {
    fontSize: "16px",
    fontWeight: 900,
  },
  manualDestinationDescription: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  manualDeliveryFields: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "14px",
    marginTop: "16px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fffaf5",
  },
  manualKnownCustomerBox: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: "12px",
    padding: "14px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    background:
      "linear-gradient(180deg, rgba(239,246,255,0.96) 0%, rgba(255,255,255,0.98) 100%)",
  },
  manualKnownCustomerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    color: "#0f172a",
  },
  manualKnownCustomerCount: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "30px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  manualKnownCustomerList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "10px",
  },
  manualKnownCustomerButton: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "6px 12px",
    alignItems: "center",
    padding: "12px",
    borderRadius: "14px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    textAlign: "left",
    cursor: "pointer",
  },
  manualKnownCustomerMain: {
    display: "grid",
    gap: "3px",
    minWidth: 0,
  },
  manualKnownCustomerAddress: {
    gridColumn: "1 / -1",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.4,
  },
  manualKnownCustomerUse: {
    gridColumn: "2 / 3",
    gridRow: "1 / 2",
    alignSelf: "center",
    padding: "7px 10px",
    borderRadius: "999px",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  manualKnownCustomerEmpty: {
    padding: "12px",
    borderRadius: "12px",
    border: "1px dashed #bfdbfe",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "13px",
  },
  promoSummaryStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "18px",
  },
  promoSummaryStatCard: {
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "14px 16px",
    display: "grid",
    gap: "6px",
  },
  promoSummaryValue: {
    color: "#0f172a",
    fontSize: "26px",
    lineHeight: 1,
  },
  promoSummaryList: {
    display: "grid",
    gap: "12px",
  },
  promoSummaryItem: {
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "14px 16px",
  },
  promoSummaryItemMain: {
    display: "grid",
    gap: "8px",
  },
  promoSummaryItemBadges: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  promoSummaryTypeChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft, #eff6ff)",
    color: "var(--brand-primary, #1d4ed8)",
    fontSize: "12px",
    fontWeight: 800,
  },
  promoSummaryStateChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 800,
  },
  promoSummaryStateChipLive: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
  },
  promoSummaryStateChipMuted: {
    backgroundColor: "#f8fafc",
    color: "#64748b",
  },
  promoSummaryMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "20px",
    position: "relative",
    zIndex: 1,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  panelTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
    lineHeight: 1.2,
  },
  panelPill: {
    padding: "7px 12px",
    borderRadius: "999px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: "12px",
  },
  panelPillClosed: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
  },
  panelPillMuted: {
    padding: "7px 12px",
    borderRadius: "999px",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    fontWeight: 800,
    fontSize: "12px",
  },
  collapseToggleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "999px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    fontSize: "12px",
    cursor: "pointer",
  },
  collapseToggleIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    lineHeight: 1,
    transition: "transform 180ms ease",
  },
  collapsedPreviewCard: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
  },
  collapsedPreviewRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  notificationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
    position: "relative",
    zIndex: 1,
  },
  promotionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  promotionCard: {
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    boxShadow: "0 12px 22px rgba(15, 23, 42, 0.04)",
  },
  promotionCardCompact: {
    minHeight: "unset",
  },
  promotionCardExpanded: {
    gap: "14px",
  },
  promotionImage: {
    width: "100%",
    aspectRatio: "16 / 8",
    objectFit: "cover",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
  },
  promotionCompactTop: {
    display: "grid",
    gridTemplateColumns: "76px minmax(0, 1fr)",
    gap: "12px",
    alignItems: "stretch",
  },
  promotionImageCompact: {
    width: "76px",
    height: "76px",
    objectFit: "cover",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
  },
  promotionImagePlaceholder: {
    width: "76px",
    height: "76px",
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "8px",
    fontSize: "12px",
    fontWeight: 800,
  },
  promotionCompactMain: {
    display: "grid",
    gap: "10px",
    minWidth: 0,
  },
  promotionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    flexWrap: "wrap",
  },
  promotionHeaderText: {
    display: "grid",
    gap: "6px",
    minWidth: 0,
  },
  promotionCompactBadgeRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  promotionHook: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: "11px",
  },
  promotionTypeChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontWeight: 800,
    fontSize: "11px",
  },
  promotionStatus: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "11px",
  },
  promotionStatusActive: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
  },
  promotionStatusPaused: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
  },
  promotionForm: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
  promotionCompactSummary: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  promotionSummaryChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: "11px",
    fontWeight: 700,
  },
  promotionExpandedContent: {
    display: "grid",
    gap: "12px",
    paddingTop: "2px",
  },
  promotionCompactFooter: {
    display: "grid",
    gap: "10px",
    paddingTop: "2px",
  },
  promotionExpandButton: {
    border: "none",
    borderTop: "1px solid #e2e8f0",
    background: "transparent",
    color: "#334155",
    fontWeight: 800,
    fontSize: "13px",
    padding: "10px 6px 0",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  promotionExpandIcon: {
    fontSize: "14px",
    lineHeight: 1,
  },
  catalogPanel: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe6f3",
    background:
      "linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(255,255,255,0.98) 100%)",
  },
  catalogPanelHeader: {
    display: "grid",
    gap: "4px",
  },
  catalogSelectGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  catalogInlineActions: {
    display: "flex",
    justifyContent: "flex-start",
    gap: "10px",
    flexWrap: "wrap",
  },
  selectedItemsList: {
    display: "grid",
    gap: "10px",
  },
  searchResultsPanel: {
    display: "grid",
    gap: "10px",
  },
  searchResultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  searchResultsTitle: {
    color: "#0f172a",
  },
  searchResultsMeta: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
  },
  searchResultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "12px",
  },
  manualComboGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "12px",
  },
  manualComboCard: {
    display: "grid",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
  },
  manualComboCardMain: {
    display: "grid",
    gap: "8px",
  },
  manualComboBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
  },
  searchResultCard: {
    display: "grid",
    gridTemplateColumns: "86px minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    alignItems: "center",
    gap: "6px 12px",
    textAlign: "left",
    minHeight: "112px",
    padding: "12px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
  },
  searchResultImage: {
    gridRow: "1 / span 2",
    width: "86px",
    height: "86px",
    objectFit: "contain",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f1f5f9",
  },
  searchResultPlaceholder: {
    gridRow: "1 / span 2",
    display: "grid",
    placeItems: "center",
    width: "86px",
    height: "86px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    background: "linear-gradient(135deg, #fff7ed 0%, #eff6ff 100%)",
    color: "#f97316",
    fontSize: "30px",
    fontWeight: 900,
  },
  searchResultCardActive: {
    border: "1px solid var(--brand-border, #93c5fd)",
    backgroundColor: "var(--brand-soft, #eff6ff)",
    boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.06)",
  },
  searchResultName: {
    gridColumn: "2 / 3",
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: 1.35,
  },
  searchResultMeta: {
    gridColumn: "2 / 3",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 600,
  },
  searchResultPrice: {
    gridColumn: "3 / 4",
    gridRow: "1 / span 2",
    justifySelf: "end",
    alignSelf: "center",
    color: "var(--brand-primary, #1d4ed8)",
    fontSize: "14px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  selectionResume: {
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid var(--brand-border, #bfdbfe)",
    backgroundColor: "var(--brand-soft, #eff6ff)",
    color: "var(--brand-primary, #1e3a8a)",
    fontSize: "13px",
    fontWeight: 600,
  },
  selectedItemCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
    padding: "14px 16px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 18px rgba(15, 23, 42, 0.03)",
  },
  manualCustomizationPanel: {
    display: "grid",
    gap: "12px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%)",
  },
  manualCustomizationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  manualCustomizationSummary: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  manualCustomizationChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 700,
  },
  manualCustomizationChipStrong: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 800,
  },
  manualIngredientList: {
    display: "grid",
    gap: "10px",
  },
  manualIngredientRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
  },
  manualIngredientImage: {
    width: "54px",
    height: "54px",
    flex: "0 0 54px",
    objectFit: "cover",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  manualIngredientInfo: {
    display: "grid",
    gap: "6px",
    flex: 1,
    minWidth: "220px",
  },
  manualIngredientNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  manualIngredientState: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#e2e8f0",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: 800,
  },
  manualIngredientControls: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  manualQuantityButton: {
    width: "36px",
    height: "36px",
    borderRadius: "999px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 800,
    cursor: "pointer",
  },
  manualQuantityValue: {
    minWidth: "28px",
    textAlign: "center",
    fontSize: "16px",
    fontWeight: 800,
  },
  fixedChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "999px",
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
  },
  manualTotalsRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  catalogNoticeError: {
    borderStyle: "solid",
    borderColor: "#fca5a5",
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
  },
  notificationCard: {
    borderRadius: "18px",
    border: "1px solid #dbeafe",
    background:
      "linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(255,255,255,0.98) 100%)",
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    boxShadow: "0 16px 30px rgba(37, 99, 235, 0.06)",
  },
  emptyNotice: {
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    backgroundColor: "#f8fafc",
  },
  servicePausedNotice: {
    marginBottom: "16px",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px solid #fecaca",
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    lineHeight: 1.55,
    fontWeight: 600,
    position: "relative",
    zIndex: 1,
  },
  manualOrderNotice: {
    marginBottom: "2px",
    padding: "14px 16px",
    borderRadius: "12px",
    fontWeight: 600,
  },
  manualOrderNoticeSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  manualOrderNoticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  manualOrderForm: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },
  manualField: {
    display: "grid",
    gap: "8px",
  },
  manualInput: {
    width: "100%",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    padding: "13px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#fbfdff",
    color: "#0f172a",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.03)",
  },
  fileInput: {
    width: "100%",
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    padding: "12px 14px",
    fontSize: "14px",
    boxSizing: "border-box",
    backgroundColor: "#f8fafc",
    color: "#334155",
  },
  manualTextarea: {
    width: "100%",
    minHeight: "126px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    padding: "13px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
    backgroundColor: "#fbfdff",
    color: "#0f172a",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.03)",
  },
  promotionFontPreview: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid #dbe6f3",
    background:
      "linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(255,255,255,0.98) 100%)",
  },
  promotionFontSample: {
    color: "#0f172a",
    fontSize: "22px",
    lineHeight: 1.15,
  },
  promotionImagePreviewCard: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "minmax(180px, 260px) minmax(0, 1fr)",
    gap: "14px",
    padding: "16px",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    alignItems: "center",
  },
  promotionImagePreview: {
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
  },
  promotionImagePreviewMeta: {
    display: "grid",
    gap: "8px",
    minWidth: 0,
  },
  scheduleResumeCard: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
  },
  scheduleResumeTitle: {
    color: "#0f172a",
    fontSize: "16px",
    lineHeight: 1.45,
  },
  scheduleModalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1200,
  },
  scheduleModalCard: {
    width: "100%",
    maxWidth: "1120px",
    maxHeight: "88vh",
    overflow: "auto",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    padding: "24px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,255,0.98) 100%)",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.24)",
  },
  manualProductModalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.52)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1240,
  },
  manualProductModalCard: {
    width: "100%",
    maxWidth: "860px",
    maxHeight: "88vh",
    overflow: "auto",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    padding: "24px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,255,0.98) 100%)",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.24)",
  },
  manualProductModalBody: {
    display: "grid",
    gap: "16px",
  },
  manualProductModalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  mesaDetailModalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.52)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1250,
  },
  mesaDetailModalCard: {
    width: "100%",
    maxWidth: "720px",
    maxHeight: "86vh",
    overflow: "auto",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    padding: "22px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,255,0.98) 100%)",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.24)",
  },
  assignmentModalCard: {
    width: "100%",
    maxWidth: "860px",
    maxHeight: "86vh",
    overflow: "auto",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    padding: "22px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,255,0.98) 100%)",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.24)",
  },
  mesaDetailModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  mesaDetailModalSubtitle: {
    marginTop: "8px",
    marginBottom: 0,
    color: "#64748b",
    lineHeight: 1.55,
    maxWidth: "560px",
  },
  mesaDetailModalBody: {
    marginTop: "20px",
    display: "grid",
    gap: "16px",
  },
  salonModalCard: {
    maxWidth: "1320px",
    maxHeight: "calc(100vh - 36px)",
    padding: "24px",
    overflowY: "auto",
  },
  salonModalBody: {
    marginTop: "20px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.65fr) minmax(320px, 0.75fr)",
    gap: "20px",
    alignItems: "stretch",
  },
  salonTableGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))",
    gap: "10px",
    maxHeight: "min(62vh, 560px)",
    overflowY: "auto",
    paddingRight: "4px",
  },
  salonTableButton: {
    minHeight: "116px",
    borderRadius: "16px",
    padding: "13px",
    textAlign: "left",
    display: "grid",
    gap: "7px",
    alignContent: "start",
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.06)",
  },
  salonTableButtonOperational: {
    border: "1px solid #86efac",
    background:
      "linear-gradient(180deg, rgba(240,253,244,0.98) 0%, rgba(220,252,231,0.92) 100%)",
  },
  salonTableButtonFree: {
    border: "1px solid #dbe2ea",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
  },
  salonTableButtonSelected: {
    borderColor: "#2563eb",
    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.16), 0 16px 30px rgba(15, 23, 42, 0.08)",
  },
  salonTableButtonHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  salonTableDot: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    backgroundColor: "#94a3b8",
    flex: "0 0 auto",
  },
  salonTableDotOperational: {
    backgroundColor: "#16a34a",
    boxShadow: "0 0 0 4px rgba(22, 163, 74, 0.13)",
  },
  salonTableBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "24px",
    padding: "0 8px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  salonTableBadgeOperational: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  salonTableBadgeFree: {
    backgroundColor: "#e2e8f0",
    color: "#475569",
  },
  salonTableName: {
    color: "#0f172a",
    fontSize: "17px",
    fontWeight: 900,
  },
  salonTableMeta: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 800,
    lineHeight: 1.35,
  },
  salonTableMetaOperational: {
    color: "#166534",
  },
  salonTableAmount: {
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 900,
  },
  salonDetailPanel: {
    minWidth: 0,
    maxHeight: "590px",
    overflowY: "auto",
    padding: "20px",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 18px 38px rgba(15,23,42,0.08)",
  },
  salonFreeDetail: {
    minHeight: "240px",
  },
  salonFreeBadge: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
  },
  salonEmptyState: {
    gridColumn: "1 / -1",
    padding: "18px",
    borderRadius: "16px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    fontWeight: 800,
  },
  salonModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
    paddingBottom: "18px",
    borderBottom: "1px solid #e2e8f0",
  },
  salonEyebrow: {
    color: "#f97316",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.12em",
  },
  salonModalTitle: {
    margin: "5px 0 0",
    color: "#0f172a",
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: "30px",
    lineHeight: 1.1,
  },
  salonHeaderActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },
  salonToolbarButton: {
    minHeight: "42px",
    padding: "0 14px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
  },
  salonToolbarButtonActive: {
    borderColor: "#fb923c",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
  },
  salonLegend: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap",
    marginTop: "13px",
  },
  salonLegendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    color: "#475569",
    fontSize: "12px",
    fontWeight: 800,
  },
  salonLegendDot: {
    width: "10px",
    height: "10px",
    borderRadius: "999px",
    display: "inline-block",
  },
  salonLegendDotFree: { backgroundColor: "#94a3b8" },
  salonLegendDotOperational: { backgroundColor: "#22c55e" },
  salonLegendDotReserved: { backgroundColor: "#f59e0b" },
  salonRoomTabs: {
    display: "flex",
    alignItems: "stretch",
    gap: "10px",
    marginTop: "16px",
    paddingBottom: "4px",
    overflowX: "auto",
  },
  salonRoomTab: {
    minWidth: "170px",
    minHeight: "58px",
    padding: "10px 14px",
    borderRadius: "15px",
    border: "1px solid #dbe2ea",
    display: "grid",
    gap: "3px",
    textAlign: "left",
    backgroundColor: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(15,23,42,0.05)",
  },
  salonRoomTabActive: {
    borderColor: "#fb923c",
    background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    color: "#9a3412",
    boxShadow: "0 10px 24px rgba(234,88,12,0.14)",
  },
  salonRoomTabName: {
    fontSize: "13px",
    fontWeight: 900,
    lineHeight: 1.2,
  },
  salonRoomTabMeta: {
    color: "#64748b",
    fontSize: "10px",
    fontWeight: 800,
  },
  salonRoomsManager: {
    display: "grid",
    gap: "14px",
    marginTop: "14px",
    padding: "16px",
    borderRadius: "20px",
    border: "1px solid #fed7aa",
    background: "linear-gradient(145deg, #fffaf5 0%, #ffffff 100%)",
    boxShadow: "0 14px 30px rgba(154,52,18,0.07)",
  },
  salonRoomsManagerHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
  },
  salonRoomsManagerTitle: {
    display: "block",
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: 900,
  },
  salonRoomsManagerHint: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.4,
  },
  salonRoomCreateForm: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  salonRoomInput: {
    width: "min(250px, 70vw)",
    minHeight: "42px",
    padding: "0 13px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "13px",
    outline: "none",
  },
  salonRoomAddButton: {
    minHeight: "42px",
    padding: "0 15px",
    borderRadius: "12px",
    border: "1px solid #ea580c",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonRoomList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "9px",
  },
  salonRoomListItem: {
    minWidth: 0,
    padding: "11px 12px",
    borderRadius: "13px",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    backgroundColor: "rgba(255,255,255,0.88)",
  },
  salonRoomListInfo: {
    minWidth: 0,
    display: "grid",
    gap: "3px",
    color: "#0f172a",
    fontSize: "12px",
  },
  salonRoomDeleteButton: {
    minHeight: "32px",
    padding: "0 10px",
    borderRadius: "9px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonRoomDeleteButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  salonTableAssignments: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "9px",
    maxHeight: "220px",
    overflowY: "auto",
    paddingTop: "2px",
  },
  salonTableAssignmentCard: {
    minWidth: 0,
    padding: "10px",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    display: "grid",
    gap: "7px",
    backgroundColor: "#ffffff",
  },
  salonTableAssignmentLabel: {
    color: "#334155",
    fontSize: "11px",
    fontWeight: 900,
  },
  salonTableAssignmentSelect: {
    width: "100%",
    minHeight: "36px",
    padding: "0 10px",
    borderRadius: "9px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: 800,
  },
  salonFloorSection: {
    minWidth: 0,
    display: "grid",
    gap: "12px",
  },
  salonFloorToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  salonFloorTitle: {
    display: "block",
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: 900,
  },
  salonFloorHint: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  salonEditBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "30px",
    padding: "0 11px",
    borderRadius: "999px",
    backgroundColor: "#ffedd5",
    color: "#c2410c",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.08em",
  },
  salonFixtureWorkspace: {
    display: "grid",
    gap: "10px",
    padding: "12px",
    borderRadius: "16px",
    border: "1px solid #fed7aa",
    background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)",
  },
  salonFixtureToolbar: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    flexWrap: "wrap",
  },
  salonFixtureToolbarLabel: {
    marginRight: "4px",
    color: "#9a3412",
    fontSize: "11px",
    fontWeight: 900,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  salonFixtureAddButton: {
    minHeight: "34px",
    padding: "0 11px",
    borderRadius: "10px",
    border: "1px solid #fdba74",
    backgroundColor: "#ffffff",
    color: "#9a3412",
    fontSize: "11px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonFixtureHelper: {
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.4,
  },
  salonFixtureEditor: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    alignItems: "center",
    gap: "12px",
    paddingTop: "10px",
    borderTop: "1px solid #fed7aa",
  },
  salonFixtureEditorIdentity: {
    minWidth: 0,
    display: "grid",
    gap: "2px",
  },
  salonFixtureEditorEyebrow: {
    color: "#c2410c",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.08em",
  },
  salonFixtureEditorTitle: {
    overflow: "hidden",
    color: "#0f172a",
    fontSize: "14px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  salonFixtureOrientationField: {
    minWidth: 0,
    display: "grid",
    gap: "5px",
  },
  salonFixtureOrientationLabel: {
    color: "#475569",
    fontSize: "10px",
    fontWeight: 800,
  },
  salonFixtureOrientationButtons: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px",
  },
  salonFixtureOrientationButton: {
    minHeight: "34px",
    padding: "0 9px",
    borderRadius: "9px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonFixtureOrientationButtonActive: {
    borderColor: "#f97316",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    boxShadow: "0 0 0 2px rgba(249,115,22,0.12)",
  },
  salonFixtureSizeField: {
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "40px minmax(100px, 1fr) 38px",
    alignItems: "center",
    gap: "8px",
    color: "#475569",
    fontSize: "10px",
    fontWeight: 800,
  },
  salonFixtureRange: {
    width: "100%",
    accentColor: "#f97316",
  },
  salonFixtureDeleteButton: {
    minHeight: "36px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonTableFormatPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    alignItems: "center",
    gap: "14px",
    paddingTop: "12px",
    borderTop: "1px solid #fed7aa",
  },
  salonTableFormatHeader: {
    minWidth: 0,
  },
  salonTableFormatEyebrow: {
    display: "block",
    color: "#c2410c",
    fontSize: "9px",
    fontWeight: 900,
    letterSpacing: "0.09em",
  },
  salonTableFormatTitle: {
    display: "block",
    marginTop: "3px",
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 900,
  },
  salonTableFormatDescription: {
    display: "block",
    marginTop: "3px",
    color: "#64748b",
    fontSize: "10px",
    lineHeight: 1.4,
  },
  salonTableShapeButtons: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "6px",
  },
  salonTableShapeButton: {
    minHeight: "36px",
    padding: "0 8px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonTableShapeButtonActive: {
    borderColor: "#f97316",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    boxShadow: "0 0 0 2px rgba(249,115,22,0.12)",
  },
  salonTableDimensionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "9px",
  },
  salonTableDimensionField: {
    minWidth: 0,
    display: "grid",
    gap: "6px",
  },
  salonTableDimensionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    color: "#475569",
    fontSize: "10px",
    fontWeight: 800,
  },
  salonTableDimensionRange: {
    width: "100%",
    accentColor: "#f97316",
  },
  salonTableRotationButton: {
    minHeight: "34px",
    borderRadius: "11px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonTableFormatActions: {
    gridColumn: "1 / -1",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexWrap: "wrap",
  },
  salonTableFormatApplyButton: {
    minHeight: "38px",
    padding: "0 14px",
    borderRadius: "11px",
    border: "1px solid #f97316",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonTableFormatApplySecondary: {
    minHeight: "38px",
    padding: "0 14px",
    borderRadius: "11px",
    border: "1px solid #fdba74",
    backgroundColor: "#ffffff",
    color: "#9a3412",
    fontSize: "10px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonTableIndividualEditor: {
    display: "grid",
    gap: "12px",
    marginTop: "14px",
    padding: "14px",
    borderRadius: "16px",
    border: "1px solid #fed7aa",
    background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 100%)",
  },
  salonFloor: {
    position: "relative",
    minHeight: "520px",
    overflow: "hidden",
    borderRadius: "26px",
    border: "1px solid #d8e1ec",
    background:
      "linear-gradient(rgba(255,255,255,0.82), rgba(248,250,252,0.9)), repeating-linear-gradient(0deg, transparent 0 39px, rgba(148,163,184,0.13) 40px), repeating-linear-gradient(90deg, transparent 0 39px, rgba(148,163,184,0.13) 40px)",
    boxShadow: "inset 0 0 0 8px rgba(248,250,252,0.75)",
    touchAction: "none",
  },
  salonFixture: {
    position: "absolute",
    zIndex: 1,
    transform: "translate(-50%, -50%)",
    minWidth: "54px",
    minHeight: "28px",
    padding: "6px 8px",
    borderRadius: "14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    overflow: "hidden",
    boxSizing: "border-box",
    textAlign: "center",
    userSelect: "none",
    touchAction: "none",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
  },
  salonFixtureVerticalDoor: {
    minWidth: "38px",
    padding: "8px 4px",
    borderRadius: "12px",
  },
  salonFixtureBar: {
    border: "2px solid #92400e",
    background: "repeating-linear-gradient(90deg, #fcd34d 0 18px, #fbbf24 18px 36px)",
    color: "#451a03",
    boxShadow: "0 12px 24px rgba(146,64,14,0.18)",
  },
  salonFixtureCounter: {
    border: "2px solid #475569",
    background: "linear-gradient(145deg, #e2e8f0 0%, #cbd5e1 100%)",
    color: "#0f172a",
    boxShadow: "0 12px 24px rgba(71,85,105,0.16)",
  },
  salonFixtureEntrance: {
    border: "2px dashed #0f766e",
    backgroundColor: "rgba(204,251,241,0.92)",
    color: "#115e59",
  },
  salonFixtureExit: {
    border: "2px dashed #be123c",
    backgroundColor: "rgba(255,228,230,0.94)",
    color: "#9f1239",
  },
  salonFixtureKitchen: {
    border: "2px solid #ea580c",
    background: "linear-gradient(145deg, #ffedd5 0%, #fed7aa 100%)",
    color: "#9a3412",
    boxShadow: "0 12px 24px rgba(234,88,12,0.16)",
  },
  salonFixtureSelected: {
    borderColor: "#2563eb",
    boxShadow: "0 0 0 4px rgba(37,99,235,0.18), 0 14px 28px rgba(15,23,42,0.18)",
  },
  salonFixtureLabel: {
    maxWidth: "100%",
    overflow: "hidden",
    fontSize: "10px",
    fontWeight: 900,
    lineHeight: 1.1,
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  salonFixtureLabelVertical: {
    maxWidth: "none",
    overflow: "visible",
    textOverflow: "clip",
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
  },
  salonFixtureMoveHint: {
    fontSize: "7px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    opacity: 0.72,
  },
  salonTableCircle: {
    position: "absolute",
    zIndex: 2,
    transform: "translate(-50%, -50%)",
    width: "88px",
    height: "88px",
    borderRadius: "50%",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    textAlign: "center",
    userSelect: "none",
    touchAction: "none",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
  },
  salonTableCircleOperational: {
    border: "2px solid #16a34a",
    background: "linear-gradient(145deg, #22c55e 0%, #15803d 100%)",
    color: "#ffffff",
    boxShadow: "0 16px 32px rgba(22,163,74,0.26)",
  },
  salonTableCircleFree: {
    border: "2px solid #cbd5e1",
    background: "linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%)",
    color: "#334155",
    boxShadow: "0 14px 28px rgba(71,85,105,0.14)",
  },
  salonTableCircleReserved: {
    border: "2px solid #f59e0b",
    background: "linear-gradient(145deg, #fef3c7 0%, #fbbf24 100%)",
    color: "#78350f",
    boxShadow: "0 14px 28px rgba(245,158,11,0.22)",
  },
  salonTableCircleSelected: {
    borderColor: "#1d4ed8",
    boxShadow: "0 0 0 4px rgba(37,99,235,0.18), 0 14px 28px rgba(15,23,42,0.18)",
  },
  salonCallBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "calc(100% - 10px)",
    padding: "3px 7px",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    color: "#b91c1c",
    fontSize: "9px",
    fontWeight: 900,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
    boxShadow: "0 3px 9px rgba(127,29,29,0.24)",
  },
  salonCircleName: { fontSize: "13px", fontWeight: 900, lineHeight: 1.1 },
  salonCircleState: { fontSize: "9px", fontWeight: 800, opacity: 0.9 },
  salonCircleMeta: {
    maxWidth: "68px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "8px",
    fontWeight: 700,
    opacity: 0.85,
  },
  salonDetailHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e2e8f0",
  },
  salonDetailEyebrow: {
    display: "block",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.1em",
  },
  salonDetailTitle: {
    margin: "4px 0 0",
    color: "#0f172a",
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: "25px",
    fontWeight: 900,
  },
  salonStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "30px",
    padding: "0 11px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 900,
  },
  salonStatusBadgeOperational: { backgroundColor: "#dcfce7", color: "#166534" },
  salonStatusBadgeFree: { backgroundColor: "#f1f5f9", color: "#475569" },
  salonStatusBadgeReserved: { backgroundColor: "#fef3c7", color: "#92400e" },
  salonCallPanel: {
    display: "grid",
    gap: "10px",
    padding: "14px",
    border: "1px solid #fca5a5",
    borderRadius: "16px",
    background: "linear-gradient(145deg, #fff7ed 0%, #fef2f2 100%)",
    boxShadow: "0 12px 28px rgba(185,28,28,0.12)",
  },
  salonCallPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  salonCallPanelEyebrow: {
    display: "block",
    color: "#dc2626",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.1em",
  },
  salonCallPanelTitle: {
    display: "block",
    marginTop: "3px",
    color: "#7f1d1d",
    fontSize: "16px",
  },
  salonCallPanelCount: {
    display: "grid",
    placeItems: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
  },
  salonCallRequest: {
    display: "grid",
    gap: "10px",
    paddingTop: "10px",
    borderTop: "1px solid rgba(248,113,113,0.3)",
  },
  salonCallRequestCopy: {
    display: "grid",
    gap: "4px",
    color: "#7f1d1d",
    fontSize: "13px",
  },
  salonCallCompleteButton: {
    minHeight: "40px",
    border: "none",
    borderRadius: "12px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonOperationalSummary: {
    marginTop: "16px",
    padding: "12px 14px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    fontWeight: 800,
  },
  salonAccountList: { display: "grid", gap: "10px", marginTop: "14px" },
  salonAccountCard: {
    display: "grid",
    gap: "12px",
    padding: "14px",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
  },
  salonAccountHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  salonAccountName: { display: "block", color: "#0f172a", fontSize: "15px", fontWeight: 900 },
  salonAccountMeta: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.4,
  },
  salonAccountAmount: { color: "#0f172a", fontSize: "14px", whiteSpace: "nowrap" },
  salonAccountActions: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  salonActionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "9px",
    marginTop: "14px",
  },
  salonPrimaryAction: {
    minHeight: "38px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #ea580c",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonSecondaryAction: {
    minHeight: "38px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonPrimaryActionLarge: {
    minHeight: "46px",
    padding: "0 13px",
    borderRadius: "12px",
    border: "1px solid #ea580c",
    backgroundColor: "#f97316",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonSecondaryActionLarge: {
    minHeight: "46px",
    padding: "0 13px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonDangerAction: {
    minHeight: "46px",
    padding: "0 13px",
    borderRadius: "12px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    fontSize: "12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonFreeMessage: {
    marginTop: "16px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    lineHeight: 1.55,
  },
  salonReservationCard: {
    display: "grid",
    gap: "6px",
    marginTop: "16px",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid #fcd34d",
    backgroundColor: "#fffbeb",
  },
  salonReservationCardActive: {
    borderColor: "#f59e0b",
    backgroundColor: "#fef3c7",
    boxShadow: "0 10px 24px rgba(217,119,6,0.12)",
  },
  salonReservationAgenda: {
    display: "grid",
    gap: "12px",
    margin: "14px 0 2px",
    padding: "16px",
    border: "1px solid #fde68a",
    borderRadius: "14px",
    backgroundColor: "#fffbeb",
  },
  salonReservationAgendaHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  salonReservationAgendaTitle: {
    display: "block",
    marginTop: "2px",
    color: "#78350f",
    fontSize: "16px",
  },
  salonReservationAgendaCount: {
    display: "grid",
    minWidth: "30px",
    minHeight: "30px",
    placeItems: "center",
    padding: "0 8px",
    borderRadius: "999px",
    backgroundColor: "#f59e0b",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: 900,
  },
  salonReservationAgendaList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "10px",
  },
  salonReservationAgendaItem: {
    display: "grid",
    gap: "6px",
    padding: "13px",
    border: "1px solid #fde68a",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
  },
  salonReservationAgendaItemBlocking: {
    borderColor: "#f59e0b",
    backgroundColor: "#fef3c7",
  },
  salonReservationAgendaItemHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
  },
  salonReservationAgendaMeta: {
    color: "#92400e",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  salonReservationAgendaEmpty: {
    padding: "14px",
    border: "1px dashed #fcd34d",
    borderRadius: "10px",
    color: "#92400e",
    fontSize: "13px",
  },
  salonReservationSchedule: {
    display: "grid",
    gap: "8px",
    marginTop: "16px",
  },
  salonReservationScheduleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 900,
  },
  salonReservationList: {
    display: "grid",
    gap: "8px",
  },
  salonReservationCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
  },
  salonReservationDate: {
    maxWidth: "150px",
    color: "#78350f",
    fontSize: "11px",
    lineHeight: 1.35,
    textAlign: "right",
  },
  salonReservationName: { color: "#78350f", fontSize: "17px" },
  salonReservationMeta: { color: "#92400e", fontSize: "12px", fontWeight: 700 },
  salonReservationNotes: { margin: "5px 0 0", color: "#78350f", fontSize: "12px", lineHeight: 1.45 },
  salonReservationActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "5px",
  },
  salonReservationEditButton: {
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #f59e0b",
    backgroundColor: "#ffffff",
    color: "#92400e",
    fontSize: "11px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonReservationDeleteButton: {
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    fontSize: "11px",
    fontWeight: 900,
    cursor: "pointer",
  },
  salonReservationForm: {
    display: "grid",
    gap: "11px",
    marginTop: "14px",
    padding: "15px",
    borderRadius: "16px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
  },
  salonReservationFormTitle: { color: "#9a3412", fontSize: "14px" },
  salonReservationColumns: { display: "grid", gridTemplateColumns: "0.65fr 1.35fr", gap: "9px" },
  salonReservationField: {
    display: "grid",
    gap: "5px",
    color: "#475569",
    fontSize: "11px",
    fontWeight: 800,
  },
  salonReservationInput: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "40px",
    padding: "9px 10px",
    borderRadius: "10px",
    border: "1px solid #fdba74",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    font: "inherit",
  },
  salonReservationCancel: {
    width: "100%",
    marginTop: "10px",
    padding: "8px",
    border: 0,
    background: "transparent",
    color: "#be123c",
    fontSize: "12px",
    fontWeight: 800,
    textDecoration: "underline",
    cursor: "pointer",
  },
  salonEmptyDetail: {
    minHeight: "300px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "28px",
    color: "#64748b",
    textAlign: "center",
    lineHeight: 1.5,
  },
  assignmentPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
  },
  assignmentAutoCard: {
    display: "grid",
    gap: "12px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
  },
  assignmentManualCard: {
    display: "grid",
    gap: "12px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  assignmentManualDeliveryCard: {
    display: "grid",
    gap: "12px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #fed7aa",
    background:
      "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
  },
  assignmentManualDeliveryNotice: {
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px dashed #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    lineHeight: 1.5,
    fontWeight: 600,
  },
  assignmentWaiterList: {
    display: "grid",
    gap: "10px",
  },
  assignmentWaiterRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    cursor: "pointer",
  },
  assignmentWaiterRowActive: {
    border: "1px solid #93c5fd",
    backgroundColor: "#eff6ff",
  },
  assignmentWaiterInfo: {
    display: "grid",
    gap: "2px",
  },
  mesaDetailCompactCard: {
    padding: "20px",
    boxShadow: "0 16px 32px rgba(15, 23, 42, 0.06)",
  },
  scheduleModalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  scheduleDayCard: {
    display: "grid",
    gap: "12px",
    padding: "16px",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.04)",
  },
  scheduleDayHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  scheduleDayToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    color: "#0f172a",
  },
  scheduleAllDayToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
  },
  scheduleTimeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  scheduleAllDayNotice: {
    padding: "12px 14px",
    borderRadius: "12px",
    backgroundColor: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#166534",
    fontWeight: 700,
    fontSize: "13px",
  },
  scheduleDisabledNotice: {
    padding: "12px 14px",
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    fontWeight: 600,
    fontSize: "13px",
  },
  manualPaymentPanel: {
    gridColumn: "1 / -1",
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "18px",
    border: "1px solid #dbe6f3",
    background:
      "linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(255,255,255,0.98) 100%)",
  },
  manualPaymentHeader: {
    display: "grid",
    gap: "4px",
  },
  manualPaymentTitle: {
    color: "#0f172a",
  },
  manualPaymentHint: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  manualPaymentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  manualFormActions: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  primaryInlineButton: {
    border: "none",
    borderRadius: "12px",
    padding: "11px 14px",
    background: "var(--brand-hero, linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%))",
    color: "var(--brand-secondary, #ffffff)",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 14px 24px rgba(15, 23, 42, 0.18)",
  },
  secondaryInlineButton: {
    border: "1px solid #dbe2ea",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
  },
  printerKitchenActionButton: {
    border: "1px solid #c4b5fd",
    borderRadius: "12px",
    padding: "11px 14px",
    background:
      "linear-gradient(180deg, rgba(250,245,255,1) 0%, rgba(243,232,255,0.98) 100%)",
    color: "#6d28d9",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(109, 40, 217, 0.12)",
  },
  manualKitchenActionButton: {
    border: "1px solid #fdba74",
    borderRadius: "12px",
    padding: "11px 14px",
    background:
      "linear-gradient(180deg, rgba(255,247,237,1) 0%, rgba(255,237,213,0.98) 100%)",
    color: "#c2410c",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(194, 65, 12, 0.12)",
  },
  dangerInlineButton: {
    border: "1px solid #fecaca",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    fontWeight: 800,
    cursor: "pointer",
  },
  serviceAlertToast: {
    position: "fixed",
    right: "20px",
    bottom: "24px",
    zIndex: 1350,
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    alignItems: "center",
    gap: "12px",
    width: "min(420px, calc(100vw - 32px))",
    padding: "14px 16px",
    borderRadius: "18px",
    border: "1px solid rgba(125, 211, 252, 0.55)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(240,249,255,0.98) 100%)",
    boxShadow: "0 22px 46px rgba(15, 23, 42, 0.16)",
  },
  serviceAlertDot: {
    width: "12px",
    height: "12px",
    borderRadius: "999px",
    backgroundColor: "#0ea5e9",
    boxShadow: "0 0 0 6px rgba(14, 165, 233, 0.16)",
  },
  serviceAlertContent: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
  },
  serviceAlertTitle: {
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: 1.3,
  },
  serviceAlertText: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  serviceAlertClose: {
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontSize: "22px",
    lineHeight: 1,
    cursor: "pointer",
    padding: 0,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
  },
  metricCard: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    borderRadius: "18px",
    padding: "20px",
    border: "1px solid #dbe2ea",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    position: "relative",
    overflow: "hidden",
    isolation: "isolate",
  },
  metricCardLive: {
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.94) 38%, rgba(248,250,252,0.96) 100%)",
  },
  metricCardInteractive: {
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    userSelect: "none",
  },
  metricCardActive: {
    border: "1px solid #93c5fd",
    boxShadow: "0 0 0 1px rgba(37, 99, 235, 0.14)",
    backgroundColor: "#f8fbff",
  },
  metricWaiterCallBadge: {
    position: "absolute",
    top: "14px",
    right: "14px",
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    minHeight: "25px",
    padding: "0 9px",
    borderRadius: "999px",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    boxShadow: "0 7px 16px rgba(185,28,28,0.28)",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.04em",
  },
  metricLiveFrame: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 0,
    overflow: "hidden",
  },
  metricLiveWash: {
    position: "absolute",
    inset: 0,
    opacity: 0.98,
  },
  metricLiveGlow: {
    position: "absolute",
    borderRadius: "999px",
    filter: "blur(14px)",
    opacity: 0.98,
  },
  metricLiveGlowTop: {
    top: "-10px",
    left: 18,
    right: 18,
    height: 22,
    animation: "inicioMetricFlowHorizontal 7.2s ease-in-out infinite",
  },
  metricLiveGlowBottom: {
    bottom: "-10px",
    left: 24,
    right: 24,
    height: 20,
    animation: "inicioMetricFlowHorizontal 7.2s ease-in-out infinite reverse",
  },
  metricLiveGlowLeft: {
    top: 18,
    bottom: 18,
    left: "-10px",
    width: 18,
    animation: "inicioMetricFlowVertical 7.2s ease-in-out infinite",
  },
  metricLiveGlowRight: {
    top: 24,
    bottom: 24,
    right: "-10px",
    width: 18,
    animation: "inicioMetricFlowVertical 7.2s ease-in-out infinite reverse",
  },
  metricLiveSheen: {
    position: "absolute",
    inset: "-20% auto -20% -45%",
    width: "58%",
    transform: "rotate(16deg)",
    opacity: 0.64,
    filter: "blur(0.4px)",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
    position: "relative",
    zIndex: 1,
  },
  metricValue: {
    fontSize: "30px",
    color: "#0f172a",
    lineHeight: 1.05,
    position: "relative",
    zIndex: 1,
  },
  metricDetail: {
    color: "#475569",
    fontSize: "13px",
    position: "relative",
    zIndex: 1,
  },
  metricLinkButton: {
    display: "inline-flex",
    alignSelf: "flex-start",
    padding: 0,
    margin: 0,
    border: "none",
    background: "none",
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    position: "relative",
    zIndex: 1,
  },
  detailPanel: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,255,0.98) 100%)",
    borderRadius: "22px",
    border: "1px solid #dbe2ea",
    padding: "24px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.05)",
  },
  detailPanelPrimary: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, var(--brand-soft, rgba(239,246,255,0.98)) 34%, var(--brand-page, rgba(239,246,255,0.95)) 100%)",
    border: "2px solid var(--brand-primary, #2563eb)",
    boxShadow:
      "0 0 0 4px var(--brand-soft, rgba(239,246,255,0.72)), 0 30px 62px rgba(15, 23, 42, 0.12)",
  },
  detailPanelPrimaryTitle: {
    color: "var(--brand-primary, #2563eb)",
    fontSize: "44px",
    lineHeight: 1.02,
    letterSpacing: 0,
  },
  detailPanelPrimarySubtitle: {
    color: "var(--brand-primary, #2563eb)",
    fontSize: "15px",
    fontWeight: 700,
    maxWidth: "860px",
  },
  detailSubtitle: {
    marginTop: "8px",
    marginBottom: 0,
    color: "#64748b",
    maxWidth: "720px",
    lineHeight: 1.55,
  },
  closeButton: {
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  closeButtonPrimary: {
    border: "1px solid var(--brand-primary, #2563eb)",
    backgroundColor: "var(--brand-soft, rgba(239,246,255,0.92))",
    color: "var(--brand-primary, #2563eb)",
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.08)",
  },
  detailList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
    marginTop: "20px",
  },
  detailEmptyState: {
    gridColumn: "1 / -1",
    padding: "20px",
    borderRadius: "18px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    fontWeight: 600,
    textAlign: "center",
  },
  orderReportPanel: {
    marginTop: "18px",
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "20px",
    border: "1px solid #dbe6f3",
    background:
      "linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(255,255,255,0.98) 100%)",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.04)",
  },
  orderReportHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  orderReportTitle: {
    color: "#0f172a",
    display: "block",
    fontSize: "16px",
    lineHeight: 1.3,
  },
  orderReportHint: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.55,
  },
  orderReportSummary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "36px",
    padding: "0 14px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  orderReportFiltersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  orderReportStatusHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  orderReportStatusActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  orderReportStatusGrid: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  orderReportStatusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "999px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
  },
  orderReportStatusChipActive: {
    border: "1px solid #93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  orderReportFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  orderReportEmptyHint: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  manualKitchenSummaryStrip: {
    marginTop: "18px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
  },
  manualKitchenSummaryCard: {
    display: "grid",
    gap: "6px",
    padding: "16px 18px",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.05)",
  },
  manualKitchenSummaryPending: {
    border: "1px solid #fdba74",
    background:
      "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
  },
  manualKitchenSummaryConfirmed: {
    border: "1px solid #fca5a5",
    background:
      "linear-gradient(180deg, rgba(255,251,251,1) 0%, rgba(255,245,245,0.98) 100%)",
  },
  manualKitchenSummaryReady: {
    border: "1px solid #86efac",
    background:
      "linear-gradient(180deg, rgba(248,255,250,1) 0%, rgba(240,253,244,0.98) 100%)",
  },
  manualKitchenSummaryLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 800,
  },
  manualKitchenSummaryValue: {
    color: "#0f172a",
    fontSize: "28px",
    lineHeight: 1,
  },
  manualKitchenSummaryHint: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.45,
    fontWeight: 600,
  },
  detailCard: {
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    padding: "18px",
    background:
      "linear-gradient(180deg, rgba(251,253,255,1) 0%, rgba(255,255,255,1) 100%)",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.035)",
  },
  pendingKitchenCard: {
    position: "relative",
    overflow: "hidden",
    gap: "16px",
  },
  pendingKitchenAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
  },
  detailCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  detailTitle: {
    color: "#0f172a",
  },
  detailMeta: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "13px",
  },
  detailBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  detailLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  detailText: {
    color: "#1e293b",
    lineHeight: 1.5,
  },
  pendingKitchenHeaderMeta: {
    display: "grid",
    justifyItems: "end",
    gap: "8px",
  },
  pendingKitchenStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.01em",
  },
  pendingKitchenSummaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "10px",
  },
  pendingKitchenInfoCard: {
    display: "grid",
    gap: "6px",
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(226, 232, 240, 0.9)",
  },
  pendingKitchenInfoText: {
    color: "#334155",
    fontSize: "13px",
    lineHeight: 1.45,
    fontWeight: 700,
  },
  pendingKitchenModeValue: {
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: 1.4,
    fontWeight: 800,
  },
  pendingKitchenHighlightBox: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "16px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
    border: "1px solid rgba(226, 232, 240, 0.95)",
  },
  pendingKitchenAlertBox: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "16px",
    background:
      "linear-gradient(180deg, rgba(255,247,237,0.98) 0%, rgba(255,237,213,0.96) 100%)",
    border: "1px solid #fdba74",
  },
  pendingKitchenHighlightLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 800,
  },
  pendingKitchenHighlightText: {
    color: "#0f172a",
    lineHeight: 1.5,
    fontWeight: 700,
  },
  pendingKitchenAlertText: {
    color: "#9a3412",
    lineHeight: 1.5,
    fontWeight: 800,
  },
  pendingKitchenNextStepBox: {
    display: "grid",
    gap: "8px",
    padding: "14px 16px",
    borderRadius: "16px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
    border: "1px dashed #cbd5e1",
  },
  pendingKitchenNextStepText: {
    color: "#334155",
    lineHeight: 1.55,
    fontWeight: 700,
  },
  pendingKitchenFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  pricingBreakdown: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  pricingBreakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#1e293b",
  },
  pricingAdjustmentList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "4px",
  },
  pricingAdjustmentItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "8px 10px",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    color: "#475569",
    fontSize: "13px",
  },
  comboList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  comboChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontWeight: 700,
    fontSize: "12px",
  },
  progressRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  progressValue: {
    color: "#0f172a",
    fontSize: "14px",
  },
  itemList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  itemWorkflowGroup: {
    display: "grid",
    gap: "8px",
    marginTop: "10px",
  },
  itemWorkflowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    color: "#475569",
    fontSize: "12px",
  },
  itemToggle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "10px 12px",
    borderRadius: "12px",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 600,
  },
  itemTogglePending: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#1e293b",
  },
  itemToggleDelivered: {
    border: "1px solid #86efac",
    backgroundColor: "#ecfdf5",
    color: "#166534",
  },
  itemToggleLocked: {
    opacity: 0.74,
    cursor: "not-allowed",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  directDeliveryActionButton: {
    border: "1px solid #0f766e",
    borderRadius: "10px",
    padding: "10px 12px",
    backgroundColor: "#f0fdfa",
    color: "#115e59",
    fontWeight: 800,
    cursor: "pointer",
  },
  paymentPendingCloseNotice: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "40px",
    padding: "9px 12px",
    border: "1px solid #f59e0b",
    borderRadius: "10px",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    fontSize: "13px",
    fontWeight: 800,
  },
  localPaymentClosePanel: {
    display: "grid",
    gap: "10px",
    width: "100%",
  },
  localPaymentCloseButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    width: "100%",
  },
  localPaymentCloseButton: {
    flex: "1 1 220px",
    minHeight: "44px",
    borderRadius: "10px",
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },
  cashPaymentCloseButton: {
    border: "1px solid #15803d",
    backgroundColor: "#f0fdf4",
    color: "#166534",
  },
  cardPaymentCloseButton: {
    border: "1px solid #1d4ed8",
    backgroundColor: "#eff6ff",
    color: "#1e40af",
  },
  localPaymentCloseButtonDisabled: {
    cursor: "not-allowed",
    opacity: 0.62,
  },
  localPaymentCloseHint: {
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  directDeliveryAssignmentSummary: {
    display: "grid",
    gap: "6px",
    padding: "12px 14px",
    border: "1px solid #99f6e4",
    borderRadius: "10px",
    backgroundColor: "#f0fdfa",
    color: "#134e4a",
  },
  readyBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 12px",
    borderRadius: "999px",
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #86efac",
    fontWeight: 700,
    fontSize: "12px",
  },
  closeOrderButton: {
    border: "none",
    borderRadius: "10px",
    padding: "10px 12px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  elapsedBadge: {
    padding: "8px 10px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "15px 16px",
    background:
      "linear-gradient(180deg, rgba(251,253,255,1) 0%, rgba(255,255,255,1) 100%)",
  },
  listTitle: {
    color: "#0f172a",
  },
  listSubtitle: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "13px",
  },
  listBadge: {
    padding: "8px 11px",
    borderRadius: "999px",
    backgroundColor: "#e0e7ff",
    color: "#4338ca",
    fontWeight: 800,
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  identityBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "18px",
  },
  identityRow: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    color: "#0f172a",
    padding: "14px 16px",
    borderRadius: "16px",
    backgroundColor: "#fbfdff",
    border: "1px solid #e2e8f0",
  },
  identityLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },
  tagWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  tag: {
    display: "inline-flex",
    padding: "9px 13px",
    borderRadius: "999px",
    backgroundColor: "#ecfeff",
    color: "#0f766e",
    fontWeight: 800,
    fontSize: "13px",
  },
  emptyText: {
    color: "#64748b",
  },
  alertList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  alertItem: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    color: "#334155",
    padding: "14px 16px",
    borderRadius: "16px",
    background:
      "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)",
    border: "1px solid #e2e8f0",
  },
  alertMark: {
    color: "#2563eb",
    fontWeight: 800,
  },
};

export default Inicio;


